import { useState, useEffect, useRef, createContext, useContext } from "react";
import "@/App.css";
import { BrowserRouter, Routes, Route, useNavigate, useLocation, Navigate, useSearchParams } from "react-router-dom";
import axios from "axios";
import { Toaster } from "@/components/ui/toaster";
import { useToast } from "@/hooks/use-toast";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from "@/components/ui/dialog";
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from "@/components/ui/alert-dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Badge } from "@/components/ui/badge";
import { Separator } from "@/components/ui/separator";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Switch } from "@/components/ui/switch";
import { Checkbox } from "@/components/ui/checkbox";
import { Accordion, AccordionItem, AccordionTrigger, AccordionContent } from "@/components/ui/accordion";
import { Calendar as CalendarComponent } from "@/components/ui/calendar";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Command, CommandEmpty, CommandGroup, CommandInput, CommandItem, CommandList } from "@/components/ui/command";
import { format } from "date-fns";
import { ptBR } from "date-fns/locale";
import { 
  LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer, Area, ComposedChart, Bar,
  PieChart as RechartsPieChart, Pie
} from "recharts";
import { 
  Users, Package, Truck, LayoutDashboard, LogOut, Plus, X,
  Pencil, Trash2, Printer, User, Building2, Building, Tag, ChevronRight,
  TrendingUp, TrendingDown, DollarSign, AlertCircle, Calendar,
  FileText, PieChart, BarChart3, CreditCard, Receipt, Coins,
  CheckCircle, Clock, XCircle, Copy, ChevronDown, ChevronUp, Lock,
  UserPlus, Power, HeadphonesIcon, Share2, Shield, FileQuestion, HelpCircle, ExternalLink,
  Settings, Download, Monitor, Edit, MessageCircle, Send, BookOpen, Calculator, MessageSquare, Wrench,
  Briefcase, MapPin, Phone, Mail, FileCheck, AlertTriangle, IdCard, Check, ChevronsUpDown, ShoppingCart, RefreshCw, Link2,
  Sun, Moon, Eye
} from "lucide-react";
import {
  Header as LandingHeader,
  Hero,
  Features,
  Screenshots,
  Testimonials,
  FAQ,
  CTA,
  Footer as LandingFooter
} from "@/components/landing";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// ==================== APP VERSION ====================
const APP_VERSION = "2.1.4";

// ==================== LOCAL STORAGE KEYS ====================
const LS_DASHBOARD_MONTH = 'dre_dashboard_month';
const LS_DASHBOARD_YEAR = 'dre_dashboard_year';
const LS_SELECTED_COMPANY = 'dre_selected_company';
const LS_THEME = 'dre_theme';

// ==================== THEME CONTEXT ====================
const ThemeContext = createContext();

export const useTheme = () => {
  const context = useContext(ThemeContext);
  if (!context) {
    throw new Error('useTheme must be used within a ThemeProvider');
  }
  return context;
};

const ThemeProvider = ({ children }) => {
  const [theme, setTheme] = useState(() => {
    // Recuperar tema do localStorage ou usar 'dark' como padrão
    const savedTheme = localStorage.getItem(LS_THEME);
    return savedTheme || 'dark';
  });

  useEffect(() => {
    // Aplicar tema ao elemento HTML
    const root = document.documentElement;
    if (theme === 'light') {
      root.classList.add('light');
      root.classList.remove('dark');
    } else {
      root.classList.remove('light');
      root.classList.add('dark');
    }
    // Salvar no localStorage
    localStorage.setItem(LS_THEME, theme);
  }, [theme]);

  const toggleTheme = () => {
    setTheme(prev => prev === 'dark' ? 'light' : 'dark');
  };

  return (
    <ThemeContext.Provider value={{ theme, setTheme, toggleTheme }}>
      {children}
    </ThemeContext.Provider>
  );
};

// ==================== AXIOS INTERCEPTOR FOR SESSION EXPIRATION ====================
// Configure axios to redirect to login on 401 (session expired)
axios.interceptors.response.use(
  (response) => response,
  (error) => {
    if (error.response?.status === 401) {
      // Session expired - redirect to login
      const currentPath = window.location.pathname;
      // Only redirect if not already on login/auth pages
      if (!currentPath.includes('/login') && !currentPath.includes('/auth') && !currentPath.includes('/admin')) {
        console.log('Session expired, redirecting to login...');
        // Clear any stored session data
        localStorage.removeItem(LS_SELECTED_COMPANY);
        // Redirect to login
        window.location.href = '/login';
        return new Promise(() => {}); // Return pending promise to prevent further processing
      }
    }
    return Promise.reject(error);
  }
);

// ==================== PLAN DEFINITIONS ====================
const PLANS = {
  1: { id: 1, name: "Básico", price: 0, description: "Gratuito" },
  2: { id: 2, name: "Advanced", price: 39.90, description: "R$ 39,90/mês" },
  3: { id: 3, name: "Contábil", price: 69.90, description: "R$ 69,90/mês" }
};

// Company limits per plan
const COMPANY_LIMITS = {
  1: 2,  // Básico: 2 empresas (1 pessoal + 1)
  2: 4,  // Advanced: 4 empresas (1 pessoal + 3)
  3: 6   // Contábil: 6 empresas (1 pessoal + 5)
};

// Features for each plan (for upsell)
const PLAN_FEATURES = {
  2: [
    "Criação de Orçamentos",
    "Envio de Recibos",
    "Checklist de Serviços",
    "Projeção de Caixa 360",
    "Relatório de Contas a Pagar",
    "Exportação dos Relatórios para PDF",
    "Gráfico Pizza para seus DRE"
  ],
  3: [
    "Escanear e Guardar Comprovantes e NF",
    "Grave por Voz Despesas e Receitas",
    "Relatório de Contas a Receber",
    "Relatórios de Caixa",
    "Manutenção do Plano de Contas",
    "Cobrança de Serviço Executado",
    "Envio da O.S. por E-mail ou WhatsApp em PDF"
  ]
};

// Menu items that require specific plans (items not listed are available in plan 1)
const MENU_PLAN_REQUIREMENTS = {
  'orcamentos': 2,
  'recibos': 2,
  'checklist': 2,
  'produtividade': 2,
  'contas-pagar': 2,
  'chart-accounts': 3,
  'report-chart-accounts': 3,
  'comprovantes': 3,
  'voz': 3,
  'contas-receber': 3,
  'caixa': 3,
  'cobranca': 3,
  'os-envio': 3,
  'audit-logs': 1  // Available for all plans
};

axios.defaults.withCredentials = true;

// ==================== MOBILE DETECTION HOOK ====================
const useIsMobile = () => {
  const [isMobile, setIsMobile] = useState(window.innerWidth < 768);
  
  useEffect(() => {
    const handleResize = () => setIsMobile(window.innerWidth < 768);
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);
  
  return isMobile;
};

// ==================== HELPERS ====================

const DDI_OPTIONS = [
  { code: "+55", country: "Brasil" },
  { code: "+1", country: "EUA/Canadá" },
  { code: "+351", country: "Portugal" },
  { code: "+34", country: "Espanha" },
  { code: "+44", country: "Reino Unido" },
];

const CLIENT_TYPES = ["Prospect", "Proposta", "Efetivo", "Inativo"];

const MONTHS = [
  "Janeiro", "Fevereiro", "Março", "Abril", "Maio", "Junho",
  "Julho", "Agosto", "Setembro", "Outubro", "Novembro", "Dezembro"
];

const formatPhone = (value) => {
  if (!value) return "";
  const numbers = value.replace(/\D/g, "");
  if (numbers.length <= 2) return `(${numbers}`;
  if (numbers.length <= 7) return `(${numbers.slice(0, 2)}) ${numbers.slice(2)}`;
  if (numbers.length <= 11) return `(${numbers.slice(0, 2)}) ${numbers.slice(2, 7)}-${numbers.slice(7)}`;
  return `(${numbers.slice(0, 2)}) ${numbers.slice(2, 7)}-${numbers.slice(7, 11)}`;
};

const unformatPhone = (value) => value.replace(/\D/g, "");

const getWhatsAppUrl = (ddi, phone) => {
  if (!phone) return "#";
  const cleanPhone = unformatPhone(phone);
  const cleanDdi = ddi ? ddi.replace("+", "") : "55";
  const message = encodeURIComponent("Olá! Quero mais informações.");
  return `https://wa.me/${cleanDdi}${cleanPhone}?text=${message}`;
};

const openWhatsApp = (ddi, phone) => {
  if (!phone) return;
  const cleanPhone = unformatPhone(phone);
  const cleanDdi = ddi ? ddi.replace("+", "") : "55";
  const message = encodeURIComponent("Olá! Quero mais informações.");
  const url = `https://wa.me/${cleanDdi}${cleanPhone}?text=${message}`;
  
  // Cria um elemento <a> temporário e simula o clique para evitar bloqueios
  const link = document.createElement('a');
  link.href = url;
  link.target = '_blank';
  link.rel = 'noopener noreferrer';
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
};

const formatCurrency = (value) => {
  return new Intl.NumberFormat('pt-BR', {
    style: 'currency',
    currency: 'BRL'
  }).format(value || 0);
};

const formatDate = (dateStr) => {
  if (!dateStr) return "";
  // Already in DD/MM/YYYY format
  if (dateStr.includes("/")) return dateStr;
  // Convert from ISO
  const date = new Date(dateStr);
  return date.toLocaleDateString('pt-BR');
};

// Format date with 2-digit year for mobile (DD/MM/YY)
const formatDateShort = (dateStr) => {
  if (!dateStr) return "";
  if (dateStr.includes("/")) {
    const parts = dateStr.split("/");
    if (parts.length === 3) {
      return `${parts[0]}/${parts[1]}/${parts[2].slice(-2)}`;
    }
  }
  const date = new Date(dateStr);
  const day = String(date.getDate()).padStart(2, '0');
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const year = String(date.getFullYear()).slice(-2);
  return `${day}/${month}/${year}`;
};

// Format date with abbreviated day of week (Seg, Ter, Qua, etc.)
const formatDateWithWeekday = (dateStr) => {
  if (!dateStr) return "";
  const weekDays = ['Dom', 'Seg', 'Ter', 'Qua', 'Qui', 'Sex', 'Sáb'];
  let date;
  if (dateStr.includes("/")) {
    const [d, m, y] = dateStr.split("/");
    date = new Date(y, parseInt(m) - 1, parseInt(d));
  } else {
    date = new Date(dateStr + 'T00:00:00');
  }
  const dayOfWeek = weekDays[date.getDay()];
  return `${dateStr} (${dayOfWeek})`;
};

const parseDate = (dateStr) => {
  if (!dateStr) return "";
  if (dateStr.includes("/")) {
    const [d, m, y] = dateStr.split("/");
    return `${y}-${m.padStart(2, '0')}-${d.padStart(2, '0')}`;
  }
  return dateStr;
};

const toInputDate = (dateStr) => {
  if (!dateStr) return "";
  if (dateStr.includes("-")) return dateStr;
  const [d, m, y] = dateStr.split("/");
  return `${y}-${m}-${d}`;
};

const fromInputDate = (dateStr) => {
  if (!dateStr) return "";
  const [y, m, d] = dateStr.split("-");
  return `${d}/${m}/${y}`;
};

const WhatsAppIcon = ({ className }) => (
  <svg className={className} viewBox="0 0 24 24" fill="currentColor">
    <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z"/>
  </svg>
);

// Eye icons for password visibility
const EyeIcon = ({ className }) => (
  <svg className={className} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
    <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z"/>
    <circle cx="12" cy="12" r="3"/>
  </svg>
);

const EyeOffIcon = ({ className }) => (
  <svg className={className} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
    <path d="M17.94 17.94A10.07 10.07 0 0 1 12 20c-7 0-11-8-11-8a18.45 18.45 0 0 1 5.06-5.94M9.9 4.24A9.12 9.12 0 0 1 12 4c7 0 11 8 11 8a18.5 18.5 0 0 1-2.16 3.19m-6.72-1.07a3 3 0 1 1-4.24-4.24"/>
    <line x1="1" y1="1" x2="23" y2="23"/>
  </svg>
);

// ==================== COOKIE CONSENT BANNER (LGPD) ====================
const CookieConsentBanner = () => {
  const [showBanner, setShowBanner] = useState(false);

  useEffect(() => {
    // Check if user has already consented
    const consent = localStorage.getItem('dre_cookie_consent');
    if (!consent) {
      setShowBanner(true);
    }
  }, []);

  const handleAccept = () => {
    localStorage.setItem('dre_cookie_consent', JSON.stringify({
      accepted: true,
      date: new Date().toISOString()
    }));
    setShowBanner(false);
  };

  const handleDecline = () => {
    localStorage.setItem('dre_cookie_consent', JSON.stringify({
      accepted: false,
      date: new Date().toISOString()
    }));
    setShowBanner(false);
  };

  if (!showBanner) return null;

  return (
    <div 
      className="fixed bottom-0 left-0 right-0 z-50 bg-card/98 backdrop-blur-lg border-t border-border shadow-lg animate-fadeIn"
      data-testid="cookie-consent-banner"
    >
      <div className="max-w-7xl mx-auto px-4 py-4">
        <div className="flex flex-col md:flex-row items-start md:items-center gap-4">
          <div className="flex-1">
            <div className="flex items-center gap-2 mb-1">
              <svg className="w-5 h-5 text-primary" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" />
              </svg>
              <span className="font-semibold text-foreground">Aviso de Privacidade</span>
            </div>
            <p className="text-sm text-muted-foreground">
              Utilizamos cookies essenciais para garantir o funcionamento adequado da aplicação, 
              manter sua sessão ativa e salvar suas preferências. Ao continuar navegando, você 
              concorda com nossa{' '}
              <a href="/privacidade" className="text-primary underline hover:text-primary/80">Política de Privacidade</a>
              {' '}e{' '}
              <a href="/termos" className="text-primary underline hover:text-primary/80">Termos de Uso</a>.
            </p>
          </div>
          <div className="flex gap-2 w-full md:w-auto">
            <Button 
              variant="outline" 
              size="sm"
              onClick={handleDecline}
              className="flex-1 md:flex-none"
              data-testid="cookie-decline-btn"
            >
              Recusar
            </Button>
            <Button 
              size="sm"
              onClick={handleAccept}
              className="flex-1 md:flex-none"
              data-testid="cookie-accept-btn"
            >
              Aceitar Cookies
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
};

// ==================== EMAIL REGISTRATION FLOW ====================
const EmailRegistrationFlow = ({ onComplete, onBack }) => {
  const [step, setStep] = useState(1);
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    password: '',
    confirmPassword: ''
  });
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const { toast } = useToast();
  const navigate = useNavigate();

  const handleNext = async () => {
    setError('');
    
    if (step === 1) {
      // Step 1: Email - Check if exists
      if (!formData.email.trim()) {
        setError('Por favor, digite seu e-mail');
        return;
      }
      if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) {
        setError('Por favor, digite um e-mail válido');
        return;
      }
      
      // Check if email already exists
      setLoading(true);
      try {
        const response = await axios.get(`${API}/auth/check-email?email=${encodeURIComponent(formData.email)}`);
        if (response.data.exists) {
          // Email exists, redirect to login
          localStorage.setItem('dre_remembered_email', formData.email);
          toast({ 
            title: "E-mail já cadastrado", 
            description: "Redirecionando para o login...",
            variant: "default"
          });
          setTimeout(() => {
            onComplete('login', formData.email);
          }, 1500);
          return;
        }
      } catch (err) {
        console.error('Error checking email:', err);
      } finally {
        setLoading(false);
      }
      
      setStep(2);
    } else if (step === 2) {
      // Step 2: Name
      if (!formData.name.trim()) {
        setError('Por favor, digite seu nome');
        return;
      }
      setStep(3);
    } else if (step === 3) {
      if (!formData.password) {
        setError('Por favor, digite uma senha');
        return;
      }
      if (formData.password.length < 6) {
        setError('A senha deve ter pelo menos 6 caracteres');
        return;
      }
      setStep(4);
    } else if (step === 4) {
      if (!formData.confirmPassword) {
        setError('Por favor, confirme sua senha');
        return;
      }
      if (formData.password !== formData.confirmPassword) {
        setError('As senhas não coincidem');
        return;
      }
      
      // Register user
      setLoading(true);
      try {
        const response = await axios.post(`${API}/auth/register`, {
          name: formData.name,
          email: formData.email,
          password: formData.password
        });
        
        // Save email to localStorage for future logins
        localStorage.setItem('dre_remembered_email', formData.email);
        
        toast({ title: "Conta criada com sucesso!" });
        navigate('/dashboard', { state: { user: response.data }, replace: true });
      } catch (err) {
        if (err.response?.status === 409) {
          setError('Este e-mail já está cadastrado');
          localStorage.setItem('dre_remembered_email', formData.email);
          setTimeout(() => {
            onComplete('login', formData.email);
          }, 1500);
        } else {
          setError(err.response?.data?.detail || 'Erro ao criar conta');
        }
      } finally {
        setLoading(false);
      }
    }
  };

  const handleBack = () => {
    if (step > 1) {
      setStep(step - 1);
      setError('');
    } else {
      onBack();
    }
  };

  const ProgressIndicator = () => (
    <div className="flex gap-2 justify-center mb-8">
      {[1, 2, 3, 4].map((s) => (
        <div 
          key={s} 
          className={`h-1 w-16 rounded-full transition-all ${
            s <= step ? 'bg-primary' : 'bg-muted'
          }`}
        />
      ))}
    </div>
  );

  return (
    <div className="min-h-screen bg-background flex flex-col">
      {/* Header with back button */}
      <div className="p-4">
        <button 
          onClick={handleBack}
          className="text-foreground hover:text-primary transition-colors"
          data-testid="back-btn"
        >
          <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
          </svg>
        </button>
      </div>

      {/* Main content */}
      <div className="flex-1 flex flex-col px-6 pt-4">
        <ProgressIndicator />
        
        {/* Step 1: Email */}
        {step === 1 && (
          <div className="space-y-6 animate-fadeIn">
            <div>
              <p className="text-lg text-muted-foreground">Bem vindo ao <span className="text-primary font-bold">DRE +</span></p>
              <h2 className="text-2xl font-bold text-foreground mt-1">Qual é o seu e-mail?</h2>
            </div>
            <div>
              <Input
                type="email"
                placeholder="Digite aqui seu e-mail"
                value={formData.email}
                onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                className="h-12 text-base border-0 border-b-2 border-primary/30 focus:border-primary rounded-none bg-transparent placeholder:text-muted-foreground/50"
                autoFocus
                data-testid="email-input"
              />
              <p className="text-xs text-muted-foreground mt-2">
                Vamos verificar se você já possui cadastro.
              </p>
            </div>
            {error && <p className="text-destructive text-sm">{error}</p>}
          </div>
        )}

        {/* Step 2: Name */}
        {step === 2 && (
          <div className="space-y-6 animate-fadeIn">
            <div>
              <p className="text-lg text-muted-foreground">
                E-mail: <span className="text-foreground">{formData.email}</span>
              </p>
              <h2 className="text-2xl font-bold text-foreground mt-1">Qual é o seu nome?</h2>
            </div>
            <div>
              <Input
                type="text"
                placeholder="Digite aqui seu nome"
                value={formData.name}
                onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                className="h-12 text-base border-0 border-b-2 border-primary/30 focus:border-primary rounded-none bg-transparent placeholder:text-muted-foreground/50"
                autoFocus
                data-testid="name-input"
              />
            </div>
            {error && <p className="text-destructive text-sm">{error}</p>}
          </div>
        )}

        {/* Step 3: Create Password */}
        {step === 3 && (
          <div className="space-y-6 animate-fadeIn">
            <div>
              <p className="text-sm text-muted-foreground">
                Olá, <span className="text-foreground font-semibold">{formData.name}</span>!
              </p>
              <p className="text-sm text-muted-foreground">
                Seu e-mail: <span className="text-foreground">{formData.email}</span>
              </p>
              <h2 className="text-2xl font-bold text-foreground mt-2">Crie sua senha!</h2>
            </div>
            <div className="relative">
              <Input
                type={showPassword ? "text" : "password"}
                placeholder="Digite a senha aqui"
                value={formData.password}
                onChange={(e) => setFormData({ ...formData, password: e.target.value })}
                className="h-12 text-base border-0 border-b-2 border-primary/30 focus:border-primary rounded-none bg-transparent placeholder:text-muted-foreground/50 pr-12"
                autoFocus
                data-testid="password-input"
              />
              <button
                type="button"
                onClick={() => setShowPassword(!showPassword)}
                className="absolute right-2 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground"
              >
                {showPassword ? <EyeOffIcon className="w-5 h-5" /> : <EyeIcon className="w-5 h-5" />}
              </button>
            </div>
            <p className="text-xs text-muted-foreground">
              • A senha deve ter pelo menos 6 caracteres.
            </p>
            {error && <p className="text-destructive text-sm">{error}</p>}
          </div>
        )}

        {/* Step 4: Confirm Password */}
        {step === 4 && (
          <div className="space-y-6 animate-fadeIn">
            <div>
              <h2 className="text-2xl font-bold text-foreground">Confirme sua senha!</h2>
            </div>
            <div className="relative">
              <Input
                type={showConfirmPassword ? "text" : "password"}
                placeholder="Confirme a senha aqui"
                value={formData.confirmPassword}
                onChange={(e) => setFormData({ ...formData, confirmPassword: e.target.value })}
                className="h-12 text-base border-0 border-b-2 border-primary/30 focus:border-primary rounded-none bg-transparent placeholder:text-muted-foreground/50 pr-12"
                autoFocus
                data-testid="confirm-password-input"
              />
              <button
                type="button"
                onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                className="absolute right-2 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground"
              >
                {showConfirmPassword ? <EyeOffIcon className="w-5 h-5" /> : <EyeIcon className="w-5 h-5" />}
              </button>
            </div>
            {error && <p className="text-destructive text-sm">{error}</p>}
          </div>
        )}
      </div>

      {/* Footer - extra padding-bottom for mobile to avoid cookie banner overlap */}
      <div className="p-6 pb-56 md:pb-6 space-y-4">
        {step === 4 && (
          <p className="text-xs text-center text-muted-foreground">
            Ao se cadastrar você concorda com nossos{' '}
            <a href="/termos" className="underline hover:text-foreground">Termos de Uso</a>
            {' '}e{' '}
            <a href="/privacidade" className="underline hover:text-foreground">Política de Privacidade</a>
          </p>
        )}
        <Button 
          onClick={handleNext} 
          className="w-full h-12 text-base font-semibold"
          disabled={loading}
          data-testid="advance-btn"
        >
          {loading ? (
            <div className="animate-spin rounded-full h-5 w-5 border-2 border-background border-t-transparent" />
          ) : (
            'AVANÇAR'
          )}
        </Button>
        <p className="text-xs text-center text-muted-foreground">Versão {APP_VERSION}</p>
      </div>
    </div>
  );
};

// ==================== EMAIL LOGIN PAGE ====================
const EmailLoginPage = ({ prefillEmail, onBack }) => {
  const [email, setEmail] = useState(prefillEmail || '');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const { toast } = useToast();
  const navigate = useNavigate();

  const handleLogin = async () => {
    setError('');
    
    if (!email.trim()) {
      setError('Por favor, digite seu e-mail');
      return;
    }
    if (!password) {
      setError('Por favor, digite sua senha');
      return;
    }

    setLoading(true);
    try {
      // Primeiro tenta login como usuário master
      const response = await axios.post(`${API}/auth/login`, { email, password });
      
      // Update remembered email
      localStorage.setItem('dre_remembered_email', email);
      
      toast({ title: "Login realizado com sucesso!" });
      navigate('/dashboard', { state: { user: response.data }, replace: true });
    } catch (err) {
      // Se falhou, tenta login como sub-usuário
      try {
        const subResponse = await axios.post(`${API}/auth/sub-user/login`, { email, password });
        
        // Salvar token de sessão no cookie
        const maxAge = 7 * 24 * 60 * 60; // 7 dias
        document.cookie = `session_token=${subResponse.data.session_token}; path=/; max-age=${maxAge}; SameSite=Lax`;
        
        localStorage.setItem('dre_remembered_email', email);
        
        toast({ title: "Login realizado com sucesso!" });
        
        // Redirecionar - o ProtectedRoute vai buscar os dados do usuário via /auth/me
        window.location.href = '/dashboard';
      } catch (subErr) {
        setError(err.response?.data?.detail || 'E-mail ou senha incorretos');
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-background flex flex-col">
      {/* Header with back button */}
      <div className="p-4">
        <button 
          onClick={onBack}
          className="text-foreground hover:text-primary transition-colors"
          data-testid="back-btn"
        >
          <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
          </svg>
        </button>
      </div>

      {/* Main content */}
      <div className="flex-1 flex flex-col px-6 pt-8">
        <div className="text-center mb-8">
          <img 
            src="/logo.png" 
            alt="DRE+ Logo" 
            className="w-20 h-20 mx-auto mb-2"
            style={{ 
              filter: 'drop-shadow(0 0 8px rgba(255,255,255,0.9)) drop-shadow(0 0 15px rgba(255,255,255,0.6))' 
            }}
          />
          <p className="text-muted-foreground">Entre com seu e-mail e senha</p>
        </div>

        <div className="space-y-4">
          <div>
            <Label className="text-muted-foreground">E-mail</Label>
            <Input
              type="email"
              placeholder="Digite seu e-mail"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              className="h-12 text-base mt-1"
              autoFocus={!prefillEmail}
              data-testid="login-email-input"
            />
          </div>
          
          <div>
            <Label className="text-muted-foreground">Senha</Label>
            <div className="relative">
              <Input
                type={showPassword ? "text" : "password"}
                placeholder="Digite sua senha"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                className="h-12 text-base mt-1 pr-12"
                autoFocus={!!prefillEmail}
                data-testid="login-password-input"
                onKeyPress={(e) => e.key === 'Enter' && handleLogin()}
              />
              <button
                type="button"
                onClick={() => setShowPassword(!showPassword)}
                className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground"
              >
                {showPassword ? <EyeOffIcon className="w-5 h-5" /> : <EyeIcon className="w-5 h-5" />}
              </button>
            </div>
            <a 
              href="/forgot-password" 
              className="text-sm text-primary hover:underline block text-right mt-1"
              data-testid="forgot-password-link"
            >
              Esqueci minha senha
            </a>
          </div>

          {error && <p className="text-destructive text-sm">{error}</p>}
        </div>
      </div>

      {/* Footer - extra padding-bottom for mobile to avoid cookie banner overlap */}
      <div className="p-6 pb-56 md:pb-6 space-y-4">
        <Button 
          onClick={handleLogin} 
          className="w-full h-12 text-base font-semibold"
          disabled={loading}
          data-testid="login-submit-btn"
        >
          {loading ? (
            <div className="animate-spin rounded-full h-5 w-5 border-2 border-background border-t-transparent" />
          ) : (
            'ENTRAR'
          )}
        </Button>
        <p className="text-xs text-center text-muted-foreground">Versão {APP_VERSION}</p>
      </div>
    </div>
  );
};

// ==================== LOGIN PAGE ====================
const LoginPage = () => {
  const [view, setView] = useState('main'); // 'main', 'register', 'login'
  const [prefillEmail, setPrefillEmail] = useState('');
  const navigate = useNavigate();

  // Check for remembered email on mount
  useEffect(() => {
    const rememberedEmail = localStorage.getItem('dre_remembered_email');
    if (rememberedEmail) {
      setPrefillEmail(rememberedEmail);
      setView('login');
    }
  }, []);

  const handleGoogleLogin = () => {
    const redirectUrl = window.location.origin + '/dashboard';
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
  };

  const handleEmailClick = () => {
    setView('register');
  };

  const handleRegistrationComplete = (action, email) => {
    if (action === 'login') {
      setPrefillEmail(email || '');
      setView('login');
    }
  };

  const handleBackToMain = () => {
    setView('main');
    setPrefillEmail('');
  };

  // Show registration flow
  if (view === 'register') {
    return (
      <EmailRegistrationFlow 
        onComplete={handleRegistrationComplete}
        onBack={handleBackToMain}
      />
    );
  }

  // Show email login page
  if (view === 'login') {
    return (
      <EmailLoginPage 
        prefillEmail={prefillEmail}
        onBack={handleBackToMain}
      />
    );
  }

  // Main login page with Google and Email options
  return (
    <div className="min-h-screen login-background flex items-center justify-center p-4 pb-36 md:pb-4">
      <div className="absolute inset-0 bg-black/60 backdrop-blur-sm"></div>
      <Card className="w-full max-w-md relative z-10 bg-card/95 backdrop-blur border-border" data-testid="login-card">
        <CardHeader className="text-center space-y-4">
          <div className="mx-auto">
            <img 
              src="/logo.png" 
              alt="DRE+ Logo" 
              className="w-24 h-24 mx-auto"
              style={{ 
                filter: 'drop-shadow(0 0 8px rgba(255,255,255,0.9)) drop-shadow(0 0 15px rgba(255,255,255,0.6))' 
              }}
              data-testid="app-logo"
            />
          </div>
          <CardDescription className="text-lg text-muted-foreground" data-testid="app-subtitle">
            Controle Financeiro para Empreendedores
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <Separator />
          <Button onClick={handleGoogleLogin} className="w-full h-12 text-base gap-3" data-testid="google-login-btn">
            <svg className="w-5 h-5" viewBox="0 0 24 24">
              <path fill="currentColor" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"/>
              <path fill="currentColor" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/>
              <path fill="currentColor" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"/>
              <path fill="currentColor" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"/>
            </svg>
            Entrar com Google
          </Button>
          <Button 
            onClick={handleEmailClick} 
            variant="outline" 
            className="w-full h-12 text-base gap-3" 
            data-testid="email-login-btn"
          >
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
            </svg>
            Usar um E-mail
          </Button>
        </CardContent>
        <div className="pb-4 text-center">
          <span className="text-xs text-muted-foreground" data-testid="app-version">Versão {APP_VERSION}</span>
        </div>
      </Card>
    </div>
  );
};

// ==================== AUTH CALLBACK ====================
const AuthCallback = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const hasProcessed = useRef(false);

  useEffect(() => {
    if (hasProcessed.current) return;
    hasProcessed.current = true;

    const processAuth = async () => {
      const hash = location.hash;
      const sessionId = new URLSearchParams(hash.replace('#', '?')).get('session_id');
      if (!sessionId) { navigate('/login'); return; }
      try {
        const response = await axios.post(`${API}/auth/session`, { session_id: sessionId });
        navigate('/dashboard', { state: { user: response.data }, replace: true });
      } catch (error) {
        navigate('/login');
      }
    };
    processAuth();
  }, [location, navigate]);

  return (
    <div className="min-h-screen flex items-center justify-center bg-background">
      <div className="text-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary mx-auto"></div>
        <p className="mt-4 text-muted-foreground">Autenticando...</p>
      </div>
    </div>
  );
};

// ==================== PROTECTED ROUTE ====================
const ProtectedRoute = ({ children }) => {
  const [isAuthenticated, setIsAuthenticated] = useState(null);
  const [user, setUser] = useState(null);
  const navigate = useNavigate();

  useEffect(() => {
    const checkAuth = async () => {
      try {
        // Always fetch complete user data from /auth/me
        const response = await axios.get(`${API}/auth/me`);
        setUser(response.data);
        setIsAuthenticated(true);
      } catch (error) {
        console.log('Auth check failed, redirecting to login');
        setIsAuthenticated(false);
        navigate('/login', { replace: true });
      }
    };
    checkAuth();
  }, [navigate]);

  if (isAuthenticated === null) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary"></div>
      </div>
    );
  }
  if (!isAuthenticated) return null;
  return children({ user });
};

// ==================== GLOBAL MENU ITEMS ====================
const GLOBAL_MENU_ITEMS = [
  { id: 'dashboard', label: 'Dashboard', icon: LayoutDashboard },
  { 
    id: 'cadastros', 
    label: 'Cadastros', 
    icon: Users,
    submenu: [
      { id: 'profile', label: 'Perfil', icon: User },
      { id: 'empresas', label: 'Contas', icon: Building },
      { id: 'wallets', label: 'Carteiras', icon: CreditCard },
      { id: 'separator-cadastros', isSeparator: true },
      { 
        id: 'administrativo', 
        label: 'Administrativo', 
        icon: Building2,
        isSubmenu: true,
        children: [
          { id: 'sub-users', label: 'Usuários', icon: UserPlus, masterOnly: true },
          { id: 'clients', label: 'Clientes', icon: Users },
          { id: 'suppliers', label: 'Fornecedores', icon: Truck },
          { id: 'products', label: 'Produtos e Serviços', icon: Package },
          { id: 'employees', label: 'Funcionários', icon: Briefcase },
        ]
      },
    ]
  },
  { 
    id: 'lancamentos', 
    label: 'Lançamentos', 
    icon: CreditCard,
    submenu: [
      { id: 'despesas', label: 'Despesas', icon: TrendingDown },
      { id: 'receitas', label: 'Receitas', icon: TrendingUp },
      { id: 'custos', label: 'Custos', icon: Coins },
      { id: 'separator-lancamentos', isSeparator: true },
      { 
        id: 'vendas', 
        label: 'Vendas', 
        icon: ShoppingCart,
        isSubmenu: true,
        children: [
          { id: 'frente-caixa', label: 'Frente de Caixa', icon: Monitor },
          { id: 'orcamentos', label: 'Orçamentos', icon: FileText, requiresPlan: 2 },
          { id: 'recibos', label: 'Recibos', icon: Receipt, requiresPlan: 2 },
        ]
      },
    ]
  },
  { 
    id: 'relatorios', 
    label: 'Relatórios', 
    icon: BarChart3,
    submenu: [
      { id: 'reports', label: 'Fluxo de Caixa', icon: BarChart3 },
      { id: 'livro-caixa', label: 'Projeção de Caixa', icon: BookOpen, requiresPlan: 2 },
      { id: 'contas-pagar', label: 'Contas a Pagar', icon: CreditCard, requiresPlan: 2 },
      { id: 'contas-receber', label: 'Contas a Receber', icon: TrendingUp, requiresPlan: 3 },
      { id: 'separator-relatorios', isSeparator: true },
      { 
        id: 'auditoria', 
        label: 'Auditoria', 
        icon: Shield,
        isSubmenu: true,
        children: [
          { id: 'report-chart-accounts', label: 'Plano de Contas', icon: Receipt, requiresPlan: 3 },
          { id: 'audit-logs', label: 'Logs', icon: FileText },
        ]
      },
    ]
  },
  { id: 'dre', label: 'DRE', icon: FileText },
  { 
    id: 'suporte', 
    label: 'Suporte', 
    icon: HeadphonesIcon,
    submenu: [
      { id: 'chat-ajuda', label: 'Chat de Ajuda', icon: MessageCircle },
      { id: 'indique', label: 'Indique o DRE+', icon: Share2 },
      { id: 'instalar-app', label: 'Instalar App', icon: Download },
      { id: 'privacidade-link', label: 'Privacidade', icon: Shield },
      { id: 'termos-link', label: 'Termos e Condições', icon: FileQuestion },
      { id: 'ajuda', label: 'Central de Ajuda', icon: HelpCircle },
      { id: 'sair', label: 'Sair', icon: LogOut },
    ]
  },
];

// ==================== HEADER NAVIGATION ====================
const Header = ({ user, currentPage, onNavigate, userPlan = 1, onLockedMenuClick, menuItemsFiltered }) => {
  const navigate = useNavigate();
  const { toast } = useToast();
  const { theme, toggleTheme } = useTheme();
  const [openMenu, setOpenMenu] = useState(null);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [deferredPrompt, setDeferredPrompt] = useState(null);
  const [isAppInstalled, setIsAppInstalled] = useState(false);
  const menuTimeoutRef = useRef(null);
  
  // Usar menuItems filtrado se passado, senão usar global
  const activeMenuItems = menuItemsFiltered || GLOBAL_MENU_ITEMS;

  // Menu hover handlers with delay to prevent accidental closing
  const handleMenuEnter = (menuId) => {
    if (menuTimeoutRef.current) {
      clearTimeout(menuTimeoutRef.current);
      menuTimeoutRef.current = null;
    }
    setOpenMenu(menuId);
  };

  const handleMenuLeave = () => {
    menuTimeoutRef.current = setTimeout(() => {
      setOpenMenu(null);
    }, 150); // 150ms delay before closing
  };

  // Cleanup timeout on unmount
  useEffect(() => {
    return () => {
      if (menuTimeoutRef.current) {
        clearTimeout(menuTimeoutRef.current);
      }
    };
  }, []);

  // PWA Install prompt handler
  useEffect(() => {
    // Check if already installed
    if (window.matchMedia('(display-mode: standalone)').matches) {
      setIsAppInstalled(true);
    }

    const handleBeforeInstallPrompt = (e) => {
      e.preventDefault();
      setDeferredPrompt(e);
    };

    const handleAppInstalled = () => {
      setIsAppInstalled(true);
      setDeferredPrompt(null);
      toast({ title: "App instalado com sucesso!" });
    };

    window.addEventListener('beforeinstallprompt', handleBeforeInstallPrompt);
    window.addEventListener('appinstalled', handleAppInstalled);

    return () => {
      window.removeEventListener('beforeinstallprompt', handleBeforeInstallPrompt);
      window.removeEventListener('appinstalled', handleAppInstalled);
    };
  }, [toast]);

  const handleInstallApp = async () => {
    if (deferredPrompt) {
      deferredPrompt.prompt();
      const { outcome } = await deferredPrompt.userChoice;
      if (outcome === 'accepted') {
        toast({ title: "Instalando o DRE+..." });
      }
      setDeferredPrompt(null);
    } else if (!isAppInstalled) {
      // Show manual instructions for browsers that don't support beforeinstallprompt
      toast({ 
        title: "Como instalar o DRE+",
        description: "No Chrome: Menu (⋮) → 'Instalar app'. No Edge: Menu (⋯) → 'Apps' → 'Instalar'.",
      });
    }
  };

  const handleLogout = async () => {
    try {
      await axios.post(`${API}/auth/logout`);
      navigate('/login');
    } catch (error) {
      toast({ title: "Erro ao sair", variant: "destructive" });
    }
  };

  // Handle share via WhatsApp
  const handleIndique = () => {
    const message = encodeURIComponent(
      "Olá. Estou recomendando a aplicação DRE+ para você testar. Tenha um maior controle das finanças com essa aplicação útil e fácil de usar. Saiba mais em https://www.dremais.com.br"
    );
    window.open(`https://wa.me/?text=${message}`, '_blank');
  };

  // Handle logout with proper redirect based on auth type
  const handleSupportLogout = async () => {
    try {
      await axios.post(`${API}/auth/logout`);
      navigate('/login');
    } catch (error) {
      toast({ title: "Erro ao sair", variant: "destructive" });
    }
  };

  const handleNavigate = (page) => {
    // Handle special Suporte menu items
    if (page === 'indique') {
      handleIndique();
      setOpenMenu(null);
      setMobileMenuOpen(false);
      return;
    }
    if (page === 'instalar-app') {
      handleInstallApp();
      setOpenMenu(null);
      setMobileMenuOpen(false);
      return;
    }
    if (page === 'privacidade-link') {
      navigate('/privacidade');
      setOpenMenu(null);
      setMobileMenuOpen(false);
      return;
    }
    if (page === 'termos-link') {
      navigate('/termos');
      setOpenMenu(null);
      setMobileMenuOpen(false);
      return;
    }
    if (page === 'ajuda') {
      window.open('https://www.dremais.com.br/ajuda', '_blank');
      setOpenMenu(null);
      setMobileMenuOpen(false);
      return;
    }
    if (page === 'sair') {
      handleSupportLogout();
      setOpenMenu(null);
      setMobileMenuOpen(false);
      return;
    }

    // Check if menu item requires a higher plan
    const requiredPlan = MENU_PLAN_REQUIREMENTS[page];
    if (requiredPlan && requiredPlan > userPlan) {
      if (onLockedMenuClick) {
        onLockedMenuClick(requiredPlan, page);
      }
      return;
    }
    onNavigate(page);
    setOpenMenu(null);
    setMobileMenuOpen(false);
  };

  // Check if a menu item is locked
  const isLocked = (itemId) => {
    const requiredPlan = MENU_PLAN_REQUIREMENTS[itemId];
    return requiredPlan && requiredPlan > userPlan;
  };

  // State for expanded nested submenus
  const [expandedSubmenu, setExpandedSubmenu] = useState(null);

  const toggleNestedSubmenu = (submenuId, e) => {
    e.stopPropagation();
    setExpandedSubmenu(expandedSubmenu === submenuId ? null : submenuId);
  };

  const isActive = (item) => {
    if (item.id === currentPage) return true;
    if (item.submenu) {
      return item.submenu.some(sub => {
        if (sub.id === currentPage) return true;
        if (sub.children) {
          return sub.children.some(child => child.id === currentPage);
        }
        return false;
      });
    }
    return false;
  };

  return (
    <header className="fixed top-0 left-0 right-0 h-16 bg-card/95 backdrop-blur border-b border-border z-50" data-testid="header">
      <div className="h-full max-w-7xl mx-auto px-4 flex items-center justify-between">
        {/* Logo + Greeting (mobile) */}
        <div className="flex items-center gap-2">
          <img 
            src="/logo.png" 
            alt="DRE+" 
            className="w-10 h-10 cursor-pointer hover:scale-105 transition-transform" 
            style={{ 
              filter: 'drop-shadow(0 0 4px rgba(255,255,255,0.9)) drop-shadow(0 0 8px rgba(255,255,255,0.5))' 
            }}
            onClick={() => onNavigate('dashboard')}
            data-testid="header-logo" 
          />
          <span className="hidden sm:block text-xs text-muted-foreground cursor-pointer" onClick={() => onNavigate('dashboard')}>Controle Financeiro</span>
          {/* Mobile greeting */}
          <span className="sm:hidden text-sm text-muted-foreground">
            Olá, <span className="text-foreground font-medium">{user?.name?.split(' ')[0]}</span>
          </span>
        </div>

        {/* Desktop Menu */}
        <nav className="hidden md:flex items-center gap-1">
          {activeMenuItems.map((item) => (
            <div key={item.id} className="relative">
              {item.submenu ? (
                <div 
                  className="relative"
                  onMouseEnter={() => handleMenuEnter(item.id)}
                  onMouseLeave={handleMenuLeave}
                >
                  <button
                    className={`flex items-center gap-2 px-4 py-2 rounded-lg transition-all ${
                      isActive(item)
                        ? 'bg-primary/20 text-primary'
                        : 'text-muted-foreground hover:bg-muted hover:text-foreground'
                    }`}
                    data-testid={`nav-${item.id}`}
                  >
                    <item.icon className="w-4 h-4" />
                    <span className="font-medium text-sm">{item.label}</span>
                    <ChevronDown className={`w-4 h-4 transition-transform ${openMenu === item.id ? 'rotate-180' : ''}`} />
                  </button>
                  
                  {/* Dropdown */}
                  {openMenu === item.id && (
                    <div 
                      className="absolute top-full left-0 pt-1 w-56 z-50"
                      onMouseEnter={() => handleMenuEnter(item.id)}
                      onMouseLeave={handleMenuLeave}
                    >
                      <div className="bg-white dark:bg-slate-900/95 backdrop-blur-sm border border-slate-200 dark:border-slate-700 rounded-lg shadow-xl py-2 animate-fadeIn">
                        {item.submenu.map((sub) => {
                          // Render separator
                          if (sub.isSeparator) {
                            return <hr key={sub.id} className="my-2 border-border" />;
                          }
                          // Render header (non-clickable)
                          if (sub.isHeader) {
                            return (
                              <div key={sub.id} className="px-4 py-2 text-xs font-semibold text-primary uppercase tracking-wider">
                                {sub.label}
                              </div>
                            );
                          }
                          // Render nested submenu (collapsible)
                          if (sub.isSubmenu && sub.children) {
                            const isExpanded = expandedSubmenu === sub.id;
                            const hasActiveChild = sub.children.some(child => child.id === currentPage);
                            return (
                              <div key={sub.id}>
                                <button
                                  onClick={(e) => toggleNestedSubmenu(sub.id, e)}
                                  className={`w-full flex items-center gap-3 px-4 py-2 text-left transition-all ${
                                    hasActiveChild
                                      ? 'bg-primary/10 text-primary'
                                      : 'text-muted-foreground hover:bg-muted hover:text-foreground'
                                  }`}
                                >
                                  <sub.icon className="w-4 h-4" />
                                  <span className="text-sm font-medium">{sub.label}</span>
                                  <ChevronDown className={`w-3 h-3 ml-auto transition-transform ${isExpanded ? 'rotate-180' : ''}`} />
                                </button>
                                {isExpanded && (
                                  <div className="bg-slate-100 dark:bg-slate-800/50 py-1">
                                    {sub.children.map((child) => {
                                      const childLocked = isLocked(child.id);
                                      return (
                                        <button
                                          key={child.id}
                                          onClick={() => handleNavigate(child.id)}
                                          className={`w-full flex items-center gap-3 pl-8 pr-4 py-2 text-left transition-all ${
                                            childLocked
                                              ? 'text-muted-foreground/70 hover:bg-muted/50'
                                              : currentPage === child.id
                                                ? 'bg-primary/20 text-primary'
                                                : 'text-muted-foreground hover:bg-muted hover:text-foreground'
                                          }`}
                                          data-testid={`nav-${child.id}`}
                                        >
                                          <child.icon className="w-4 h-4" />
                                          <span className="text-sm">{child.label}</span>
                                          {childLocked && <Lock className="w-3 h-3 ml-auto text-muted-foreground/50" />}
                                        </button>
                                      );
                                    })}
                                  </div>
                                )}
                              </div>
                            );
                          }
                          const locked = isLocked(sub.id);
                          return (
                            <button
                              key={sub.id}
                              onClick={() => handleNavigate(sub.id)}
                              className={`w-full flex items-center gap-3 px-4 py-2 text-left transition-all ${
                                locked
                                  ? 'text-muted-foreground/70 hover:bg-muted/50'
                                  : currentPage === sub.id
                                    ? 'bg-primary/20 text-primary'
                                    : 'text-muted-foreground hover:bg-muted hover:text-foreground'
                              }`}
                              data-testid={`nav-${sub.id}`}
                            >
                              <sub.icon className="w-4 h-4" />
                              <span className="text-sm">{sub.label}</span>
                              {locked && <Lock className="w-3 h-3 ml-auto text-muted-foreground/50" />}
                            </button>
                          );
                        })}
                      </div>
                    </div>
                  )}
                </div>
              ) : (
                <button
                  onClick={() => handleNavigate(item.id)}
                  className={`flex items-center gap-2 px-4 py-2 rounded-lg transition-all ${
                    isActive(item)
                      ? 'bg-primary/20 text-primary'
                      : 'text-muted-foreground hover:bg-muted hover:text-foreground'
                  }`}
                  data-testid={`nav-${item.id}`}
                >
                  <item.icon className="w-4 h-4" />
                  <span className="font-medium text-sm">{item.label}</span>
                </button>
              )}
            </div>
          ))}
        </nav>

        {/* User Menu */}
        <div className="flex items-center gap-3">
          {/* Theme Toggle Button */}
          <Button 
            variant="ghost" 
            size="icon" 
            onClick={toggleTheme}
            className="hidden sm:flex"
            data-testid="theme-toggle-btn"
            title={theme === 'dark' ? 'Mudar para tema claro' : 'Mudar para tema escuro'}
          >
            {theme === 'dark' ? (
              <Sun className="w-4 h-4 text-amber-400" />
            ) : (
              <Moon className="w-4 h-4 text-slate-600" />
            )}
          </Button>
          
          <button 
            onClick={() => onNavigate('profile')}
            className="hidden sm:flex items-center gap-2 hover:opacity-80 transition-opacity cursor-pointer"
            data-testid="user-profile-btn"
            title="Meu Perfil"
          >
            {user?.picture ? (
              <img src={user.picture} alt="" className="w-8 h-8 rounded-full" />
            ) : (
              <div className="w-8 h-8 rounded-full bg-primary/20 flex items-center justify-center">
                <User className="w-4 h-4 text-primary" />
              </div>
            )}
            <span className="text-sm font-medium hidden lg:block">{user?.name?.split(' ')[0]}</span>
          </button>
          <Button variant="ghost" size="icon" onClick={handleSupportLogout} className="hidden md:flex" data-testid="logout-btn" title="Sair">
            <LogOut className="w-4 h-4" />
          </Button>
          
          {/* Mobile Menu Button */}
          <button 
            className="md:hidden flex items-center gap-1 px-2 py-1 rounded-md hover:bg-muted transition-colors"
            onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
            data-testid="mobile-menu-btn"
          >
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              {mobileMenuOpen ? (
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              ) : (
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 12h16M4 18h16" />
              )}
            </svg>
            <span className="text-sm font-medium">Menu</span>
          </button>
        </div>
      </div>

      {/* Mobile Menu */}
      {mobileMenuOpen && (
        <div className="md:hidden absolute top-16 left-0 right-0 bg-card border-b border-border shadow-lg animate-fadeIn">
          <nav className="p-4 space-y-2">
            {activeMenuItems.map((item) => (
              <div key={item.id}>
                {item.submenu ? (
                  <div>
                    <button
                      onClick={() => setOpenMenu(openMenu === item.id ? null : item.id)}
                      className={`w-full flex items-center justify-between gap-2 px-4 py-3 rounded-lg transition-all ${
                        isActive(item)
                          ? 'bg-primary/20 text-primary'
                          : 'text-muted-foreground hover:bg-muted hover:text-foreground'
                      }`}
                    >
                      <div className="flex items-center gap-3">
                        <item.icon className="w-5 h-5" />
                        <span className="font-medium">{item.label}</span>
                      </div>
                      <ChevronDown className={`w-4 h-4 transition-transform ${openMenu === item.id ? 'rotate-180' : ''}`} />
                    </button>
                    
                    {openMenu === item.id && (
                      <div className="ml-2 mt-2 space-y-1 bg-white dark:bg-slate-800/50 rounded-lg p-2 border border-slate-200 dark:border-slate-700/50 shadow-md">
                        {item.submenu.map((sub) => {
                          // Render separator for mobile
                          if (sub.isSeparator) {
                            return <hr key={sub.id} className="my-2 border-border" />;
                          }
                          // Render header (non-clickable)
                          if (sub.isHeader) {
                            return (
                              <div key={sub.id} className="px-4 py-2 text-xs font-semibold text-primary uppercase tracking-wider">
                                {sub.label}
                              </div>
                            );
                          }
                          // Render nested submenu (collapsible) for mobile
                          if (sub.isSubmenu && sub.children) {
                            const isExpanded = expandedSubmenu === sub.id;
                            const hasActiveChild = sub.children.some(child => child.id === currentPage);
                            return (
                              <div key={sub.id}>
                                <button
                                  onClick={(e) => toggleNestedSubmenu(sub.id, e)}
                                  className={`w-full flex items-center gap-3 px-4 py-2 rounded-lg text-left transition-all ${
                                    hasActiveChild
                                      ? 'bg-primary/10 text-primary'
                                      : 'text-muted-foreground hover:bg-muted hover:text-foreground'
                                  }`}
                                >
                                  <sub.icon className="w-4 h-4" />
                                  <span className="text-sm font-medium">{sub.label}</span>
                                  <ChevronDown className={`w-3 h-3 ml-auto transition-transform ${isExpanded ? 'rotate-180' : ''}`} />
                                </button>
                                {isExpanded && (
                                  <div className="ml-4 mt-1 space-y-1 bg-slate-50 dark:bg-slate-700/30 rounded-lg p-2">
                                    {sub.children.map((child) => (
                                      <button
                                        key={child.id}
                                        onClick={() => handleNavigate(child.id)}
                                        className={`w-full flex items-center gap-3 px-4 py-2 rounded-lg text-left transition-all ${
                                          currentPage === child.id
                                            ? 'bg-primary/20 text-primary'
                                            : 'text-muted-foreground hover:bg-muted hover:text-foreground'
                                        }`}
                                      >
                                        <child.icon className="w-4 h-4" />
                                        <span className="text-sm">{child.label}</span>
                                      </button>
                                    ))}
                                  </div>
                                )}
                              </div>
                            );
                          }
                          return (
                            <button
                              key={sub.id}
                              onClick={() => !sub.disabled && handleNavigate(sub.id)}
                              disabled={sub.disabled}
                              className={`w-full flex items-center gap-3 px-4 py-2 rounded-lg text-left transition-all ${
                                sub.disabled
                                  ? 'text-muted-foreground/50 cursor-not-allowed'
                                  : currentPage === sub.id
                                    ? 'bg-primary/20 text-primary'
                                    : 'text-muted-foreground hover:bg-muted hover:text-foreground'
                              }`}
                            >
                              <sub.icon className="w-4 h-4" />
                              <span className="text-sm">{sub.label}</span>
                              {sub.disabled && <Badge variant="outline" className="ml-auto text-xs">Em breve</Badge>}
                            </button>
                          );
                        })}
                      </div>
                    )}
                  </div>
                ) : (
                  <button
                    onClick={() => handleNavigate(item.id)}
                    className={`w-full flex items-center gap-3 px-4 py-3 rounded-lg transition-all ${
                      isActive(item)
                        ? 'bg-primary/20 text-primary'
                        : 'text-muted-foreground hover:bg-muted hover:text-foreground'
                    }`}
                  >
                    <item.icon className="w-5 h-5" />
                    <span className="font-medium">{item.label}</span>
                  </button>
                )}
              </div>
            ))}
            
            {/* Mobile Theme Toggle */}
            <div className="flex items-center justify-between px-4 py-3 border-t border-border mt-2">
              <div className="flex items-center gap-3 text-muted-foreground">
                {theme === 'dark' ? <Moon className="w-5 h-5" /> : <Sun className="w-5 h-5" />}
                <span className="font-medium">Tema {theme === 'dark' ? 'Escuro' : 'Claro'}</span>
              </div>
              <Switch 
                checked={theme === 'light'}
                onCheckedChange={toggleTheme}
                data-testid="mobile-theme-toggle"
              />
            </div>
            
            {/* Mobile Logout Button */}
            <button
              onClick={handleSupportLogout}
              className="w-full flex items-center gap-3 px-4 py-3 rounded-lg transition-all text-muted-foreground hover:bg-muted hover:text-foreground mt-2 border-t border-border pt-4"
              data-testid="mobile-logout-btn"
            >
              <LogOut className="w-5 h-5" />
              <span className="font-medium">Sair</span>
            </button>
          </nav>
        </div>
      )}
    </header>
  );
};

// ==================== PLAN UPSELL COMPONENT ====================
const PlanUpsellSection = ({ userPlan, onContractClick, externalExpanded = false, setExternalExpanded }) => {
  const [isExpanded, setIsExpanded] = useState(false);

  // Sync with external expanded state
  useEffect(() => {
    if (externalExpanded) {
      setIsExpanded(true);
      // Reset external state after expanding
      if (setExternalExpanded) setExternalExpanded(false);
    }
  }, [externalExpanded, setExternalExpanded]);

  // Don't show if user is on the highest plan
  if (userPlan >= 3) return null;

  const getTitle = () => {
    if (userPlan === 1) return "Amplie as funcionalidades";
    if (userPlan === 2) return "Seja Profissional - Migre para o plano Contábil";
    return "";
  };

  const getMobileTitle = () => {
    if (userPlan === 1) return "Amplie as funcionalidades";
    if (userPlan === 2) return (
      <>
        Seja Profissional
        <br />
        Migre para o Plano Contábil
      </>
    );
    return "";
  };

  return (
    <Card id="plan-upsell-section" className="mt-6 border-primary/30 bg-gradient-to-r from-primary/5 to-transparent">
      <div 
        className="p-4 cursor-pointer flex items-center justify-between"
        onClick={() => setIsExpanded(!isExpanded)}
        data-testid="upsell-toggle"
      >
        <div className="flex items-center gap-2">
          <span className="text-lg font-medium text-primary">
            <span className="hidden md:inline">{isExpanded ? '−' : '+'} {getTitle()}</span>
            <span className="md:hidden">{isExpanded ? '−' : '+'} {getMobileTitle()}</span>
          </span>
        </div>
        <ChevronDown className={`w-5 h-5 text-primary transition-transform ${isExpanded ? 'rotate-180' : ''}`} />
      </div>
      
      {isExpanded && (
        <CardContent className="pt-0 pb-6 space-y-6 animate-fadeIn">
          {/* Plano Advanced - Only show if user is on plan 1 */}
          {userPlan === 1 && (
            <div className="border border-border rounded-lg p-4 bg-card">
              <div className="flex items-center gap-2 mb-3">
                <Badge className="bg-blue-500">Advanced</Badge>
                <span className="text-lg font-semibold">R$ 39,90/mês</span>
              </div>
              <p className="text-muted-foreground mb-4">
                Contrate o plano Advanced com mais benefícios. Tenha mais funcionalidades:
              </p>
              <ul className="space-y-2 mb-4">
                {PLAN_FEATURES[2].map((feature, idx) => (
                  <li key={idx} className="flex items-center gap-2 text-sm">
                    <CheckCircle className="w-4 h-4 text-emerald-500 flex-shrink-0" />
                    <span>{feature}</span>
                  </li>
                ))}
              </ul>
              <Button 
                onClick={() => onContractClick && onContractClick(2)}
                className="w-full"
                data-testid="contract-advanced-btn"
              >
                Quero Contratar
              </Button>
            </div>
          )}

          {/* Plano Contábil - Show for plan 1 and 2 */}
          <div className="border border-border rounded-lg p-4 bg-card">
            <div className="flex items-center gap-2 mb-3">
              <Badge className="bg-purple-500">Contábil</Badge>
              <span className="text-lg font-semibold">R$ 69,90/mês</span>
            </div>
            <p className="text-muted-foreground mb-4">
              {userPlan === 1 
                ? "Você também pode contratar o plano Contábil com novas funções importantes para o seu negócio."
                : "Contrate o plano Contábil com novas funções importantes para o seu negócio."
              }
            </p>
            <ul className="space-y-2 mb-4">
              {PLAN_FEATURES[3].map((feature, idx) => (
                <li key={idx} className="flex items-center gap-2 text-sm">
                  <CheckCircle className="w-4 h-4 text-emerald-500 flex-shrink-0" />
                  <span>{feature}</span>
                </li>
              ))}
            </ul>
            <Button 
              onClick={() => onContractClick && onContractClick(3)}
              className="w-full"
              data-testid="contract-contabil-btn"
            >
              Quero Contratar
            </Button>
          </div>
        </CardContent>
      )}
    </Card>
  );
};

// ==================== PLAN EXPIRATION MODAL ====================
const PlanExpirationModal = ({ user, userPlan, isOpen, onClose, onExpandUpsell }) => {
  const { toast } = useToast();
  const isFreePlan = userPlan === 1;
  
  // Handle email button for free plan
  const handleEmailClick = () => {
    const subject = encodeURIComponent(`DRE+ ${user?.email} - Desejo manter o plano Gratuito`);
    const body = encodeURIComponent(`E-mail do cliente: ${user?.email}`);
    window.location.href = `mailto:suporte@dremais.com.br?subject=${subject}&body=${body}`;
    onClose();
  };
  
  // Handle contract button for free plan
  const handleContractClick = () => {
    onClose();
    // Small delay to allow modal to close before expanding upsell
    setTimeout(() => {
      if (onExpandUpsell) onExpandUpsell();
    }, 300);
  };
  
  // Handle paid plan buttons (in development)
  const handlePaidPlanAction = (action) => {
    toast({
      title: `${action} em desenvolvimento`,
      description: "Em breve você poderá realizar esta ação. Aguarde a integração com o sistema de pagamentos."
    });
    onClose();
  };

  return (
    <AlertDialog open={isOpen} onOpenChange={onClose}>
      <AlertDialogContent className="bg-card border-border max-w-md">
        <AlertDialogHeader>
          <AlertDialogTitle className="text-foreground flex items-center gap-2">
            <Calendar className="w-5 h-5 text-amber-500" />
            {isFreePlan ? 'Seu Plano Gratuito' : 'Renovação do Plano'}
          </AlertDialogTitle>
          <AlertDialogDescription className="text-muted-foreground">
            {isFreePlan ? (
              <div className="space-y-4">
                <p>
                  Confirme por e-mail, que deseja se manter no plano gratuito. Ou contrate uma versão com mais recursos.
                </p>
                <div className="bg-muted/50 rounded-lg p-3 text-sm">
                  <p className="text-foreground font-medium">Plano Atual: Básico (Gratuito)</p>
                  <p className="text-muted-foreground">Acesse recursos avançados com nossos planos pagos!</p>
                </div>
              </div>
            ) : (
              <div className="space-y-4">
                <p>
                  Obrigado pela parceria. Seu plano está vencendo, renove sua aplicação sem perder nada.
                </p>
                <div className="bg-muted/50 rounded-lg p-3 text-sm">
                  <p className="text-foreground font-medium">Plano Atual: {PLANS[userPlan]?.name}</p>
                  <p className="text-muted-foreground">Continue aproveitando todos os recursos!</p>
                </div>
              </div>
            )}
          </AlertDialogDescription>
        </AlertDialogHeader>
        <AlertDialogFooter className="flex-col sm:flex-row gap-2">
          {isFreePlan ? (
            <>
              <AlertDialogCancel 
                onClick={handleEmailClick}
                className="bg-muted hover:bg-muted/80 text-foreground border-border"
                data-testid="expiration-email-btn"
              >
                E-mail
              </AlertDialogCancel>
              <AlertDialogAction 
                onClick={handleContractClick}
                className="bg-primary hover:bg-primary/90"
                data-testid="expiration-contract-btn"
              >
                Contratar
              </AlertDialogAction>
            </>
          ) : (
            <>
              <AlertDialogCancel 
                onClick={() => handlePaidPlanAction('Renovação')}
                className="bg-muted hover:bg-muted/80 text-foreground border-border"
                data-testid="expiration-renew-btn"
              >
                Renovar
              </AlertDialogCancel>
              <AlertDialogAction 
                onClick={() => handlePaidPlanAction('Contratação')}
                className="bg-primary hover:bg-primary/90"
                data-testid="expiration-upgrade-btn"
              >
                Contratar
              </AlertDialogAction>
            </>
          )}
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );
};

// ==================== DASHBOARD ====================
const Dashboard = ({ user, userPlan = 1, onContractClick, upsellExpanded = false, setUpsellExpanded, onNavigate, companyId, selectedCompany, companies = [], onCompanyChange, loadingCompanies }) => {
  const [summary, setSummary] = useState(null);
  
  // Load initial month/year from localStorage or use current date
  const getInitialMonth = () => {
    const saved = localStorage.getItem(LS_DASHBOARD_MONTH);
    return saved ? Number(saved) : new Date().getMonth() + 1;
  };
  const getInitialYear = () => {
    const saved = localStorage.getItem(LS_DASHBOARD_YEAR);
    return saved ? Number(saved) : new Date().getFullYear();
  };
  
  const [selectedMonth, setSelectedMonth] = useState(getInitialMonth);
  const [selectedYear, setSelectedYear] = useState(getInitialYear);
  const [loading, setLoading] = useState(true);

  // Save to localStorage whenever month or year changes
  useEffect(() => {
    localStorage.setItem(LS_DASHBOARD_MONTH, String(selectedMonth));
    localStorage.setItem(LS_DASHBOARD_YEAR, String(selectedYear));
    loadSummary();
  }, [selectedMonth, selectedYear, companyId]);

  const loadSummary = async () => {
    try {
      setLoading(true);
      let url = `${API}/dashboard/summary?month=${selectedMonth}&year=${selectedYear}`;
      if (companyId) {
        url += `&company_id=${companyId}`;
      }
      const response = await axios.get(url);
      setSummary(response.data);
    } catch (error) {
      console.error('Error loading summary:', error);
    } finally {
      setLoading(false);
    }
  };

  const years = Array.from({ length: 5 }, (_, i) => new Date().getFullYear() - 2 + i);

  return (
    <div className="animate-fadeIn space-y-6" data-testid="dashboard-page">
      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
        <div>
          <h2 className="text-3xl font-semibold tracking-tight">Dashboard</h2>
          {/* Greeting only on desktop - mobile shows in header */}
          <p className="hidden md:block text-muted-foreground">Olá, {user?.name?.split(' ')[0]}!</p>
        </div>
        <div className="flex flex-col md:flex-row gap-2">
          {/* Company selector */}
          {!loadingCompanies && companies.length > 0 && (
            <Select value={companyId || ''} onValueChange={onCompanyChange}>
              <SelectTrigger className="w-full md:w-48" data-testid="company-select">
                <Building className="w-4 h-4 mr-2 text-muted-foreground" />
                <SelectValue placeholder="Selecione a empresa" />
              </SelectTrigger>
              <SelectContent>
                {companies.map((company) => (
                  <SelectItem key={company.id} value={company.id}>
                    {company.is_personal ? '👤 ' : '🏢 '}{company.razao_social}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          )}
          {/* Month/Year selectors - row on mobile, inline on desktop */}
          <div className="flex gap-2">
            <Select value={String(selectedMonth)} onValueChange={(v) => setSelectedMonth(Number(v))}>
              <SelectTrigger className="flex-1 md:w-36" data-testid="month-select">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {MONTHS.map((month, i) => (
                  <SelectItem key={i} value={String(i + 1)}>{month}</SelectItem>
                ))}
              </SelectContent>
            </Select>
            <Select value={String(selectedYear)} onValueChange={(v) => setSelectedYear(Number(v))}>
              <SelectTrigger className="w-24" data-testid="year-select">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {years.map((year) => (
                  <SelectItem key={year} value={String(year)}>{year}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        </div>
      </div>

      {loading ? (
        <div className="flex justify-center py-12">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
        </div>
      ) : summary && (
        <>
          {/* Main Cards */}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
            <Card 
              className="card-hover border-l-4 border-l-emerald-500 cursor-pointer md:cursor-default" 
              data-testid="card-receitas"
              onClick={() => onNavigate && onNavigate('receitas')}
            >
              <CardContent className="p-6">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm text-muted-foreground">Receitas</p>
                    <p className="text-2xl font-bold text-emerald-500 tabular-nums">{formatCurrency(summary.total_receitas)}</p>
                  </div>
                  <div className="p-3 rounded-lg bg-emerald-500/10">
                    <TrendingUp className="w-6 h-6 text-emerald-500" />
                  </div>
                </div>
              </CardContent>
            </Card>

            <Card 
              className="card-hover border-l-4 border-l-red-500 cursor-pointer md:cursor-default" 
              data-testid="card-despesas"
              onClick={() => onNavigate && onNavigate('despesas')}
            >
              <CardContent className="p-6">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm text-muted-foreground">Despesas</p>
                    <p className="text-2xl font-bold text-red-500 tabular-nums">{formatCurrency(summary.total_despesas)}</p>
                  </div>
                  <div className="p-3 rounded-lg bg-red-500/10">
                    <TrendingDown className="w-6 h-6 text-red-500" />
                  </div>
                </div>
              </CardContent>
            </Card>

            <Card 
              className="card-hover border-l-4 border-l-amber-500 cursor-pointer md:cursor-default" 
              data-testid="card-custos"
              onClick={() => onNavigate && onNavigate('custos')}
            >
              <CardContent className="p-6">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm text-muted-foreground">Custos</p>
                    <p className="text-2xl font-bold text-amber-500 tabular-nums">{formatCurrency(summary.total_custos)}</p>
                  </div>
                  <div className="p-3 rounded-lg bg-amber-500/10">
                    <Coins className="w-6 h-6 text-amber-500" />
                  </div>
                </div>
              </CardContent>
            </Card>

            <Card className={`card-hover border-l-4 ${summary.is_profit ? 'border-l-blue-500' : 'border-l-red-600'}`} data-testid="card-resultado">
              <CardContent className="p-6">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm text-muted-foreground">{summary.is_profit ? 'Lucro' : 'Prejuízo'}</p>
                    <p className={`text-2xl font-bold tabular-nums ${summary.is_profit ? 'text-blue-500' : 'text-red-600'}`}>
                      {formatCurrency(Math.abs(summary.resultado))}
                    </p>
                  </div>
                  <div className={`p-3 rounded-lg ${summary.is_profit ? 'bg-blue-500/10' : 'bg-red-600/10'}`}>
                    <DollarSign className={`w-6 h-6 ${summary.is_profit ? 'text-blue-500' : 'text-red-600'}`} />
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>

          {/* Alerts Section */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
            {/* Contas a Vencer */}
            <Card className="card-hover" data-testid="upcoming-payables">
              <CardHeader className="pb-3">
                <CardTitle className="text-lg flex items-center gap-2">
                  <Clock className="w-5 h-5 text-amber-500" />
                  Contas a Vencer (7 dias)
                </CardTitle>
              </CardHeader>
              <CardContent>
                {summary.upcoming_payables?.filter(t => t.amount > 0).length > 0 ? (
                  <div className="space-y-2">
                    {summary.upcoming_payables.filter(t => t.amount > 0).map((t) => (
                      <div key={t.id} className="flex items-center justify-between p-2 rounded bg-muted/50">
                        <div>
                          <p className="text-sm font-medium">{t.description}</p>
                          <p className="text-xs text-muted-foreground">{formatDateWithWeekday(t.due_date)}</p>
                        </div>
                        <p className="text-sm font-mono text-red-500">{formatCurrency(t.amount)}</p>
                      </div>
                    ))}
                  </div>
                ) : (
                  <p className="text-sm text-muted-foreground text-center py-4">Nenhuma conta a vencer</p>
                )}
              </CardContent>
            </Card>

            {/* Receitas a Receber */}
            <Card 
              className="card-hover cursor-pointer transition-all hover:border-emerald-500/50" 
              data-testid="upcoming-receivables"
              onClick={() => onNavigate('contas-receber')}
            >
              <CardHeader className="pb-3">
                <CardTitle className="text-lg flex items-center gap-2">
                  <Calendar className="w-5 h-5 text-emerald-500" />
                  Receitas a Receber
                  <ChevronRight className="w-4 h-4 ml-auto text-muted-foreground" />
                </CardTitle>
              </CardHeader>
              <CardContent>
                {(summary.upcoming_receivables?.filter(t => t.amount > 0).length > 0 || summary.overdue_receivables?.filter(t => t.amount > 0).length > 0) ? (
                  <div className="space-y-2">
                    {/* Overdue receivables first (in red) */}
                    {summary.overdue_receivables?.filter(t => t.amount > 0).map((t) => (
                      <div key={t.id} className="flex items-center justify-between p-2 rounded bg-red-500/10 border-l-2 border-red-500">
                        <div>
                          <p className="text-sm font-medium">{t.description}</p>
                          <p className="text-xs text-red-400">{formatDateWithWeekday(t.due_date)} (vencido)</p>
                        </div>
                        <p className="text-sm font-mono text-red-500">{formatCurrency(t.amount)}</p>
                      </div>
                    ))}
                    {/* Upcoming receivables */}
                    {summary.upcoming_receivables?.filter(t => t.amount > 0).map((t) => (
                      <div key={t.id} className="flex items-center justify-between p-2 rounded bg-muted/50">
                        <div>
                          <p className="text-sm font-medium">{t.description}</p>
                          <p className="text-xs text-muted-foreground">{formatDateWithWeekday(t.due_date)}</p>
                        </div>
                        <p className="text-sm font-mono text-emerald-500">{formatCurrency(t.amount)}</p>
                      </div>
                    ))}
                  </div>
                ) : (
                  <p className="text-sm text-muted-foreground text-center py-4">Nenhuma receita a receber</p>
                )}
              </CardContent>
            </Card>

            {/* Overdue Alerts */}
            {(summary.overdue_payables?.filter(t => t.amount > 0).length > 0 || summary.overdue_receivables?.filter(t => t.amount > 0).length > 0) && (
              <>
                {summary.overdue_payables?.filter(t => t.amount > 0).length > 0 && (
                  <Card 
                    className="border-red-500/50 cursor-pointer hover:border-red-500 transition-colors" 
                    data-testid="overdue-payables"
                    onClick={() => onNavigate && onNavigate('despesas', { showOverdue: true })}
                  >
                    <CardHeader className="pb-3">
                      <CardTitle className="text-lg flex items-center gap-2 text-red-500">
                        <AlertCircle className="w-5 h-5" />
                        Contas Vencidas ({formatCurrency(summary.overdue_payables.filter(t => t.amount > 0).reduce((sum, t) => sum + t.amount, 0))})
                      </CardTitle>
                    </CardHeader>
                    <CardContent>
                      <div className="space-y-2">
                        {summary.overdue_payables.filter(t => t.amount > 0).map((t) => (
                          <div key={t.id} className="flex items-center justify-between p-2 rounded bg-red-500/10">
                            <div>
                              <p className="text-sm font-medium">{t.description}</p>
                              <p className="text-xs text-red-400">{formatDateWithWeekday(t.due_date)}</p>
                            </div>
                            <p className="text-sm font-mono text-red-500">{formatCurrency(t.amount)}</p>
                          </div>
                        ))}
                      </div>
                      <p className="text-xs text-muted-foreground mt-3 text-center">Clique para ver todas as despesas vencidas</p>
                    </CardContent>
                  </Card>
                )}
              </>
            )}
          </div>

          {/* Plan Upsell Section */}
          <PlanUpsellSection 
            userPlan={userPlan} 
            onContractClick={onContractClick} 
            externalExpanded={upsellExpanded}
            setExternalExpanded={setUpsellExpanded}
          />
        </>
      )}
    </div>
  );
};

// ==================== TRANSACTION PAGE (Receitas/Despesas/Custos) ====================
const TransactionPage = ({ transactionType, title, colorClass, onNavigate, companyId, initialShowOverdue = false }) => {
  const [transactions, setTransactions] = useState([]);
  const [accounts, setAccounts] = useState([]);
  const [costCenters, setCostCenters] = useState([]);
  const [paymentMethods, setPaymentMethods] = useState([]);
  const [clients, setClients] = useState([]);
  const [suppliers, setSuppliers] = useState([]);
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [editingTransaction, setEditingTransaction] = useState(null);
  // Quick supplier creation dialog
  const [isQuickSupplierDialogOpen, setIsQuickSupplierDialogOpen] = useState(false);
  const [quickSupplierName, setQuickSupplierName] = useState('');
  // Quick client creation dialog
  const [isQuickClientDialogOpen, setIsQuickClientDialogOpen] = useState(false);
  const [quickClientName, setQuickClientName] = useState('');
  // Quick expense category creation dialog
  const [isQuickCategoryDialogOpen, setIsQuickCategoryDialogOpen] = useState(false);
  const [quickCategoryName, setQuickCategoryName] = useState('');
  const [isCreatingCategory, setIsCreatingCategory] = useState(false);
  // Account search combobox state
  const [accountSearchOpen, setAccountSearchOpen] = useState(false);
  const [accountSearchValue, setAccountSearchValue] = useState('');
  // Initialize with initialShowOverdue prop value
  const [showOverdueOnly, setShowOverdueOnly] = useState(initialShowOverdue);
  const [filterExpenseType, setFilterExpenseType] = useState('all'); // 'all', 'FIXA', 'VARIAVEL'
  // Mirror transaction info popup
  const [mirrorInfoTransaction, setMirrorInfoTransaction] = useState(null);
  // Closing day confirmation modal
  const [closingDayConfirmation, setClosingDayConfirmation] = useState(null);
  const [pendingTransactionData, setPendingTransactionData] = useState(null);
  // Cancel confirmation modal
  const [cancelConfirmation, setCancelConfirmation] = useState(null);
  // Ordenação por vencimento
  const [sortByDueDate, setSortByDueDate] = useState(null); // null, 'asc', 'desc'
  
  // Sync showOverdueOnly when initialShowOverdue prop changes (e.g., navigating from Dashboard card)
  useEffect(() => {
    console.log('initialShowOverdue changed to:', initialShowOverdue);
    setShowOverdueOnly(initialShowOverdue);
  }, [initialShowOverdue]);
  
  // Load initial month/year from localStorage (set by Dashboard)
  const getInitialMonth = () => {
    const saved = localStorage.getItem(LS_DASHBOARD_MONTH);
    return saved ? Number(saved) : new Date().getMonth() + 1;
  };
  const getInitialYear = () => {
    const saved = localStorage.getItem(LS_DASHBOARD_YEAR);
    return saved ? Number(saved) : new Date().getFullYear();
  };
  
  const [selectedMonth, setSelectedMonth] = useState(getInitialMonth);
  const [selectedYear, setSelectedYear] = useState(getInitialYear);
  const [loading, setLoading] = useState(true);
  const [wallets, setWallets] = useState([]);
  const [formData, setFormData] = useState({
    description: '', amount: '', due_date: '', account_id: '',
    cost_center_id: '', payment_method_id: '', client_id: '', supplier_id: '',
    wallet_id: '', company_id: companyId || '',
    status: 'PENDENTE', installments: 1, copy_months: 0, notes: '', nf: '', expense_type: '', barcode: ''
  });
  const { toast } = useToast();
  const isMobile = useIsMobile();
  
  // Touch tracking for mobile - to distinguish tap from scroll
  const touchStartRef = useRef({ x: 0, y: 0, time: 0 });
  
  const handleRowTouchStart = (e) => {
    touchStartRef.current = {
      x: e.touches[0].clientX,
      y: e.touches[0].clientY,
      time: Date.now()
    };
  };
  
  const handleRowClick = (trans, e) => {
    // Check if click was on an interactive element
    const target = e.target;
    if (target.closest('button') || target.closest('[role="combobox"]') || target.closest('[data-radix-collection-item]')) {
      return; // Don't open edit if clicking on button or select
    }
    
    // For touch devices, check if it was a tap (not scroll)
    if (touchStartRef.current.time > 0) {
      const deltaX = Math.abs(e.clientX - touchStartRef.current.x);
      const deltaY = Math.abs(e.clientY - touchStartRef.current.y);
      const deltaTime = Date.now() - touchStartRef.current.time;
      
      // If moved more than 10px or took more than 300ms, it's probably a scroll
      if (deltaX > 10 || deltaY > 10 || deltaTime > 300) {
        touchStartRef.current = { x: 0, y: 0, time: 0 };
        return;
      }
    }
    
    // Open edit dialog
    openEdit(trans);
  };
  
  // Update company_id in formData when companyId prop changes
  useEffect(() => {
    setFormData(prev => ({ ...prev, company_id: companyId || '' }));
  }, [companyId]);
  
  // Debug: log when component mounts/unmounts
  useEffect(() => {
    console.log(`TransactionPage mounted for: ${transactionType}`);
    return () => console.log(`TransactionPage unmounted for: ${transactionType}`);
  }, [transactionType]);

  // Load data when component mounts or when filters change
  useEffect(() => {
    let isCancelled = false;
    
    // Reset state immediately when type changes
    setTransactions([]);
    setAccounts([]);
    setLoading(true);
    
    const fetchData = async () => {
      try {
        const companyParam = companyId ? `&company_id=${companyId}` : '';
        console.log(`Fetching data for: ${transactionType}, showOverdueOnly: ${showOverdueOnly}`);
        
        // Build transaction URL based on overdue filter
        let transUrl;
        if (showOverdueOnly && transactionType === 'DESPESA') {
          // Fetch all overdue transactions (ignoring month/year)
          transUrl = `${API}/transactions?transaction_type=${transactionType}&status=PENDENTE&overdue=true${companyParam}`;
        } else {
          transUrl = `${API}/transactions?transaction_type=${transactionType}&month=${selectedMonth}&year=${selectedYear}${companyParam}`;
        }
        
        const [transRes, accRes, ccRes, pmRes, clientsRes, suppliersRes, walletsRes] = await Promise.all([
          axios.get(transUrl),
          axios.get(`${API}/chart-accounts/analytical?account_type=${transactionType}`),
          axios.get(`${API}/cost-centers`),
          axios.get(`${API}/payment-methods`),
          axios.get(`${API}/clients${companyId ? `?company_id=${companyId}` : ''}`),
          axios.get(`${API}/suppliers${companyId ? `?company_id=${companyId}` : ''}`),
          axios.get(`${API}/wallets${companyId ? `?company_id=${companyId}` : ''}`)
        ]);
        
        if (!isCancelled) {
          console.log(`Data loaded for: ${transactionType}, transactions: ${transRes.data.length}`);
          setTransactions(transRes.data);
          setAccounts(accRes.data);
          setCostCenters(ccRes.data);
          setPaymentMethods(pmRes.data);
          setClients(clientsRes.data);
          setSuppliers(suppliersRes.data);
          setWallets(walletsRes.data);
          setLoading(false);
        }
      } catch (error) {
        if (!isCancelled) {
          console.error(`Error loading data for: ${transactionType}`, error);
          toast({ title: "Erro ao carregar dados", variant: "destructive" });
          setLoading(false);
        }
      }
    };
    
    fetchData();
    
    return () => {
      isCancelled = true;
    };
  }, [selectedMonth, selectedYear, transactionType, toast, companyId, showOverdueOnly]);

  const reloadData = async () => {
    setLoading(true);
    try {
      const companyParam = companyId ? `&company_id=${companyId}` : '';
      const [transRes, accRes] = await Promise.all([
        axios.get(`${API}/transactions?transaction_type=${transactionType}&month=${selectedMonth}&year=${selectedYear}${companyParam}`),
        axios.get(`${API}/chart-accounts/analytical?account_type=${transactionType}`)
      ]);
      setTransactions(transRes.data);
      setAccounts(accRes.data);
    } catch (error) {
      toast({ title: "Erro ao carregar dados", variant: "destructive" });
    }
    setLoading(false);
  };

  // Quick supplier creation function
  const handleCreateQuickSupplier = async () => {
    if (!quickSupplierName.trim()) return;
    try {
      const res = await axios.post(`${API}/suppliers`, { 
        name: quickSupplierName,
        company_id: companyId || null 
      });
      toast({ title: "Fornecedor criado!" });
      setQuickSupplierName('');
      setIsQuickSupplierDialogOpen(false);
      // Reload suppliers and auto-select the new one
      const companyParam = companyId ? `?company_id=${companyId}` : '';
      const suppliersRes = await axios.get(`${API}/suppliers${companyParam}`);
      setSuppliers(suppliersRes.data);
      setFormData(prev => ({ ...prev, supplier_id: res.data.id }));
    } catch (error) {
      toast({ title: "Erro ao criar fornecedor", variant: "destructive" });
    }
  };

  // Quick client creation function
  const handleCreateQuickClient = async () => {
    if (!quickClientName.trim()) return;
    try {
      const res = await axios.post(`${API}/clients`, { 
        name: quickClientName,
        company_id: companyId || null 
      });
      toast({ title: "Cliente criado!" });
      setQuickClientName('');
      setIsQuickClientDialogOpen(false);
      // Reload clients and auto-select the new one
      const companyParam = companyId ? `?company_id=${companyId}` : '';
      const clientsRes = await axios.get(`${API}/clients${companyParam}`);
      setClients(clientsRes.data);
      setFormData(prev => ({ ...prev, client_id: res.data.id }));
    } catch (error) {
      toast({ title: "Erro ao criar cliente", variant: "destructive" });
    }
  };

  // Quick expense category creation function
  const handleCreateQuickCategory = async () => {
    if (!quickCategoryName.trim()) return;
    setIsCreatingCategory(true);
    try {
      const res = await axios.post(`${API}/chart-accounts/expense-category`, { 
        name: quickCategoryName
      });
      toast({ title: "Categoria criada!", description: `Código: ${res.data.account.code}` });
      setQuickCategoryName('');
      setIsQuickCategoryDialogOpen(false);
      // Reload accounts and auto-select the new one
      const accRes = await axios.get(`${API}/chart-accounts/analytical?account_type=DESPESA`);
      setAccounts(accRes.data);
      // Find the newly created account and select it by id
      const newAccount = accRes.data.find(acc => acc.code === res.data.account.code);
      if (newAccount && newAccount.id) {
        setFormData(prev => ({ ...prev, account_id: newAccount.id }));
      }
    } catch (error) {
      const message = error.response?.data?.detail || "Erro ao criar categoria";
      toast({ title: message, variant: "destructive" });
    }
    setIsCreatingCategory(false);
  };

  // Remove accents from string for comparison
  const removeAccents = (str) => {
    return str.normalize('NFD').replace(/[\u0300-\u036f]/g, '');
  };

  // Custom filter function for account search
  const filterAccounts = (searchValue) => {
    if (!searchValue.trim()) return accounts;
    
    // Normalize search: uppercase and remove accents
    const search = removeAccents(searchValue.toUpperCase().trim());
    // Remove all non-numeric characters for code comparison
    const searchDigits = search.replace(/\D/g, '');
    
    return accounts.filter(acc => {
      // Normalize account name: uppercase and remove accents
      const normalizedName = removeAccents(acc.name.toUpperCase());
      
      // Match by name (case insensitive, accent insensitive)
      const nameMatch = normalizedName.includes(search);
      
      // Match by code - remove dots from code for comparison
      const codeDigits = acc.code.replace(/\D/g, '');
      const codeMatch = searchDigits.length > 0 && codeDigits.includes(searchDigits);
      
      // Also try exact code match with formatting
      const exactCodeMatch = acc.code.toUpperCase().includes(search);
      
      return nameMatch || codeMatch || exactCodeMatch;
    });
  };

  // Get selected account name for display
  const getSelectedAccountDisplay = () => {
    const selected = accounts.find(acc => acc.id === formData.account_id);
    return selected ? `${selected.code} - ${selected.name}` : '';
  };

  const handleSubmit = async (e, confirmClosingDay = false) => {
    if (e) e.preventDefault();
    try {
      const data = {
        ...formData,
        transaction_type: transactionType,
        amount: parseFloat(formData.amount) || 0,
        due_date: fromInputDate(formData.due_date),
        installments: parseInt(formData.installments) || 1,
        copy_months: parseInt(formData.copy_months) || 0,
        company_id: companyId || null,
        confirm_closing_day: confirmClosingDay
      };
      
      if (editingTransaction) {
        await axios.put(`${API}/transactions/${editingTransaction.id}`, data);
        toast({ title: `${title.slice(0, -1)} atualizada!` });
      } else {
        const response = await axios.post(`${API}/transactions`, data);
        
        // Check if backend requires closing day confirmation
        if (response.data.requires_confirmation) {
          setClosingDayConfirmation(response.data);
          setPendingTransactionData(data);
          return; // Don't close dialog, wait for user confirmation
        }
        
        toast({ title: `${title.slice(0, -1)} criada!` });
      }
      setIsDialogOpen(false);
      resetForm();
      reloadData();
    } catch (error) {
      const errorMsg = error.response?.data?.detail || "Erro ao salvar";
      toast({ title: errorMsg, variant: "destructive" });
    }
  };
  
  // Handle closing day confirmation
  const handleClosingDayConfirm = async () => {
    if (!pendingTransactionData) return;
    
    try {
      const data = {
        ...pendingTransactionData,
        confirm_closing_day: true
      };
      
      await axios.post(`${API}/transactions`, data);
      toast({ title: `${title.slice(0, -1)} criada com data ajustada!` });
      
      setClosingDayConfirmation(null);
      setPendingTransactionData(null);
      setIsDialogOpen(false);
      resetForm();
      reloadData();
    } catch (error) {
      const errorMsg = error.response?.data?.detail || "Erro ao salvar";
      toast({ title: errorMsg, variant: "destructive" });
    }
  };
  
  const handleClosingDayCancel = () => {
    setClosingDayConfirmation(null);
    setPendingTransactionData(null);
    // Keep dialog open so user can change the date
  };

  const handleDelete = async (id) => {
    if (!window.confirm("Confirma exclusão?")) return;
    try {
      await axios.delete(`${API}/transactions/${id}`);
      toast({ title: "Excluído com sucesso!" });
      reloadData();
    } catch (error) {
      toast({ title: "Erro ao excluir", variant: "destructive" });
    }
  };

  const handleDuplicate = async (trans) => {
    if (!window.confirm("Deseja realmente duplicar este lançamento?")) return;
    try {
      const data = {
        transaction_type: trans.transaction_type,
        description: trans.description,
        amount: trans.amount,
        due_date: trans.due_date,
        account_id: trans.account_id,
        cost_center_id: trans.cost_center_id,
        payment_method_id: trans.payment_method_id,
        wallet_id: trans.wallet_id,
        client_id: trans.client_id,
        supplier_id: trans.supplier_id,
        status: 'PENDENTE',
        installments: 1,
        copy_months: 0,
        notes: trans.notes,
        company_id: companyId || null,
        skip_closing_day_check: true // Skip closing day check for duplicates
      };
      await axios.post(`${API}/transactions`, data);
      toast({ title: "Lançamento duplicado com sucesso!" });
      reloadData();
    } catch (error) {
      toast({ title: "Erro ao duplicar lançamento", variant: "destructive" });
    }
  };

  const handleStatusChange = async (id, newStatus, confirmedCancel = false) => {
    // If canceling, show confirmation dialog first
    if (newStatus === 'CANCELADO' && !confirmedCancel) {
      const trans = transactions.find(t => t.id === id);
      setCancelConfirmation({
        id,
        transaction: trans,
        type: transactionType
      });
      return;
    }
    
    try {
      const paidDate = newStatus === 'PAGO' || newStatus === 'RECEBIDO' 
        ? fromInputDate(new Date().toISOString().split('T')[0]) 
        : null;
      
      // If canceling, also zero out the amount
      if (newStatus === 'CANCELADO') {
        await axios.patch(`${API}/transactions/${id}/cancel`);
        toast({ title: transactionType === 'RECEITA' ? "Recebimento cancelado!" : "Despesa cancelada!" });
      } else {
        await axios.patch(`${API}/transactions/${id}/status?status=${newStatus}${paidDate ? `&paid_date=${paidDate}` : ''}`);
        toast({ title: "Status atualizado!" });
      }
      reloadData();
    } catch (error) {
      toast({ title: "Erro ao atualizar status", variant: "destructive" });
    }
  };
  
  const handleConfirmCancel = async () => {
    if (!cancelConfirmation) return;
    await handleStatusChange(cancelConfirmation.id, 'CANCELADO', true);
    setCancelConfirmation(null);
  };
  
  const handleReactivate = async (id) => {
    try {
      await axios.patch(`${API}/transactions/${id}/reactivate`);
      toast({ title: transactionType === 'RECEITA' ? "Recebimento reativado!" : "Despesa reativada!" });
      reloadData();
    } catch (error) {
      toast({ title: "Erro ao reativar", variant: "destructive" });
    }
  };

  const openEdit = (trans) => {
    setEditingTransaction(trans);
    setFormData({
      description: trans.description || '',
      amount: trans.amount || '',
      due_date: toInputDate(trans.due_date) || '',
      account_id: trans.account_id || '',
      cost_center_id: trans.cost_center_id || '',
      payment_method_id: trans.payment_method_id || '',
      client_id: trans.client_id || '',
      supplier_id: trans.supplier_id || '',
      wallet_id: trans.wallet_id || '',
      status: trans.status || 'PENDENTE',
      installments: trans.installments || 1,
      copy_months: 0,
      notes: trans.notes || '',
      nf: trans.nf || '',
      expense_type: trans.expense_type || '',
      barcode: trans.barcode || ''
    });
    setIsDialogOpen(true);
  };

  const resetForm = () => {
    setEditingTransaction(null);
    setFormData({
      description: '', amount: '', due_date: '', account_id: '',
      cost_center_id: '', payment_method_id: '', client_id: '', supplier_id: '',
      wallet_id: '',
      status: 'PENDENTE', installments: 1, copy_months: 0, notes: '', nf: '', expense_type: '', barcode: ''
    });
  };

  const getStatusBadge = (status) => {
    const styles = {
      'PENDENTE': 'bg-amber-500/20 text-amber-400',
      'PAGO': 'bg-emerald-500/20 text-emerald-400',
      'RECEBIDO': 'bg-emerald-500/20 text-emerald-400',
      'CANCELADO': 'bg-slate-500/20 text-slate-400'
    };
    return styles[status] || styles['PENDENTE'];
  };

  const years = Array.from({ length: 5 }, (_, i) => new Date().getFullYear() - 2 + i);
  const totalAmount = transactions.reduce((sum, t) => sum + t.amount, 0);

  // Mobile button text
  const getButtonText = () => {
    if (isMobile) return 'Incluir';
    if (transactionType === 'RECEITA') return 'Nova Receita';
    if (transactionType === 'DESPESA') return 'Nova Despesa';
    return 'Novo Custo';
  };

  return (
    <div className="animate-fadeIn space-y-6 max-w-full" data-testid={`${transactionType.toLowerCase()}-page`}>
      {/* Header section with title, back button, and filters */}
      <div className="space-y-4 max-w-full">
        {/* Title row with back button */}
        <div className="relative max-w-full">
          <div>
            <h2 className="text-2xl md:text-3xl font-semibold tracking-tight pr-12 md:pr-0">{title}</h2>
            <p className={`text-lg font-mono ${colorClass}`}>Total: {formatCurrency(totalAmount)}</p>
          </div>
          {/* Back button - mobile only, absolute positioned */}
          {onNavigate && (
            <button
              onClick={() => onNavigate('dashboard')}
              className="md:hidden absolute top-0 right-0 w-8 h-8 rounded-full bg-muted flex items-center justify-center hover:bg-muted/80 transition-colors"
              data-testid="back-to-dashboard-btn"
            >
              <XCircle className="w-5 h-5 text-muted-foreground" />
            </button>
          )}
        </div>
        {/* Filters row */}
        <div className="flex gap-2 flex-wrap">
          <Select value={String(selectedMonth)} onValueChange={(v) => { setSelectedMonth(Number(v)); setShowOverdueOnly(false); }} disabled={showOverdueOnly}>
            <SelectTrigger className={`w-28 md:w-36 ${showOverdueOnly ? 'opacity-50' : ''}`}><SelectValue /></SelectTrigger>
            <SelectContent>
              {MONTHS.map((month, i) => (<SelectItem key={i} value={String(i + 1)}>{month}</SelectItem>))}
            </SelectContent>
          </Select>
          <Select value={String(selectedYear)} onValueChange={(v) => { setSelectedYear(Number(v)); setShowOverdueOnly(false); }} disabled={showOverdueOnly}>
            <SelectTrigger className={`w-20 md:w-24 ${showOverdueOnly ? 'opacity-50' : ''}`}><SelectValue /></SelectTrigger>
            <SelectContent>
              {years.map((year) => (<SelectItem key={year} value={String(year)}>{year}</SelectItem>))}
            </SelectContent>
          </Select>
          {transactionType === 'DESPESA' && (
            <Button 
              variant={showOverdueOnly ? "destructive" : "outline"} 
              onClick={() => setShowOverdueOnly(!showOverdueOnly)}
              className="gap-2"
              data-testid="filter-overdue-btn"
            >
              <AlertCircle className="w-4 h-4" />
              Vencidas
            </Button>
          )}
          {transactionType === 'DESPESA' && (
            <Select value={filterExpenseType} onValueChange={setFilterExpenseType}>
              <SelectTrigger className="w-28 md:w-32" data-testid="filter-expense-type">
                <SelectValue placeholder="Tipo" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Todos Tipos</SelectItem>
                <SelectItem value="FIXA">Fixa</SelectItem>
                <SelectItem value="VARIAVEL">Variável</SelectItem>
              </SelectContent>
            </Select>
          )}
          <Button onClick={() => { resetForm(); setIsDialogOpen(true); }} className="gap-2" data-testid={`add-${transactionType.toLowerCase()}-btn`}>
            <Plus className="w-4 h-4" /> {getButtonText()}
          </Button>
        </div>
      </div>

      <Card className="overflow-x-auto">
        <Table className="data-table">
          <TableHeader>
            <TableRow>
              <TableHead 
                className="cursor-pointer hover:bg-muted/50 select-none"
                onClick={() => {
                  if (sortByDueDate === null) setSortByDueDate('asc');
                  else if (sortByDueDate === 'asc') setSortByDueDate('desc');
                  else setSortByDueDate(null);
                }}
              >
                <div className="flex items-center gap-1">
                  Vencimento
                  {sortByDueDate === 'asc' && <ChevronUp className="w-4 h-4" />}
                  {sortByDueDate === 'desc' && <ChevronDown className="w-4 h-4" />}
                  {sortByDueDate === null && <ChevronsUpDown className="w-3 h-3 opacity-50" />}
                </div>
              </TableHead>
              <TableHead>Descrição</TableHead>
              {!isMobile && <TableHead>Categoria</TableHead>}
              {!isMobile && (transactionType === 'DESPESA' || transactionType === 'RECEITA') && <TableHead>Carteira</TableHead>}
              <TableHead>Status</TableHead>
              {!isMobile && transactionType === 'DESPESA' && <TableHead>Tipo</TableHead>}
              <TableHead className="text-right">Valor</TableHead>
              <TableHead className="text-right">Ações</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {loading ? (
              <TableRow>
                <TableCell colSpan={isMobile ? 5 : (transactionType === 'DESPESA' ? 8 : (transactionType === 'RECEITA' ? 7 : 6))} className="text-center py-8">
                  <div className="flex items-center justify-center gap-2 text-muted-foreground">
                    <div className="h-4 w-4 animate-spin rounded-full border-2 border-primary border-t-transparent"></div>
                    Carregando...
                  </div>
                </TableCell>
              </TableRow>
            ) : transactions.length === 0 ? (
              <TableRow>
                <TableCell colSpan={isMobile ? 5 : (transactionType === 'DESPESA' ? 8 : (transactionType === 'RECEITA' ? 7 : 6))} className="text-center text-muted-foreground py-8">
                  Nenhum lançamento encontrado
                </TableCell>
              </TableRow>
            ) : (
              transactions
                .filter(trans => {
                  if (transactionType !== 'DESPESA' || filterExpenseType === 'all') return true;
                  const expType = trans.expense_type || 'FIXA';
                  return expType === filterExpenseType;
                })
                .sort((a, b) => {
                  if (!sortByDueDate) return 0;
                  const dateA = new Date(a.due_date.split('/').reverse().join('-'));
                  const dateB = new Date(b.due_date.split('/').reverse().join('-'));
                  return sortByDueDate === 'asc' ? dateA - dateB : dateB - dateA;
                })
                .map((trans) => (
                <TableRow 
                  key={trans.id} 
                  className={`cursor-pointer transition-colors ${trans.is_mirror ? 'bg-slate-700/50 hover:bg-slate-600/60' : 'hover:bg-muted/50'} ${trans.is_zero_reference ? 'bg-gray-800/50 opacity-60' : ''}`}
                  onClick={(e) => {
                    // Se for transação espelho, mostrar popup informativo em vez de abrir edição
                    if (trans.is_mirror) {
                      e.stopPropagation();
                      setMirrorInfoTransaction(trans);
                      return;
                    }
                    handleRowClick(trans, e);
                  }}
                  onTouchStart={handleRowTouchStart}
                  data-testid={`transaction-row-${trans.id}`}
                >
                  <TableCell className="font-mono text-sm">{isMobile ? formatDateShort(trans.due_date) : trans.due_date}</TableCell>
                  <TableCell>
                    <div className="flex items-center gap-2">
                      <div className="flex-1">
                        <div className="flex items-center gap-2">
                          <p className="font-medium text-sm md:text-base">{trans.description}</p>
                          {/* Badges para transações especiais */}
                          {trans.is_mirror && !trans.is_accumulated && (
                            <Badge variant="outline" className="text-xs bg-blue-100 text-blue-700 border-blue-300">
                              <Link2 className="w-3 h-3 mr-1" />
                              Espelho
                            </Badge>
                          )}
                          {trans.is_accumulated && (
                            <Badge variant="outline" className="text-xs bg-purple-100 text-purple-700 border-purple-300">
                              <RefreshCw className="w-3 h-3 mr-1" />
                              Acumulado
                            </Badge>
                          )}
                          {trans.is_zero_reference && (
                            <Badge variant="outline" className="text-xs bg-gray-100 text-gray-600 border-gray-300">
                              Ref
                            </Badge>
                          )}
                        </div>
                        {trans.supplier_name && <p className="text-xs text-muted-foreground">{trans.supplier_name}</p>}
                        {trans.client_name && <p className="text-xs text-muted-foreground">{trans.client_name}</p>}
                        {trans.source_company_name && (
                          <p className="text-xs text-blue-600">Origem: {trans.source_company_name}</p>
                        )}
                      </div>
                    </div>
                  </TableCell>
                  {!isMobile && <TableCell className="text-sm text-muted-foreground">{trans.account_name}</TableCell>}
                  {!isMobile && (transactionType === 'DESPESA' || transactionType === 'RECEITA') && (
                    <TableCell className="text-sm">
                      {trans.wallet_type ? (
                        <div>
                          <p className="font-medium text-sm">{trans.wallet_type === 'Cartão de Crédito' ? 'Crédito' : trans.wallet_type}</p>
                          {trans.wallet_name && <p className="text-xs text-muted-foreground">{trans.wallet_name}</p>}
                        </div>
                      ) : '-'}
                    </TableCell>
                  )}
                  <TableCell onClick={(e) => e.stopPropagation()}>
                    {trans.is_mirror ? (
                      <span className={`px-2 py-1 rounded text-xs font-medium ${getStatusBadge(trans.status)}`}>
                        {trans.status === 'PENDENTE' ? 'Pendente' : trans.status === 'PAGO' || trans.status === 'RECEBIDO' ? (transactionType === 'RECEITA' ? 'Recebido' : 'Pago') : 'Cancelado'}
                      </span>
                    ) : (
                      trans.status === 'CANCELADO' ? (
                        <div className="flex items-center gap-1">
                          <span className={`px-2 py-1 rounded text-xs font-medium ${getStatusBadge(trans.status)}`}>
                            Cancelado
                          </span>
                          <Button 
                            variant="ghost" 
                            size="sm"
                            className="h-7 px-2 text-xs text-emerald-600 hover:text-emerald-700"
                            onClick={() => handleReactivate(trans.id)}
                            title="Reativar lançamento"
                          >
                            <RefreshCw className="w-3 h-3 mr-1" />
                            Reativar
                          </Button>
                        </div>
                      ) : (
                        <Select value={trans.status} onValueChange={(v) => handleStatusChange(trans.id, v)}>
                          <SelectTrigger className={`w-24 md:w-28 h-7 text-xs ${getStatusBadge(trans.status)}`}>
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="PENDENTE">Pendente</SelectItem>
                            <SelectItem value={transactionType === 'RECEITA' ? 'RECEBIDO' : 'PAGO'}>
                              {transactionType === 'RECEITA' ? 'Recebido' : 'Pago'}
                            </SelectItem>
                            <SelectItem value="CANCELADO">Cancelar</SelectItem>
                          </SelectContent>
                        </Select>
                      )
                    )}
                  </TableCell>
                  {!isMobile && transactionType === 'DESPESA' && (
                    <TableCell className="text-sm">
                      <span className={`px-2 py-1 rounded text-xs font-medium ${trans.expense_type === 'VARIAVEL' ? 'bg-amber-100 text-amber-700' : 'bg-blue-100 text-blue-700'}`}>
                        {trans.expense_type === 'VARIAVEL' ? 'Variável' : 'Fixa'}
                      </span>
                    </TableCell>
                  )}
                  <TableCell className={`text-right font-mono tabular-nums text-sm ${colorClass}`}>
                    {formatCurrency(trans.amount)}
                  </TableCell>
                  <TableCell className="text-right" onClick={(e) => e.stopPropagation()}>
                    {trans.is_mirror ? (
                      <Button 
                        variant="ghost" 
                        size="icon" 
                        onClick={() => setMirrorInfoTransaction(trans)}
                        title="Ver informações"
                        className="text-blue-500"
                      >
                        <HelpCircle className="w-4 h-4" />
                      </Button>
                    ) : (
                      <div className="flex items-center justify-end gap-1">
                        <Button 
                          variant="ghost" 
                          size="icon" 
                          onClick={() => openEdit(trans)} 
                          title="Editar lançamento"
                          data-testid={`edit-transaction-${trans.id}`}
                        >
                          <Pencil className="w-4 h-4" />
                        </Button>
                        <Button 
                          variant="ghost" 
                          size="icon" 
                          onClick={() => handleDuplicate(trans)} 
                          title="Duplicar lançamento"
                          data-testid={`duplicate-transaction-${trans.id}`}
                        >
                          <Copy className="w-4 h-4 text-blue-500" />
                        </Button>
                        <Button 
                          variant="ghost" 
                          size="icon" 
                          onClick={() => handleDelete(trans.id)} 
                          title="Excluir lançamento"
                          data-testid={`delete-transaction-${trans.id}`}
                        >
                          <Trash2 className="w-4 h-4 text-destructive" />
                        </Button>
                      </div>
                    )}
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </Card>

      {/* Transaction Dialog */}
      <Dialog open={isDialogOpen} onOpenChange={setIsDialogOpen}>
        <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>{editingTransaction ? 'Editar' : (transactionType === 'CUSTO' ? 'Novo' : 'Nova')} {title.slice(0, -1)}</DialogTitle>
            <DialogDescription>Preencha os dados do lançamento</DialogDescription>
          </DialogHeader>
          <form onSubmit={handleSubmit} className="space-y-4">
            <div>
              <Label>Categoria (Plano de Contas) *</Label>
              <div className="flex gap-2">
                <Popover open={accountSearchOpen} onOpenChange={setAccountSearchOpen}>
                  <PopoverTrigger asChild>
                    <Button
                      variant="outline"
                      role="combobox"
                      aria-expanded={accountSearchOpen}
                      className="w-full justify-between font-normal"
                      data-testid="account-select"
                    >
                      {formData.account_id ? getSelectedAccountDisplay() : "Selecione ou pesquise a conta..."}
                      <ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
                    </Button>
                  </PopoverTrigger>
                  <PopoverContent className="w-full p-0" align="start">
                    <Command shouldFilter={false}>
                      <CommandInput 
                        placeholder="Digite o nome ou código da conta..." 
                        value={accountSearchValue}
                        onValueChange={setAccountSearchValue}
                      />
                      <CommandList>
                        <CommandEmpty>Nenhuma conta encontrada.</CommandEmpty>
                        <CommandGroup>
                          {filterAccounts(accountSearchValue).map((acc) => (
                            <CommandItem
                              key={acc.id}
                              value={acc.id}
                              onSelect={() => {
                                setFormData({...formData, account_id: acc.id});
                                setAccountSearchOpen(false);
                                setAccountSearchValue('');
                              }}
                            >
                              <Check
                                className={`mr-2 h-4 w-4 ${formData.account_id === acc.id ? "opacity-100" : "opacity-0"}`}
                              />
                              <span className="font-mono text-xs mr-2 text-muted-foreground">{acc.code}</span>
                              <span>{acc.name}</span>
                            </CommandItem>
                          ))}
                        </CommandGroup>
                      </CommandList>
                    </Command>
                  </PopoverContent>
                </Popover>
                {/* Botão + para criar nova categoria - apenas para DESPESA */}
                {transactionType === 'DESPESA' && (
                  <Button
                    type="button"
                    variant="outline"
                    size="icon"
                    onClick={() => setIsQuickCategoryDialogOpen(true)}
                    title="Criar nova categoria de despesa"
                    data-testid="add-category-btn"
                  >
                    <Plus className="h-4 w-4" />
                  </Button>
                )}
              </div>
            </div>
            {/* Tipo de Despesa - apenas para DESPESA */}
            {transactionType === 'DESPESA' && (
              <div>
                <Label>Tipo de Despesa</Label>
                <Select value={formData.expense_type} onValueChange={(v) => setFormData({...formData, expense_type: v})}>
                  <SelectTrigger data-testid="expense-type-select"><SelectValue placeholder="Selecione o tipo" /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="FIXA">Fixa</SelectItem>
                    <SelectItem value="VARIAVEL">Variável</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            )}
            <div>
              <Label>Descrição *</Label>
              <Input value={formData.description} onChange={(e) => setFormData({...formData, description: e.target.value})} required data-testid="description-input" />
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Valor (R$) *</Label>
                <Input type="number" step="0.01" min="0" value={formData.amount} onChange={(e) => setFormData({...formData, amount: e.target.value})} required data-testid="amount-input" />
              </div>
              <div>
                <Label>Vencimento *</Label>
                <Popover>
                  <PopoverTrigger asChild>
                    <Button
                      variant="outline"
                      className={`w-full justify-start text-left font-normal ${!formData.due_date && "text-muted-foreground"}`}
                      data-testid="due-date-input"
                    >
                      <Calendar className="mr-2 h-4 w-4" />
                      {formData.due_date ? format(new Date(formData.due_date + 'T00:00:00'), "dd/MM/yyyy", { locale: ptBR }) : <span>Selecione a data</span>}
                    </Button>
                  </PopoverTrigger>
                  <PopoverContent className="w-auto p-0" align="start">
                    <CalendarComponent
                      mode="single"
                      selected={formData.due_date ? new Date(formData.due_date + 'T00:00:00') : undefined}
                      onSelect={(date) => {
                        if (date) {
                          const yyyy = date.getFullYear();
                          const mm = String(date.getMonth() + 1).padStart(2, '0');
                          const dd = String(date.getDate()).padStart(2, '0');
                          setFormData({...formData, due_date: `${yyyy}-${mm}-${dd}`});
                        }
                      }}
                      initialFocus
                      locale={ptBR}
                    />
                  </PopoverContent>
                </Popover>
              </div>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Centro de Custo</Label>
                <Select value={formData.cost_center_id} onValueChange={(v) => setFormData({...formData, cost_center_id: v})}>
                  <SelectTrigger><SelectValue placeholder="Selecione" /></SelectTrigger>
                  <SelectContent>
                    {costCenters.map((cc) => (<SelectItem key={cc.id} value={cc.id}>{cc.name}</SelectItem>))}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label>Forma de Pagamento</Label>
                <Select value={formData.payment_method_id} onValueChange={(v) => setFormData({...formData, payment_method_id: v})}>
                  <SelectTrigger><SelectValue placeholder="Selecione" /></SelectTrigger>
                  <SelectContent>
                    {paymentMethods.map((pm) => (<SelectItem key={pm.id} value={pm.id}>{pm.name}</SelectItem>))}
                  </SelectContent>
                </Select>
              </div>
            </div>
            {transactionType === 'RECEITA' ? (
              <div>
                <Label>Cliente</Label>
                <div className="flex gap-2">
                  <Select value={formData.client_id} onValueChange={(v) => setFormData({...formData, client_id: v})}>
                    <SelectTrigger className="flex-1"><SelectValue placeholder="Selecione o cliente" /></SelectTrigger>
                    <SelectContent>
                      {clients.map((c) => (<SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>))}
                    </SelectContent>
                  </Select>
                  <Button type="button" variant="outline" size="icon" onClick={() => setIsQuickClientDialogOpen(true)} title="Adicionar cliente">
                    <Plus className="w-4 h-4" />
                  </Button>
                </div>
              </div>
            ) : (
              <div>
                <Label>Fornecedor</Label>
                <div className="flex gap-2">
                  <Select value={formData.supplier_id} onValueChange={(v) => setFormData({...formData, supplier_id: v})}>
                    <SelectTrigger className="flex-1"><SelectValue placeholder="Selecione o fornecedor" /></SelectTrigger>
                    <SelectContent>
                      {suppliers.map((s) => (<SelectItem key={s.id} value={s.id}>{s.name}</SelectItem>))}
                    </SelectContent>
                  </Select>
                  <Button type="button" variant="outline" size="icon" onClick={() => setIsQuickSupplierDialogOpen(true)} title="Adicionar fornecedor">
                    <Plus className="w-4 h-4" />
                  </Button>
                </div>
              </div>
            )}
            {/* Carteira - para Despesas e Receitas */}
            {(transactionType === 'DESPESA' || transactionType === 'RECEITA') && (
              <div>
                <Label>Carteira</Label>
                <Select value={formData.wallet_id} onValueChange={(v) => setFormData({...formData, wallet_id: v})}>
                  <SelectTrigger><SelectValue placeholder="Selecione a carteira" /></SelectTrigger>
                  <SelectContent>
                    {wallets.map((w) => (
                      <SelectItem key={w.id} value={w.id}>
                        {w.account} ({w.wallet_type === 'Cartão de Crédito' ? 'Crédito' : w.wallet_type})
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            )}
            {/* NF - Nota Fiscal - apenas para Despesas */}
            {transactionType === 'DESPESA' && (
              <div>
                <Label>NF (Nota Fiscal)</Label>
                <Input 
                  value={formData.nf} 
                  onChange={(e) => setFormData({...formData, nf: e.target.value})} 
                  placeholder="Número da nota fiscal"
                  data-testid="nf-input"
                />
              </div>
            )}
            {/* Código de Barras - apenas para Despesas */}
            {transactionType === 'DESPESA' && (
              <div>
                <Label>Código de Barras</Label>
                <div className="flex gap-2">
                  <Input 
                    className="flex-1 font-mono text-sm"
                    value={formData.barcode} 
                    onChange={(e) => setFormData({...formData, barcode: e.target.value.replace(/\D/g, '')})} 
                    placeholder="Digite os números do código de barras"
                    data-testid="barcode-input"
                  />
                  <Button 
                    type="button" 
                    variant="outline" 
                    size="icon"
                    onClick={() => {
                      if (formData.barcode) {
                        navigator.clipboard.writeText(formData.barcode);
                        toast({ title: "Copiado!", description: "Código de barras copiado para a área de transferência" });
                      }
                    }}
                    disabled={!formData.barcode}
                    title="Copiar código de barras"
                    data-testid="copy-barcode-btn"
                  >
                    <Copy className="w-4 h-4" />
                  </Button>
                </div>
                <p className="text-xs text-muted-foreground mt-1">Cole no seu banco para efetuar o pagamento</p>
              </div>
            )}
            {!editingTransaction && (
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label>Parcelas</Label>
                  <Input type="number" min="1" max="48" value={formData.installments} onChange={(e) => setFormData({...formData, installments: e.target.value})} data-testid="installments-input" />
                </div>
                <div>
                  <Label>Copiar para (meses)</Label>
                  <Input type="number" min="0" max="12" value={formData.copy_months} onChange={(e) => setFormData({...formData, copy_months: e.target.value})} data-testid="copy-months-input" />
                </div>
              </div>
            )}
            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setIsDialogOpen(false)}>Cancelar</Button>
              <Button type="submit" data-testid="save-transaction-btn">Salvar</Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* Quick Supplier Creation Dialog */}
      <Dialog open={isQuickSupplierDialogOpen} onOpenChange={setIsQuickSupplierDialogOpen}>
        <DialogContent className="max-w-sm">
          <DialogHeader>
            <DialogTitle>Novo Fornecedor</DialogTitle>
            <DialogDescription>Adicione rapidamente um novo fornecedor</DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div>
              <Label>Razão Social / Nome *</Label>
              <Input 
                value={quickSupplierName} 
                onChange={(e) => setQuickSupplierName(e.target.value)} 
                placeholder="Nome do fornecedor"
                data-testid="quick-supplier-name-input"
              />
            </div>
            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setIsQuickSupplierDialogOpen(false)}>Cancelar</Button>
              <Button onClick={handleCreateQuickSupplier} disabled={!quickSupplierName.trim()} data-testid="save-quick-supplier-btn">Criar</Button>
            </DialogFooter>
          </div>
        </DialogContent>
      </Dialog>

      {/* Quick Client Creation Dialog */}
      <Dialog open={isQuickClientDialogOpen} onOpenChange={setIsQuickClientDialogOpen}>
        <DialogContent className="max-w-sm">
          <DialogHeader>
            <DialogTitle>Novo Cliente</DialogTitle>
            <DialogDescription>Adicione rapidamente um novo cliente</DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div>
              <Label>Nome / Razão Social *</Label>
              <Input 
                value={quickClientName} 
                onChange={(e) => setQuickClientName(e.target.value)} 
                placeholder="Nome do cliente"
                data-testid="quick-client-name-input"
              />
            </div>
            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setIsQuickClientDialogOpen(false)}>Cancelar</Button>
              <Button onClick={handleCreateQuickClient} disabled={!quickClientName.trim()} data-testid="save-quick-client-btn">Criar</Button>
            </DialogFooter>
          </div>
        </DialogContent>
      </Dialog>

      {/* Quick Category Creation Dialog - apenas para DESPESA */}
      <Dialog open={isQuickCategoryDialogOpen} onOpenChange={setIsQuickCategoryDialogOpen}>
        <DialogContent className="max-w-sm">
          <DialogHeader>
            <DialogTitle>Nova Categoria de Despesa</DialogTitle>
            <DialogDescription>
              Crie uma nova categoria personalizada para suas despesas.
              O código será gerado automaticamente.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div>
              <Label>Nome da Categoria *</Label>
              <Input 
                value={quickCategoryName} 
                onChange={(e) => setQuickCategoryName(e.target.value)} 
                placeholder="Ex: Material de Escritório"
                data-testid="quick-category-name-input"
                autoFocus
              />
            </div>
            <p className="text-xs text-muted-foreground">
              A nova categoria será vinculada ao grupo "Despesas Diversas" (3.5.1.XX) 
              e ficará disponível para todas as suas empresas.
            </p>
            <DialogFooter>
              <Button 
                type="button" 
                variant="outline" 
                onClick={() => {
                  setIsQuickCategoryDialogOpen(false);
                  setQuickCategoryName('');
                }}
              >
                Cancelar
              </Button>
              <Button 
                onClick={handleCreateQuickCategory} 
                disabled={!quickCategoryName.trim() || isCreatingCategory}
                data-testid="save-quick-category-btn"
              >
                {isCreatingCategory ? "Criando..." : "Criar Categoria"}
              </Button>
            </DialogFooter>
          </div>
        </DialogContent>
      </Dialog>
      
      {/* Mirror Transaction Info Dialog */}
      <Dialog open={!!mirrorInfoTransaction} onOpenChange={() => setMirrorInfoTransaction(null)}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              {mirrorInfoTransaction?.is_accumulated ? (
                <>
                  <RefreshCw className="w-5 h-5 text-purple-500" />
                  Transação Acumulada
                </>
              ) : (
                <>
                  <Link2 className="w-5 h-5 text-blue-500" />
                  Transação Espelho
                </>
              )}
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div className="p-4 bg-blue-50 border border-blue-200 rounded-lg">
              <p className="text-sm text-blue-800">
                {mirrorInfoTransaction?.is_accumulated ? (
                  <>
                    Esta é uma transação <strong>acumulada</strong> que representa o total mensal de {mirrorInfoTransaction?.transaction_type?.toLowerCase()}s 
                    de <strong>{mirrorInfoTransaction?.source_company_name}</strong>.
                  </>
                ) : (
                  <>
                    Esta é uma transação <strong>espelho</strong> que reflete automaticamente um lançamento 
                    de <strong>{mirrorInfoTransaction?.source_company_name}</strong>.
                  </>
                )}
              </p>
            </div>
            
            <div className="space-y-2">
              <p className="text-sm"><strong>Descrição:</strong> {mirrorInfoTransaction?.description}</p>
              <p className="text-sm"><strong>Valor:</strong> {mirrorInfoTransaction?.amount ? formatCurrency(mirrorInfoTransaction.amount) : '-'}</p>
              <p className="text-sm"><strong>Vencimento:</strong> {mirrorInfoTransaction?.due_date}</p>
              <p className="text-sm"><strong>Status:</strong> {mirrorInfoTransaction?.status}</p>
              <p className="text-sm"><strong>Empresa de origem:</strong> {mirrorInfoTransaction?.source_company_name}</p>
            </div>
            
            <div className="p-3 bg-amber-50 border border-amber-200 rounded-lg">
              <p className="text-xs text-amber-800">
                <AlertTriangle className="w-4 h-4 inline mr-1" />
                Transações espelho/acumuladas não podem ser editadas ou excluídas diretamente. 
                Para alterá-las, edite o lançamento original na empresa de origem ou altere as configurações de conciliação da conta.
              </p>
            </div>
            
            <div className="flex justify-end">
              <Button onClick={() => setMirrorInfoTransaction(null)}>Fechar</Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
      
      {/* Closing Day Confirmation Dialog */}
      <Dialog open={!!closingDayConfirmation} onOpenChange={() => handleClosingDayCancel()}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Calendar className="w-5 h-5 text-amber-500" />
              Ajuste de Data de Vencimento
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div className="p-4 bg-amber-50 border border-amber-200 rounded-lg">
              <p className="text-sm text-amber-800">
                {closingDayConfirmation?.message}
              </p>
            </div>
            
            <div className="space-y-2 text-sm">
              <p><strong>Data informada:</strong> {closingDayConfirmation?.original_date}</p>
              <p><strong>Dia de fechamento:</strong> {closingDayConfirmation?.closing_day}</p>
              <p><strong>Nova data de vencimento:</strong> <span className="text-primary font-semibold">{closingDayConfirmation?.adjusted_date}</span></p>
            </div>
            
            <div className="p-3 bg-blue-50 border border-blue-200 rounded-lg">
              <p className="text-xs text-blue-800">
                <AlertCircle className="w-4 h-4 inline mr-1" />
                Será criada uma referência (R$ 0,00) na data original para controle.
              </p>
            </div>
            
            <div className="flex justify-end gap-2">
              <Button variant="outline" onClick={handleClosingDayCancel}>Cancelar</Button>
              <Button onClick={handleClosingDayConfirm}>Confirmar Ajuste</Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
      
      {/* Cancel Confirmation Dialog */}
      <Dialog open={!!cancelConfirmation} onOpenChange={() => setCancelConfirmation(null)}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-amber-500">
              <AlertTriangle className="w-5 h-5" />
              Confirmar Cancelamento
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div className="p-4 bg-amber-50 border border-amber-200 rounded-lg">
              <p className="text-sm text-amber-800">
                {cancelConfirmation?.type === 'RECEITA' ? (
                  <>
                    Este <strong>recebimento</strong> será cancelado e removido do <strong>Saldo Credor</strong>, 
                    mas poderá ser reativado a qualquer momento.
                  </>
                ) : (
                  <>
                    Esta <strong>despesa</strong> será cancelada e removida do <strong>Saldo Devedor</strong>, 
                    mas poderá ser reativada a qualquer momento.
                  </>
                )}
              </p>
            </div>
            
            <div className="space-y-2 text-sm">
              <p><strong>Descrição:</strong> {cancelConfirmation?.transaction?.description}</p>
              <p><strong>Valor atual:</strong> {cancelConfirmation?.transaction?.amount ? formatCurrency(cancelConfirmation.transaction.amount) : 'R$ 0,00'}</p>
              <p><strong>Vencimento:</strong> {cancelConfirmation?.transaction?.due_date}</p>
            </div>
            
            <div className="p-3 bg-slate-100 border border-slate-200 rounded-lg">
              <p className="text-xs text-slate-600">
                <AlertCircle className="w-4 h-4 inline mr-1" />
                O valor será zerado (R$ 0,00) e o status alterado para "Cancelado".
              </p>
            </div>
            
            <div className="flex justify-end gap-2">
              <Button variant="outline" onClick={() => setCancelConfirmation(null)}>Voltar</Button>
              <Button variant="destructive" onClick={handleConfirmCancel}>Confirmar Cancelamento</Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

// ==================== DRE PAGE ====================
const DREPage = ({ userPlan = 1, onLockedFeatureClick, companyId, selectedCompany, user }) => {
  const [dreData, setDreData] = useState(null);
  
  // Load initial month/year from localStorage (set by Dashboard)
  const getInitialMonth = () => {
    const saved = localStorage.getItem(LS_DASHBOARD_MONTH);
    return saved ? Number(saved) : new Date().getMonth() + 1;
  };
  const getInitialYear = () => {
    const saved = localStorage.getItem(LS_DASHBOARD_YEAR);
    return saved ? Number(saved) : new Date().getFullYear();
  };
  
  const [selectedYear, setSelectedYear] = useState(getInitialYear);
  const [selectedMonths, setSelectedMonths] = useState([getInitialMonth()]);
  const [showChart, setShowChart] = useState(false);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    loadDRE();
  }, [selectedYear, selectedMonths, companyId]);

  const loadDRE = async () => {
    try {
      setLoading(true);
      const monthsParam = selectedMonths.join(',');
      let params = `months=${monthsParam}&year=${selectedYear}`;
      if (companyId) {
        params += `&company_id=${companyId}`;
      }
      const response = await axios.get(`${API}/reports/dre?${params}`);
      setDreData(response.data);
    } catch (error) {
      console.error('Error loading DRE:', error);
    } finally {
      setLoading(false);
    }
  };

  // Toggle month selection
  const toggleMonth = (month) => {
    setSelectedMonths(prev => {
      if (prev.includes(month)) {
        // Don't allow deselecting if it's the only one
        if (prev.length === 1) return prev;
        return prev.filter(m => m !== month);
      } else {
        return [...prev, month].sort((a, b) => a - b);
      }
    });
  };

  // Select all months
  const selectAllMonths = () => {
    setSelectedMonths([1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 11, 12]);
  };

  // Handle chart button click - check plan
  const handleChartClick = () => {
    // Plan 1 = Básico (blocked), Plan 2+ = Advanced or higher (allowed)
    if (userPlan < 2) {
      if (onLockedFeatureClick) {
        onLockedFeatureClick(2); // Requires Advanced plan (2)
      }
      return;
    }
    setShowChart(!showChart);
  };

  // Handle PDF export - check plan
  const handlePDFExport = () => {
    if (userPlan < 2) {
      if (onLockedFeatureClick) {
        onLockedFeatureClick(2); // Requires Advanced plan (2)
      }
      return;
    }
    exportDREtoPDF();
  };

  // Export DRE to PDF
  const exportDREtoPDF = () => {
    if (!dreData) return;
    
    // Build period text based on selected months
    const monthNames = selectedMonths.length === 12 
      ? 'Anual' 
      : selectedMonths.map(m => MONTHS[m - 1].slice(0, 3)).join(', ');
    const periodText = `${monthNames} ${selectedYear}`;
    
    // Add company info to PDF - show name + CPF/CNPJ
    const getCompanyInfoForPDF = () => {
      if (!selectedCompany) return '';
      
      if (selectedCompany.is_personal) {
        // Personal account: show user name + CPF
        const docType = selectedCompany.document_type || 'CPF';
        const docValue = selectedCompany.cnpj || '';
        const formattedDoc = docValue ? ` - ${docType}: ${docValue}` : '';
        return `<p style="color: #666; font-size: 14px; margin: 5px 0 15px 0; font-weight: 500;">${user?.name || 'Conta Principal'}${formattedDoc}</p>`;
      } else {
        // Company: show company name + CNPJ
        const docType = selectedCompany.document_type || 'CNPJ';
        const docValue = selectedCompany.cnpj || '';
        const formattedDoc = docValue ? ` - ${docType}: ${docValue}` : '';
        return `<p style="color: #666; font-size: 14px; margin: 5px 0 15px 0; font-weight: 500;">${selectedCompany.razao_social}${formattedDoc}</p>`;
      }
    };
    const companyInfo = getCompanyInfoForPDF();
    
    const printContent = `
      <!DOCTYPE html>
      <html>
      <head>
        <meta charset="utf-8">
        <title>DRE - ${periodText}</title>
        <style>
          body { font-family: Arial, sans-serif; padding: 20px; color: #333; }
          h1 { color: #10b981; border-bottom: 2px solid #10b981; padding-bottom: 10px; }
          h2 { color: #666; font-size: 14px; margin-top: 0; }
          .summary { display: flex; gap: 20px; margin: 20px 0; flex-wrap: wrap; }
          .summary-card { padding: 15px; border-radius: 8px; min-width: 150px; }
          .summary-card.receitas { background: #d1fae5; border-left: 4px solid #10b981; }
          .summary-card.custos { background: #fef3c7; border-left: 4px solid #f59e0b; }
          .summary-card.despesas { background: #fee2e2; border-left: 4px solid #ef4444; }
          .summary-card.resultado { background: #dbeafe; border-left: 4px solid #3b82f6; }
          .summary-card.prejuizo { background: #fee2e2; border-left: 4px solid #dc2626; }
          .summary-card p { margin: 0; }
          .summary-card .label { font-size: 12px; color: #666; }
          .summary-card .value { font-size: 18px; font-weight: bold; }
          .summary-card .value.receitas { color: #10b981; }
          .summary-card .value.custos { color: #f59e0b; }
          .summary-card .value.despesas { color: #ef4444; }
          .summary-card .value.lucro { color: #3b82f6; }
          .summary-card .value.prejuizo { color: #dc2626; }
          table { width: 100%; border-collapse: collapse; margin-top: 20px; }
          th, td { padding: 10px; text-align: left; border-bottom: 1px solid #e5e7eb; }
          th { background: #f3f4f6; font-weight: 600; }
          .text-right { text-align: right; }
          .row-receitas { color: #10b981; font-weight: 600; }
          .row-custos { color: #f59e0b; font-weight: 600; }
          .row-despesas { color: #ef4444; font-weight: 600; }
          .row-lucro { color: #3b82f6; font-weight: 700; }
          .row-prejuizo { color: #dc2626; font-weight: 700; }
          .item-row td:first-child { padding-left: 30px; }
          .footer { margin-top: 30px; text-align: center; font-size: 12px; color: #999; }
          @media print { body { padding: 0; } }
        </style>
      </head>
      <body>
        <h1>DRE - Demonstrativo de Resultado</h1>
        ${companyInfo}
        <h2>${periodText}</h2>
        
        <div class="summary">
          <div class="summary-card receitas">
            <p class="label">Receitas</p>
            <p class="value receitas">${formatCurrency(dreData.receitas.total)}</p>
          </div>
          <div class="summary-card custos">
            <p class="label">(-) Custos</p>
            <p class="value custos">${formatCurrency(dreData.custos.total)}</p>
          </div>
          <div class="summary-card despesas">
            <p class="label">(-) Despesas</p>
            <p class="value despesas">${formatCurrency(dreData.despesas.total)}</p>
          </div>
          <div class="summary-card ${dreData.is_profit ? 'resultado' : 'prejuizo'}">
            <p class="label">${dreData.is_profit ? 'Lucro Líquido' : 'Prejuízo'}</p>
            <p class="value ${dreData.is_profit ? 'lucro' : 'prejuizo'}">${formatCurrency(Math.abs(dreData.resultado_liquido))}</p>
          </div>
        </div>
        
        <table>
          <thead>
            <tr>
              <th>Descrição</th>
              <th class="text-right">Valor</th>
            </tr>
          </thead>
          <tbody>
            <tr class="row-receitas">
              <td>RECEITAS</td>
              <td class="text-right">${formatCurrency(dreData.receitas.total)}</td>
            </tr>
            ${dreData.receitas.items.map(item => `
              <tr class="item-row">
                <td>${item.name}</td>
                <td class="text-right">${formatCurrency(item.value)}</td>
              </tr>
            `).join('')}
            <tr class="row-custos">
              <td>(-) CUSTOS</td>
              <td class="text-right">${formatCurrency(dreData.custos.total)}</td>
            </tr>
            ${dreData.custos.items.map(item => `
              <tr class="item-row">
                <td>${item.name}</td>
                <td class="text-right">${formatCurrency(item.value)}</td>
              </tr>
            `).join('')}
            <tr>
              <td><strong>(=) LUCRO BRUTO</strong></td>
              <td class="text-right"><strong>${formatCurrency(dreData.lucro_bruto)}</strong></td>
            </tr>
            <tr class="row-despesas">
              <td>(-) DESPESAS</td>
              <td class="text-right">${formatCurrency(dreData.despesas.total)}</td>
            </tr>
            ${dreData.despesas.items.map(item => `
              <tr class="item-row">
                <td>${item.name}</td>
                <td class="text-right">${formatCurrency(item.value)}</td>
              </tr>
            `).join('')}
            <tr class="${dreData.is_profit ? 'row-lucro' : 'row-prejuizo'}">
              <td><strong>(=) ${dreData.is_profit ? 'LUCRO LÍQUIDO' : 'PREJUÍZO'}</strong></td>
              <td class="text-right"><strong>${formatCurrency(Math.abs(dreData.resultado_liquido))}</strong></td>
            </tr>
          </tbody>
        </table>
        
        <div class="footer">
          <p>Gerado pelo DRE+ em ${new Date().toLocaleDateString('pt-BR')} às ${new Date().toLocaleTimeString('pt-BR')}</p>
        </div>
      </body>
      </html>
    `;
    
    const printWindow = window.open('', '_blank');
    printWindow.document.write(printContent);
    printWindow.document.close();
    printWindow.focus();
    setTimeout(() => {
      printWindow.print();
    }, 250);
  };

  const years = Array.from({ length: 5 }, (_, i) => new Date().getFullYear() - 2 + i);

  // Simple SVG Pie Chart
  const PieChartComponent = ({ data }) => {
    if (!data || data.length === 0) return null;
    
    const total = data.reduce((sum, item) => sum + item.value, 0);
    if (total === 0) return null;
    
    const colors = ['#10b981', '#3b82f6', '#f59e0b', '#ef4444', '#8b5cf6', '#ec4899', '#14b8a6', '#f97316'];
    let currentAngle = 0;
    
    const slices = data.map((item, i) => {
      const percentage = (item.value / total) * 100;
      const angle = (item.value / total) * 360;
      const startAngle = currentAngle;
      currentAngle += angle;
      
      // Handle single item (full circle) case
      if (data.length === 1 || percentage >= 99.9) {
        return {
          isFullCircle: true,
          color: colors[i % colors.length],
          name: item.name,
          value: item.value,
          percentage
        };
      }
      
      const startRad = (startAngle - 90) * Math.PI / 180;
      const endRad = (currentAngle - 90) * Math.PI / 180;
      
      const x1 = 50 + 40 * Math.cos(startRad);
      const y1 = 50 + 40 * Math.sin(startRad);
      const x2 = 50 + 40 * Math.cos(endRad);
      const y2 = 50 + 40 * Math.sin(endRad);
      
      const largeArc = angle > 180 ? 1 : 0;
      
      return {
        isFullCircle: false,
        path: `M 50 50 L ${x1} ${y1} A 40 40 0 ${largeArc} 1 ${x2} ${y2} Z`,
        color: colors[i % colors.length],
        name: item.name,
        value: item.value,
        percentage
      };
    });

    return (
      <div className="flex flex-col md:flex-row items-center gap-4">
        <svg viewBox="0 0 100 100" className="w-48 h-48">
          {slices.map((slice, i) => (
            slice.isFullCircle ? (
              <circle key={i} cx="50" cy="50" r="40" fill={slice.color} stroke="hsl(var(--background))" strokeWidth="0.5" />
            ) : (
              <path key={i} d={slice.path} fill={slice.color} stroke="hsl(var(--background))" strokeWidth="0.5" />
            )
          ))}
        </svg>
        <div className="space-y-1 text-sm">
          {slices.map((slice, i) => (
            <div key={i} className="flex items-center gap-2">
              <div className="w-3 h-3 rounded" style={{ backgroundColor: slice.color }}></div>
              <span className="text-muted-foreground">{slice.name.split(' - ')[1] || slice.name}</span>
              <span className="font-mono">{slice.percentage.toFixed(1)}%</span>
            </div>
          ))}
        </div>
      </div>
    );
  };

  // Get company/personal info for display
  const getCompanyDisplayInfo = () => {
    if (!selectedCompany) return null;
    
    if (selectedCompany.is_personal) {
      const docType = selectedCompany.document_type || 'CPF';
      const docValue = selectedCompany.cnpj || '';
      return {
        name: user?.name || 'Conta Principal',
        document: docValue ? `${docType}: ${docValue}` : null
      };
    } else {
      const docType = selectedCompany.document_type || 'CNPJ';
      const docValue = selectedCompany.cnpj || '';
      return {
        name: selectedCompany.razao_social,
        document: docValue ? `${docType}: ${docValue}` : null
      };
    }
  };
  const companyDisplayInfo = getCompanyDisplayInfo();

  return (
    <div className="animate-fadeIn space-y-6" data-testid="dre-page">
      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
        <div>
          <h2 className="text-3xl font-semibold tracking-tight">DRE - Demonstrativo de Resultado</h2>
          {companyDisplayInfo && (
            <p className="text-muted-foreground font-medium">
              {companyDisplayInfo.name}
              {companyDisplayInfo.document && <span className="text-sm ml-2">({companyDisplayInfo.document})</span>}
            </p>
          )}
        </div>
        <div className="flex items-center gap-2">
          <Select value={String(selectedYear)} onValueChange={(v) => setSelectedYear(Number(v))}>
            <SelectTrigger className="w-24" data-testid="dre-year-select"><SelectValue /></SelectTrigger>
            <SelectContent>
              {years.map((year) => (<SelectItem key={year} value={String(year)}>{year}</SelectItem>))}
            </SelectContent>
          </Select>
          <Button variant="outline" onClick={handleChartClick} className="gap-2" data-testid="dre-chart-btn">
            <PieChart className="w-4 h-4" /> {showChart ? 'Tabela' : 'Gráfico'}
          </Button>
          <Button variant="outline" onClick={handlePDFExport} className="gap-2" data-testid="dre-pdf-btn">
            <Download className="w-4 h-4" /> PDF
          </Button>
        </div>
      </div>

      {/* Month selector */}
      <Card>
        <CardContent className="p-3">
          <div className="flex flex-wrap items-center gap-2">
            <span className="text-sm text-muted-foreground mr-2">Meses:</span>
            {MONTHS.map((month, i) => (
              <button
                key={i}
                onClick={() => toggleMonth(i + 1)}
                className={`px-2 py-1 text-xs rounded border transition-colors ${
                  selectedMonths.includes(i + 1)
                    ? 'bg-primary text-primary-foreground border-primary'
                    : 'bg-background border-border hover:bg-muted'
                }`}
                data-testid={`dre-month-${i + 1}`}
              >
                {month.slice(0, 3)}
              </button>
            ))}
            <button
              onClick={selectAllMonths}
              className="px-2 py-1 text-xs rounded border border-border hover:bg-muted ml-2"
              data-testid="dre-month-all"
            >
              Todos
            </button>
          </div>
        </CardContent>
      </Card>

      {loading ? (
        <div className="flex justify-center py-12">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
        </div>
      ) : dreData && (
        <div className="space-y-6">
          {/* Summary Cards */}
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
            <Card className="border-l-4 border-l-emerald-500">
              <CardContent className="p-4">
                <p className="text-sm text-muted-foreground">Receitas</p>
                <p className="text-xl font-bold text-emerald-500 tabular-nums">{formatCurrency(dreData.receitas.total)}</p>
              </CardContent>
            </Card>
            <Card className="border-l-4 border-l-amber-500">
              <CardContent className="p-4">
                <p className="text-sm text-muted-foreground">(-) Custos</p>
                <p className="text-xl font-bold text-amber-500 tabular-nums">{formatCurrency(dreData.custos.total)}</p>
              </CardContent>
            </Card>
            <Card className="border-l-4 border-l-red-500">
              <CardContent className="p-4">
                <p className="text-sm text-muted-foreground">(-) Despesas</p>
                <p className="text-xl font-bold text-red-500 tabular-nums">{formatCurrency(dreData.despesas.total)}</p>
              </CardContent>
            </Card>
            <Card className={`border-l-4 ${dreData.is_profit ? 'border-l-blue-500' : 'border-l-red-600'}`}>
              <CardContent className="p-4">
                <p className="text-sm text-muted-foreground">{dreData.is_profit ? 'Lucro Líquido' : 'Prejuízo'}</p>
                <p className={`text-xl font-bold tabular-nums ${dreData.is_profit ? 'text-blue-500' : 'text-red-600'}`}>
                  {formatCurrency(Math.abs(dreData.resultado_liquido))}
                </p>
              </CardContent>
            </Card>
          </div>

          {showChart ? (
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              <Card>
                <CardHeader><CardTitle className="text-emerald-500">Receitas</CardTitle></CardHeader>
                <CardContent><PieChartComponent data={dreData.receitas.items} /></CardContent>
              </Card>
              <Card>
                <CardHeader><CardTitle className="text-amber-500">Custos</CardTitle></CardHeader>
                <CardContent><PieChartComponent data={dreData.custos.items} /></CardContent>
              </Card>
              <Card>
                <CardHeader><CardTitle className="text-red-500">Despesas</CardTitle></CardHeader>
                <CardContent><PieChartComponent data={dreData.despesas.items} /></CardContent>
              </Card>
            </div>
          ) : (
            <Card>
              <CardContent className="p-0">
                <Table>
                  <TableHeader>
                    <TableRow className="bg-muted/50">
                      <TableHead className="w-1/2">Descrição</TableHead>
                      <TableHead className="text-right">Valor</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {/* Receitas */}
                    <TableRow className="bg-emerald-500/10">
                      <TableCell className="font-bold text-emerald-500">RECEITAS</TableCell>
                      <TableCell className="text-right font-bold text-emerald-500 tabular-nums">{formatCurrency(dreData.receitas.total)}</TableCell>
                    </TableRow>
                    {dreData.receitas.items.map((item, i) => (
                      <TableRow key={`r-${i}`}>
                        <TableCell className="pl-8 text-muted-foreground">{item.name}</TableCell>
                        <TableCell className="text-right tabular-nums">{formatCurrency(item.value)}</TableCell>
                      </TableRow>
                    ))}
                    
                    {/* Custos */}
                    <TableRow className="bg-amber-500/10">
                      <TableCell className="font-bold text-amber-500">(-) CUSTOS</TableCell>
                      <TableCell className="text-right font-bold text-amber-500 tabular-nums">{formatCurrency(dreData.custos.total)}</TableCell>
                    </TableRow>
                    {dreData.custos.items.map((item, i) => (
                      <TableRow key={`c-${i}`}>
                        <TableCell className="pl-8 text-muted-foreground">{item.name}</TableCell>
                        <TableCell className="text-right tabular-nums">{formatCurrency(item.value)}</TableCell>
                      </TableRow>
                    ))}
                    
                    {/* Lucro Bruto */}
                    <TableRow className="bg-blue-500/10">
                      <TableCell className="font-bold text-blue-400">(=) LUCRO BRUTO</TableCell>
                      <TableCell className="text-right font-bold text-blue-400 tabular-nums">{formatCurrency(dreData.lucro_bruto)}</TableCell>
                    </TableRow>
                    
                    {/* Despesas */}
                    <TableRow className="bg-red-500/10">
                      <TableCell className="font-bold text-red-500">(-) DESPESAS</TableCell>
                      <TableCell className="text-right font-bold text-red-500 tabular-nums">{formatCurrency(dreData.despesas.total)}</TableCell>
                    </TableRow>
                    {dreData.despesas.items.map((item, i) => (
                      <TableRow key={`d-${i}`}>
                        <TableCell className="pl-8 text-muted-foreground">{item.name}</TableCell>
                        <TableCell className="text-right tabular-nums">{formatCurrency(item.value)}</TableCell>
                      </TableRow>
                    ))}
                    
                    {/* Resultado */}
                    <TableRow className={dreData.is_profit ? 'bg-blue-500/20' : 'bg-red-600/20'}>
                      <TableCell className={`font-bold text-lg ${dreData.is_profit ? 'text-blue-500' : 'text-red-600'}`}>
                        (=) {dreData.is_profit ? 'LUCRO LÍQUIDO' : 'PREJUÍZO'}
                      </TableCell>
                      <TableCell className={`text-right font-bold text-lg tabular-nums ${dreData.is_profit ? 'text-blue-500' : 'text-red-600'}`}>
                        {formatCurrency(Math.abs(dreData.resultado_liquido))}
                      </TableCell>
                    </TableRow>
                  </TableBody>
                </Table>
              </CardContent>
            </Card>
          )}
        </div>
      )}
    </div>
  );
};

// ==================== REPORTS PAGE ====================
const ReportsPage = ({ userPlan = 1, onLockedFeatureClick, companyId, selectedCompany, user }) => {
  const [cashFlow, setCashFlow] = useState(null);
  const [selectedYear, setSelectedYear] = useState(new Date().getFullYear());
  const [loading, setLoading] = useState(true);
  const isMobile = useIsMobile();

  useEffect(() => {
    loadCashFlow();
  }, [selectedYear, companyId]);

  const loadCashFlow = async () => {
    try {
      setLoading(true);
      let url = `${API}/reports/cash-flow?year=${selectedYear}`;
      if (companyId) {
        url += `&company_id=${companyId}`;
      }
      const response = await axios.get(url);
      setCashFlow(response.data);
    } catch (error) {
      console.error('Error loading cash flow:', error);
    } finally {
      setLoading(false);
    }
  };

  // Handle PDF export - check plan
  const handlePDFExport = () => {
    if (userPlan < 2) {
      if (onLockedFeatureClick) {
        onLockedFeatureClick(2); // Requires Advanced plan (2)
      }
      return;
    }
    exportCashFlowToPDF();
  };

  // Export Cash Flow to PDF
  const exportCashFlowToPDF = () => {
    if (!cashFlow) return;
    
    // Get company/personal info for PDF
    const getCompanyInfoForPDF = () => {
      if (!selectedCompany) return '';
      
      if (selectedCompany.is_personal) {
        const docType = selectedCompany.document_type || 'CPF';
        const docValue = selectedCompany.cnpj || '';
        const formattedDoc = docValue ? ` - ${docType}: ${docValue}` : '';
        return `<p style="color: #666; font-size: 14px; margin: 5px 0 15px 0; font-weight: 500;">${user?.name || 'Conta Principal'}${formattedDoc}</p>`;
      } else {
        const docType = selectedCompany.document_type || 'CNPJ';
        const docValue = selectedCompany.cnpj || '';
        const formattedDoc = docValue ? ` - ${docType}: ${docValue}` : '';
        return `<p style="color: #666; font-size: 14px; margin: 5px 0 15px 0; font-weight: 500;">${selectedCompany.razao_social}${formattedDoc}</p>`;
      }
    };
    const companyInfo = getCompanyInfoForPDF();
    
    const printContent = `
      <!DOCTYPE html>
      <html>
      <head>
        <meta charset="utf-8">
        <title>Fluxo de Caixa - ${selectedYear}</title>
        <style>
          body { font-family: Arial, sans-serif; padding: 20px; color: #333; }
          h1 { color: #10b981; border-bottom: 2px solid #10b981; padding-bottom: 10px; }
          h2 { color: #666; font-size: 14px; margin-top: 0; }
          .summary { display: flex; gap: 20px; margin: 20px 0; flex-wrap: wrap; }
          .summary-card { padding: 15px; border-radius: 8px; min-width: 150px; }
          .summary-card.receitas { background: #d1fae5; border-left: 4px solid #10b981; }
          .summary-card.despesas { background: #fee2e2; border-left: 4px solid #ef4444; }
          .summary-card.custos { background: #fef3c7; border-left: 4px solid #f59e0b; }
          .summary-card.saldo-positivo { background: #dbeafe; border-left: 4px solid #3b82f6; }
          .summary-card.saldo-negativo { background: #fee2e2; border-left: 4px solid #dc2626; }
          .summary-card p { margin: 0; }
          .summary-card .label { font-size: 12px; color: #666; }
          .summary-card .value { font-size: 18px; font-weight: bold; }
          .summary-card .value.receitas { color: #10b981; }
          .summary-card .value.despesas { color: #ef4444; }
          .summary-card .value.custos { color: #f59e0b; }
          .summary-card .value.saldo-positivo { color: #3b82f6; }
          .summary-card .value.saldo-negativo { color: #dc2626; }
          table { width: 100%; border-collapse: collapse; margin-top: 20px; }
          th, td { padding: 10px; text-align: left; border-bottom: 1px solid #e5e7eb; }
          th { background: #f3f4f6; font-weight: 600; }
          .text-right { text-align: right; }
          .text-receitas { color: #10b981; }
          .text-despesas { color: #ef4444; }
          .text-custos { color: #f59e0b; }
          .text-saldo-positivo { color: #3b82f6; font-weight: bold; }
          .text-saldo-negativo { color: #dc2626; font-weight: bold; }
          .footer { margin-top: 30px; text-align: center; font-size: 12px; color: #999; }
          @media print { body { padding: 0; } }
        </style>
      </head>
      <body>
        <h1>Relatório de Fluxo de Caixa</h1>
        ${companyInfo}
        <h2>Ano ${selectedYear}</h2>
        
        <div class="summary">
          <div class="summary-card receitas">
            <p class="label">Total Receitas</p>
            <p class="value receitas">${formatCurrency(cashFlow.total_receitas)}</p>
          </div>
          <div class="summary-card despesas">
            <p class="label">Total Despesas</p>
            <p class="value despesas">${formatCurrency(cashFlow.total_despesas)}</p>
          </div>
          <div class="summary-card custos">
            <p class="label">Total Custos</p>
            <p class="value custos">${formatCurrency(cashFlow.total_custos)}</p>
          </div>
          <div class="summary-card ${cashFlow.total_saldo >= 0 ? 'saldo-positivo' : 'saldo-negativo'}">
            <p class="label">Saldo Anual</p>
            <p class="value ${cashFlow.total_saldo >= 0 ? 'saldo-positivo' : 'saldo-negativo'}">${formatCurrency(cashFlow.total_saldo)}</p>
          </div>
        </div>
        
        <table>
          <thead>
            <tr>
              <th>Mês</th>
              <th class="text-right">Receitas</th>
              <th class="text-right">Despesas</th>
              <th class="text-right">Custos</th>
              <th class="text-right">Saldo</th>
            </tr>
          </thead>
          <tbody>
            ${cashFlow.months.map(m => `
              <tr>
                <td>${MONTHS[m.month - 1]}</td>
                <td class="text-right text-receitas">${formatCurrency(m.receitas)}</td>
                <td class="text-right text-despesas">${formatCurrency(m.despesas)}</td>
                <td class="text-right text-custos">${formatCurrency(m.custos)}</td>
                <td class="text-right ${m.saldo >= 0 ? 'text-saldo-positivo' : 'text-saldo-negativo'}">${formatCurrency(m.saldo)}</td>
              </tr>
            `).join('')}
          </tbody>
        </table>
        
        <div class="footer">
          <p>Gerado pelo DRE+ em ${new Date().toLocaleDateString('pt-BR')} às ${new Date().toLocaleTimeString('pt-BR')}</p>
        </div>
      </body>
      </html>
    `;
    
    const printWindow = window.open('', '_blank');
    printWindow.document.write(printContent);
    printWindow.document.close();
    printWindow.focus();
    setTimeout(() => {
      printWindow.print();
    }, 250);
  };

  // Format currency without R$ symbol for mobile
  const formatValue = (value) => {
    if (isMobile) {
      return new Intl.NumberFormat('pt-BR', {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2
      }).format(value || 0);
    }
    return formatCurrency(value);
  };

  const years = Array.from({ length: 5 }, (_, i) => new Date().getFullYear() - 2 + i);

  // Get company/personal info for display
  const getCompanyDisplayInfo = () => {
    if (!selectedCompany) return null;
    
    if (selectedCompany.is_personal) {
      const docType = selectedCompany.document_type || 'CPF';
      const docValue = selectedCompany.cnpj || '';
      return {
        name: user?.name || 'Conta Principal',
        document: docValue ? `${docType}: ${docValue}` : null
      };
    } else {
      const docType = selectedCompany.document_type || 'CNPJ';
      const docValue = selectedCompany.cnpj || '';
      return {
        name: selectedCompany.razao_social,
        document: docValue ? `${docType}: ${docValue}` : null
      };
    }
  };
  const companyDisplayInfo = getCompanyDisplayInfo();

  return (
    <div className="animate-fadeIn space-y-6" data-testid="reports-page">
      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
        <div>
          <h2 className="text-3xl font-semibold tracking-tight">Relatórios</h2>
          {companyDisplayInfo && (
            <p className="text-muted-foreground font-medium">
              {companyDisplayInfo.name}
              {companyDisplayInfo.document && <span className="text-sm ml-2">({companyDisplayInfo.document})</span>}
            </p>
          )}
          <p className="text-muted-foreground text-sm">Fluxo de Caixa Anual</p>
        </div>
        <div className="flex items-center gap-2">
          <Select value={String(selectedYear)} onValueChange={(v) => setSelectedYear(Number(v))}>
            <SelectTrigger className="w-24"><SelectValue /></SelectTrigger>
            <SelectContent>
              {years.map((year) => (<SelectItem key={year} value={String(year)}>{year}</SelectItem>))}
            </SelectContent>
          </Select>
          <Button variant="outline" onClick={handlePDFExport} className="gap-2" data-testid="reports-pdf-btn">
            <Download className="w-4 h-4" /> PDF
          </Button>
        </div>
      </div>

      {loading ? (
        <div className="flex justify-center py-12">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
        </div>
      ) : cashFlow && (
        <>
          {/* Summary */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            <Card className="border-l-4 border-l-emerald-500">
              <CardContent className="p-3 md:p-4">
                <p className="text-xs md:text-sm text-muted-foreground">Total Receitas</p>
                <p className="text-base md:text-xl font-bold text-emerald-500 tabular-nums">{formatValue(cashFlow.total_receitas)}</p>
              </CardContent>
            </Card>
            <Card className="border-l-4 border-l-red-500">
              <CardContent className="p-3 md:p-4">
                <p className="text-xs md:text-sm text-muted-foreground">Total Despesas</p>
                <p className="text-base md:text-xl font-bold text-red-500 tabular-nums">{formatValue(cashFlow.total_despesas)}</p>
              </CardContent>
            </Card>
            <Card className="border-l-4 border-l-amber-500">
              <CardContent className="p-3 md:p-4">
                <p className="text-xs md:text-sm text-muted-foreground">Total Custos</p>
                <p className="text-base md:text-xl font-bold text-amber-500 tabular-nums">{formatValue(cashFlow.total_custos)}</p>
              </CardContent>
            </Card>
            <Card className={`border-l-4 ${cashFlow.total_saldo >= 0 ? 'border-l-blue-500' : 'border-l-red-600'}`}>
              <CardContent className="p-3 md:p-4">
                <p className="text-xs md:text-sm text-muted-foreground">Saldo Anual</p>
                <p className={`text-base md:text-xl font-bold tabular-nums ${cashFlow.total_saldo >= 0 ? 'text-blue-500' : 'text-red-600'}`}>
                  {formatValue(cashFlow.total_saldo)}
                </p>
              </CardContent>
            </Card>
            {cashFlow.saldo_inicial !== undefined && (
              <Card className="border-l-4 border-l-purple-500">
                <CardContent className="p-3 md:p-4">
                  <p className="text-xs md:text-sm text-muted-foreground">Saldo Inicial</p>
                  <p className="text-base md:text-xl font-bold text-purple-500 tabular-nums">{formatValue(cashFlow.saldo_inicial)}</p>
                </CardContent>
              </Card>
            )}
            {cashFlow.saldo_final !== undefined && (
              <Card className={`border-l-4 ${cashFlow.saldo_final >= 0 ? 'border-l-green-500' : 'border-l-red-600'}`}>
                <CardContent className="p-3 md:p-4">
                  <p className="text-xs md:text-sm text-muted-foreground">Saldo Final</p>
                  <p className={`text-base md:text-xl font-bold tabular-nums ${cashFlow.saldo_final >= 0 ? 'text-green-500' : 'text-red-600'}`}>
                    {formatValue(cashFlow.saldo_final)}
                  </p>
                </CardContent>
              </Card>
            )}
          </div>

          {/* Monthly Table */}
          <Card>
            <CardContent className="p-0">
              <Table>
                <TableHeader>
                  <TableRow className="bg-muted/50">
                    <TableHead className="text-xs md:text-sm">Mês</TableHead>
                    <TableHead className="text-right text-emerald-500 text-xs md:text-sm">{isMobile ? 'Rec.' : 'Receitas'}</TableHead>
                    <TableHead className="text-right text-red-500 text-xs md:text-sm">{isMobile ? 'Desp.' : 'Despesas'}</TableHead>
                    <TableHead className="text-right text-amber-500 text-xs md:text-sm">{isMobile ? 'Cust.' : 'Custos'}</TableHead>
                    <TableHead className="text-right text-xs md:text-sm">Saldo</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {cashFlow.months.map((m) => (
                    <TableRow key={m.month}>
                      <TableCell className="font-medium text-xs md:text-sm">{isMobile ? MONTHS[m.month - 1].slice(0, 3) : MONTHS[m.month - 1]}</TableCell>
                      <TableCell className="text-right font-mono tabular-nums text-emerald-500 text-xs md:text-sm">{formatValue(m.receitas)}</TableCell>
                      <TableCell className="text-right font-mono tabular-nums text-red-500 text-xs md:text-sm">{formatValue(m.despesas)}</TableCell>
                      <TableCell className="text-right font-mono tabular-nums text-amber-500 text-xs md:text-sm">{formatValue(m.custos)}</TableCell>
                      <TableCell className={`text-right font-mono tabular-nums font-bold text-xs md:text-sm ${m.saldo >= 0 ? 'text-blue-500' : 'text-red-600'}`}>
                        {formatValue(m.saldo)}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </CardContent>
          </Card>
        </>
      )}
    </div>
  );
};

// ==================== RECEIPTS PAGE ====================
const ReceiptsPage = ({ userPlan = 1, onLockedFeatureClick, companyId, selectedCompany, user, onNavigate }) => {
  const [clients, setClients] = useState([]);
  const [loading, setLoading] = useState(true);
  const [formData, setFormData] = useState({
    client_id: '',
    amount: '',
    reference: '',
    date: new Date().toISOString().split('T')[0],
    payment_method: '',
    signature: ''
  });
  const { toast } = useToast();

  // Fetch clients on mount
  useEffect(() => {
    const fetchClients = async () => {
      try {
        const companyParam = companyId ? `?company_id=${companyId}` : '';
        const res = await axios.get(`${API}/clients${companyParam}`);
        setClients(res.data);
      } catch (error) {
        console.error('Error loading clients:', error);
      } finally {
        setLoading(false);
      }
    };
    fetchClients();
  }, [companyId]);

  // Get selected client data
  const selectedClient = clients.find(c => c.id === formData.client_id);

  // Format CPF
  const formatCPF = (value) => {
    if (!value) return '';
    const digits = value.replace(/\D/g, '').slice(0, 11);
    if (digits.length <= 3) return digits;
    if (digits.length <= 6) return `${digits.slice(0,3)}.${digits.slice(3)}`;
    if (digits.length <= 9) return `${digits.slice(0,3)}.${digits.slice(3,6)}.${digits.slice(6)}`;
    return `${digits.slice(0,3)}.${digits.slice(3,6)}.${digits.slice(6,9)}-${digits.slice(9)}`;
  };

  // Format CNPJ
  const formatCNPJ = (value) => {
    if (!value) return '';
    const digits = value.replace(/\D/g, '').slice(0, 14);
    if (digits.length <= 2) return digits;
    if (digits.length <= 5) return `${digits.slice(0,2)}.${digits.slice(2)}`;
    if (digits.length <= 8) return `${digits.slice(0,2)}.${digits.slice(2,5)}.${digits.slice(5)}`;
    if (digits.length <= 12) return `${digits.slice(0,2)}.${digits.slice(2,5)}.${digits.slice(5,8)}/${digits.slice(8)}`;
    return `${digits.slice(0,2)}.${digits.slice(2,5)}.${digits.slice(5,8)}/${digits.slice(8,12)}-${digits.slice(12)}`;
  };

  // Format document based on type
  const formatDocument = (doc, type) => {
    if (!doc) return '';
    if (type === 'CPF' || doc.length <= 11) return formatCPF(doc);
    return formatCNPJ(doc);
  };

  // Convert number to words in Brazilian Portuguese
  const numberToWords = (num) => {
    if (num === 0) return 'zero reais';
    
    const units = ['', 'um', 'dois', 'três', 'quatro', 'cinco', 'seis', 'sete', 'oito', 'nove'];
    const teens = ['dez', 'onze', 'doze', 'treze', 'quatorze', 'quinze', 'dezesseis', 'dezessete', 'dezoito', 'dezenove'];
    const tens = ['', '', 'vinte', 'trinta', 'quarenta', 'cinquenta', 'sessenta', 'setenta', 'oitenta', 'noventa'];
    const hundreds = ['', 'cento', 'duzentos', 'trezentos', 'quatrocentos', 'quinhentos', 'seiscentos', 'setecentos', 'oitocentos', 'novecentos'];

    const convertGroup = (n) => {
      if (n === 0) return '';
      if (n === 100) return 'cem';
      
      let result = '';
      const h = Math.floor(n / 100);
      const t = Math.floor((n % 100) / 10);
      const u = n % 10;
      
      if (h > 0) result += hundreds[h];
      if (t === 1) {
        result += (result ? ' e ' : '') + teens[u];
      } else {
        if (t > 0) result += (result ? ' e ' : '') + tens[t];
        if (u > 0) result += (result ? ' e ' : '') + units[u];
      }
      return result;
    };

    const intPart = Math.floor(num);
    const decPart = Math.round((num - intPart) * 100);
    
    let result = '';
    
    if (intPart >= 1000000) {
      const millions = Math.floor(intPart / 1000000);
      result += millions === 1 ? 'um milhão' : convertGroup(millions) + ' milhões';
      const rest = intPart % 1000000;
      if (rest > 0) result += ' ' + (rest < 100 ? 'e ' : '') + convertGroup(Math.floor(rest / 1000)) + (Math.floor(rest / 1000) > 0 ? ' mil' : '');
      if (rest % 1000 > 0) result += (Math.floor(rest / 1000) > 0 ? ' e ' : '') + convertGroup(rest % 1000);
    } else if (intPart >= 1000) {
      const thousands = Math.floor(intPart / 1000);
      result += thousands === 1 ? 'mil' : convertGroup(thousands) + ' mil';
      const rest = intPart % 1000;
      if (rest > 0) result += (rest < 100 ? ' e ' : ' ') + convertGroup(rest);
    } else {
      result = convertGroup(intPart);
    }
    
    result += intPart === 1 ? ' real' : ' reais';
    
    if (decPart > 0) {
      result += ' e ' + convertGroup(decPart) + (decPart === 1 ? ' centavo' : ' centavos');
    }
    
    return result;
  };

  // Get signature options based on company type
  const getSignatureOptions = () => {
    if (!selectedCompany) return [];
    
    if (selectedCompany.is_personal) {
      return [{ value: user?.name || 'Usuário', label: user?.name || 'Usuário' }];
    } else {
      const options = [];
      if (selectedCompany.responsavel_1) {
        options.push({ value: selectedCompany.responsavel_1, label: selectedCompany.responsavel_1 });
      }
      if (selectedCompany.responsavel_2) {
        options.push({ value: selectedCompany.responsavel_2, label: selectedCompany.responsavel_2 });
      }
      if (options.length === 0) {
        options.push({ value: selectedCompany.razao_social, label: selectedCompany.razao_social });
      }
      return options;
    }
  };

  // Get company/issuer document
  const getIssuerDocument = () => {
    if (!selectedCompany) return { type: '', value: '' };
    
    if (selectedCompany.is_personal) {
      // For personal account, use user's CPF from profile if available
      return { 
        type: selectedCompany.document_type || 'CPF', 
        value: selectedCompany.cnpj || '' 
      };
    } else {
      return { 
        type: selectedCompany.document_type || 'CNPJ', 
        value: selectedCompany.cnpj || '' 
      };
    }
  };

  // Export to PDF
  const exportToPDF = () => {
    if (!formData.client_id || !formData.amount || !formData.reference || !formData.payment_method || !formData.signature) {
      toast({ title: "Preencha todos os campos obrigatórios", variant: "destructive" });
      return;
    }

    const client = selectedClient;
    const issuerDoc = getIssuerDocument();
    const amountNum = parseFloat(formData.amount.replace(/[^\d,]/g, '').replace(',', '.')) || 0;
    const amountFormatted = amountNum.toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' });
    const amountInWords = numberToWords(amountNum);
    
    // Format date
    const dateObj = new Date(formData.date + 'T12:00:00');
    const dateFormatted = dateObj.toLocaleDateString('pt-BR', { day: '2-digit', month: 'long', year: 'numeric' });

    const companyName = selectedCompany?.nome_fantasia || selectedCompany?.razao_social || 'Empresa';
    const companyRazao = selectedCompany?.razao_social || '';
    const issuerDocFormatted = formatDocument(issuerDoc.value, issuerDoc.type);
    const clientDocFormatted = formatDocument(client?.document, client?.document_type);

    const printContent = `
      <!DOCTYPE html>
      <html>
      <head>
        <meta charset="utf-8">
        <title>Recibo - ${client?.name}</title>
        <link rel="preconnect" href="https://fonts.googleapis.com">
        <link rel="preconnect" href="https://fonts.gstatic.com" crossorigin>
        <link href="https://fonts.googleapis.com/css2?family=Great+Vibes&display=swap" rel="stylesheet">
        <style>
          @page { size: A4; margin: 20mm; }
          body { 
            font-family: 'Georgia', serif; 
            padding: 40px; 
            color: #333;
            background: linear-gradient(135deg, #faf8f5 0%, #f5f0e8 100%);
            min-height: 100vh;
          }
          .receipt-container {
            background: #fffef9;
            border: 2px solid #d4c4a8;
            border-radius: 8px;
            padding: 40px;
            max-width: 700px;
            margin: 0 auto;
            box-shadow: 0 4px 20px rgba(0,0,0,0.1);
          }
          .header {
            display: flex;
            justify-content: space-between;
            align-items: flex-start;
            margin-bottom: 40px;
            padding-bottom: 20px;
            border-bottom: 2px solid #d4c4a8;
          }
          .issuer-info {
            flex: 1;
          }
          .issuer-info .company-name {
            font-size: 28px;
            font-weight: bold;
            color: #8b7355;
            margin: 0 0 5px 0;
          }
          .issuer-info .razao-social {
            font-size: 14px;
            color: #666;
            margin: 0 0 3px 0;
          }
          .issuer-info .document {
            font-size: 13px;
            color: #666;
          }
          .receipt-title {
            text-align: right;
          }
          .receipt-title h1 {
            font-size: 32px;
            font-weight: bold;
            color: #8b7355;
            margin: 0;
            letter-spacing: 3px;
          }
          .receipt-title .amount-box {
            background: linear-gradient(135deg, #e8dcc8 0%, #d4c4a8 100%);
            padding: 15px 25px;
            border-radius: 8px;
            margin-top: 10px;
          }
          .receipt-title .amount {
            font-size: 24px;
            font-weight: bold;
            color: #5c4a32;
          }
          .body-text {
            font-size: 16px;
            line-height: 2;
            margin: 30px 0;
            text-align: justify;
          }
          .body-text .highlight {
            font-weight: bold;
            color: #5c4a32;
          }
          .body-text .amount-words {
            font-style: italic;
            background: #f5f0e8;
            padding: 2px 8px;
            border-radius: 4px;
          }
          .footer {
            margin-top: 50px;
            display: flex;
            justify-content: space-between;
            align-items: flex-end;
          }
          .date-section {
            font-size: 14px;
            color: #666;
          }
          .signature-section {
            text-align: center;
            min-width: 250px;
          }
          .signature-name {
            font-family: 'Great Vibes', cursive;
            font-size: 28px;
            color: #2c1810;
            margin-bottom: 10px;
          }
          .signature-line {
            border-top: 1px solid #8b7355;
            padding-top: 10px;
            font-size: 14px;
            color: #666;
          }
          .payment-method {
            display: inline-block;
            background: #e8dcc8;
            padding: 4px 12px;
            border-radius: 4px;
            font-weight: 500;
          }
        </style>
      </head>
      <body>
        <div class="receipt-container">
          <div class="header">
            <div class="issuer-info">
              <p class="company-name">${companyName}</p>
              ${companyRazao && companyRazao !== companyName ? `<p class="razao-social">${companyRazao}</p>` : ''}
              ${issuerDocFormatted ? `<p class="document">${issuerDoc.type}: ${issuerDocFormatted}</p>` : ''}
            </div>
            <div class="receipt-title">
              <h1>RECIBO</h1>
              <div class="amount-box">
                <span class="amount">${amountFormatted}</span>
              </div>
            </div>
          </div>
          
          <div class="body-text">
            ${selectedCompany?.is_personal ? `<p>Eu, <span class="highlight">${user?.name || ''}</span>,</p>` : ''}
            <p>
              Recebi de <span class="highlight">${client?.name || ''}</span>, portador do ${client?.document_type || 'CPF'} <span class="highlight">${clientDocFormatted || '___.___.___-__'}</span>,
            </p>
            <p>
              O valor de <span class="amount-words">${amountInWords}</span> (${amountFormatted})
            </p>
            <p>
              Recebido via <span class="payment-method">${formData.payment_method}</span>
            </p>
            <p>
              Referente a: <span class="highlight">${formData.reference}</span>
            </p>
          </div>
          
          <div class="footer">
            <div class="date-section">
              <p>Data de emissão: ${dateFormatted}</p>
            </div>
            <div class="signature-section">
              <p class="signature-name">${formData.signature}</p>
              <div class="signature-line">Assinatura</div>
            </div>
          </div>
        </div>
      </body>
      </html>
    `;

    const printWindow = window.open('', '_blank');
    printWindow.document.write(printContent);
    printWindow.document.close();
    setTimeout(() => printWindow.print(), 250);
  };

  // Format currency input
  const handleAmountChange = (value) => {
    const digits = value.replace(/\D/g, '');
    const num = parseInt(digits) / 100;
    if (isNaN(num)) {
      setFormData({...formData, amount: ''});
      return;
    }
    const formatted = num.toLocaleString('pt-BR', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
    setFormData({...formData, amount: formatted});
  };

  const signatureOptions = getSignatureOptions();
  const paymentMethods = ['Boleto', 'Cartão de Crédito', 'Cartão de Débito', 'Dinheiro', 'PIX', 'Transferência Bancária'];

  // Locked feature check
  if (userPlan < 2) {
    return (
      <div className="flex flex-col items-center justify-center h-[60vh] text-center p-6">
        <Lock className="w-16 h-16 text-muted-foreground mb-4" />
        <h2 className="text-2xl font-semibold mb-2">Recibos</h2>
        <p className="text-muted-foreground mb-4">
          Esta funcionalidade está disponível a partir do plano Advanced.
        </p>
        <Button onClick={() => onLockedFeatureClick && onLockedFeatureClick(2)}>
          Fazer Upgrade
        </Button>
      </div>
    );
  }

  return (
    <div className="animate-fadeIn space-y-6" data-testid="receipts-page">
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-2xl md:text-3xl font-semibold tracking-tight">Recibos</h2>
          <p className="text-muted-foreground">Gere recibos profissionais para seus clientes</p>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Formulário */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Receipt className="w-5 h-5" />
              Dados do Recibo
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div>
              <Label>Cliente *</Label>
              <Select value={formData.client_id} onValueChange={(v) => setFormData({...formData, client_id: v})}>
                <SelectTrigger data-testid="receipt-client-select">
                  <SelectValue placeholder="Selecione o cliente" />
                </SelectTrigger>
                <SelectContent>
                  {clients.map(c => (
                    <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
              {selectedClient && (
                <p className="text-xs text-muted-foreground mt-1">
                  {selectedClient.document_type}: {formatDocument(selectedClient.document, selectedClient.document_type)}
                </p>
              )}
            </div>

            <div>
              <Label>Valor (R$) *</Label>
              <Input 
                value={formData.amount}
                onChange={(e) => handleAmountChange(e.target.value)}
                placeholder="0,00"
                data-testid="receipt-amount-input"
              />
            </div>

            <div>
              <Label>Referente a *</Label>
              <Textarea 
                value={formData.reference}
                onChange={(e) => setFormData({...formData, reference: e.target.value})}
                placeholder="Descreva o serviço ou produto"
                rows={3}
                data-testid="receipt-reference-input"
              />
            </div>

            <div>
              <Label>Data *</Label>
              <Input 
                type="date"
                value={formData.date}
                onChange={(e) => setFormData({...formData, date: e.target.value})}
                data-testid="receipt-date-input"
              />
            </div>

            <div>
              <Label>Forma de Recebimento *</Label>
              <Select value={formData.payment_method} onValueChange={(v) => setFormData({...formData, payment_method: v})}>
                <SelectTrigger data-testid="receipt-payment-select">
                  <SelectValue placeholder="Selecione" />
                </SelectTrigger>
                <SelectContent>
                  {paymentMethods.map(m => (
                    <SelectItem key={m} value={m}>{m}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div>
              <Label>Assinatura *</Label>
              <Select value={formData.signature} onValueChange={(v) => setFormData({...formData, signature: v})}>
                <SelectTrigger data-testid="receipt-signature-select">
                  <SelectValue placeholder="Selecione quem assina" />
                </SelectTrigger>
                <SelectContent>
                  {signatureOptions.map(opt => (
                    <SelectItem key={opt.value} value={opt.value}>{opt.label}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
              {signatureOptions.length === 0 && (
                <p className="text-xs text-amber-500 mt-1">
                  Configure os responsáveis no cadastro da empresa
                </p>
              )}
            </div>

            <div className="flex gap-2">
              <Button 
                variant="outline"
                onClick={() => onNavigate && onNavigate('dashboard')}
                className="flex-1 gap-2"
                data-testid="receipt-cancel-btn"
              >
                Cancelar
              </Button>
              <Button 
                onClick={exportToPDF} 
                className="flex-1 gap-2"
                disabled={!formData.client_id || !formData.amount || !formData.reference || !formData.payment_method || !formData.signature}
                data-testid="receipt-export-btn"
              >
                <Printer className="w-4 h-4" />
                Gerar Recibo (PDF)
              </Button>
            </div>
          </CardContent>
        </Card>

        {/* Preview */}
        <Card className="bg-gradient-to-br from-amber-50 to-orange-50 dark:from-amber-950/20 dark:to-orange-950/20">
          <CardHeader>
            <CardTitle>Prévia do Recibo</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="bg-white dark:bg-gray-900 p-6 rounded-lg border shadow-sm space-y-4">
              <div className="flex justify-between items-start">
                <div>
                  <p className="font-bold text-lg text-amber-700 dark:text-amber-400">
                    {selectedCompany?.nome_fantasia || selectedCompany?.razao_social || 'Nome Fantasia'}
                  </p>
                  <p className="text-sm text-muted-foreground">{selectedCompany?.razao_social || 'Razão Social'}</p>
                  <p className="text-xs text-muted-foreground">
                    {getIssuerDocument().type}: {formatDocument(getIssuerDocument().value, getIssuerDocument().type) || '000.000.000-00'}
                  </p>
                </div>
                <div className="text-right">
                  <p className="text-xl font-bold text-amber-700 dark:text-amber-400">RECIBO</p>
                  <div className="bg-amber-100 dark:bg-amber-900/30 px-4 py-2 rounded mt-2">
                    <p className="font-bold">R$ {formData.amount || '0,00'}</p>
                  </div>
                </div>
              </div>
              
              <div className="border-t pt-4 space-y-2 text-sm">
                {selectedCompany?.is_personal && (
                  <p><strong>Eu,</strong> {user?.name || '[Usuário]'},</p>
                )}
                <p>
                  <strong>Recebi de:</strong> {selectedClient?.name || '[Cliente]'}, portador do {selectedClient?.document_type || 'CPF'} {selectedClient?.document ? formatDocument(selectedClient.document, selectedClient.document_type) : '___.___.___-__'},
                </p>
                <p><strong>Valor por extenso:</strong> {formData.amount ? numberToWords(parseFloat(formData.amount.replace(/[^\d,]/g, '').replace(',', '.')) || 0) : '[valor]'}</p>
                <p><strong>Forma:</strong> {formData.payment_method || '[forma de pagamento]'}</p>
                <p><strong>Referente a:</strong> {formData.reference || '[descrição]'}</p>
              </div>
              
              <div className="border-t pt-4 flex justify-between items-end text-sm">
                <p className="text-muted-foreground">
                  Data: {formData.date ? new Date(formData.date + 'T12:00:00').toLocaleDateString('pt-BR') : '__/__/____'}
                </p>
                <div className="text-center">
                  <p className="mb-2 text-2xl text-amber-900" style={{ fontFamily: "'Great Vibes', cursive" }}>{formData.signature || '[responsável]'}</p>
                  <div className="border-t border-gray-400 w-48 pt-1">
                    <p className="text-xs text-muted-foreground">Assinatura</p>
                  </div>
                </div>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

// ==================== ACCOUNTS PAYABLE PAGE ====================
const AccountsPayablePage = ({ userPlan = 1, onLockedFeatureClick, companyId, selectedCompany }) => {
  const [data, setData] = useState(null);
  
  // Load initial month/year from localStorage (set by Dashboard)
  const getInitialYear = () => {
    const saved = localStorage.getItem(LS_DASHBOARD_YEAR);
    return saved ? Number(saved) : new Date().getFullYear();
  };
  const getInitialMonth = () => {
    const saved = localStorage.getItem(LS_DASHBOARD_MONTH);
    return saved ? Number(saved) : new Date().getMonth() + 1;
  };
  
  const [selectedYear, setSelectedYear] = useState(getInitialYear);
  const [selectedMonths, setSelectedMonths] = useState([getInitialMonth()]);
  const [loading, setLoading] = useState(true);
  const isMobile = useIsMobile();

  useEffect(() => {
    loadData();
  }, [selectedYear, selectedMonths, companyId]);

  const loadData = async () => {
    try {
      setLoading(true);
      const monthsParam = selectedMonths.join(',');
      let url = `${API}/reports/accounts-payable?year=${selectedYear}&months=${monthsParam}`;
      if (companyId) {
        url += `&company_id=${companyId}`;
      }
      const response = await axios.get(url);
      setData(response.data);
    } catch (error) {
      console.error('Error loading accounts payable:', error);
    } finally {
      setLoading(false);
    }
  };

  // Toggle month selection
  const toggleMonth = (month) => {
    setSelectedMonths(prev => {
      if (prev.includes(month)) {
        // Don't allow deselecting if it's the only one
        if (prev.length === 1) return prev;
        return prev.filter(m => m !== month);
      } else {
        return [...prev, month].sort((a, b) => a - b);
      }
    });
  };

  // Select all months
  const selectAllMonths = () => {
    setSelectedMonths([1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 11, 12]);
  };

  // Format currency without R$ symbol for mobile
  const formatValue = (value) => {
    if (isMobile) {
      return new Intl.NumberFormat('pt-BR', {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2
      }).format(value || 0);
    }
    return formatCurrency(value);
  };

  // Format date - handle both DD/MM/YYYY and ISO formats
  const formatDateLocal = (dateStr) => {
    if (!dateStr) return '-';
    // If already in DD/MM/YYYY format, return as is
    if (dateStr.includes('/')) return dateStr;
    // Otherwise parse as ISO and format
    const dt = new Date(dateStr);
    return dt.toLocaleDateString('pt-BR');
  };

  // Truncate text for table
  const truncateText = (text, maxLength = 20) => {
    if (!text) return '-';
    if (text.length <= maxLength) return text;
    return text.substring(0, maxLength - 2) + '...';
  };

  // Get status badge
  const getStatusBadge = (status) => {
    if (status === 'PAGO') {
      return <Badge className="bg-emerald-600 text-xs">Pago</Badge>;
    }
    if (status === 'CANCELADO') {
      return <Badge className="bg-gray-500 text-xs">Cancelado</Badge>;
    }
    return <Badge variant="outline" className="text-amber-500 border-amber-500 text-xs">Pendente</Badge>;
  };

  // Handle PDF export
  const handlePDFExport = () => {
    if (userPlan < 2) {
      if (onLockedFeatureClick) {
        onLockedFeatureClick(2);
      }
      return;
    }
    exportToPDF();
  };

  // Export to PDF
  const exportToPDF = () => {
    if (!data) return;
    
    const monthNames = selectedMonths.map(m => MONTHS[m - 1]).join(', ');
    
    // Helper function for PDF date formatting
    const pdfFormatDate = (dateStr) => {
      if (!dateStr) return '-';
      if (dateStr.includes('/')) return dateStr;
      const dt = new Date(dateStr);
      return dt.toLocaleDateString('pt-BR');
    };
    
    const printContent = `
      <!DOCTYPE html>
      <html>
      <head>
        <meta charset="utf-8">
        <title>Contas a Pagar - ${monthNames} ${selectedYear}</title>
        <style>
          body { font-family: Arial, sans-serif; padding: 20px; color: #333; font-size: 12px; }
          h1 { color: #10b981; border-bottom: 2px solid #10b981; padding-bottom: 10px; font-size: 20px; }
          h2 { color: #666; font-size: 12px; margin-top: 0; }
          .summary { display: flex; gap: 15px; margin: 15px 0; flex-wrap: wrap; }
          .summary-card { padding: 10px 15px; border-radius: 8px; min-width: 140px; }
          .summary-card.despesas { background: #fee2e2; border-left: 4px solid #ef4444; }
          .summary-card.custos { background: #fef3c7; border-left: 4px solid #f59e0b; }
          .summary-card.total { background: #dbeafe; border-left: 4px solid #3b82f6; }
          .summary-card p { margin: 0; }
          .summary-card .label { font-size: 10px; color: #666; }
          .summary-card .value { font-size: 16px; font-weight: bold; }
          .summary-card .value.despesas { color: #ef4444; }
          .summary-card .value.custos { color: #f59e0b; }
          .summary-card .value.total { color: #3b82f6; }
          table { width: 100%; border-collapse: collapse; margin-top: 15px; font-size: 11px; }
          th, td { padding: 6px 8px; text-align: left; border-bottom: 1px solid #e5e7eb; }
          th { background: #f3f4f6; font-weight: 600; font-size: 10px; }
          .text-right { text-align: right; }
          .text-center { text-align: center; }
          .badge { padding: 2px 6px; border-radius: 4px; font-size: 9px; }
          .badge-pago { background: #d1fae5; color: #047857; }
          .badge-pendente { background: #fef3c7; color: #b45309; }
          .badge-despesa { background: #fee2e2; color: #dc2626; }
          .badge-custo { background: #fef3c7; color: #d97706; }
          .footer { margin-top: 20px; text-align: center; font-size: 10px; color: #999; }
          @media print { body { padding: 10px; } }
        </style>
      </head>
      <body>
        <h1>Contas a Pagar</h1>
        <h2>${monthNames} de ${selectedYear}</h2>
        
        <div class="summary">
          <div class="summary-card despesas">
            <p class="label">Total Despesas</p>
            <p class="value despesas">${formatCurrency(data.total_despesas)}</p>
          </div>
          <div class="summary-card custos">
            <p class="label">Total Custos</p>
            <p class="value custos">${formatCurrency(data.total_custos)}</p>
          </div>
          <div class="summary-card total">
            <p class="label">Total Geral</p>
            <p class="value total">${formatCurrency(data.total_geral)}</p>
          </div>
        </div>
        
        <table>
          <thead>
            <tr>
              <th>Vencimento</th>
              <th>Descrição</th>
              <th>Fornecedor</th>
              <th>Categoria</th>
              <th class="text-center">Status</th>
              <th class="text-center">Tipo</th>
              <th class="text-center">Fixa/Var</th>
              <th class="text-center">Doc</th>
              <th class="text-right">Valor</th>
            </tr>
          </thead>
          <tbody>
            ${data.transactions.map(t => `
              <tr>
                <td>${pdfFormatDate(t.due_date)}</td>
                <td>${t.description || '-'}</td>
                <td>${t.supplier_name || '-'}</td>
                <td>${t.account_name || '-'}</td>
                <td class="text-center"><span class="badge ${t.status === 'PAGO' ? 'badge-pago' : 'badge-pendente'}">${t.status === 'PAGO' ? 'Pago' : 'Pendente'}</span></td>
                <td class="text-center"><span class="badge ${t.transaction_type === 'DESPESA' ? 'badge-despesa' : 'badge-custo'}">${t.transaction_type === 'DESPESA' ? 'Despesa' : 'Custo'}</span></td>
                <td class="text-center"><span style="padding: 2px 6px; border-radius: 4px; font-size: 11px; background: ${t.expense_type === 'VARIAVEL' ? '#fef3c7' : '#dbeafe'}; color: ${t.expense_type === 'VARIAVEL' ? '#b45309' : '#1d4ed8'};">${t.expense_type === 'VARIAVEL' ? 'Var' : 'Fixa'}</span></td>
                <td class="text-center">${t.payment_method_short || '-'}</td>
                <td class="text-right">${formatCurrency(t.amount)}</td>
              </tr>
            `).join('')}
          </tbody>
        </table>
        
        <div class="footer">
          <p>Gerado pelo DRE+ em ${new Date().toLocaleDateString('pt-BR')} às ${new Date().toLocaleTimeString('pt-BR')}</p>
        </div>
      </body>
      </html>
    `;
    
    const printWindow = window.open('', '_blank');
    printWindow.document.write(printContent);
    printWindow.document.close();
    printWindow.focus();
    setTimeout(() => {
      printWindow.print();
    }, 250);
  };

  const years = Array.from({ length: 5 }, (_, i) => new Date().getFullYear() - 2 + i);

  return (
    <div className="animate-fadeIn space-y-6" data-testid="accounts-payable-page">
      <div className="flex flex-col gap-4">
        <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
          <div>
            <h2 className="text-3xl font-semibold tracking-tight">Contas a Pagar</h2>
            <p className="text-muted-foreground">Despesas e Custos por período</p>
          </div>
          <div className="flex items-center gap-2">
            <Select value={String(selectedYear)} onValueChange={(v) => setSelectedYear(Number(v))}>
              <SelectTrigger className="w-24"><SelectValue /></SelectTrigger>
              <SelectContent>
                {years.map((year) => (<SelectItem key={year} value={String(year)}>{year}</SelectItem>))}
              </SelectContent>
            </Select>
            <Button variant="outline" onClick={handlePDFExport} className="gap-2" data-testid="payables-pdf-btn">
              <Download className="w-4 h-4" /> PDF
            </Button>
          </div>
        </div>
        
        {/* Month selector */}
        <Card>
          <CardContent className="p-3">
            <div className="flex flex-wrap items-center gap-2">
              <span className="text-sm text-muted-foreground mr-2">Meses:</span>
              {MONTHS.map((month, i) => (
                <button
                  key={i}
                  onClick={() => toggleMonth(i + 1)}
                  className={`px-2 py-1 text-xs rounded border transition-colors ${
                    selectedMonths.includes(i + 1)
                      ? 'bg-primary text-primary-foreground border-primary'
                      : 'bg-background border-border hover:bg-muted'
                  }`}
                >
                  {month.slice(0, 3)}
                </button>
              ))}
              <button
                onClick={selectAllMonths}
                className="px-2 py-1 text-xs rounded border border-border hover:bg-muted ml-2"
              >
                Todos
              </button>
            </div>
          </CardContent>
        </Card>
      </div>

      {loading ? (
        <div className="flex justify-center py-12">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
        </div>
      ) : data && (
        <>
          {/* Summary */}
          <div className="grid grid-cols-3 gap-4">
            <Card className="border-l-4 border-l-red-500">
              <CardContent className="p-3 md:p-4">
                <p className="text-xs md:text-sm text-muted-foreground">Total Despesas</p>
                <p className="text-base md:text-xl font-bold text-red-500 tabular-nums">{formatValue(data.total_despesas)}</p>
              </CardContent>
            </Card>
            <Card className="border-l-4 border-l-amber-500">
              <CardContent className="p-3 md:p-4">
                <p className="text-xs md:text-sm text-muted-foreground">Total Custos</p>
                <p className="text-base md:text-xl font-bold text-amber-500 tabular-nums">{formatValue(data.total_custos)}</p>
              </CardContent>
            </Card>
            <Card className="border-l-4 border-l-blue-500">
              <CardContent className="p-3 md:p-4">
                <p className="text-xs md:text-sm text-muted-foreground">Total Geral</p>
                <p className="text-base md:text-xl font-bold text-blue-500 tabular-nums">{formatValue(data.total_geral)}</p>
              </CardContent>
            </Card>
          </div>

          {/* Transactions Table */}
          <Card>
            <CardContent className="p-0">
              <Table>
                <TableHeader>
                  <TableRow className="bg-muted/50">
                    <TableHead className="text-xs md:text-sm">{isMobile ? 'Venc.' : 'Vencimento'}</TableHead>
                    <TableHead className="text-xs md:text-sm">{isMobile ? 'Desc.' : 'Descrição'}</TableHead>
                    {!isMobile && <TableHead className="text-xs md:text-sm">Fornecedor</TableHead>}
                    {!isMobile && <TableHead className="text-xs md:text-sm">Categoria</TableHead>}
                    <TableHead className="text-center text-xs md:text-sm">Status</TableHead>
                    <TableHead className="text-center text-xs md:text-sm">Tipo</TableHead>
                    <TableHead className="text-center text-xs md:text-sm">Fixa/Var</TableHead>
                    <TableHead className="text-center text-xs md:text-sm">Doc</TableHead>
                    <TableHead className="text-right text-xs md:text-sm">Valor</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {data.transactions.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={isMobile ? 7 : 9} className="text-center py-8 text-muted-foreground">
                        Nenhum lançamento encontrado para o período selecionado
                      </TableCell>
                    </TableRow>
                  ) : (
                    data.transactions.map((t, idx) => (
                      <TableRow key={idx}>
                        <TableCell className="font-mono text-xs md:text-sm">{formatDateLocal(t.due_date)}</TableCell>
                        <TableCell className="text-xs md:text-sm" title={t.description}>
                          {t.is_accumulated || t.description?.includes('[Acumulado]') ? (
                            <div>
                              <p>{truncateText(t.description, isMobile ? 20 : 35)}</p>
                              <p className="text-xs text-muted-foreground">Conta: {t.source_company_name || '-'}</p>
                            </div>
                          ) : (
                            truncateText(t.description, isMobile ? 15 : 25)
                          )}
                        </TableCell>
                        {!isMobile && <TableCell className="text-xs md:text-sm" title={t.supplier_name}>{truncateText(t.supplier_name, 20)}</TableCell>}
                        {!isMobile && <TableCell className="text-xs md:text-sm" title={t.account_name}>{truncateText(t.account_name, 20)}</TableCell>}
                        <TableCell className="text-center">{getStatusBadge(t.status)}</TableCell>
                        <TableCell className="text-center">
                          <Badge className={`text-xs ${t.transaction_type === 'DESPESA' ? 'bg-red-600' : 'bg-amber-600'}`}>
                            {isMobile ? (t.transaction_type === 'DESPESA' ? 'D' : 'C') : (t.transaction_type === 'DESPESA' ? 'Despesa' : 'Custo')}
                          </Badge>
                        </TableCell>
                        <TableCell className="text-center text-xs md:text-sm">
                          <span className={`px-2 py-1 rounded text-xs font-medium ${t.expense_type === 'VARIAVEL' ? 'bg-amber-100 text-amber-700' : 'bg-blue-100 text-blue-700'}`}>
                            {t.expense_type === 'VARIAVEL' ? 'Var' : 'Fixa'}
                          </span>
                        </TableCell>
                        <TableCell className="text-center text-xs md:text-sm">{t.payment_method_short || '-'}</TableCell>
                        <TableCell className="text-right font-mono tabular-nums text-xs md:text-sm">{formatValue(t.amount)}</TableCell>
                      </TableRow>
                    ))
                  )}
                </TableBody>
              </Table>
            </CardContent>
          </Card>
        </>
      )}
    </div>
  );
};

// ==================== ACCOUNTS RECEIVABLE PAGE ====================
const AccountsReceivablePage = ({ userPlan = 1, onLockedFeatureClick, companyId, selectedCompany }) => {
  const [data, setData] = useState(null);
  
  // Load initial month/year from localStorage (set by Dashboard)
  const getInitialYear = () => {
    const saved = localStorage.getItem(LS_DASHBOARD_YEAR);
    return saved ? Number(saved) : new Date().getFullYear();
  };
  const getInitialMonth = () => {
    const saved = localStorage.getItem(LS_DASHBOARD_MONTH);
    return saved ? Number(saved) : new Date().getMonth() + 1;
  };
  
  const [selectedYear, setSelectedYear] = useState(getInitialYear);
  const [selectedMonths, setSelectedMonths] = useState([getInitialMonth()]);
  const [loading, setLoading] = useState(true);
  const isMobile = useIsMobile();

  useEffect(() => {
    loadData();
  }, [selectedYear, selectedMonths, companyId]);

  const loadData = async () => {
    try {
      setLoading(true);
      const monthsParam = selectedMonths.join(',');
      let url = `${API}/reports/accounts-receivable?year=${selectedYear}&months=${monthsParam}`;
      if (companyId) {
        url += `&company_id=${companyId}`;
      }
      const response = await axios.get(url);
      setData(response.data);
    } catch (error) {
      console.error('Error loading accounts receivable:', error);
    } finally {
      setLoading(false);
    }
  };

  // Toggle month selection
  const toggleMonth = (month) => {
    setSelectedMonths(prev => {
      if (prev.includes(month)) {
        if (prev.length === 1) return prev;
        return prev.filter(m => m !== month);
      } else {
        return [...prev, month].sort((a, b) => a - b);
      }
    });
  };

  // Select all months
  const selectAllMonths = () => {
    setSelectedMonths([1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 11, 12]);
  };

  // Format currency without R$ symbol for mobile
  const formatValue = (value) => {
    if (isMobile) {
      return new Intl.NumberFormat('pt-BR', {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2
      }).format(value || 0);
    }
    return formatCurrency(value);
  };

  // Format date - handle both DD/MM/YYYY and ISO formats
  const formatDateLocal = (dateStr) => {
    if (!dateStr) return '-';
    if (dateStr.includes('/')) return dateStr;
    const dt = new Date(dateStr);
    return dt.toLocaleDateString('pt-BR');
  };

  // Truncate text for table
  const truncateText = (text, maxLength = 20) => {
    if (!text) return '-';
    if (text.length <= maxLength) return text;
    return text.substring(0, maxLength - 2) + '...';
  };

  // Get status badge for receitas
  const getStatusBadge = (status) => {
    if (status === 'RECEBIDO') {
      return <Badge className="bg-emerald-600 text-xs">Recebido</Badge>;
    }
    if (status === 'CANCELADO') {
      return <Badge className="bg-gray-500 text-xs">Cancelado</Badge>;
    }
    return <Badge variant="outline" className="text-amber-500 border-amber-500 text-xs">Pendente</Badge>;
  };

  // Handle PDF export
  const handlePDFExport = () => {
    if (userPlan < 3) {
      if (onLockedFeatureClick) {
        onLockedFeatureClick(3);
      }
      return;
    }
    exportToPDF();
  };

  // Export to PDF
  const exportToPDF = () => {
    if (!data) return;
    
    const monthNames = selectedMonths.map(m => MONTHS[m - 1]).join(', ');
    
    // Helper function for PDF date formatting
    const pdfFormatDate = (dateStr) => {
      if (!dateStr) return '-';
      if (dateStr.includes('/')) return dateStr;
      const dt = new Date(dateStr);
      return dt.toLocaleDateString('pt-BR');
    };
    
    const printContent = `
      <!DOCTYPE html>
      <html>
      <head>
        <meta charset="utf-8">
        <title>Contas a Receber - ${monthNames} ${selectedYear}</title>
        <style>
          body { font-family: Arial, sans-serif; padding: 20px; color: #333; font-size: 12px; }
          h1 { color: #10b981; border-bottom: 2px solid #10b981; padding-bottom: 10px; font-size: 20px; }
          h2 { color: #666; font-size: 12px; margin-top: 0; }
          .summary { display: flex; gap: 15px; margin: 15px 0; flex-wrap: wrap; }
          .summary-card { padding: 10px 15px; border-radius: 8px; min-width: 140px; }
          .summary-card.pendente { background: #fef3c7; border-left: 4px solid #f59e0b; }
          .summary-card.recebido { background: #d1fae5; border-left: 4px solid #10b981; }
          .summary-card.total { background: #dbeafe; border-left: 4px solid #3b82f6; }
          .summary-card p { margin: 0; }
          .summary-card .label { font-size: 10px; color: #666; }
          .summary-card .value { font-size: 16px; font-weight: bold; }
          .summary-card .value.pendente { color: #f59e0b; }
          .summary-card .value.recebido { color: #10b981; }
          .summary-card .value.total { color: #3b82f6; }
          table { width: 100%; border-collapse: collapse; margin-top: 15px; font-size: 11px; }
          th, td { padding: 6px 8px; text-align: left; border-bottom: 1px solid #e5e7eb; }
          th { background: #f3f4f6; font-weight: 600; font-size: 10px; }
          .text-right { text-align: right; }
          .text-center { text-align: center; }
          .badge { padding: 2px 6px; border-radius: 4px; font-size: 9px; }
          .badge-recebido { background: #d1fae5; color: #047857; }
          .badge-pendente { background: #fef3c7; color: #b45309; }
          .footer { margin-top: 20px; text-align: center; font-size: 10px; color: #999; }
          @media print { body { padding: 10px; } }
        </style>
      </head>
      <body>
        <h1>Contas a Receber</h1>
        <h2>${monthNames} de ${selectedYear}</h2>
        
        <div class="summary">
          <div class="summary-card pendente">
            <p class="label">Total Pendente</p>
            <p class="value pendente">${formatCurrency(data.total_pendente)}</p>
          </div>
          <div class="summary-card recebido">
            <p class="label">Total Recebido</p>
            <p class="value recebido">${formatCurrency(data.total_recebido)}</p>
          </div>
          <div class="summary-card total">
            <p class="label">Total Geral</p>
            <p class="value total">${formatCurrency(data.total_geral)}</p>
          </div>
        </div>
        
        <table>
          <thead>
            <tr>
              <th>Vencimento</th>
              <th>Descrição</th>
              <th>Cliente</th>
              <th>Categoria</th>
              <th>Carteira</th>
              <th class="text-center">Status</th>
              <th class="text-center">Doc</th>
              <th class="text-right">Valor</th>
            </tr>
          </thead>
          <tbody>
            ${data.transactions.map(t => `
              <tr>
                <td>${pdfFormatDate(t.due_date)}</td>
                <td>${t.description || '-'}</td>
                <td>${t.client_name || '-'}</td>
                <td>${t.account_name || '-'}</td>
                <td>${t.wallet_type ? (t.wallet_type === 'Cartão de Crédito' ? 'Crédito' : t.wallet_type) + (t.wallet_name ? ' - ' + t.wallet_name : '') : '-'}</td>
                <td class="text-center"><span class="badge ${t.status === 'RECEBIDO' ? 'badge-recebido' : 'badge-pendente'}">${t.status === 'RECEBIDO' ? 'Recebido' : 'Pendente'}</span></td>
                <td class="text-center">${t.payment_method_short || '-'}</td>
                <td class="text-right">${formatCurrency(t.amount)}</td>
              </tr>
            `).join('')}
          </tbody>
        </table>
        
        <div class="footer">
          <p>Gerado pelo DRE+ em ${new Date().toLocaleDateString('pt-BR')} às ${new Date().toLocaleTimeString('pt-BR')}</p>
        </div>
      </body>
      </html>
    `;
    
    const printWindow = window.open('', '_blank');
    printWindow.document.write(printContent);
    printWindow.document.close();
    printWindow.focus();
    setTimeout(() => {
      printWindow.print();
    }, 250);
  };

  const years = Array.from({ length: 5 }, (_, i) => new Date().getFullYear() - 2 + i);

  return (
    <div className="animate-fadeIn space-y-6" data-testid="accounts-receivable-page">
      <div className="flex flex-col gap-4">
        <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
          <div>
            <h2 className="text-3xl font-semibold tracking-tight">Contas a Receber</h2>
            <p className="text-muted-foreground">Receitas por período</p>
          </div>
          <div className="flex items-center gap-2">
            <Select value={String(selectedYear)} onValueChange={(v) => setSelectedYear(Number(v))}>
              <SelectTrigger className="w-24"><SelectValue /></SelectTrigger>
              <SelectContent>
                {years.map((year) => (<SelectItem key={year} value={String(year)}>{year}</SelectItem>))}
              </SelectContent>
            </Select>
            <Button variant="outline" onClick={handlePDFExport} className="gap-2" data-testid="receivables-pdf-btn">
              <Download className="w-4 h-4" /> PDF
            </Button>
          </div>
        </div>
        
        {/* Month selector */}
        <Card>
          <CardContent className="p-3">
            <div className="flex flex-wrap items-center gap-2">
              <span className="text-sm text-muted-foreground mr-2">Meses:</span>
              {MONTHS.map((month, i) => (
                <button
                  key={i}
                  onClick={() => toggleMonth(i + 1)}
                  className={`px-2 py-1 text-xs rounded border transition-colors ${
                    selectedMonths.includes(i + 1)
                      ? 'bg-primary text-primary-foreground border-primary'
                      : 'bg-background border-border hover:bg-muted'
                  }`}
                >
                  {month.slice(0, 3)}
                </button>
              ))}
              <button
                onClick={selectAllMonths}
                className="px-2 py-1 text-xs rounded border border-border hover:bg-muted ml-2"
              >
                Todos
              </button>
            </div>
          </CardContent>
        </Card>
      </div>

      {loading ? (
        <div className="flex justify-center py-12">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
        </div>
      ) : data && (
        <>
          {/* Summary */}
          <div className="grid grid-cols-3 gap-4">
            <Card className="border-l-4 border-l-amber-500">
              <CardContent className="p-3 md:p-4">
                <p className="text-xs md:text-sm text-muted-foreground">Total Pendente</p>
                <p className="text-base md:text-xl font-bold text-amber-500 tabular-nums">{formatValue(data.total_pendente)}</p>
              </CardContent>
            </Card>
            <Card className="border-l-4 border-l-emerald-500">
              <CardContent className="p-3 md:p-4">
                <p className="text-xs md:text-sm text-muted-foreground">Total Recebido</p>
                <p className="text-base md:text-xl font-bold text-emerald-500 tabular-nums">{formatValue(data.total_recebido)}</p>
              </CardContent>
            </Card>
            <Card className="border-l-4 border-l-blue-500">
              <CardContent className="p-3 md:p-4">
                <p className="text-xs md:text-sm text-muted-foreground">Total Geral</p>
                <p className="text-base md:text-xl font-bold text-blue-500 tabular-nums">{formatValue(data.total_geral)}</p>
              </CardContent>
            </Card>
          </div>

          {/* Transactions Table */}
          <Card>
            <CardContent className="p-0">
              <Table>
                <TableHeader>
                  <TableRow className="bg-muted/50">
                    <TableHead className="text-xs md:text-sm">{isMobile ? 'Venc.' : 'Vencimento'}</TableHead>
                    <TableHead className="text-xs md:text-sm">{isMobile ? 'Desc.' : 'Descrição'}</TableHead>
                    {!isMobile && <TableHead className="text-xs md:text-sm">Cliente</TableHead>}
                    {!isMobile && <TableHead className="text-xs md:text-sm">Categoria</TableHead>}
                    {!isMobile && <TableHead className="text-xs md:text-sm">Carteira</TableHead>}
                    <TableHead className="text-center text-xs md:text-sm">Status</TableHead>
                    <TableHead className="text-center text-xs md:text-sm">Doc</TableHead>
                    <TableHead className="text-right text-xs md:text-sm">Valor</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {data.transactions.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={isMobile ? 5 : 8} className="text-center py-8 text-muted-foreground">
                        Nenhum lançamento encontrado para o período selecionado
                      </TableCell>
                    </TableRow>
                  ) : (
                    data.transactions.map((t, idx) => (
                      <TableRow key={idx}>
                        <TableCell className="font-mono text-xs md:text-sm">{formatDateLocal(t.due_date)}</TableCell>
                        <TableCell className="text-xs md:text-sm" title={t.description}>
                          {t.is_accumulated || t.description?.includes('[Acumulado]') ? (
                            <div>
                              <p>{truncateText(t.description, isMobile ? 20 : 35)}</p>
                              <p className="text-xs text-muted-foreground">Conta: {t.source_company_name || '-'}</p>
                            </div>
                          ) : (
                            truncateText(t.description, isMobile ? 15 : 25)
                          )}
                        </TableCell>
                        {!isMobile && <TableCell className="text-xs md:text-sm" title={t.client_name}>{truncateText(t.client_name, 20)}</TableCell>}
                        {!isMobile && <TableCell className="text-xs md:text-sm" title={t.account_name}>{truncateText(t.account_name, 20)}</TableCell>}
                        {!isMobile && (
                          <TableCell className="text-xs md:text-sm">
                            {t.wallet_type ? (
                              <div>
                                <span className="font-medium">{t.wallet_type === 'Cartão de Crédito' ? 'Crédito' : t.wallet_type}</span>
                                {t.wallet_name && <p className="text-xs text-muted-foreground">{t.wallet_name}</p>}
                              </div>
                            ) : '-'}
                          </TableCell>
                        )}
                        <TableCell className="text-center">{getStatusBadge(t.status)}</TableCell>
                        <TableCell className="text-center text-xs md:text-sm">{t.payment_method_short || '-'}</TableCell>
                        <TableCell className="text-right font-mono tabular-nums text-xs md:text-sm">{formatValue(t.amount)}</TableCell>
                      </TableRow>
                    ))
                  )}
                </TableBody>
              </Table>
            </CardContent>
          </Card>
        </>
      )}
    </div>
  );
};

// ==================== CASH BOOK PAGE (LIVRO CAIXA) ====================
const CashBookPage = ({ userPlan = 1, onLockedFeatureClick, companyId, selectedCompany }) => {
  const [data, setData] = useState(null);
  
  // Load initial month/year from localStorage (set by Dashboard)
  const getInitialYear = () => {
    const saved = localStorage.getItem(LS_DASHBOARD_YEAR);
    return saved ? Number(saved) : new Date().getFullYear();
  };
  const getInitialMonth = () => {
    const saved = localStorage.getItem(LS_DASHBOARD_MONTH);
    return saved ? Number(saved) : new Date().getMonth() + 1;
  };
  
  const [selectedYear, setSelectedYear] = useState(getInitialYear);
  const [selectedMonths, setSelectedMonths] = useState([getInitialMonth()]);
  const [loading, setLoading] = useState(true);
  const isMobile = useIsMobile();
  const { toast } = useToast();

  // Check plan access - requires Advanced (2) or higher
  const hasAccess = userPlan >= 2;

  useEffect(() => {
    if (hasAccess) {
      loadData();
    } else {
      setLoading(false);
    }
  }, [selectedYear, selectedMonths, companyId, hasAccess]);

  const loadData = async () => {
    try {
      setLoading(true);
      const monthsParam = selectedMonths.join(',');
      let url = `${API}/reports/cash-book?year=${selectedYear}&months=${monthsParam}`;
      if (companyId) {
        url += `&company_id=${companyId}`;
      }
      const response = await axios.get(url);
      setData(response.data);
    } catch (error) {
      console.error('Error loading cash book:', error);
      toast({ title: "Erro ao carregar Projeção de Caixa", variant: "destructive" });
    } finally {
      setLoading(false);
    }
  };

  // Toggle month selection
  const toggleMonth = (month) => {
    setSelectedMonths(prev => {
      if (prev.includes(month)) {
        if (prev.length === 1) return prev;
        return prev.filter(m => m !== month);
      } else {
        return [...prev, month].sort((a, b) => a - b);
      }
    });
  };

  // Select all months
  const selectAllMonths = () => {
    setSelectedMonths([1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 11, 12]);
  };

  // Format currency without R$ symbol for mobile
  const formatValue = (value) => {
    if (isMobile) {
      return new Intl.NumberFormat('pt-BR', {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2
      }).format(value || 0);
    }
    return formatCurrency(value);
  };

  // Format date - handle both DD/MM/YYYY and ISO formats
  const formatDateLocal = (dateStr) => {
    if (!dateStr) return '-';
    if (dateStr.includes('/')) return dateStr;
    const dt = new Date(dateStr);
    return dt.toLocaleDateString('pt-BR');
  };

  // Truncate text for table
  const truncateText = (text, maxLength = 20) => {
    if (!text) return '-';
    if (text.length <= maxLength) return text;
    return text.substring(0, maxLength - 2) + '...';
  };

  // Handle PDF export
  const handlePDFExport = () => {
    if (userPlan < 2) {
      if (onLockedFeatureClick) {
        onLockedFeatureClick(2);
      }
      return;
    }
    exportToPDF();
  };

  // Export to PDF
  const exportToPDF = () => {
    if (!data) return;
    
    const monthNames = selectedMonths.map(m => MONTHS[m - 1]).join(', ');
    
    // Helper function for PDF date formatting
    const pdfFormatDate = (dateStr) => {
      if (!dateStr) return '-';
      if (dateStr.includes('/')) return dateStr;
      const dt = new Date(dateStr);
      return dt.toLocaleDateString('pt-BR');
    };
    
    const printContent = `
      <!DOCTYPE html>
      <html>
      <head>
        <meta charset="utf-8">
        <title>Projeção de Caixa 360 - ${monthNames} ${selectedYear}</title>
        <style>
          body { font-family: Arial, sans-serif; padding: 20px; color: #333; font-size: 12px; }
          h1 { color: #6366f1; border-bottom: 2px solid #6366f1; padding-bottom: 10px; font-size: 20px; }
          h2 { color: #666; font-size: 12px; margin-top: 0; }
          h3 { color: #333; font-size: 14px; margin-top: 20px; margin-bottom: 10px; border-bottom: 1px solid #e5e7eb; padding-bottom: 5px; }
          .company-info { font-size: 11px; color: #666; margin-bottom: 15px; }
          .summary { display: flex; gap: 15px; margin: 15px 0; flex-wrap: wrap; }
          .summary-card { padding: 10px 15px; border-radius: 8px; min-width: 140px; }
          .summary-card.receitas { background: #d1fae5; border-left: 4px solid #10b981; }
          .summary-card.despesas { background: #fee2e2; border-left: 4px solid #ef4444; }
          .summary-card.custos { background: #fef3c7; border-left: 4px solid #f59e0b; }
          .summary-card.saldo { background: #dbeafe; border-left: 4px solid #3b82f6; }
          .summary-card p { margin: 0; }
          .summary-card .label { font-size: 10px; color: #666; }
          .summary-card .value { font-size: 16px; font-weight: bold; }
          .summary-card .value.receitas { color: #10b981; }
          .summary-card .value.despesas { color: #ef4444; }
          .summary-card .value.custos { color: #f59e0b; }
          .summary-card .value.saldo { color: #3b82f6; }
          .summary-card .value.negative { color: #ef4444; }
          table { width: 100%; border-collapse: collapse; margin-top: 10px; font-size: 11px; }
          th, td { padding: 6px 8px; text-align: left; border-bottom: 1px solid #e5e7eb; }
          th { background: #f3f4f6; font-weight: 600; font-size: 10px; }
          .text-right { text-align: right; }
          .text-center { text-align: center; }
          .totals-section { margin-top: 20px; background: #f8fafc; padding: 15px; border-radius: 8px; }
          .totals-row { display: flex; justify-content: space-between; padding: 5px 0; border-bottom: 1px solid #e5e7eb; }
          .totals-row:last-child { border-bottom: none; font-weight: bold; }
          .totals-row .label { color: #666; }
          .totals-row .values { display: flex; gap: 30px; }
          .badge { padding: 2px 6px; border-radius: 4px; font-size: 9px; }
          .badge-receita { background: #d1fae5; color: #047857; }
          .badge-despesa { background: #fee2e2; color: #dc2626; }
          .badge-custo { background: #fef3c7; color: #d97706; }
          .footer { margin-top: 20px; text-align: center; font-size: 10px; color: #999; }
          @media print { body { padding: 10px; } }
        </style>
      </head>
      <body>
        <h1>Projeção de Caixa 360</h1>
        <h2>${monthNames} de ${selectedYear}</h2>
        ${data.company ? `<p class="company-info"><strong>${data.company.razao_social || ''}</strong> ${data.company.cnpj ? `- CNPJ: ${data.company.cnpj}` : ''}</p>` : ''}
        
        <div class="summary">
          <div class="summary-card receitas">
            <p class="label">Total Receitas</p>
            <p class="value receitas">${formatCurrency(data.cards.total_receitas)}</p>
          </div>
          <div class="summary-card despesas">
            <p class="label">Total Despesas</p>
            <p class="value despesas">${formatCurrency(data.cards.total_despesas)}</p>
          </div>
          <div class="summary-card custos">
            <p class="label">Total Custos</p>
            <p class="value custos">${formatCurrency(data.cards.total_custos)}</p>
          </div>
          <div class="summary-card saldo">
            <p class="label">Saldo Final</p>
            <p class="value ${data.cards.saldo_final >= 0 ? 'saldo' : 'negative'}">${formatCurrency(data.cards.saldo_final)}</p>
          </div>
        </div>

        <h3>Entradas (Receitas)</h3>
        <table>
          <thead>
            <tr>
              <th>Data</th>
              <th>Descrição</th>
              <th class="text-right">Previsão</th>
              <th class="text-right">Realizado</th>
            </tr>
          </thead>
          <tbody>
            ${data.entradas.length === 0 ? `
              <tr><td colspan="4" style="text-align: center; color: #999;">Nenhuma receita no período</td></tr>
            ` : data.entradas.map(t => `
              <tr>
                <td>${pdfFormatDate(t.date)}</td>
                <td>${t.description || '-'}</td>
                <td class="text-right">${formatCurrency(t.previsao)}</td>
                <td class="text-right">${formatCurrency(t.realizado)}</td>
              </tr>
            `).join('')}
            <tr style="background: #f3f4f6; font-weight: bold;">
              <td colspan="2">Total Receitas (A)</td>
              <td class="text-right">${formatCurrency(data.totals.receitas.previsao)}</td>
              <td class="text-right">${formatCurrency(data.totals.receitas.realizado)}</td>
            </tr>
          </tbody>
        </table>

        <h3>Saídas (Despesas e Custos)</h3>
        <table>
          <thead>
            <tr>
              <th>Data</th>
              <th>Descrição</th>
              <th class="text-center">Tipo</th>
              <th class="text-right">Previsão</th>
              <th class="text-right">Realizado</th>
            </tr>
          </thead>
          <tbody>
            ${data.saidas.length === 0 ? `
              <tr><td colspan="5" style="text-align: center; color: #999;">Nenhuma despesa/custo no período</td></tr>
            ` : data.saidas.map(t => `
              <tr>
                <td>${pdfFormatDate(t.date)}</td>
                <td>${t.description || '-'}</td>
                <td class="text-center"><span class="badge ${t.type === 'DESPESA' ? 'badge-despesa' : 'badge-custo'}">${t.type_label}</span></td>
                <td class="text-right">${formatCurrency(t.previsao)}</td>
                <td class="text-right">${formatCurrency(t.realizado)}</td>
              </tr>
            `).join('')}
            <tr style="background: #fef3c7;">
              <td colspan="3">Total Despesas (B)</td>
              <td class="text-right">${formatCurrency(data.totals.despesas.previsao)}</td>
              <td class="text-right">${formatCurrency(data.totals.despesas.realizado)}</td>
            </tr>
            <tr style="background: #fef3c7;">
              <td colspan="3">Total Custos (C)</td>
              <td class="text-right">${formatCurrency(data.totals.custos.previsao)}</td>
              <td class="text-right">${formatCurrency(data.totals.custos.realizado)}</td>
            </tr>
          </tbody>
        </table>

        <div class="totals-section">
          <div class="totals-row">
            <span class="label">Total do Período (D = A - B - C)</span>
            <span class="values">
              <span>Previsão: ${formatCurrency(data.totals.total_geral.previsao)}</span>
              <span>Realizado: ${formatCurrency(data.totals.total_geral.realizado)}</span>
            </span>
          </div>
          <div class="totals-row">
            <span class="label">Saldo Acumulado Anterior (E)</span>
            <span class="values">
              <span>Previsão: ${formatCurrency(data.totals.saldo_anterior.previsao)}</span>
              <span>Realizado: ${formatCurrency(data.totals.saldo_anterior.realizado)}</span>
            </span>
          </div>
          <div class="totals-row">
            <span class="label">Saldo Acumulado Final (F = D + E)</span>
            <span class="values">
              <span>Previsão: ${formatCurrency(data.totals.acumulado.previsao)}</span>
              <span>Realizado: ${formatCurrency(data.totals.acumulado.realizado)}</span>
            </span>
          </div>
          <div class="totals-row" style="background: #e0e7ff; margin-top: 10px; padding: 10px; border: 1px solid #6366f1;">
            <span class="label" style="font-weight: bold;">Saldo Inicial da Conta</span>
            <span class="values">
              <span style="font-size: 16px; font-weight: bold; color: ${(data.saldo_inicial || 0) >= 0 ? '#6366f1' : '#ef4444'};">
                Cadastrado: ${formatCurrency(data.saldo_inicial || 0)}
              </span>
            </span>
          </div>
          <div class="totals-row" style="background: #d1fae5; margin-top: 10px; padding: 10px; border: 2px solid #10b981;">
            <span class="label" style="font-weight: bold;">Saldo Final (G = F.Previsão + F.Realizado)</span>
            <span class="values">
              <span style="font-size: 18px; font-weight: bold; color: ${data.cards.saldo_final >= 0 ? '#10b981' : '#ef4444'};">
                Total: ${formatCurrency(data.cards.saldo_final)}
              </span>
            </span>
          </div>
          <div class="totals-row" style="background: #ede9fe; margin-top: 10px; padding: 10px; border: 2px solid #8b5cf6;">
            <span class="label" style="font-weight: bold;">Saldo Final Acumulado (H = G - E)</span>
            <span class="values">
              <span style="font-size: 18px; font-weight: bold; color: ${(data.cards.saldo_final - (data.totals.saldo_anterior.previsao + data.totals.saldo_anterior.realizado)) >= 0 ? '#8b5cf6' : '#ef4444'};">
                Total: ${formatCurrency(data.cards.saldo_final - (data.totals.saldo_anterior.previsao + data.totals.saldo_anterior.realizado))}
              </span>
            </span>
          </div>
        </div>
        
        <div class="footer">
          <p>Gerado pelo DRE+ em ${new Date().toLocaleDateString('pt-BR')} às ${new Date().toLocaleTimeString('pt-BR')}</p>
        </div>
      </body>
      </html>
    `;
    
    const printWindow = window.open('', '_blank');
    printWindow.document.write(printContent);
    printWindow.document.close();
    printWindow.focus();
    setTimeout(() => {
      printWindow.print();
    }, 250);
  };

  const years = Array.from({ length: 5 }, (_, i) => new Date().getFullYear() - 2 + i);

  // Show locked message if no access
  if (!hasAccess) {
    return (
      <div className="animate-fadeIn space-y-6" data-testid="cash-book-page">
        <div className="flex flex-col gap-4">
          <div>
            <h2 className="text-3xl font-semibold tracking-tight">Projeção de Caixa 360</h2>
            <p className="text-muted-foreground">Consolidado de Entradas e Saídas</p>
          </div>
        </div>
        <Card className="border-amber-500/50 bg-amber-500/5">
          <CardContent className="p-6 text-center">
            <Lock className="w-12 h-12 mx-auto text-amber-500 mb-4" />
            <h3 className="text-lg font-semibold mb-2">Funcionalidade Bloqueada</h3>
            <p className="text-muted-foreground mb-4">
              A Projeção de Caixa está disponível a partir do plano Advanced.
            </p>
            <Button onClick={() => onLockedFeatureClick && onLockedFeatureClick(2)}>
              Ver Planos
            </Button>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="animate-fadeIn space-y-6" data-testid="cash-book-page">
      <div className="flex flex-col gap-4">
        <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
          <div>
            <h2 className="text-3xl font-semibold tracking-tight">Projeção de Caixa 360</h2>
            <p className="text-muted-foreground">Consolidado de Entradas e Saídas</p>
          </div>
          <div className="flex items-center gap-2">
            <Select value={String(selectedYear)} onValueChange={(v) => setSelectedYear(Number(v))}>
              <SelectTrigger className="w-24"><SelectValue /></SelectTrigger>
              <SelectContent>
                {years.map((year) => (<SelectItem key={year} value={String(year)}>{year}</SelectItem>))}
              </SelectContent>
            </Select>
            <Button variant="outline" onClick={handlePDFExport} className="gap-2" data-testid="cash-book-pdf-btn">
              <Download className="w-4 h-4" /> PDF
            </Button>
          </div>
        </div>
        
        {/* Month selector */}
        <Card>
          <CardContent className="p-3">
            <div className="flex flex-wrap items-center gap-2">
              <span className="text-sm text-muted-foreground mr-2">Meses:</span>
              {MONTHS.map((month, i) => (
                <button
                  key={i}
                  onClick={() => toggleMonth(i + 1)}
                  className={`px-2 py-1 text-xs rounded border transition-colors ${
                    selectedMonths.includes(i + 1)
                      ? 'bg-primary text-primary-foreground border-primary'
                      : 'bg-background border-border hover:bg-muted'
                  }`}
                >
                  {month.slice(0, 3)}
                </button>
              ))}
              <button
                onClick={selectAllMonths}
                className="px-2 py-1 text-xs rounded border border-border hover:bg-muted ml-2"
              >
                Todos
              </button>
            </div>
          </CardContent>
        </Card>
      </div>

      {loading ? (
        <div className="flex justify-center py-12">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
        </div>
      ) : data && (
        <>
          {/* Summary Cards */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            <Card className="border-l-4 border-l-emerald-500">
              <CardContent className="p-3 md:p-4">
                <p className="text-xs md:text-sm text-muted-foreground">Total Receitas</p>
                <p className="text-base md:text-xl font-bold text-emerald-500 tabular-nums">{formatValue(data.cards.total_receitas)}</p>
              </CardContent>
            </Card>
            <Card className="border-l-4 border-l-red-500">
              <CardContent className="p-3 md:p-4">
                <p className="text-xs md:text-sm text-muted-foreground">Total Despesas</p>
                <p className="text-base md:text-xl font-bold text-red-500 tabular-nums">{formatValue(data.cards.total_despesas)}</p>
              </CardContent>
            </Card>
            <Card className="border-l-4 border-l-amber-500">
              <CardContent className="p-3 md:p-4">
                <p className="text-xs md:text-sm text-muted-foreground">Total Custos</p>
                <p className="text-base md:text-xl font-bold text-amber-500 tabular-nums">{formatValue(data.cards.total_custos)}</p>
              </CardContent>
            </Card>
            <Card className={`border-l-4 ${data.cards.saldo_final >= 0 ? 'border-l-blue-500' : 'border-l-red-500'}`}>
              <CardContent className="p-3 md:p-4">
                <p className="text-xs md:text-sm text-muted-foreground">Saldo Final</p>
                <p className={`text-base md:text-xl font-bold tabular-nums ${data.cards.saldo_final >= 0 ? 'text-blue-500' : 'text-red-500'}`}>
                  {formatValue(data.cards.saldo_final)}
                </p>
              </CardContent>
            </Card>
          </div>

          {/* Chart - Evolução do Saldo */}
          {data.chart_data && data.chart_data.length > 1 && (
            <Card>
              <CardHeader className="pb-3">
                <CardTitle className="text-lg flex items-center gap-2">
                  <BarChart3 className="w-5 h-5 text-primary" />
                  Evolução do Saldo
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="h-[300px] w-full">
                  <ResponsiveContainer width="100%" height="100%">
                    <ComposedChart data={data.chart_data} margin={{ top: 10, right: 10, left: 0, bottom: 0 }}>
                      <CartesianGrid strokeDasharray="3 3" className="stroke-muted" />
                      <XAxis 
                        dataKey="month" 
                        tick={{ fontSize: 12 }}
                        className="text-muted-foreground"
                      />
                      <YAxis 
                        tick={{ fontSize: 12 }}
                        tickFormatter={(value) => {
                          if (Math.abs(value) >= 1000) {
                            return `${(value / 1000).toFixed(0)}k`;
                          }
                          return value;
                        }}
                        className="text-muted-foreground"
                      />
                      <Tooltip 
                        formatter={(value, name) => {
                          const labels = {
                            receitas: 'Receitas',
                            despesas: 'Despesas',
                            custos: 'Custos',
                            saldo_mes: 'Saldo do Mês',
                            saldo_acumulado: 'Saldo Acumulado'
                          };
                          return [formatCurrency(value), labels[name] || name];
                        }}
                        contentStyle={{ 
                          backgroundColor: 'hsl(var(--card))', 
                          borderColor: 'hsl(var(--border))',
                          borderRadius: '8px'
                        }}
                        labelStyle={{ color: 'hsl(var(--foreground))' }}
                      />
                      <Legend 
                        formatter={(value) => {
                          const labels = {
                            receitas: 'Receitas',
                            despesas: 'Despesas',
                            custos: 'Custos',
                            saldo_acumulado: 'Saldo Acumulado'
                          };
                          return labels[value] || value;
                        }}
                      />
                      <Bar dataKey="receitas" fill="#10b981" radius={[4, 4, 0, 0]} />
                      <Bar dataKey="despesas" fill="#ef4444" radius={[4, 4, 0, 0]} />
                      <Bar dataKey="custos" fill="#f59e0b" radius={[4, 4, 0, 0]} />
                      <Line 
                        type="monotone" 
                        dataKey="saldo_acumulado" 
                        stroke="#3b82f6" 
                        strokeWidth={3}
                        dot={{ fill: '#3b82f6', strokeWidth: 2, r: 4 }}
                        activeDot={{ r: 6 }}
                      />
                    </ComposedChart>
                  </ResponsiveContainer>
                </div>
              </CardContent>
            </Card>
          )}

          {/* Daily Cash Flow Table - Shows running balance */}
          <Card>
            <CardHeader className="pb-3">
              <CardTitle className="text-lg flex items-center gap-2">
                <DollarSign className="w-5 h-5 text-blue-500" />
                Fluxo Diário de Caixa (Saldo Acumulado)
              </CardTitle>
              <p className="text-xs text-muted-foreground mt-1">
                Visão do saldo após cada movimentação, ordenado por data de vencimento
              </p>
            </CardHeader>
            <CardContent className="p-0">
              <Table>
                <TableHeader>
                  <TableRow className="bg-blue-500/20 dark:bg-blue-900/40">
                    <TableHead className="text-xs md:text-sm">Data</TableHead>
                    <TableHead className="text-xs md:text-sm">Descrição</TableHead>
                    <TableHead className="text-center text-xs md:text-sm">Tipo</TableHead>
                    <TableHead className="text-right text-xs md:text-sm text-emerald-600">Entrada</TableHead>
                    <TableHead className="text-right text-xs md:text-sm text-red-600">Saída</TableHead>
                    <TableHead className="text-right text-xs md:text-sm font-bold">Saldo</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {/* Saldo Inicial Row */}
                  <TableRow className="bg-indigo-500/10">
                    <TableCell className="font-mono text-xs md:text-sm">-</TableCell>
                    <TableCell className="text-xs md:text-sm font-medium">Saldo Inicial da Conta</TableCell>
                    <TableCell className="text-center text-xs md:text-sm">-</TableCell>
                    <TableCell className="text-right font-mono tabular-nums text-xs md:text-sm">-</TableCell>
                    <TableCell className="text-right font-mono tabular-nums text-xs md:text-sm">-</TableCell>
                    <TableCell className={`text-right font-mono tabular-nums text-xs md:text-sm font-bold ${(data.saldo_inicial || 0) >= 0 ? 'text-blue-600' : 'text-red-600'}`}>
                      {formatValue(data.saldo_inicial || 0)}
                    </TableCell>
                  </TableRow>
                  {(() => {
                    // Combine and sort all transactions by date
                    const allTransactions = [
                      ...data.entradas.map(t => ({ ...t, tipo: 'RECEITA', valor: (t.previsao || 0) + (t.realizado || 0) })),
                      ...data.saidas.map(t => ({ ...t, tipo: t.transaction_type || 'DESPESA', valor: -((t.previsao || 0) + (t.realizado || 0)) }))
                    ].sort((a, b) => {
                      const dateA = new Date(a.date.split('/').reverse().join('-'));
                      const dateB = new Date(b.date.split('/').reverse().join('-'));
                      return dateA - dateB;
                    });
                    
                    let runningBalance = data.saldo_inicial || 0;
                    
                    return allTransactions.map((t, idx) => {
                      runningBalance += t.valor;
                      const isNegative = runningBalance < 0;
                      
                      return (
                        <TableRow key={idx} className={isNegative ? 'bg-red-500/10' : ''}>
                          <TableCell className="font-mono text-xs md:text-sm">{t.date}</TableCell>
                          <TableCell className="text-xs md:text-sm" title={t.description}>{truncateText(t.description, isMobile ? 15 : 30)}</TableCell>
                          <TableCell className="text-center">
                            <span className={`px-1.5 py-0.5 rounded text-xs ${
                              t.tipo === 'RECEITA' ? 'bg-emerald-100 text-emerald-700' : 
                              t.tipo === 'CUSTO' ? 'bg-amber-100 text-amber-700' : 'bg-red-100 text-red-700'
                            }`}>
                              {t.tipo === 'RECEITA' ? 'Rec' : t.tipo === 'CUSTO' ? 'Cus' : 'Des'}
                            </span>
                          </TableCell>
                          <TableCell className="text-right font-mono tabular-nums text-xs md:text-sm text-emerald-600">
                            {t.valor > 0 ? formatValue(t.valor) : '-'}
                          </TableCell>
                          <TableCell className="text-right font-mono tabular-nums text-xs md:text-sm text-red-600">
                            {t.valor < 0 ? formatValue(Math.abs(t.valor)) : '-'}
                          </TableCell>
                          <TableCell className={`text-right font-mono tabular-nums text-xs md:text-sm font-bold ${isNegative ? 'text-red-600 bg-red-100' : 'text-blue-600'}`}>
                            {formatValue(runningBalance)}
                            {isNegative && <span className="ml-1 text-red-500">⚠</span>}
                          </TableCell>
                        </TableRow>
                      );
                    });
                  })()}
                </TableBody>
              </Table>
            </CardContent>
          </Card>

          {/* Entries Table (Receitas) */}
          <Card>
            <CardHeader className="pb-3">
              <CardTitle className="text-lg flex items-center gap-2">
                <TrendingUp className="w-5 h-5 text-emerald-500" />
                Entradas (Receitas)
              </CardTitle>
            </CardHeader>
            <CardContent className="p-0">
              <Table>
                <TableHeader>
                  <TableRow className="bg-emerald-500/20 dark:bg-emerald-900/40">
                    <TableHead className="text-xs md:text-sm">{isMobile ? 'Data' : 'Data'}</TableHead>
                    <TableHead className="text-xs md:text-sm">Descrição</TableHead>
                    <TableHead className="text-right text-xs md:text-sm">Previsão</TableHead>
                    <TableHead className="text-right text-xs md:text-sm border-l-2 border-border">Realizado</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {data.entradas.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={4} className="text-center py-6 text-muted-foreground">
                        Nenhuma receita no período selecionado
                      </TableCell>
                    </TableRow>
                  ) : (
                    data.entradas.map((t, idx) => (
                      <TableRow key={idx}>
                        <TableCell className="font-mono text-xs md:text-sm">{formatDateLocal(t.date)}</TableCell>
                        <TableCell className="text-xs md:text-sm" title={t.description}>
                          {t.is_accumulated || t.description?.includes('[Acumulado]') ? (
                            <div>
                              <p>{truncateText(t.description, isMobile ? 25 : 45)}</p>
                              <p className="text-xs text-muted-foreground">Conta: {t.source_company_name || '-'}</p>
                            </div>
                          ) : (
                            truncateText(t.description, isMobile ? 20 : 40)
                          )}
                        </TableCell>
                        <TableCell className="text-right font-mono tabular-nums text-xs md:text-sm text-muted-foreground">{formatValue(t.previsao)}</TableCell>
                        <TableCell className="text-right font-mono tabular-nums text-xs md:text-sm text-emerald-600 border-l-2 border-border">{formatValue(t.realizado)}</TableCell>
                      </TableRow>
                    ))
                  )}
                  {/* Total Row */}
                  <TableRow className="bg-emerald-500/30 dark:bg-emerald-900/60 font-semibold">
                    <TableCell colSpan={2} className="text-xs md:text-sm">Total Receitas (A)</TableCell>
                    <TableCell className="text-right font-mono tabular-nums text-xs md:text-sm">{formatValue(data.totals.receitas.previsao)}</TableCell>
                    <TableCell className="text-right font-mono tabular-nums text-xs md:text-sm text-emerald-600 border-l-2 border-border">{formatValue(data.totals.receitas.realizado)}</TableCell>
                  </TableRow>
                </TableBody>
              </Table>
            </CardContent>
          </Card>

          {/* Exits Table (Despesas + Custos) */}
          <Card>
            <CardHeader className="pb-3">
              <CardTitle className="text-lg flex items-center gap-2">
                <TrendingDown className="w-5 h-5 text-red-500" />
                Saídas (Despesas e Custos)
              </CardTitle>
            </CardHeader>
            <CardContent className="p-0">
              <Table>
                <TableHeader>
                  <TableRow className="bg-red-500/20 dark:bg-red-900/40">
                    <TableHead className="text-xs md:text-sm">Data</TableHead>
                    <TableHead className="text-xs md:text-sm">Descrição</TableHead>
                    <TableHead className="text-center text-xs md:text-sm">Tipo</TableHead>
                    <TableHead className="text-right text-xs md:text-sm">Previsão</TableHead>
                    <TableHead className="text-right text-xs md:text-sm border-l-2 border-border">Realizado</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {data.saidas.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={5} className="text-center py-6 text-muted-foreground">
                        Nenhuma despesa/custo no período selecionado
                      </TableCell>
                    </TableRow>
                  ) : (
                    data.saidas.map((t, idx) => (
                      <TableRow key={idx}>
                        <TableCell className="font-mono text-xs md:text-sm">{formatDateLocal(t.date)}</TableCell>
                        <TableCell className="text-xs md:text-sm" title={t.description}>
                          {t.is_accumulated || t.description?.includes('[Acumulado]') ? (
                            <div>
                              <p>{truncateText(t.description, isMobile ? 20 : 40)}</p>
                              <p className="text-xs text-muted-foreground">Conta: {t.source_company_name || '-'}</p>
                            </div>
                          ) : (
                            truncateText(t.description, isMobile ? 15 : 35)
                          )}
                        </TableCell>
                        <TableCell className="text-center">
                          <Badge className={`text-xs ${t.type === 'DESPESA' ? 'bg-red-600' : 'bg-amber-600'}`}>
                            {isMobile ? (t.type === 'DESPESA' ? 'D' : 'C') : t.type_label}
                          </Badge>
                        </TableCell>
                        <TableCell className="text-right font-mono tabular-nums text-xs md:text-sm text-muted-foreground">{formatValue(t.previsao)}</TableCell>
                        <TableCell className="text-right font-mono tabular-nums text-xs md:text-sm text-red-600 border-l-2 border-border">{formatValue(t.realizado)}</TableCell>
                      </TableRow>
                    ))
                  )}
                  {/* Total Despesas Row */}
                  <TableRow className="bg-red-500/30 dark:bg-red-900/60">
                    <TableCell colSpan={3} className="text-xs md:text-sm font-medium">Total Despesas (B)</TableCell>
                    <TableCell className="text-right font-mono tabular-nums text-xs md:text-sm">{formatValue(data.totals.despesas.previsao)}</TableCell>
                    <TableCell className="text-right font-mono tabular-nums text-xs md:text-sm text-red-600 border-l-2 border-border">{formatValue(data.totals.despesas.realizado)}</TableCell>
                  </TableRow>
                  {/* Total Custos Row */}
                  <TableRow className="bg-amber-500/30 dark:bg-amber-900/60">
                    <TableCell colSpan={3} className="text-xs md:text-sm font-medium">Total Custos (C)</TableCell>
                    <TableCell className="text-right font-mono tabular-nums text-xs md:text-sm">{formatValue(data.totals.custos.previsao)}</TableCell>
                    <TableCell className="text-right font-mono tabular-nums text-xs md:text-sm text-amber-600 border-l-2 border-border">{formatValue(data.totals.custos.realizado)}</TableCell>
                  </TableRow>
                </TableBody>
              </Table>
            </CardContent>
          </Card>

          {/* Totals Summary Card */}
          <Card className="bg-slate-500/10 dark:bg-slate-800/50">
            <CardHeader className="pb-3">
              <CardTitle className="text-lg flex items-center gap-2">
                <Calculator className="w-5 h-5" />
                Resumo de Totais
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-3">
              {/* Total do Período */}
              <div className="flex flex-col md:flex-row md:items-center justify-between py-2 border-b border-border">
                <span className="text-sm font-medium">Total do Período (D = A - B - C)</span>
                <div className="flex gap-4 md:gap-8 mt-1 md:mt-0">
                  <div className="text-right">
                    <span className="text-xs text-muted-foreground block">Previsão</span>
                    <span className={`font-mono tabular-nums ${data.totals.total_geral.previsao >= 0 ? 'text-blue-400' : 'text-red-400'}`}>
                      {formatValue(data.totals.total_geral.previsao)}
                    </span>
                  </div>
                  <div className="text-right">
                    <span className="text-xs text-muted-foreground block">Realizado</span>
                    <span className={`font-mono tabular-nums ${data.totals.total_geral.realizado >= 0 ? 'text-emerald-400' : 'text-red-400'}`}>
                      {formatValue(data.totals.total_geral.realizado)}
                    </span>
                  </div>
                </div>
              </div>
              
              {/* Saldo Anterior */}
              <div className="flex flex-col md:flex-row md:items-center justify-between py-2 border-b border-border">
                <span className="text-sm font-medium">Saldo Acumulado Anterior (E)</span>
                <div className="flex gap-4 md:gap-8 mt-1 md:mt-0">
                  <div className="text-right">
                    <span className="text-xs text-muted-foreground block">Previsão</span>
                    <span className={`font-mono tabular-nums ${data.totals.saldo_anterior.previsao >= 0 ? 'text-blue-400' : 'text-red-400'}`}>
                      {formatValue(data.totals.saldo_anterior.previsao)}
                    </span>
                  </div>
                  <div className="text-right">
                    <span className="text-xs text-muted-foreground block">Realizado</span>
                    <span className={`font-mono tabular-nums ${data.totals.saldo_anterior.realizado >= 0 ? 'text-emerald-400' : 'text-red-400'}`}>
                      {formatValue(data.totals.saldo_anterior.realizado)}
                    </span>
                  </div>
                </div>
              </div>
              
              {/* Saldo Acumulado Final */}
              <div className="flex flex-col md:flex-row md:items-center justify-between py-2 bg-primary/20 dark:bg-primary/30 rounded-lg px-3 -mx-3">
                <span className="text-sm font-bold">Saldo Acumulado Final (F = D + E)</span>
                <div className="flex gap-4 md:gap-8 mt-1 md:mt-0">
                  <div className="text-right">
                    <span className="text-xs text-muted-foreground block">Previsão</span>
                    <span className={`font-mono tabular-nums font-bold text-lg ${data.totals.acumulado.previsao >= 0 ? 'text-blue-400' : 'text-red-400'}`}>
                      {formatValue(data.totals.acumulado.previsao)}
                    </span>
                  </div>
                  <div className="text-right">
                    <span className="text-xs text-muted-foreground block">Realizado</span>
                    <span className={`font-mono tabular-nums font-bold text-lg ${data.totals.acumulado.realizado >= 0 ? 'text-emerald-400' : 'text-red-400'}`}>
                      {formatValue(data.totals.acumulado.realizado)}
                    </span>
                  </div>
                </div>
              </div>
              
              {/* Saldo Inicial da Conta/Empresa */}
              <div className="flex flex-col md:flex-row md:items-center justify-between py-2 mt-2 bg-indigo-500/20 dark:bg-indigo-900/50 rounded-lg px-3 -mx-3 border border-indigo-500/50">
                <span className="text-sm font-bold">Saldo Inicial da Conta</span>
                <div className="flex gap-4 md:gap-8 mt-1 md:mt-0">
                  <div className="text-right">
                    <span className="text-xs text-muted-foreground block">Cadastrado</span>
                    <span className={`font-mono tabular-nums font-bold text-lg ${(data.saldo_inicial || 0) >= 0 ? 'text-indigo-400' : 'text-red-400'}`}>
                      {formatValue(data.saldo_inicial || 0)}
                    </span>
                  </div>
                </div>
              </div>
              
              {/* Saldo Final - Prova Real */}
              <div className="flex flex-col md:flex-row md:items-center justify-between py-3 mt-2 bg-emerald-500/20 dark:bg-emerald-900/50 rounded-lg px-3 -mx-3 border-2 border-emerald-500/50">
                <span className="text-sm font-bold">Saldo Final (G = F.Previsão + F.Realizado)</span>
                <div className="flex gap-4 md:gap-8 mt-1 md:mt-0">
                  <div className="text-right">
                    <span className="text-xs text-muted-foreground block">Total</span>
                    <span className={`font-mono tabular-nums font-bold text-xl ${data.cards.saldo_final >= 0 ? 'text-emerald-400' : 'text-red-400'}`}>
                      {formatValue(data.cards.saldo_final)}
                    </span>
                  </div>
                </div>
              </div>
              
              {/* Saldo Final Acumulado - H = G - E */}
              <div className="flex flex-col md:flex-row md:items-center justify-between py-3 mt-2 bg-purple-500/20 dark:bg-purple-900/50 rounded-lg px-3 -mx-3 border-2 border-purple-500/50">
                <span className="text-sm font-bold">Saldo Final Acumulado (H = G - E)</span>
                <div className="flex gap-4 md:gap-8 mt-1 md:mt-0">
                  <div className="text-right">
                    <span className="text-xs text-muted-foreground block">Total</span>
                    <span className={`font-mono tabular-nums font-bold text-xl ${(data.cards.saldo_final - (data.totals.saldo_anterior.previsao + data.totals.saldo_anterior.realizado)) >= 0 ? 'text-purple-400' : 'text-red-400'}`}>
                      {formatValue(data.cards.saldo_final - (data.totals.saldo_anterior.previsao + data.totals.saldo_anterior.realizado))}
                    </span>
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>
        </>
      )}
    </div>
  );
};

// ==================== REPORT CHART ACCOUNTS PAGE (CLIENT) ====================
const ReportChartAccountsPage = ({ userPlan = 1, onLockedFeatureClick }) => {
  const [accounts, setAccounts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [stats, setStats] = useState({ total: 0, analiticas: { receitas: 0, custos: 0, despesas: 0 } });
  const [filterType, setFilterType] = useState('all');
  const [filterLevel, setFilterLevel] = useState('all');
  const [filterSynthetic, setFilterSynthetic] = useState('all');
  const [searchTerm, setSearchTerm] = useState('');
  const [showLockedModal, setShowLockedModal] = useState(false);
  const { toast } = useToast();

  // Check plan access
  const hasAccess = userPlan >= 3;

  const loadAccounts = async () => {
    if (!hasAccess) {
      setLoading(false);
      return;
    }
    try {
      setLoading(true);
      const [accountsRes, statsRes] = await Promise.all([
        axios.get(`${API}/admin/chart-accounts`),
        axios.get(`${API}/admin/chart-accounts/stats`)
      ]);
      setAccounts(accountsRes.data);
      setStats(statsRes.data);
    } catch (error) {
      toast({ title: "Erro ao carregar contas", variant: "destructive" });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { 
    if (hasAccess) {
      loadAccounts(); 
    }
  }, [hasAccess]);

  const filteredAccounts = accounts.filter(acc => {
    if (filterType !== 'all' && acc.account_type !== filterType) return false;
    if (filterLevel !== 'all' && acc.level !== parseInt(filterLevel)) return false;
    if (filterSynthetic === 'synthetic' && !acc.is_synthetic) return false;
    if (filterSynthetic === 'analytical' && acc.is_synthetic) return false;
    if (searchTerm && !acc.name.toLowerCase().includes(searchTerm.toLowerCase()) && !acc.code.includes(searchTerm)) return false;
    return true;
  });

  const getTypeColor = (type) => {
    switch (type) {
      case 'RECEITA': return 'bg-emerald-100 text-emerald-700';
      case 'CUSTO': return 'bg-amber-100 text-amber-700';
      case 'DESPESA': return 'bg-red-100 text-red-700';
      default: return 'bg-slate-100 text-slate-700';
    }
  };

  const getLevelLabel = (level) => {
    switch (level) {
      case 1: return 'Classe';
      case 2: return 'Grupo';
      case 3: return 'Subgrupo';
      case 4: return 'Sintética';
      case 5: return 'Analítica';
      default: return level;
    }
  };

  const handlePrint = () => {
    if (!hasAccess) {
      setShowLockedModal(true);
      return;
    }

    const filterDescription = [];
    if (filterType !== 'all') filterDescription.push(`Tipo: ${filterType}`);
    if (filterLevel !== 'all') filterDescription.push(`Nível: ${filterLevel}`);
    if (filterSynthetic !== 'all') filterDescription.push(`Categoria: ${filterSynthetic === 'synthetic' ? 'Sintéticas' : 'Analíticas'}`);
    if (searchTerm) filterDescription.push(`Busca: "${searchTerm}"`);

    const printContent = `
      <!DOCTYPE html>
      <html>
      <head>
        <title>Relatório - Plano de Contas</title>
        <style>
          body { font-family: Arial, sans-serif; padding: 20px; font-size: 12px; }
          h1 { font-size: 18px; margin-bottom: 5px; }
          .subtitle { color: #666; margin-bottom: 15px; font-size: 11px; }
          .filters { background: #f5f5f5; padding: 10px; margin-bottom: 15px; border-radius: 4px; font-size: 11px; }
          .stats { display: flex; gap: 20px; margin-bottom: 15px; }
          .stat { background: #f0f0f0; padding: 8px 12px; border-radius: 4px; }
          .stat-label { font-size: 10px; color: #666; }
          .stat-value { font-size: 16px; font-weight: bold; }
          table { width: 100%; border-collapse: collapse; margin-top: 10px; }
          th, td { border: 1px solid #ddd; padding: 6px 8px; text-align: left; }
          th { background: #f5f5f5; font-weight: bold; }
          .code { font-family: monospace; }
          .synthetic { color: #888; font-style: italic; }
          .type-receita { color: #059669; }
          .type-custo { color: #d97706; }
          .type-despesa { color: #dc2626; }
          .footer { margin-top: 20px; text-align: center; font-size: 10px; color: #666; }
          @media print { body { padding: 0; } }
        </style>
      </head>
      <body>
        <h1>Relatório - Plano de Contas</h1>
        <p class="subtitle">Gerado em ${new Date().toLocaleString('pt-BR')}</p>
        
        ${filterDescription.length > 0 ? `<div class="filters"><strong>Filtros aplicados:</strong> ${filterDescription.join(' | ')}</div>` : ''}
        
        <div class="stats">
          <div class="stat"><div class="stat-label">Total</div><div class="stat-value">${filteredAccounts.length}</div></div>
          <div class="stat"><div class="stat-label">Receitas</div><div class="stat-value">${filteredAccounts.filter(a => a.account_type === 'RECEITA').length}</div></div>
          <div class="stat"><div class="stat-label">Custos</div><div class="stat-value">${filteredAccounts.filter(a => a.account_type === 'CUSTO').length}</div></div>
          <div class="stat"><div class="stat-label">Despesas</div><div class="stat-value">${filteredAccounts.filter(a => a.account_type === 'DESPESA').length}</div></div>
        </div>
        
        <table>
          <thead>
            <tr>
              <th>Código</th>
              <th>Nome</th>
              <th>Tipo</th>
              <th>Nível</th>
              <th>Categoria</th>
            </tr>
          </thead>
          <tbody>
            ${filteredAccounts.map(acc => `
              <tr>
                <td class="code">${acc.code}</td>
                <td class="${acc.is_synthetic ? 'synthetic' : ''}" style="padding-left: ${(acc.level - 1) * 12}px">${acc.name}</td>
                <td class="type-${acc.account_type.toLowerCase()}">${acc.account_type}</td>
                <td>${acc.level}</td>
                <td>${getLevelLabel(acc.level)}</td>
              </tr>
            `).join('')}
          </tbody>
        </table>
        
        <div class="footer">DRE+ Controle Financeiro - Relatório de Plano de Contas</div>
      </body>
      </html>
    `;

    const printWindow = window.open('', '_blank');
    printWindow.document.write(printContent);
    printWindow.document.close();
    printWindow.focus();
    setTimeout(() => { printWindow.print(); }, 250);
  };

  // Show locked modal if no access
  if (!hasAccess) {
    return (
      <div className="space-y-6 animate-fadeIn">
        <div className="flex items-center gap-3">
          <Receipt className="w-6 h-6 text-primary" />
          <h1 className="text-2xl font-bold">Relatório - Plano de Contas</h1>
        </div>
        
        <Card className="border-2 border-dashed border-muted-foreground/30">
          <CardContent className="flex flex-col items-center justify-center py-16">
            <div className="w-20 h-20 rounded-full bg-muted flex items-center justify-center mb-6">
              <Lock className="w-10 h-10 text-muted-foreground" />
            </div>
            <h2 className="text-xl font-semibold mb-2">Recurso Bloqueado</h2>
            <p className="text-muted-foreground text-center max-w-md mb-6">
              O relatório de Plano de Contas está disponível a partir do <strong>Plano Contábil</strong>.
              Faça upgrade para ter acesso completo a este recurso.
            </p>
            <Button onClick={() => onLockedFeatureClick && onLockedFeatureClick('report-chart-accounts')} className="bg-primary hover:bg-primary/90">
              <Lock className="w-4 h-4 mr-2" /> Ver Planos Disponíveis
            </Button>
          </CardContent>
        </Card>

        {/* Locked Modal */}
        <Dialog open={showLockedModal} onOpenChange={setShowLockedModal}>
          <DialogContent>
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2">
                <Lock className="w-5 h-5 text-amber-500" />
                Recurso Exclusivo
              </DialogTitle>
              <DialogDescription>
                O relatório de Plano de Contas está disponível a partir do <strong>Plano Contábil</strong>.
              </DialogDescription>
            </DialogHeader>
            <div className="py-4">
              <p className="text-sm text-muted-foreground">
                Com o Plano Contábil você tem acesso a:
              </p>
              <ul className="mt-3 space-y-2 text-sm">
                <li className="flex items-center gap-2">
                  <CheckCircle className="w-4 h-4 text-emerald-500" />
                  Relatório completo do Plano de Contas
                </li>
                <li className="flex items-center gap-2">
                  <CheckCircle className="w-4 h-4 text-emerald-500" />
                  Exportação para PDF
                </li>
                <li className="flex items-center gap-2">
                  <CheckCircle className="w-4 h-4 text-emerald-500" />
                  Filtros avançados por tipo e nível
                </li>
                <li className="flex items-center gap-2">
                  <CheckCircle className="w-4 h-4 text-emerald-500" />
                  Contas a Receber
                </li>
              </ul>
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setShowLockedModal(false)}>Fechar</Button>
              <Button onClick={() => { setShowLockedModal(false); onLockedFeatureClick && onLockedFeatureClick('report-chart-accounts'); }}>
                Ver Planos
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>
    );
  }

  return (
    <div className="space-y-6 animate-fadeIn">
      {/* Header */}
      <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-4">
        <div className="flex items-center gap-3">
          <Receipt className="w-6 h-6 text-primary" />
          <div>
            <h1 className="text-2xl font-bold">Relatório - Plano de Contas</h1>
            <p className="text-muted-foreground text-sm">Visualize e exporte o plano de contas completo</p>
          </div>
        </div>
        <Button onClick={handlePrint} className="bg-primary hover:bg-primary/90">
          <Printer className="w-4 h-4 mr-2" /> Imprimir / PDF
        </Button>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
        <Card>
          <CardContent className="p-4">
            <p className="text-muted-foreground text-sm">Total Geral</p>
            <p className="text-2xl font-bold">{stats.total}</p>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-4">
            <p className="text-muted-foreground text-sm">Filtradas</p>
            <p className="text-2xl font-bold text-blue-600">{filteredAccounts.length}</p>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-4">
            <p className="text-emerald-600 text-sm">Receitas (Anal.)</p>
            <p className="text-2xl font-bold">{stats.analiticas?.receitas || 0}</p>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-4">
            <p className="text-amber-600 text-sm">Custos (Anal.)</p>
            <p className="text-2xl font-bold">{stats.analiticas?.custos || 0}</p>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-4">
            <p className="text-red-600 text-sm">Despesas (Anal.)</p>
            <p className="text-2xl font-bold">{stats.analiticas?.despesas || 0}</p>
          </CardContent>
        </Card>
      </div>

      {/* Filters */}
      <Card>
        <CardContent className="p-4">
          <div className="grid grid-cols-1 md:grid-cols-5 gap-4">
            <div>
              <Label>Buscar</Label>
              <Input
                placeholder="Nome ou código..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
              />
            </div>
            <div>
              <Label>Tipo</Label>
              <Select value={filterType} onValueChange={setFilterType}>
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">Todos</SelectItem>
                  <SelectItem value="RECEITA">Receitas</SelectItem>
                  <SelectItem value="CUSTO">Custos</SelectItem>
                  <SelectItem value="DESPESA">Despesas</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label>Nível</Label>
              <Select value={filterLevel} onValueChange={setFilterLevel}>
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">Todos</SelectItem>
                  <SelectItem value="1">1 - Classe</SelectItem>
                  <SelectItem value="2">2 - Grupo</SelectItem>
                  <SelectItem value="3">3 - Subgrupo</SelectItem>
                  <SelectItem value="4">4 - Sintética</SelectItem>
                  <SelectItem value="5">5 - Analítica</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label>Categoria</Label>
              <Select value={filterSynthetic} onValueChange={setFilterSynthetic}>
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">Todas</SelectItem>
                  <SelectItem value="synthetic">Sintéticas</SelectItem>
                  <SelectItem value="analytical">Analíticas</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="flex items-end">
              <Button variant="outline" onClick={() => { setSearchTerm(''); setFilterType('all'); setFilterLevel('all'); setFilterSynthetic('all'); }} className="w-full">
                Limpar Filtros
              </Button>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Table */}
      <Card>
        <CardContent className="p-0">
          {loading ? (
            <div className="flex items-center justify-center py-12">
              <div className="h-8 w-8 animate-spin rounded-full border-4 border-primary border-t-transparent"></div>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Código</TableHead>
                    <TableHead>Nome</TableHead>
                    <TableHead>Tipo</TableHead>
                    <TableHead>Nível</TableHead>
                    <TableHead>Categoria</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {filteredAccounts.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={5} className="text-center text-muted-foreground py-8">
                        Nenhuma conta encontrada com os filtros selecionados
                      </TableCell>
                    </TableRow>
                  ) : (
                    filteredAccounts.map((acc) => (
                      <TableRow key={acc.id}>
                        <TableCell className="font-mono text-muted-foreground">{acc.code}</TableCell>
                        <TableCell style={{ paddingLeft: `${(acc.level - 1) * 16}px` }}>
                          {acc.is_synthetic ? <span className="text-muted-foreground italic">{acc.name}</span> : acc.name}
                        </TableCell>
                        <TableCell>
                          <Badge className={getTypeColor(acc.account_type)}>{acc.account_type}</Badge>
                        </TableCell>
                        <TableCell className="text-muted-foreground">{acc.level}</TableCell>
                        <TableCell className="text-muted-foreground text-sm">{getLevelLabel(acc.level)}</TableCell>
                      </TableRow>
                    ))
                  )}
                </TableBody>
              </Table>
            </div>
          )}
          <div className="p-4 border-t">
            <p className="text-sm text-muted-foreground">
              Exibindo {filteredAccounts.length} de {accounts.length} contas
            </p>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

// ==================== CLIENTS PAGE ====================
const ClientsPage = ({ onNavigate, companyId }) => {
  const [clients, setClients] = useState([]);
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [editingClient, setEditingClient] = useState(null);
  const [formData, setFormData] = useState({
    name: '', nome_fantasia: '', email: '', client_type: 'Prospect', ddi: '+55', phone: '', 
    address: '', numero: '', cep: '', bairro: '', cidade: '', uf: '',
    notes: '', document_type: '', document: '', inscricao_estadual: '',
    email_financeiro: '', ddi_financeiro: '+55', phone_financeiro: ''
  });
  const { toast } = useToast();
  const isMobile = useIsMobile();

  useEffect(() => { loadClients(); }, [companyId]);

  const loadClients = async () => {
    try {
      const url = companyId ? `${API}/clients?company_id=${companyId}` : `${API}/clients`;
      const response = await axios.get(url);
      setClients(response.data);
    } catch (error) {
      toast({ title: "Erro ao carregar clientes", variant: "destructive" });
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      const data = { ...formData, phone: unformatPhone(formData.phone), company_id: companyId || null };
      if (editingClient) {
        await axios.put(`${API}/clients/${editingClient.id}`, data);
        toast({ title: "Cliente atualizado!" });
      } else {
        await axios.post(`${API}/clients`, data);
        toast({ title: "Cliente criado!" });
      }
      setIsDialogOpen(false);
      resetForm();
      loadClients();
    } catch (error) {
      toast({ title: "Erro ao salvar cliente", variant: "destructive" });
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm("Confirma exclusão?")) return;
    try {
      await axios.delete(`${API}/clients/${id}`);
      toast({ title: "Cliente excluído!" });
      loadClients();
    } catch (error) {
      toast({ title: "Erro ao excluir", variant: "destructive" });
    }
  };

  const openEdit = (client) => {
    setEditingClient(client);
    // Format document for display if exists
    let displayDoc = client.document || '';
    if (client.document && client.document_type) {
      displayDoc = client.document_type === 'CPF' ? formatCPF(client.document) : 
                   client.document_type === 'CNPJ' ? formatCNPJ(client.document) : client.document;
    }
    setFormData({
      name: client.name || '', nome_fantasia: client.nome_fantasia || '', 
      email: client.email || '', client_type: client.client_type || 'Prospect',
      ddi: client.ddi || '+55', phone: formatPhone(client.phone || ''), 
      address: client.address || '', numero: client.numero || '', cep: client.cep || '',
      bairro: client.bairro || '', cidade: client.cidade || '', uf: client.uf || '',
      notes: client.notes || '', document_type: client.document_type || '', document: displayDoc,
      inscricao_estadual: client.inscricao_estadual || '',
      email_financeiro: client.email_financeiro || '',
      ddi_financeiro: client.ddi_financeiro || '+55',
      phone_financeiro: formatPhone(client.phone_financeiro || '')
    });
    setIsDialogOpen(true);
  };

  const resetForm = () => {
    setEditingClient(null);
    setFormData({ 
      name: '', nome_fantasia: '', email: '', client_type: 'Prospect', ddi: '+55', phone: '', 
      address: '', numero: '', cep: '', bairro: '', cidade: '', uf: '',
      notes: '', document_type: '', document: '', inscricao_estadual: '',
      email_financeiro: '', ddi_financeiro: '+55', phone_financeiro: ''
    });
  };

  // Helper functions for document formatting
  const formatCPF = (value) => {
    const digits = value.replace(/\D/g, '').slice(0, 11);
    if (digits.length <= 3) return digits;
    if (digits.length <= 6) return `${digits.slice(0,3)}.${digits.slice(3)}`;
    if (digits.length <= 9) return `${digits.slice(0,3)}.${digits.slice(3,6)}.${digits.slice(6)}`;
    return `${digits.slice(0,3)}.${digits.slice(3,6)}.${digits.slice(6,9)}-${digits.slice(9)}`;
  };

  const formatCNPJ = (value) => {
    const digits = value.replace(/\D/g, '').slice(0, 14);
    if (digits.length <= 2) return digits;
    if (digits.length <= 5) return `${digits.slice(0,2)}.${digits.slice(2)}`;
    if (digits.length <= 8) return `${digits.slice(0,2)}.${digits.slice(2,5)}.${digits.slice(5)}`;
    if (digits.length <= 12) return `${digits.slice(0,2)}.${digits.slice(2,5)}.${digits.slice(5,8)}/${digits.slice(8)}`;
    return `${digits.slice(0,2)}.${digits.slice(2,5)}.${digits.slice(5,8)}/${digits.slice(8,12)}-${digits.slice(12)}`;
  };

  const handleDocumentChange = (value) => {
    const formatted = formData.document_type === 'CPF' ? formatCPF(value) : 
                      formData.document_type === 'CNPJ' ? formatCNPJ(value) : value;
    setFormData({...formData, document: formatted});
  };

  const formatDocumentDisplay = (doc, type) => {
    if (!doc) return '';
    const digits = doc.replace(/\D/g, '');
    if (type === 'CPF') return formatCPF(digits);
    if (type === 'CNPJ') return formatCNPJ(digits);
    return doc;
  };

  const getTypeBadgeClass = (type) => {
    const classes = { 'Prospect': 'badge-prospect', 'Proposta': 'badge-proposta', 'Efetivo': 'badge-efetivo', 'Inativo': 'badge-inativo' };
    return classes[type] || 'badge-prospect';
  };

  return (
    <div className="animate-fadeIn" data-testid="clients-page">
      <div className="flex items-center justify-between mb-6">
        <h2 className="text-2xl md:text-3xl font-semibold tracking-tight">Clientes</h2>
        <div className="flex items-center gap-2">
          <Button onClick={() => { resetForm(); setIsDialogOpen(true); }} className="gap-2" data-testid="add-client-btn">
            <Plus className="w-4 h-4" /> {isMobile ? 'Incluir' : 'Novo Cliente'}
          </Button>
          {isMobile && onNavigate && (
            <button
              onClick={() => onNavigate('dashboard')}
              className="p-2 rounded-lg hover:bg-muted transition-colors text-muted-foreground hover:text-foreground"
              title="Fechar"
              data-testid="close-clients-btn"
            >
              <XCircle className="w-6 h-6" />
            </button>
          )}
        </div>
      </div>

      <Card className="overflow-x-auto">
        <Table className="data-table">
          <TableHeader>
            <TableRow>
              <TableHead>Nome</TableHead>
              <TableHead>Tipo</TableHead>
              {!isMobile && <TableHead>Comercial</TableHead>}
              {!isMobile && <TableHead>Email</TableHead>}
              <TableHead className="text-right">Ações</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {clients.map((client) => (
              <TableRow key={client.id}>
                <TableCell>
                  <div className="font-medium">{client.name}</div>
                  {client.nome_fantasia && (
                    <div className="text-sm text-muted-foreground">{client.nome_fantasia}</div>
                  )}
                </TableCell>
                <TableCell><Badge className={getTypeBadgeClass(client.client_type)}>{client.client_type}</Badge></TableCell>
                {!isMobile && <TableCell className="font-mono text-sm">{client.phone ? `${client.ddi} ${formatPhone(client.phone)}` : '-'}</TableCell>}
                {!isMobile && <TableCell>{client.email || '-'}</TableCell>}
                <TableCell className="text-right">
                  <div className="flex items-center justify-end gap-1 md:gap-2">
                    <Button variant="ghost" size="icon" onClick={() => openEdit(client)}><Pencil className="w-4 h-4" /></Button>
                    <Button variant="ghost" size="icon" onClick={() => handleDelete(client.id)}><Trash2 className="w-4 h-4 text-destructive" /></Button>
                    {client.phone && (
                      <button 
                        onClick={() => openWhatsApp(client.ddi, client.phone)}
                        className="whatsapp-btn p-2 hover:bg-green-500/10 rounded"
                        title="Abrir WhatsApp"
                      >
                        <WhatsAppIcon className="w-5 h-5" />
                      </button>
                    )}
                  </div>
                </TableCell>
              </TableRow>
            ))}
            {clients.length === 0 && (
              <TableRow><TableCell colSpan={isMobile ? 3 : 5} className="text-center text-muted-foreground py-8">Nenhum cliente cadastrado</TableCell></TableRow>
            )}
          </TableBody>
        </Table>
      </Card>

      <Dialog open={isDialogOpen} onOpenChange={setIsDialogOpen}>
        <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>{editingClient ? 'Editar Cliente' : 'Novo Cliente'}</DialogTitle>
            <DialogDescription>Preencha os dados do cliente</DialogDescription>
          </DialogHeader>
          <form onSubmit={handleSubmit} className="space-y-4">
            <div>
              <Label>Tipo de Cliente</Label>
              <Select value={formData.client_type} onValueChange={(v) => setFormData({...formData, client_type: v})}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>{CLIENT_TYPES.map((type) => (<SelectItem key={type} value={type}>{type}</SelectItem>))}</SelectContent>
              </Select>
            </div>
            
            <Accordion type="multiple" defaultValue={['dados-cliente']} className="w-full space-y-2">
              {/* Dados do Cliente - Collapsible */}
              <AccordionItem value="dados-cliente" className="border rounded-lg">
                <AccordionTrigger className="px-4 hover:no-underline">
                  <div className="flex items-center gap-2">
                    <User className="w-4 h-4 text-primary" />
                    <span>Dados do Cliente</span>
                  </div>
                </AccordionTrigger>
                <AccordionContent className="px-4 pb-4">
                  <div className="space-y-4">
                    <div>
                      <Label>Nome/Razão Social *</Label>
                      <Input value={formData.name} onChange={(e) => setFormData({...formData, name: e.target.value})} required data-testid="client-name" />
                    </div>
                    <div>
                      <Label>Nome Fantasia</Label>
                      <Input value={formData.nome_fantasia} onChange={(e) => setFormData({...formData, nome_fantasia: e.target.value})} placeholder="Nome fantasia (opcional)" data-testid="client-nome-fantasia" />
                    </div>
                    <div className="grid grid-cols-3 gap-4">
                      <div>
                        <Label>Tipo Documento</Label>
                        <Select value={formData.document_type} onValueChange={(v) => setFormData({...formData, document_type: v, document: ''})}>
                          <SelectTrigger><SelectValue placeholder="Selecione" /></SelectTrigger>
                          <SelectContent>
                            <SelectItem value="CPF">CPF</SelectItem>
                            <SelectItem value="CNPJ">CNPJ</SelectItem>
                          </SelectContent>
                        </Select>
                      </div>
                      <div className="col-span-2">
                        <Label>{formData.document_type || 'CPF/CNPJ'}</Label>
                        <Input 
                          value={formData.document} 
                          onChange={(e) => handleDocumentChange(e.target.value)} 
                          placeholder={formData.document_type === 'CNPJ' ? '00.000.000/0000-00' : '000.000.000-00'}
                          disabled={!formData.document_type}
                          data-testid="client-document-input"
                        />
                      </div>
                    </div>
                    <div>
                      <Label>Inscrição Estadual</Label>
                      <Input 
                        value={formData.inscricao_estadual} 
                        onChange={(e) => setFormData({...formData, inscricao_estadual: e.target.value})} 
                        placeholder="Inscrição Estadual (opcional)"
                        data-testid="client-inscricao-estadual"
                      />
                    </div>
                  </div>
                </AccordionContent>
              </AccordionItem>

              {/* Contatos - Collapsible */}
              <AccordionItem value="contatos" className="border rounded-lg">
                <AccordionTrigger className="px-4 hover:no-underline">
                  <div className="flex items-center gap-2">
                    <Phone className="w-4 h-4 text-primary" />
                    <span>Contatos</span>
                  </div>
                </AccordionTrigger>
                <AccordionContent className="px-4 pb-4">
                  <div className="space-y-4">
                    <div>
                      <Label>E-mail</Label>
                      <Input type="email" value={formData.email} onChange={(e) => setFormData({...formData, email: e.target.value})} placeholder="email@empresa.com" />
                    </div>
                    <div className="grid grid-cols-3 gap-2">
                      <div>
                        <Label>DDI</Label>
                        <Select value={formData.ddi} onValueChange={(v) => setFormData({...formData, ddi: v})}>
                          <SelectTrigger><SelectValue /></SelectTrigger>
                          <SelectContent>{DDI_OPTIONS.map((opt) => (<SelectItem key={opt.code} value={opt.code}>{opt.code} {opt.country}</SelectItem>))}</SelectContent>
                        </Select>
                      </div>
                      <div className="col-span-2">
                        <Label>Telefone Comercial</Label>
                        <Input value={formData.phone} onChange={(e) => setFormData({...formData, phone: formatPhone(e.target.value)})} placeholder="(11) 99999-9999" />
                      </div>
                    </div>
                    <Separator className="my-2" />
                    <p className="text-sm font-medium text-muted-foreground">Contato Financeiro</p>
                    <div>
                      <Label>E-mail Financeiro</Label>
                      <Input type="email" value={formData.email_financeiro} onChange={(e) => setFormData({...formData, email_financeiro: e.target.value})} placeholder="financeiro@empresa.com" />
                    </div>
                    <div className="grid grid-cols-3 gap-2">
                      <div>
                        <Label>DDI</Label>
                        <Select value={formData.ddi_financeiro} onValueChange={(v) => setFormData({...formData, ddi_financeiro: v})}>
                          <SelectTrigger><SelectValue /></SelectTrigger>
                          <SelectContent>{DDI_OPTIONS.map((opt) => (<SelectItem key={opt.code} value={opt.code}>{opt.code} {opt.country}</SelectItem>))}</SelectContent>
                        </Select>
                      </div>
                      <div className="col-span-2">
                        <Label>Telefone Financeiro</Label>
                        <Input value={formData.phone_financeiro} onChange={(e) => setFormData({...formData, phone_financeiro: formatPhone(e.target.value)})} placeholder="(11) 99999-9999" />
                      </div>
                    </div>
                  </div>
                </AccordionContent>
              </AccordionItem>

              {/* Endereço - Collapsible */}
              <AccordionItem value="endereco" className="border rounded-lg">
                <AccordionTrigger className="px-4 hover:no-underline">
                  <div className="flex items-center gap-2">
                    <MapPin className="w-4 h-4 text-primary" />
                    <span>Endereço</span>
                  </div>
                </AccordionTrigger>
                <AccordionContent className="px-4 pb-4">
                  <div className="space-y-4">
                    <div className="grid grid-cols-3 gap-2">
                      <div className="col-span-2 space-y-2">
                        <Label>Logradouro</Label>
                        <Input 
                          value={formData.address} 
                          onChange={(e) => setFormData({...formData, address: e.target.value})} 
                          placeholder="Rua, Av..."
                          data-testid="client-address"
                        />
                      </div>
                      <div className="space-y-2">
                        <Label>Número</Label>
                        <Input 
                          value={formData.numero} 
                          onChange={(e) => setFormData({...formData, numero: e.target.value})} 
                          placeholder="Nº"
                          data-testid="client-numero"
                        />
                      </div>
                    </div>
                    <div className="grid grid-cols-2 gap-2">
                      <div className="space-y-2">
                        <Label>Bairro</Label>
                        <Input 
                          value={formData.bairro} 
                          onChange={(e) => setFormData({...formData, bairro: e.target.value})} 
                          placeholder="Bairro"
                          data-testid="client-bairro"
                        />
                      </div>
                      <div className="space-y-2">
                        <Label>CEP</Label>
                        <Input 
                          value={formData.cep} 
                          onChange={(e) => setFormData({...formData, cep: e.target.value})} 
                          placeholder="00000-000"
                          data-testid="client-cep"
                        />
                      </div>
                    </div>
                    <div className="grid grid-cols-3 gap-2">
                      <div className="col-span-2 space-y-2">
                        <Label>Cidade</Label>
                        <Input 
                          value={formData.cidade} 
                          onChange={(e) => setFormData({...formData, cidade: e.target.value})} 
                          placeholder="Cidade"
                          data-testid="client-cidade"
                        />
                      </div>
                      <div className="space-y-2">
                        <Label>UF</Label>
                        <Select value={formData.uf || '_none'} onValueChange={(v) => setFormData({...formData, uf: v === '_none' ? '' : v})}>
                          <SelectTrigger data-testid="client-uf"><SelectValue placeholder="UF" /></SelectTrigger>
                          <SelectContent>
                            <SelectItem value="_none">Selecione</SelectItem>
                            {UF_OPTIONS.map(uf => <SelectItem key={uf} value={uf}>{uf}</SelectItem>)}
                          </SelectContent>
                        </Select>
                      </div>
                    </div>
                  </div>
                </AccordionContent>
              </AccordionItem>
            </Accordion>
            
            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setIsDialogOpen(false)}>Cancelar</Button>
              <Button type="submit">Salvar</Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
};

// ==================== SUPPLIERS PAGE ====================
const SuppliersPage = ({ onNavigate, companyId }) => {
  const [suppliers, setSuppliers] = useState([]);
  const [segments, setSegments] = useState([]);
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [isSegmentDialogOpen, setIsSegmentDialogOpen] = useState(false);
  const [editingSupplier, setEditingSupplier] = useState(null);
  const [newSegmentName, setNewSegmentName] = useState('');
  const [formData, setFormData] = useState({ 
    name: '', email: '', ddi: '+55', phone: '', segment_id: '', vendor_name: '', address: '', notes: '', chave_pix: '',
    // Campos para Contato Financeiro
    financial_contact_name: '', financial_ddi: '+55', financial_phone: '', financial_email: '',
    // Campos para Endereço
    logradouro: '', numero: '', bairro: '', cep: '', cidade: '', uf: ''
  });
  const { toast } = useToast();
  const isMobile = useIsMobile();

  // DDI Options
  const DDI_OPTIONS = [
    { code: '+55', country: '🇧🇷' },
    { code: '+1', country: '🇺🇸' },
    { code: '+351', country: '🇵🇹' },
    { code: '+34', country: '🇪🇸' },
    { code: '+44', country: '🇬🇧' }
  ];

  // Phone formatting function
  const formatPhone = (value) => {
    const digits = value.replace(/\D/g, '').slice(0, 11);
    if (digits.length <= 2) return digits;
    if (digits.length <= 7) return `(${digits.slice(0, 2)}) ${digits.slice(2)}`;
    return `(${digits.slice(0, 2)}) ${digits.slice(2, 7)}-${digits.slice(7)}`;
  };

  const handlePhoneChange = (value) => {
    setFormData({...formData, phone: formatPhone(value)});
  };

  const handleFinancialPhoneChange = (value) => {
    setFormData({...formData, financial_phone: formatPhone(value)});
  };

  useEffect(() => { loadData(); }, [companyId]);

  const loadData = async () => {
    try {
      const companyParam = companyId ? `?company_id=${companyId}` : '';
      const [suppliersRes, segmentsRes] = await Promise.all([
        axios.get(`${API}/suppliers${companyParam}`), 
        axios.get(`${API}/segments${companyParam}`)
      ]);
      setSuppliers(suppliersRes.data);
      setSegments(segmentsRes.data);
    } catch (error) {
      toast({ title: "Erro ao carregar dados", variant: "destructive" });
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      const data = { ...formData, company_id: companyId || null };
      if (editingSupplier) {
        await axios.put(`${API}/suppliers/${editingSupplier.id}`, data);
        toast({ title: "Fornecedor atualizado!" });
      } else {
        await axios.post(`${API}/suppliers`, data);
        toast({ title: "Fornecedor criado!" });
      }
      setIsDialogOpen(false);
      resetForm();
      loadData();
    } catch (error) {
      toast({ title: "Erro ao salvar", variant: "destructive" });
    }
  };

  const handleAddSegment = async () => {
    if (!newSegmentName.trim()) return;
    try {
      await axios.post(`${API}/segments`, { name: newSegmentName.trim(), company_id: companyId || null });
      toast({ title: "Segmento criado!" });
      setNewSegmentName('');
      setIsSegmentDialogOpen(false);
      loadData();
    } catch (error) {
      toast({ title: "Erro ao criar segmento", variant: "destructive" });
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm("Confirma exclusão?")) return;
    try {
      await axios.delete(`${API}/suppliers/${id}`);
      toast({ title: "Fornecedor excluído!" });
      loadData();
    } catch (error) {
      toast({ title: "Erro ao excluir", variant: "destructive" });
    }
  };

  const openEdit = (supplier) => {
    setEditingSupplier(supplier);
    setFormData({
      name: supplier.name || '', email: supplier.email || '', ddi: supplier.ddi || '+55', phone: formatPhone(supplier.phone || ''),
      segment_id: supplier.segment_id || '', vendor_name: supplier.vendor_name || '', address: supplier.address || '', notes: supplier.notes || '',
      chave_pix: supplier.chave_pix || '',
      // Campos de Contato Financeiro
      financial_contact_name: supplier.financial_contact_name || '',
      financial_ddi: supplier.financial_ddi || '+55',
      financial_phone: formatPhone(supplier.financial_phone || ''),
      financial_email: supplier.financial_email || '',
      // Campos de Endereço
      logradouro: supplier.logradouro || '',
      numero: supplier.numero || '',
      bairro: supplier.bairro || '',
      cep: supplier.cep || '',
      cidade: supplier.cidade || '',
      uf: supplier.uf || ''
    });
    setIsDialogOpen(true);
  };

  const resetForm = () => {
    setEditingSupplier(null);
    setFormData({ 
      name: '', email: '', ddi: '+55', phone: '', segment_id: '', vendor_name: '', address: '', notes: '', chave_pix: '',
      financial_contact_name: '', financial_ddi: '+55', financial_phone: '', financial_email: '',
      logradouro: '', numero: '', bairro: '', cep: '', cidade: '', uf: ''
    });
  };

  return (
    <div className="animate-fadeIn" data-testid="suppliers-page">
      <div className="flex items-center justify-between mb-6 gap-2">
        <h2 className="text-2xl md:text-3xl font-semibold tracking-tight truncate min-w-0">Fornecedor</h2>
        <div className="flex items-center gap-1 md:gap-2 flex-shrink-0">
          <Button onClick={() => { resetForm(); setIsDialogOpen(true); }} className="gap-1 md:gap-2 px-2 md:px-4" size={isMobile ? "sm" : "default"}><Plus className="w-4 h-4" /> {isMobile ? 'Incluir' : 'Novo Fornecedor'}</Button>
          {isMobile && onNavigate && (
            <button
              onClick={() => onNavigate('dashboard')}
              className="p-1.5 rounded-lg hover:bg-muted transition-colors text-muted-foreground hover:text-foreground"
              title="Fechar"
              data-testid="close-suppliers-btn"
            >
              <XCircle className="w-5 h-5" />
            </button>
          )}
        </div>
      </div>

      <Card className="overflow-x-auto">
        <Table className="data-table">
          <TableHeader>
            <TableRow>
              <TableHead>Nome</TableHead>
              <TableHead>Segmento</TableHead>
              {!isMobile && <TableHead>Vendedor</TableHead>}
              {!isMobile && <TableHead>Telefone</TableHead>}
              <TableHead className="text-right">Ações</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {suppliers.map((supplier) => (
              <TableRow key={supplier.id}>
                <TableCell className="font-medium">{supplier.name}</TableCell>
                <TableCell>{supplier.segment_name && <Badge variant="outline" className="bg-primary/10 text-primary border-primary/30 text-xs"><Tag className="w-3 h-3 mr-1" />{supplier.segment_name}</Badge>}</TableCell>
                {!isMobile && <TableCell>{supplier.vendor_name || '-'}</TableCell>}
                {!isMobile && <TableCell className="font-mono text-sm">{supplier.phone || '-'}</TableCell>}
                <TableCell className="text-right">
                  <div className="flex items-center justify-end gap-1">
                    <Button variant="ghost" size="icon" className="h-8 w-8" onClick={() => openEdit(supplier)}><Pencil className="w-4 h-4" /></Button>
                    <Button variant="ghost" size="icon" className="h-8 w-8" onClick={() => handleDelete(supplier.id)}><Trash2 className="w-4 h-4 text-destructive" /></Button>
                  </div>
                </TableCell>
              </TableRow>
            ))}
            {suppliers.length === 0 && (<TableRow><TableCell colSpan={isMobile ? 3 : 5} className="text-center text-muted-foreground py-8">Nenhum fornecedor cadastrado</TableCell></TableRow>)}
          </TableBody>
        </Table>
      </Card>

      <Dialog open={isDialogOpen} onOpenChange={setIsDialogOpen}>
        <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>{editingSupplier ? 'Editar Fornecedor' : 'Novo Fornecedor'}</DialogTitle>
            <DialogDescription>Preencha os dados do fornecedor</DialogDescription>
          </DialogHeader>
          <form onSubmit={handleSubmit} className="space-y-4">
            <div><Label>Nome *</Label><Input value={formData.name} onChange={(e) => setFormData({...formData, name: e.target.value})} required /></div>
            <div>
              <Label>Segmento</Label>
              <div className="flex gap-2">
                <Select value={formData.segment_id} onValueChange={(v) => setFormData({...formData, segment_id: v})}>
                  <SelectTrigger className="flex-1"><SelectValue placeholder="Selecione o segmento" /></SelectTrigger>
                  <SelectContent>{segments.map((seg) => (<SelectItem key={seg.id} value={seg.id}>{seg.name}</SelectItem>))}</SelectContent>
                </Select>
                <Button type="button" variant="outline" size="icon" onClick={() => setIsSegmentDialogOpen(true)}><Plus className="w-4 h-4" /></Button>
              </div>
            </div>
            
            {/* Contatos - Collapsible */}
            <Accordion type="multiple" className="w-full">
              <AccordionItem value="contatos" className="border rounded-lg">
                <AccordionTrigger className="px-4 hover:no-underline">
                  <div className="flex items-center gap-2">
                    <Phone className="w-4 h-4 text-primary" />
                    <span>Contatos</span>
                  </div>
                </AccordionTrigger>
                <AccordionContent className="px-4 pb-4">
                  <div className="space-y-4">
                    {/* Contato Comercial */}
                    <p className="text-sm font-medium text-muted-foreground">Contato Comercial</p>
                    <div><Label>Nome do Vendedor</Label><Input value={formData.vendor_name} onChange={(e) => setFormData({...formData, vendor_name: e.target.value})} placeholder="Nome do vendedor/representante" /></div>
                    <div className="grid grid-cols-3 gap-2">
                      <div>
                        <Label>DDI</Label>
                        <Select value={formData.ddi} onValueChange={(v) => setFormData({...formData, ddi: v})}>
                          <SelectTrigger><SelectValue /></SelectTrigger>
                          <SelectContent>{DDI_OPTIONS.map((opt) => (<SelectItem key={opt.code} value={opt.code}>{opt.code} {opt.country}</SelectItem>))}</SelectContent>
                        </Select>
                      </div>
                      <div className="col-span-2">
                        <Label>Celular do Vendedor</Label>
                        <Input 
                          value={formData.phone} 
                          onChange={(e) => handlePhoneChange(e.target.value)} 
                          placeholder="(00) 00000-0000"
                          data-testid="supplier-phone-input"
                        />
                      </div>
                    </div>
                    <div><Label>Email do Vendedor</Label><Input type="email" value={formData.email} onChange={(e) => setFormData({...formData, email: e.target.value})} placeholder="vendedor@empresa.com" /></div>
                    
                    <Separator className="my-2" />
                    
                    {/* Contato Financeiro */}
                    <p className="text-sm font-medium text-muted-foreground">Contato Financeiro</p>
                    <div><Label>Nome do Responsável Financeiro</Label><Input value={formData.financial_contact_name} onChange={(e) => setFormData({...formData, financial_contact_name: e.target.value})} placeholder="Nome do responsável financeiro" /></div>
                    <div className="grid grid-cols-3 gap-2">
                      <div>
                        <Label>DDI</Label>
                        <Select value={formData.financial_ddi} onValueChange={(v) => setFormData({...formData, financial_ddi: v})}>
                          <SelectTrigger><SelectValue /></SelectTrigger>
                          <SelectContent>{DDI_OPTIONS.map((opt) => (<SelectItem key={opt.code} value={opt.code}>{opt.code} {opt.country}</SelectItem>))}</SelectContent>
                        </Select>
                      </div>
                      <div className="col-span-2">
                        <Label>Celular Financeiro</Label>
                        <Input 
                          value={formData.financial_phone} 
                          onChange={(e) => handleFinancialPhoneChange(e.target.value)} 
                          placeholder="(00) 00000-0000"
                          data-testid="supplier-financial-phone-input"
                        />
                      </div>
                    </div>
                    <div><Label>E-mail do Financeiro</Label><Input type="email" value={formData.financial_email} onChange={(e) => setFormData({...formData, financial_email: e.target.value})} placeholder="financeiro@empresa.com" /></div>
                  </div>
                </AccordionContent>
              </AccordionItem>

              {/* Endereço - Collapsible */}
              <AccordionItem value="endereco" className="border rounded-lg">
                <AccordionTrigger className="px-4 hover:no-underline">
                  <div className="flex items-center gap-2">
                    <MapPin className="w-4 h-4 text-primary" />
                    <span>Endereço</span>
                  </div>
                </AccordionTrigger>
                <AccordionContent className="px-4 pb-4">
                  <div className="space-y-4">
                    <div className="grid grid-cols-3 gap-2">
                      <div className="col-span-2">
                        <Label>Logradouro</Label>
                        <Input 
                          value={formData.logradouro} 
                          onChange={(e) => setFormData({...formData, logradouro: e.target.value})} 
                          placeholder="Rua, Av..."
                          data-testid="supplier-logradouro"
                        />
                      </div>
                      <div>
                        <Label>Número</Label>
                        <Input 
                          value={formData.numero} 
                          onChange={(e) => setFormData({...formData, numero: e.target.value})} 
                          placeholder="Nº"
                          data-testid="supplier-numero"
                        />
                      </div>
                    </div>
                    <div className="grid grid-cols-2 gap-2">
                      <div>
                        <Label>Bairro</Label>
                        <Input 
                          value={formData.bairro} 
                          onChange={(e) => setFormData({...formData, bairro: e.target.value})} 
                          placeholder="Bairro"
                          data-testid="supplier-bairro"
                        />
                      </div>
                      <div>
                        <Label>CEP</Label>
                        <Input 
                          value={formData.cep} 
                          onChange={(e) => setFormData({...formData, cep: e.target.value})} 
                          placeholder="00000-000"
                          data-testid="supplier-cep"
                        />
                      </div>
                    </div>
                    <div className="grid grid-cols-3 gap-2">
                      <div className="col-span-2">
                        <Label>Cidade</Label>
                        <Input 
                          value={formData.cidade} 
                          onChange={(e) => setFormData({...formData, cidade: e.target.value})} 
                          placeholder="Cidade"
                          data-testid="supplier-cidade"
                        />
                      </div>
                      <div>
                        <Label>UF</Label>
                        <Select value={formData.uf} onValueChange={(v) => setFormData({...formData, uf: v})}>
                          <SelectTrigger data-testid="supplier-uf"><SelectValue placeholder="UF" /></SelectTrigger>
                          <SelectContent>
                            {['AC','AL','AP','AM','BA','CE','DF','ES','GO','MA','MT','MS','MG','PA','PB','PR','PE','PI','RJ','RN','RS','RO','RR','SC','SP','SE','TO'].map(uf => (
                              <SelectItem key={uf} value={uf}>{uf}</SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      </div>
                    </div>
                  </div>
                </AccordionContent>
              </AccordionItem>
            </Accordion>

            <div>
              <Label>Chave PIX</Label>
              <div className="flex gap-2">
                <Input 
                  className="flex-1"
                  value={formData.chave_pix} 
                  onChange={(e) => setFormData({...formData, chave_pix: e.target.value})} 
                  placeholder="CPF, CNPJ, Email, Celular ou Chave Aleatória" 
                  data-testid="supplier-pix-input" 
                />
                <Button 
                  type="button" 
                  variant="outline" 
                  size="icon"
                  onClick={() => {
                    if (formData.chave_pix) {
                      navigator.clipboard.writeText(formData.chave_pix);
                      toast({ title: "Copiado!", description: "Chave PIX copiada para a área de transferência" });
                    }
                  }}
                  disabled={!formData.chave_pix}
                  title="Copiar chave PIX"
                  data-testid="copy-pix-btn"
                >
                  <Copy className="w-4 h-4" />
                </Button>
              </div>
            </div>
            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setIsDialogOpen(false)}>Cancelar</Button>
              <Button type="submit">Salvar</Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      <Dialog open={isSegmentDialogOpen} onOpenChange={setIsSegmentDialogOpen}>
        <DialogContent className="max-w-sm">
          <DialogHeader>
            <DialogTitle>Novo Segmento</DialogTitle>
            <DialogDescription>Adicione um novo segmento de fornecedor</DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div><Label>Nome do Segmento (máx. 50 caracteres)</Label><Input value={newSegmentName} onChange={(e) => setNewSegmentName(e.target.value.slice(0, 50))} placeholder="Ex: Controle de Acesso" /></div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setIsSegmentDialogOpen(false)}>Cancelar</Button>
              <Button onClick={handleAddSegment}>Adicionar</Button>
            </DialogFooter>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

// ==================== PRODUCTS AND SERVICES PAGE ====================
const ProductsPage = ({ onNavigate, companyId }) => {
  const [activeTab, setActiveTab] = useState('products');
  const [products, setProducts] = useState([]);
  const [services, setServices] = useState([]);
  const [segments, setSegments] = useState([]);
  const [serviceCategories, setServiceCategories] = useState([]);
  const [suppliers, setSuppliers] = useState([]);
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [isServiceDialogOpen, setIsServiceDialogOpen] = useState(false);
  const [isCategoryDialogOpen, setIsCategoryDialogOpen] = useState(false);
  const [isSuppliersDialogOpen, setIsSuppliersDialogOpen] = useState(false);
  const [selectedSegmentSuppliers, setSelectedSegmentSuppliers] = useState([]);
  const [selectedSegmentName, setSelectedSegmentName] = useState('');
  const [editingProduct, setEditingProduct] = useState(null);
  const [editingService, setEditingService] = useState(null);
  const [formData, setFormData] = useState({ code: '', name: '', description: '', price: 0, cost_price: 0, segment_id: '', notes: '' });
  const [serviceFormData, setServiceFormData] = useState({ 
    code: '', name: '', description: '', category_id: '', billing_unit: 'hora', 
    price: 0, estimated_hours: 0, estimated_minutes: 0, difficulty_level: 1, is_active: true 
  });
  const [newCategoryName, setNewCategoryName] = useState('');
  const { toast } = useToast();
  const isMobile = useIsMobile();

  useEffect(() => { loadData(); }, [companyId]);

  const loadData = async () => {
    try {
      const companyParam = companyId ? `?company_id=${companyId}` : '';
      const [productsRes, servicesRes, segmentsRes, categoriesRes, suppliersRes] = await Promise.all([
        axios.get(`${API}/products${companyParam}`), 
        axios.get(`${API}/services${companyParam}`),
        axios.get(`${API}/segments${companyParam}`), 
        axios.get(`${API}/service-categories${companyParam}`),
        axios.get(`${API}/suppliers${companyParam}`)
      ]);
      setProducts(productsRes.data);
      setServices(servicesRes.data);
      setSegments(segmentsRes.data);
      setServiceCategories(categoriesRes.data);
      setSuppliers(suppliersRes.data);
    } catch (error) {
      toast({ title: "Erro ao carregar dados", variant: "destructive" });
    }
  };

  // Product handlers
  const handleSubmitProduct = async (e) => {
    e.preventDefault();
    try {
      const data = { 
        ...formData, 
        price: parseFloat(formData.price) || 0, 
        cost_price: parseFloat(formData.cost_price) || 0,
        company_id: companyId || null 
      };
      if (editingProduct) {
        await axios.put(`${API}/products/${editingProduct.id}`, data);
        toast({ title: "Produto atualizado!" });
      } else {
        await axios.post(`${API}/products`, data);
        toast({ title: "Produto criado!" });
      }
      setIsDialogOpen(false);
      resetProductForm();
      loadData();
    } catch (error) {
      const msg = error.response?.data?.detail || "Erro ao salvar";
      toast({ title: msg, variant: "destructive" });
    }
  };

  const handleDeleteProduct = async (id) => {
    if (!window.confirm("Confirma exclusão do produto?")) return;
    try {
      await axios.delete(`${API}/products/${id}`);
      toast({ title: "Produto excluído!" });
      loadData();
    } catch (error) {
      toast({ title: "Erro ao excluir", variant: "destructive" });
    }
  };

  const openEditProduct = (product) => {
    setEditingProduct(product);
    setFormData({ 
      code: product.code || '', 
      name: product.name || '', 
      description: product.description || '', 
      price: product.price || 0, 
      cost_price: product.cost_price || 0,
      segment_id: product.segment_id || '', 
      notes: product.notes || '' 
    });
    setIsDialogOpen(true);
  };

  const resetProductForm = () => {
    setEditingProduct(null);
    setFormData({ code: '', name: '', description: '', price: 0, cost_price: 0, segment_id: '', notes: '' });
  };

  // Service handlers
  const handleSubmitService = async (e) => {
    e.preventDefault();
    try {
      const data = { 
        ...serviceFormData, 
        price: parseFloat(serviceFormData.price) || 0,
        estimated_hours: parseInt(serviceFormData.estimated_hours) || 0,
        estimated_minutes: parseInt(serviceFormData.estimated_minutes) || 0,
        difficulty_level: parseInt(serviceFormData.difficulty_level) || 1,
        company_id: companyId || null 
      };
      if (editingService) {
        await axios.put(`${API}/services/${editingService.id}`, data);
        toast({ title: "Serviço atualizado!" });
      } else {
        await axios.post(`${API}/services`, data);
        toast({ title: "Serviço criado!" });
      }
      setIsServiceDialogOpen(false);
      resetServiceForm();
      loadData();
    } catch (error) {
      const msg = error.response?.data?.detail || "Erro ao salvar";
      toast({ title: msg, variant: "destructive" });
    }
  };

  const handleDeleteService = async (id) => {
    if (!window.confirm("Confirma exclusão do serviço?")) return;
    try {
      await axios.delete(`${API}/services/${id}`);
      toast({ title: "Serviço excluído!" });
      loadData();
    } catch (error) {
      toast({ title: "Erro ao excluir", variant: "destructive" });
    }
  };

  const openEditService = (service) => {
    setEditingService(service);
    setServiceFormData({ 
      code: service.code || '', name: service.name || '', description: service.description || '', 
      category_id: service.category_id || '', billing_unit: service.billing_unit || 'hora',
      price: service.price || 0, estimated_hours: service.estimated_hours || 0, 
      estimated_minutes: service.estimated_minutes || 0, difficulty_level: service.difficulty_level || 1,
      is_active: service.is_active !== false
    });
    setIsServiceDialogOpen(true);
  };

  const resetServiceForm = () => {
    setEditingService(null);
    setServiceFormData({ 
      code: '', name: '', description: '', category_id: '', billing_unit: 'hora', 
      price: 0, estimated_hours: 0, estimated_minutes: 0, difficulty_level: 1, is_active: true 
    });
  };

  // Category handlers
  const handleCreateCategory = async () => {
    if (!newCategoryName.trim()) return;
    try {
      await axios.post(`${API}/service-categories`, { name: newCategoryName, company_id: companyId || null });
      toast({ title: "Categoria criada!" });
      setNewCategoryName('');
      setIsCategoryDialogOpen(false);
      loadData();
    } catch (error) {
      toast({ title: "Erro ao criar categoria", variant: "destructive" });
    }
  };

  const showSuppliersBySegment = (segmentId, segmentName) => {
    const filtered = suppliers.filter(s => s.segment_id === segmentId);
    setSelectedSegmentSuppliers(filtered);
    setSelectedSegmentName(segmentName);
    setIsSuppliersDialogOpen(true);
  };

  const billingUnits = [
    { value: 'hora', label: 'Por Hora' },
    { value: 'visita', label: 'Por Visita' },
    { value: 'item', label: 'Por Item' },
    { value: 'km', label: 'Por Km' },
    { value: 'pacote', label: 'Pacote' }
  ];

  return (
    <div className="animate-fadeIn" data-testid="products-page">
      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4 mb-6">
        <h2 className="text-2xl md:text-3xl font-semibold tracking-tight">Produtos e Serviços</h2>
        <div className="flex items-center gap-2">
          {activeTab === 'products' ? (
            <Button onClick={() => { resetProductForm(); setIsDialogOpen(true); }} className="gap-2">
              <Plus className="w-4 h-4" /> {isMobile ? 'Incluir' : 'Novo Produto'}
            </Button>
          ) : (
            <Button onClick={() => { resetServiceForm(); setIsServiceDialogOpen(true); }} className="gap-2">
              <Plus className="w-4 h-4" /> {isMobile ? 'Incluir' : 'Novo Serviço'}
            </Button>
          )}
          {isMobile && onNavigate && (
            <button onClick={() => onNavigate('dashboard')} className="p-2 rounded-lg hover:bg-muted transition-colors text-muted-foreground hover:text-foreground" title="Fechar">
              <XCircle className="w-6 h-6" />
            </button>
          )}
        </div>
      </div>

      {/* Tabs */}
      <Tabs value={activeTab} onValueChange={setActiveTab} className="mb-4">
        <TabsList>
          <TabsTrigger value="products" className="gap-2"><Package className="w-4 h-4" />Produtos ({products.length})</TabsTrigger>
          <TabsTrigger value="services" className="gap-2"><Wrench className="w-4 h-4" />Serviços ({services.length})</TabsTrigger>
        </TabsList>
      </Tabs>

      {/* Products Tab */}
      {activeTab === 'products' && (
        <Card className="overflow-x-auto">
          <Table className="data-table">
            <TableHeader>
              <TableRow>
                <TableHead className="w-24">Código</TableHead>
                <TableHead>Nome</TableHead>
                {!isMobile && <TableHead>Descrição</TableHead>}
                <TableHead>Segmento</TableHead>
                {!isMobile && <TableHead className="text-right">Preço</TableHead>}
                <TableHead className="text-right">Ações</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {products.length === 0 ? (
                <TableRow><TableCell colSpan={6} className="text-center text-muted-foreground py-8">Nenhum produto cadastrado</TableCell></TableRow>
              ) : products.map((product) => (
                <TableRow key={product.id} className="cursor-pointer card-hover" onClick={() => openEditProduct(product)}>
                  <TableCell className="font-mono text-sm">{product.code || '-'}</TableCell>
                  <TableCell className="font-medium">{product.name}</TableCell>
                  {!isMobile && <TableCell className="text-muted-foreground">{product.description || '-'}</TableCell>}
                  <TableCell>
                    {product.segment_name ? (
                      <Badge variant="outline" className="cursor-pointer hover:bg-primary/20" onClick={(e) => { e.stopPropagation(); showSuppliersBySegment(product.segment_id, product.segment_name); }}>
                        {product.segment_name}
                      </Badge>
                    ) : '-'}
                  </TableCell>
                  {!isMobile && <TableCell className="text-right font-mono">{formatCurrency(product.price)}</TableCell>}
                  <TableCell className="text-right" onClick={(e) => e.stopPropagation()}>
                    <Button variant="ghost" size="icon" onClick={() => handleDeleteProduct(product.id)}><Trash2 className="w-4 h-4 text-red-500" /></Button>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </Card>
      )}
      {/* Services Tab */}
      {activeTab === 'services' && (
        <Card className="overflow-x-auto">
          <Table className="data-table">
            <TableHeader>
              <TableRow>
                <TableHead className="w-24">Código</TableHead>
                <TableHead>Nome</TableHead>
                {!isMobile && <TableHead>Categoria</TableHead>}
                <TableHead>Cobrança</TableHead>
                <TableHead className="text-right">Preço</TableHead>
                <TableHead className="text-center">Status</TableHead>
                <TableHead className="text-right">Ações</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {services.length === 0 ? (
                <TableRow><TableCell colSpan={7} className="text-center text-muted-foreground py-8">Nenhum serviço cadastrado</TableCell></TableRow>
              ) : services.map((service) => (
                <TableRow key={service.id} className="cursor-pointer card-hover" onClick={() => openEditService(service)}>
                  <TableCell className="font-mono text-sm">{service.code || '-'}</TableCell>
                  <TableCell className="font-medium">{service.name}</TableCell>
                  {!isMobile && <TableCell><Badge variant="outline">{service.category_name || '-'}</Badge></TableCell>}
                  <TableCell className="capitalize">{billingUnits.find(u => u.value === service.billing_unit)?.label || service.billing_unit}</TableCell>
                  <TableCell className="text-right font-mono">{formatCurrency(service.price)}</TableCell>
                  <TableCell className="text-center">
                    <Badge className={service.is_active ? 'bg-emerald-500' : 'bg-gray-500'}>{service.is_active ? 'Ativo' : 'Inativo'}</Badge>
                  </TableCell>
                  <TableCell className="text-right" onClick={(e) => e.stopPropagation()}>
                    <Button variant="ghost" size="icon" onClick={() => handleDeleteService(service.id)}><Trash2 className="w-4 h-4 text-red-500" /></Button>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </Card>
      )}

      <Dialog open={isDialogOpen} onOpenChange={setIsDialogOpen}>
        <DialogContent className="max-w-md max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>{editingProduct ? 'Editar Produto' : 'Novo Produto'}</DialogTitle>
          </DialogHeader>
          <form onSubmit={handleSubmitProduct} className="space-y-4">
            <div><Label>Código</Label><Input value={formData.code} onChange={(e) => setFormData({...formData, code: e.target.value})} placeholder="Código do produto" /></div>
            <div><Label>Nome *</Label><Input value={formData.name} onChange={(e) => setFormData({...formData, name: e.target.value})} placeholder="Nome do produto" required /></div>
            <div><Label>Descrição</Label><Textarea value={formData.description} onChange={(e) => setFormData({...formData, description: e.target.value})} placeholder="Descrição" /></div>
            <div className="grid grid-cols-2 gap-4">
              <div><Label>Custo de Compra</Label><Input type="number" step="0.01" value={formData.cost_price} onChange={(e) => setFormData({...formData, cost_price: e.target.value})} placeholder="0,00" /></div>
              <div><Label>Preço de Venda</Label><Input type="number" step="0.01" value={formData.price} onChange={(e) => setFormData({...formData, price: e.target.value})} placeholder="0,00" /></div>
            </div>
            <div>
              <Label>Segmento</Label>
              <Select value={formData.segment_id || '_none'} onValueChange={(v) => setFormData({...formData, segment_id: v === '_none' ? '' : v})}>
                <SelectTrigger><SelectValue placeholder="Selecione" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="_none">Nenhum</SelectItem>
                  {segments.map(s => <SelectItem key={s.id} value={s.id}>{s.name}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div><Label>Observações</Label><Textarea value={formData.notes} onChange={(e) => setFormData({...formData, notes: e.target.value})} placeholder="Observações" /></div>
            <DialogFooter><Button type="button" variant="outline" onClick={() => setIsDialogOpen(false)}>Cancelar</Button><Button type="submit">Salvar</Button></DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* Service Dialog */}
      <Dialog open={isServiceDialogOpen} onOpenChange={setIsServiceDialogOpen}>
        <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>{editingService ? 'Editar Serviço' : 'Novo Serviço'}</DialogTitle>
          </DialogHeader>
          <form onSubmit={handleSubmitService} className="space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <div><Label>Código</Label><Input value={serviceFormData.code} onChange={(e) => setServiceFormData({...serviceFormData, code: e.target.value})} placeholder="Código" /></div>
              <div className="flex items-center gap-2 pt-6">
                <Switch checked={serviceFormData.is_active} onCheckedChange={(v) => setServiceFormData({...serviceFormData, is_active: v})} />
                <Label>{serviceFormData.is_active ? 'Ativo' : 'Inativo'}</Label>
              </div>
            </div>
            <div><Label>Nome do Serviço *</Label><Input value={serviceFormData.name} onChange={(e) => setServiceFormData({...serviceFormData, name: e.target.value})} placeholder="Nome do serviço" required /></div>
            <div><Label>Descrição Detalhada</Label><Textarea value={serviceFormData.description} onChange={(e) => setServiceFormData({...serviceFormData, description: e.target.value})} placeholder="Descrição detalhada do serviço" rows={3} /></div>
            <div>
              <Label>Categoria</Label>
              <div className="flex gap-2">
                <Select value={serviceFormData.category_id || '_none'} onValueChange={(v) => setServiceFormData({...serviceFormData, category_id: v === '_none' ? '' : v})}>
                  <SelectTrigger className="flex-1"><SelectValue placeholder="Selecione" /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="_none">Nenhuma</SelectItem>
                    {serviceCategories.map(c => <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>)}
                  </SelectContent>
                </Select>
                <Button type="button" variant="outline" size="icon" onClick={() => setIsCategoryDialogOpen(true)} title="Adicionar categoria"><Plus className="w-4 h-4" /></Button>
              </div>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Unidade de Cobrança</Label>
                <Select value={serviceFormData.billing_unit} onValueChange={(v) => setServiceFormData({...serviceFormData, billing_unit: v})}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {billingUnits.map(u => <SelectItem key={u.value} value={u.value}>{u.label}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div><Label>Preço Padrão (R$)</Label><Input type="number" step="0.01" value={serviceFormData.price} onChange={(e) => setServiceFormData({...serviceFormData, price: e.target.value})} /></div>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Tempo Estimado</Label>
                <div className="flex gap-2">
                  <div className="flex-1">
                    <Input type="number" min="0" value={serviceFormData.estimated_hours} onChange={(e) => setServiceFormData({...serviceFormData, estimated_hours: e.target.value})} placeholder="Horas" />
                    <span className="text-xs text-muted-foreground">Horas</span>
                  </div>
                  <div className="flex-1">
                    <Input type="number" min="0" max="59" value={serviceFormData.estimated_minutes} onChange={(e) => setServiceFormData({...serviceFormData, estimated_minutes: e.target.value})} placeholder="Min" />
                    <span className="text-xs text-muted-foreground">Minutos</span>
                  </div>
                </div>
              </div>
              <div>
                <Label>Nível de Dificuldade</Label>
                <Select value={String(serviceFormData.difficulty_level)} onValueChange={(v) => setServiceFormData({...serviceFormData, difficulty_level: parseInt(v)})}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="1">1 - Muito Fácil</SelectItem>
                    <SelectItem value="2">2 - Fácil</SelectItem>
                    <SelectItem value="3">3 - Médio</SelectItem>
                    <SelectItem value="4">4 - Difícil</SelectItem>
                    <SelectItem value="5">5 - Muito Difícil</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
            <DialogFooter><Button type="button" variant="outline" onClick={() => setIsServiceDialogOpen(false)}>Cancelar</Button><Button type="submit">Salvar</Button></DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* Category Dialog */}
      <Dialog open={isCategoryDialogOpen} onOpenChange={setIsCategoryDialogOpen}>
        <DialogContent className="max-w-sm">
          <DialogHeader><DialogTitle>Nova Categoria de Serviço</DialogTitle></DialogHeader>
          <div className="space-y-4">
            <div><Label>Nome da Categoria</Label><Input value={newCategoryName} onChange={(e) => setNewCategoryName(e.target.value)} placeholder="Ex: Instalação, Manutenção, Consultoria" /></div>
            <DialogFooter><Button type="button" variant="outline" onClick={() => setIsCategoryDialogOpen(false)}>Cancelar</Button><Button onClick={handleCreateCategory} disabled={!newCategoryName.trim()}>Criar</Button></DialogFooter>
          </div>
        </DialogContent>
      </Dialog>

      {/* Suppliers by Segment Dialog */}
      <Dialog open={isSuppliersDialogOpen} onOpenChange={setIsSuppliersDialogOpen}>
        <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2"><Printer className="w-5 h-5 text-primary" />Fornecedores - {selectedSegmentName}</DialogTitle>
            <DialogDescription>Lista de fornecedores do segmento selecionado</DialogDescription>
          </DialogHeader>
          <div className="space-y-3 max-h-80 overflow-y-auto">
            {selectedSegmentSuppliers.length > 0 ? (
              selectedSegmentSuppliers.map((supplier) => (
                <Card key={supplier.id} className="p-4 card-hover">
                  <div className="flex items-start justify-between">
                    <div>
                      <p className="font-medium flex items-center gap-2"><Building2 className="w-4 h-4 text-muted-foreground" />{supplier.name}</p>
                      {supplier.vendor_name && <p className="text-sm text-muted-foreground flex items-center gap-2 mt-1"><User className="w-3 h-3" />Vendedor: {supplier.vendor_name}</p>}
                      {supplier.phone && <p className="text-sm text-muted-foreground flex items-center gap-2 mt-1"><CreditCard className="w-3 h-3" />{supplier.phone}</p>}
                    </div>
                  </div>
                </Card>
              ))
            ) : (<p className="text-center text-muted-foreground py-8">Nenhum fornecedor cadastrado neste segmento</p>)}
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

// ==================== EMPLOYEES PAGE (FUNCIONÁRIOS) ====================
const UF_OPTIONS = [
  'AC', 'AL', 'AP', 'AM', 'BA', 'CE', 'DF', 'ES', 'GO', 'MA', 'MT', 'MS', 'MG', 
  'PA', 'PB', 'PR', 'PE', 'PI', 'RJ', 'RN', 'RS', 'RO', 'RR', 'SC', 'SP', 'SE', 'TO'
];

const CONTRACT_TYPES = [
  { value: 'clt', label: 'CLT' },
  { value: 'pj', label: 'PJ (Pessoa Jurídica)' },
  { value: 'estagio', label: 'Estágio' },
  { value: 'aprendiz', label: 'Jovem Aprendiz' },
  { value: 'temporario', label: 'Temporário' },
];

const SALARY_TYPES = [
  { value: 'mensalista', label: 'Mensalista' },
  { value: 'horista', label: 'Horista' },
];

const GENDER_OPTIONS = [
  { value: 'masculino', label: 'Masculino' },
  { value: 'feminino', label: 'Feminino' },
  { value: 'outro', label: 'Outro' },
];

const MARITAL_STATUS_OPTIONS = [
  { value: 'solteiro', label: 'Solteiro(a)' },
  { value: 'casado', label: 'Casado(a)' },
  { value: 'divorciado', label: 'Divorciado(a)' },
  { value: 'viuvo', label: 'Viúvo(a)' },
  { value: 'uniao_estavel', label: 'União Estável' },
];

const CNH_CATEGORIES = ['A', 'B', 'AB', 'C', 'D', 'E', 'ACC'];

const EmployeesPage = ({ onNavigate, companyId }) => {
  const [employees, setEmployees] = useState([]);
  const [positions, setPositions] = useState([]);
  const [departments, setDepartments] = useState([]);
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [isPositionDialogOpen, setIsPositionDialogOpen] = useState(false);
  const [isDepartmentDialogOpen, setIsDepartmentDialogOpen] = useState(false);
  const [editingEmployee, setEditingEmployee] = useState(null);
  const [newPositionName, setNewPositionName] = useState('');
  const [newDepartmentName, setNewDepartmentName] = useState('');
  const [deleteConfirmOpen, setDeleteConfirmOpen] = useState(false);
  const [employeeToDelete, setEmployeeToDelete] = useState(null);
  const [loading, setLoading] = useState(true);
  const { toast } = useToast();
  const isMobile = useIsMobile();

  const emptyFormData = {
    // Identificação
    name: '', cpf: '', birth_date: '',
    // Admissão e Jornada
    admission_date: '', salary_type: 'mensalista', salary: 0,
    // Dados pessoais
    gender: '', marital_status: '', mother_name: '', birthplace: '', birthplace_uf: '',
    // Contato
    email: '', phone: '', address: '',
    // Vínculo
    position_id: '', department_id: '', contract_type: '', manager_id: '',
    // Documentos trabalhistas
    ctps_number: '', ctps_series: '', ctps_uf: '', ctps_issue_date: '',
    pis: '', pix_key: '', rg: '', rg_issuer: '', rg_uf: '',
    cnh: '', cnh_category: '', cnh_expiry: '',
    // Contato de emergência
    emergency_name: '', emergency_relation: '', emergency_phone: '',
    // Status
    is_active: true
  };

  const [formData, setFormData] = useState(emptyFormData);

  // Phone formatting
  const formatPhoneNumber = (value) => {
    const digits = value.replace(/\D/g, '').slice(0, 11);
    if (digits.length <= 2) return digits;
    if (digits.length <= 7) return `(${digits.slice(0, 2)}) ${digits.slice(2)}`;
    return `(${digits.slice(0, 2)}) ${digits.slice(2, 7)}-${digits.slice(7)}`;
  };

  // CPF formatting
  const formatCPF = (value) => {
    const digits = value.replace(/\D/g, '').slice(0, 11);
    if (digits.length <= 3) return digits;
    if (digits.length <= 6) return `${digits.slice(0, 3)}.${digits.slice(3)}`;
    if (digits.length <= 9) return `${digits.slice(0, 3)}.${digits.slice(3, 6)}.${digits.slice(6)}`;
    return `${digits.slice(0, 3)}.${digits.slice(3, 6)}.${digits.slice(6, 9)}-${digits.slice(9)}`;
  };

  useEffect(() => { loadData(); }, [companyId]);

  const loadData = async () => {
    try {
      setLoading(true);
      const companyParam = companyId ? `?company_id=${companyId}` : '';
      const [employeesRes, positionsRes, departmentsRes] = await Promise.all([
        axios.get(`${API}/employees${companyParam}`),
        axios.get(`${API}/positions${companyParam}`),
        axios.get(`${API}/departments${companyParam}`)
      ]);
      setEmployees(employeesRes.data);
      setPositions(positionsRes.data);
      setDepartments(departmentsRes.data);
    } catch (error) {
      toast({ title: "Erro ao carregar dados", variant: "destructive" });
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      const data = {
        ...formData,
        salary: parseFloat(formData.salary) || 0,
        company_id: companyId || null
      };
      
      if (editingEmployee) {
        await axios.put(`${API}/employees/${editingEmployee.id}`, data);
        toast({ title: "Funcionário atualizado!" });
      } else {
        await axios.post(`${API}/employees`, data);
        toast({ title: "Funcionário cadastrado!" });
      }
      setIsDialogOpen(false);
      resetForm();
      loadData();
    } catch (error) {
      const msg = error.response?.data?.detail || "Erro ao salvar";
      toast({ title: msg, variant: "destructive" });
    }
  };

  const handleDelete = async () => {
    if (!employeeToDelete) return;
    try {
      await axios.delete(`${API}/employees/${employeeToDelete.id}`);
      toast({ title: "Funcionário excluído!" });
      setDeleteConfirmOpen(false);
      setEmployeeToDelete(null);
      loadData();
    } catch (error) {
      toast({ title: "Erro ao excluir", variant: "destructive" });
    }
  };

  const openEdit = (employee) => {
    setEditingEmployee(employee);
    setFormData({
      name: employee.name || '',
      cpf: employee.cpf || '',
      birth_date: employee.birth_date || '',
      admission_date: employee.admission_date || '',
      salary_type: employee.salary_type || 'mensalista',
      salary: employee.salary || 0,
      gender: employee.gender || '',
      marital_status: employee.marital_status || '',
      mother_name: employee.mother_name || '',
      birthplace: employee.birthplace || '',
      birthplace_uf: employee.birthplace_uf || '',
      email: employee.email || '',
      phone: employee.phone || '',
      address: employee.address || '',
      position_id: employee.position_id || '',
      department_id: employee.department_id || '',
      contract_type: employee.contract_type || '',
      manager_id: employee.manager_id || '',
      ctps_number: employee.ctps_number || '',
      ctps_series: employee.ctps_series || '',
      ctps_uf: employee.ctps_uf || '',
      ctps_issue_date: employee.ctps_issue_date || '',
      pis: employee.pis || '',
      pix_key: employee.pix_key || '',
      rg: employee.rg || '',
      rg_issuer: employee.rg_issuer || '',
      rg_uf: employee.rg_uf || '',
      cnh: employee.cnh || '',
      cnh_category: employee.cnh_category || '',
      cnh_expiry: employee.cnh_expiry || '',
      emergency_name: employee.emergency_name || '',
      emergency_relation: employee.emergency_relation || '',
      emergency_phone: employee.emergency_phone || '',
      is_active: employee.is_active !== false
    });
    setIsDialogOpen(true);
  };

  const resetForm = () => {
    setEditingEmployee(null);
    setFormData(emptyFormData);
  };

  const confirmDelete = (employee, e) => {
    e.stopPropagation();
    setEmployeeToDelete(employee);
    setDeleteConfirmOpen(true);
  };

  // Create Position
  const handleCreatePosition = async () => {
    if (!newPositionName.trim()) return;
    try {
      const res = await axios.post(`${API}/positions`, { name: newPositionName, company_id: companyId || null });
      toast({ title: "Cargo criado!" });
      setNewPositionName('');
      setIsPositionDialogOpen(false);
      // Set the position_id first, then load data to update the list
      const newPositionId = res.data.id;
      await loadData();
      setFormData(prev => ({ ...prev, position_id: newPositionId }));
    } catch (error) {
      toast({ title: "Erro ao criar cargo", variant: "destructive" });
    }
  };

  // Create Department
  const handleCreateDepartment = async () => {
    if (!newDepartmentName.trim()) return;
    try {
      const res = await axios.post(`${API}/departments`, { name: newDepartmentName, company_id: companyId || null });
      toast({ title: "Departamento criado!" });
      setNewDepartmentName('');
      setIsDepartmentDialogOpen(false);
      // Set the department_id first, then load data to update the list
      const newDepartmentId = res.data.id;
      await loadData();
      setFormData(prev => ({ ...prev, department_id: newDepartmentId }));
    } catch (error) {
      toast({ title: "Erro ao criar departamento", variant: "destructive" });
    }
  };

  // Get other employees for manager selection (exclude current employee if editing)
  const getManagerOptions = () => {
    return employees.filter(emp => editingEmployee ? emp.id !== editingEmployee.id : true);
  };

  return (
    <div className="animate-fadeIn" data-testid="employees-page">
      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4 mb-6">
        <h2 className="text-2xl md:text-3xl font-semibold tracking-tight">Funcionários</h2>
        <div className="flex items-center gap-2">
          <Button onClick={() => { resetForm(); setIsDialogOpen(true); }} className="gap-2" data-testid="new-employee-btn">
            <Plus className="w-4 h-4" /> {isMobile ? 'Incluir' : 'Novo Funcionário'}
          </Button>
          {isMobile && onNavigate && (
            <button onClick={() => onNavigate('dashboard')} className="p-2 rounded-lg hover:bg-muted transition-colors text-muted-foreground hover:text-foreground" title="Fechar">
              <XCircle className="w-6 h-6" />
            </button>
          )}
        </div>
      </div>

      {/* Statistics */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
        <Card className="p-4">
          <div className="flex items-center gap-3">
            <div className="p-2 bg-emerald-500/20 rounded-lg">
              <Users className="w-5 h-5 text-emerald-500" />
            </div>
            <div>
              <p className="text-sm text-muted-foreground">Total</p>
              <p className="text-xl font-semibold">{employees.length}</p>
            </div>
          </div>
        </Card>
        <Card className="p-4">
          <div className="flex items-center gap-3">
            <div className="p-2 bg-blue-500/20 rounded-lg">
              <CheckCircle className="w-5 h-5 text-blue-500" />
            </div>
            <div>
              <p className="text-sm text-muted-foreground">Ativos</p>
              <p className="text-xl font-semibold">{employees.filter(e => e.is_active).length}</p>
            </div>
          </div>
        </Card>
        <Card className="p-4">
          <div className="flex items-center gap-3">
            <div className="p-2 bg-amber-500/20 rounded-lg">
              <Briefcase className="w-5 h-5 text-amber-500" />
            </div>
            <div>
              <p className="text-sm text-muted-foreground">Cargos</p>
              <p className="text-xl font-semibold">{positions.length}</p>
            </div>
          </div>
        </Card>
        <Card className="p-4">
          <div className="flex items-center gap-3">
            <div className="p-2 bg-purple-500/20 rounded-lg">
              <Building2 className="w-5 h-5 text-purple-500" />
            </div>
            <div>
              <p className="text-sm text-muted-foreground">Departamentos</p>
              <p className="text-xl font-semibold">{departments.length}</p>
            </div>
          </div>
        </Card>
      </div>

      {/* Employees Table */}
      <Card className="overflow-x-auto">
        <Table className="data-table">
          <TableHeader>
            <TableRow>
              <TableHead>Nome</TableHead>
              {!isMobile && <TableHead>CPF</TableHead>}
              <TableHead>Cargo</TableHead>
              {!isMobile && <TableHead>Departamento</TableHead>}
              <TableHead className="text-center">Status</TableHead>
              <TableHead className="text-right">Ações</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {loading ? (
              <TableRow><TableCell colSpan={6} className="text-center text-muted-foreground py-8">Carregando...</TableCell></TableRow>
            ) : employees.length === 0 ? (
              <TableRow><TableCell colSpan={6} className="text-center text-muted-foreground py-8">Nenhum funcionário cadastrado</TableCell></TableRow>
            ) : employees.map((employee) => (
              <TableRow key={employee.id} className="cursor-pointer card-hover" onClick={() => openEdit(employee)} data-testid={`employee-row-${employee.id}`}>
                <TableCell className="font-medium">{employee.name}</TableCell>
                {!isMobile && <TableCell className="font-mono text-sm">{employee.cpf || '-'}</TableCell>}
                <TableCell><Badge variant="outline">{employee.position_name || '-'}</Badge></TableCell>
                {!isMobile && <TableCell><Badge variant="secondary">{employee.department_name || '-'}</Badge></TableCell>}
                <TableCell className="text-center">
                  <Badge className={employee.is_active ? 'bg-emerald-500' : 'bg-gray-500'}>{employee.is_active ? 'Ativo' : 'Inativo'}</Badge>
                </TableCell>
                <TableCell className="text-right" onClick={(e) => e.stopPropagation()}>
                  <Button variant="ghost" size="icon" onClick={(e) => confirmDelete(employee, e)} data-testid={`delete-employee-${employee.id}`}>
                    <Trash2 className="w-4 h-4 text-red-500" />
                  </Button>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </Card>

      {/* Employee Form Dialog */}
      <Dialog open={isDialogOpen} onOpenChange={setIsDialogOpen}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>{editingEmployee ? 'Editar Funcionário' : 'Novo Funcionário'}</DialogTitle>
            <DialogDescription>Preencha os dados do funcionário. Campos com * são obrigatórios.</DialogDescription>
          </DialogHeader>
          <form onSubmit={handleSubmit} className="space-y-4">
            <Accordion type="multiple" defaultValue={['identification', 'admission']} className="w-full">
              {/* Identificação */}
              <AccordionItem value="identification">
                <AccordionTrigger className="hover:no-underline">
                  <div className="flex items-center gap-2">
                    <IdCard className="w-4 h-4 text-primary" />
                    <span>Identificação</span>
                  </div>
                </AccordionTrigger>
                <AccordionContent>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4 pt-2">
                    <div className="md:col-span-2">
                      <Label>Nome Completo *</Label>
                      <Input 
                        value={formData.name} 
                        onChange={(e) => setFormData({...formData, name: e.target.value})} 
                        placeholder="Nome completo do funcionário" 
                        required 
                        data-testid="employee-name-input"
                      />
                    </div>
                    <div>
                      <Label>CPF</Label>
                      <Input 
                        value={formData.cpf} 
                        onChange={(e) => setFormData({...formData, cpf: formatCPF(e.target.value)})} 
                        placeholder="000.000.000-00" 
                        data-testid="employee-cpf-input"
                      />
                    </div>
                    <div>
                      <Label>Data de Nascimento</Label>
                      <Input 
                        type="date" 
                        value={formData.birth_date} 
                        onChange={(e) => setFormData({...formData, birth_date: e.target.value})} 
                        data-testid="employee-birth-date-input"
                      />
                    </div>
                  </div>
                </AccordionContent>
              </AccordionItem>

              {/* Admissão e Jornada */}
              <AccordionItem value="admission">
                <AccordionTrigger className="hover:no-underline">
                  <div className="flex items-center gap-2">
                    <Briefcase className="w-4 h-4 text-primary" />
                    <span>Admissão e Jornada</span>
                  </div>
                </AccordionTrigger>
                <AccordionContent>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4 pt-2">
                    <div>
                      <Label>Data de Admissão</Label>
                      <Input 
                        type="date" 
                        value={formData.admission_date} 
                        onChange={(e) => setFormData({...formData, admission_date: e.target.value})} 
                        data-testid="employee-admission-date-input"
                      />
                    </div>
                    <div>
                      <Label>Tipo de Contrato</Label>
                      <Select value={formData.contract_type || '_none'} onValueChange={(v) => setFormData({...formData, contract_type: v === '_none' ? '' : v})}>
                        <SelectTrigger data-testid="employee-contract-type-select"><SelectValue placeholder="Selecione" /></SelectTrigger>
                        <SelectContent>
                          <SelectItem value="_none">Selecione</SelectItem>
                          {CONTRACT_TYPES.map(ct => <SelectItem key={ct.value} value={ct.value}>{ct.label}</SelectItem>)}
                        </SelectContent>
                      </Select>
                    </div>
                    <div>
                      <Label>Tipo de Salário</Label>
                      <Select value={formData.salary_type} onValueChange={(v) => setFormData({...formData, salary_type: v})}>
                        <SelectTrigger data-testid="employee-salary-type-select"><SelectValue /></SelectTrigger>
                        <SelectContent>
                          {SALARY_TYPES.map(st => <SelectItem key={st.value} value={st.value}>{st.label}</SelectItem>)}
                        </SelectContent>
                      </Select>
                    </div>
                    <div>
                      <Label>Salário (R$)</Label>
                      <Input 
                        type="number" 
                        step="0.01" 
                        value={formData.salary} 
                        onChange={(e) => setFormData({...formData, salary: e.target.value})} 
                        placeholder="0,00" 
                        data-testid="employee-salary-input"
                      />
                    </div>
                    <div>
                      <Label>Cargo</Label>
                      <div className="flex gap-2">
                        <Select value={formData.position_id || '_none'} onValueChange={(v) => setFormData({...formData, position_id: v === '_none' ? '' : v})}>
                          <SelectTrigger className="flex-1" data-testid="employee-position-select"><SelectValue placeholder="Selecione" /></SelectTrigger>
                          <SelectContent>
                            <SelectItem value="_none">Nenhum</SelectItem>
                            {positions.map(p => <SelectItem key={p.id} value={p.id}>{p.name}</SelectItem>)}
                          </SelectContent>
                        </Select>
                        <Button type="button" variant="outline" size="icon" onClick={() => setIsPositionDialogOpen(true)} title="Adicionar cargo" data-testid="add-position-btn">
                          <Plus className="w-4 h-4" />
                        </Button>
                      </div>
                    </div>
                    <div>
                      <Label>Departamento</Label>
                      <div className="flex gap-2">
                        <Select value={formData.department_id || '_none'} onValueChange={(v) => setFormData({...formData, department_id: v === '_none' ? '' : v})}>
                          <SelectTrigger className="flex-1" data-testid="employee-department-select"><SelectValue placeholder="Selecione" /></SelectTrigger>
                          <SelectContent>
                            <SelectItem value="_none">Nenhum</SelectItem>
                            {departments.map(d => <SelectItem key={d.id} value={d.id}>{d.name}</SelectItem>)}
                          </SelectContent>
                        </Select>
                        <Button type="button" variant="outline" size="icon" onClick={() => setIsDepartmentDialogOpen(true)} title="Adicionar departamento" data-testid="add-department-btn">
                          <Plus className="w-4 h-4" />
                        </Button>
                      </div>
                    </div>
                    <div className="md:col-span-2">
                      <Label>Gestor</Label>
                      <Select value={formData.manager_id || '_none'} onValueChange={(v) => setFormData({...formData, manager_id: v === '_none' ? '' : v})}>
                        <SelectTrigger data-testid="employee-manager-select"><SelectValue placeholder="Selecione o gestor" /></SelectTrigger>
                        <SelectContent>
                          <SelectItem value="_none">Nenhum</SelectItem>
                          {getManagerOptions().map(emp => <SelectItem key={emp.id} value={emp.id}>{emp.name}</SelectItem>)}
                        </SelectContent>
                      </Select>
                    </div>
                  </div>
                </AccordionContent>
              </AccordionItem>

              {/* Dados Pessoais */}
              <AccordionItem value="personal">
                <AccordionTrigger className="hover:no-underline">
                  <div className="flex items-center gap-2">
                    <User className="w-4 h-4 text-primary" />
                    <span>Dados Pessoais</span>
                  </div>
                </AccordionTrigger>
                <AccordionContent>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4 pt-2">
                    <div>
                      <Label>Gênero</Label>
                      <Select value={formData.gender || '_none'} onValueChange={(v) => setFormData({...formData, gender: v === '_none' ? '' : v})}>
                        <SelectTrigger><SelectValue placeholder="Selecione" /></SelectTrigger>
                        <SelectContent>
                          <SelectItem value="_none">Selecione</SelectItem>
                          {GENDER_OPTIONS.map(g => <SelectItem key={g.value} value={g.value}>{g.label}</SelectItem>)}
                        </SelectContent>
                      </Select>
                    </div>
                    <div>
                      <Label>Estado Civil</Label>
                      <Select value={formData.marital_status || '_none'} onValueChange={(v) => setFormData({...formData, marital_status: v === '_none' ? '' : v})}>
                        <SelectTrigger><SelectValue placeholder="Selecione" /></SelectTrigger>
                        <SelectContent>
                          <SelectItem value="_none">Selecione</SelectItem>
                          {MARITAL_STATUS_OPTIONS.map(ms => <SelectItem key={ms.value} value={ms.value}>{ms.label}</SelectItem>)}
                        </SelectContent>
                      </Select>
                    </div>
                    <div className="md:col-span-2">
                      <Label>Nome da Mãe</Label>
                      <Input 
                        value={formData.mother_name} 
                        onChange={(e) => setFormData({...formData, mother_name: e.target.value})} 
                        placeholder="Nome completo da mãe" 
                      />
                    </div>
                    <div>
                      <Label>Naturalidade (Cidade)</Label>
                      <Input 
                        value={formData.birthplace} 
                        onChange={(e) => setFormData({...formData, birthplace: e.target.value})} 
                        placeholder="Cidade de nascimento" 
                      />
                    </div>
                    <div>
                      <Label>UF de Nascimento</Label>
                      <Select value={formData.birthplace_uf || '_none'} onValueChange={(v) => setFormData({...formData, birthplace_uf: v === '_none' ? '' : v})}>
                        <SelectTrigger><SelectValue placeholder="Selecione" /></SelectTrigger>
                        <SelectContent>
                          <SelectItem value="_none">Selecione</SelectItem>
                          {UF_OPTIONS.map(uf => <SelectItem key={uf} value={uf}>{uf}</SelectItem>)}
                        </SelectContent>
                      </Select>
                    </div>
                  </div>
                </AccordionContent>
              </AccordionItem>

              {/* Contato */}
              <AccordionItem value="contact">
                <AccordionTrigger className="hover:no-underline">
                  <div className="flex items-center gap-2">
                    <Phone className="w-4 h-4 text-primary" />
                    <span>Contato</span>
                  </div>
                </AccordionTrigger>
                <AccordionContent>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4 pt-2">
                    <div>
                      <Label>E-mail</Label>
                      <Input 
                        type="email" 
                        value={formData.email} 
                        onChange={(e) => setFormData({...formData, email: e.target.value})} 
                        placeholder="email@exemplo.com" 
                      />
                    </div>
                    <div>
                      <Label>Telefone</Label>
                      <Input 
                        value={formData.phone} 
                        onChange={(e) => setFormData({...formData, phone: formatPhoneNumber(e.target.value)})} 
                        placeholder="(00) 00000-0000" 
                      />
                    </div>
                    <div className="md:col-span-2">
                      <Label>Endereço</Label>
                      <Input 
                        value={formData.address} 
                        onChange={(e) => setFormData({...formData, address: e.target.value})} 
                        placeholder="Endereço completo" 
                      />
                    </div>
                  </div>
                </AccordionContent>
              </AccordionItem>

              {/* Documentos Trabalhistas */}
              <AccordionItem value="documents">
                <AccordionTrigger className="hover:no-underline">
                  <div className="flex items-center gap-2">
                    <FileCheck className="w-4 h-4 text-primary" />
                    <span>Documentos Trabalhistas</span>
                  </div>
                </AccordionTrigger>
                <AccordionContent>
                  <div className="space-y-4 pt-2">
                    {/* RG */}
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                      <div>
                        <Label>RG</Label>
                        <Input 
                          value={formData.rg} 
                          onChange={(e) => setFormData({...formData, rg: e.target.value})} 
                          placeholder="Número do RG" 
                        />
                      </div>
                      <div>
                        <Label>Órgão Expedidor</Label>
                        <Input 
                          value={formData.rg_issuer} 
                          onChange={(e) => setFormData({...formData, rg_issuer: e.target.value})} 
                          placeholder="SSP" 
                        />
                      </div>
                      <div>
                        <Label>UF do RG</Label>
                        <Select value={formData.rg_uf || '_none'} onValueChange={(v) => setFormData({...formData, rg_uf: v === '_none' ? '' : v})}>
                          <SelectTrigger><SelectValue placeholder="UF" /></SelectTrigger>
                          <SelectContent>
                            <SelectItem value="_none">Selecione</SelectItem>
                            {UF_OPTIONS.map(uf => <SelectItem key={uf} value={uf}>{uf}</SelectItem>)}
                          </SelectContent>
                        </Select>
                      </div>
                    </div>

                    {/* CTPS */}
                    <Separator className="my-2" />
                    <p className="text-sm font-medium text-muted-foreground">CTPS (Carteira de Trabalho)</p>
                    <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                      <div>
                        <Label>Número</Label>
                        <Input 
                          value={formData.ctps_number} 
                          onChange={(e) => setFormData({...formData, ctps_number: e.target.value})} 
                          placeholder="Número" 
                        />
                      </div>
                      <div>
                        <Label>Série</Label>
                        <Input 
                          value={formData.ctps_series} 
                          onChange={(e) => setFormData({...formData, ctps_series: e.target.value})} 
                          placeholder="Série" 
                        />
                      </div>
                      <div>
                        <Label>UF</Label>
                        <Select value={formData.ctps_uf || '_none'} onValueChange={(v) => setFormData({...formData, ctps_uf: v === '_none' ? '' : v})}>
                          <SelectTrigger><SelectValue placeholder="UF" /></SelectTrigger>
                          <SelectContent>
                            <SelectItem value="_none">Selecione</SelectItem>
                            {UF_OPTIONS.map(uf => <SelectItem key={uf} value={uf}>{uf}</SelectItem>)}
                          </SelectContent>
                        </Select>
                      </div>
                      <div>
                        <Label>Data Emissão</Label>
                        <Input 
                          type="date" 
                          value={formData.ctps_issue_date} 
                          onChange={(e) => setFormData({...formData, ctps_issue_date: e.target.value})} 
                        />
                      </div>
                    </div>

                    {/* PIS e PIX */}
                    <Separator className="my-2" />
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <div>
                        <Label>PIS/PASEP</Label>
                        <Input 
                          value={formData.pis} 
                          onChange={(e) => setFormData({...formData, pis: e.target.value})} 
                          placeholder="Número do PIS" 
                        />
                      </div>
                      <div>
                        <Label>Chave PIX (para pagamento)</Label>
                        <Input 
                          value={formData.pix_key} 
                          onChange={(e) => setFormData({...formData, pix_key: e.target.value})} 
                          placeholder="CPF, e-mail, telefone ou chave aleatória" 
                        />
                      </div>
                    </div>

                    {/* CNH */}
                    <Separator className="my-2" />
                    <p className="text-sm font-medium text-muted-foreground">CNH (Carteira de Motorista)</p>
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                      <div>
                        <Label>Número da CNH</Label>
                        <Input 
                          value={formData.cnh} 
                          onChange={(e) => setFormData({...formData, cnh: e.target.value})} 
                          placeholder="Número" 
                        />
                      </div>
                      <div>
                        <Label>Categoria</Label>
                        <Select value={formData.cnh_category || '_none'} onValueChange={(v) => setFormData({...formData, cnh_category: v === '_none' ? '' : v})}>
                          <SelectTrigger><SelectValue placeholder="Selecione" /></SelectTrigger>
                          <SelectContent>
                            <SelectItem value="_none">Selecione</SelectItem>
                            {CNH_CATEGORIES.map(cat => <SelectItem key={cat} value={cat}>{cat}</SelectItem>)}
                          </SelectContent>
                        </Select>
                      </div>
                      <div>
                        <Label>Validade</Label>
                        <Input 
                          type="date" 
                          value={formData.cnh_expiry} 
                          onChange={(e) => setFormData({...formData, cnh_expiry: e.target.value})} 
                        />
                      </div>
                    </div>
                  </div>
                </AccordionContent>
              </AccordionItem>

              {/* Contato de Emergência */}
              <AccordionItem value="emergency">
                <AccordionTrigger className="hover:no-underline">
                  <div className="flex items-center gap-2">
                    <AlertTriangle className="w-4 h-4 text-primary" />
                    <span>Contato de Emergência</span>
                  </div>
                </AccordionTrigger>
                <AccordionContent>
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-4 pt-2">
                    <div>
                      <Label>Nome</Label>
                      <Input 
                        value={formData.emergency_name} 
                        onChange={(e) => setFormData({...formData, emergency_name: e.target.value})} 
                        placeholder="Nome do contato" 
                      />
                    </div>
                    <div>
                      <Label>Parentesco</Label>
                      <Input 
                        value={formData.emergency_relation} 
                        onChange={(e) => setFormData({...formData, emergency_relation: e.target.value})} 
                        placeholder="Ex: Cônjuge, Pai, Mãe" 
                      />
                    </div>
                    <div>
                      <Label>Telefone</Label>
                      <Input 
                        value={formData.emergency_phone} 
                        onChange={(e) => setFormData({...formData, emergency_phone: formatPhoneNumber(e.target.value)})} 
                        placeholder="(00) 00000-0000" 
                      />
                    </div>
                  </div>
                </AccordionContent>
              </AccordionItem>

              {/* Status */}
              <AccordionItem value="status">
                <AccordionTrigger className="hover:no-underline">
                  <div className="flex items-center gap-2">
                    <CheckCircle className="w-4 h-4 text-primary" />
                    <span>Status</span>
                  </div>
                </AccordionTrigger>
                <AccordionContent>
                  <div className="flex items-center gap-3 pt-2">
                    <Switch 
                      checked={formData.is_active} 
                      onCheckedChange={(v) => setFormData({...formData, is_active: v})} 
                      data-testid="employee-active-switch"
                    />
                    <Label>{formData.is_active ? 'Funcionário Ativo' : 'Funcionário Inativo'}</Label>
                  </div>
                </AccordionContent>
              </AccordionItem>
            </Accordion>

            <DialogFooter className="pt-4">
              <Button type="button" variant="outline" onClick={() => setIsDialogOpen(false)}>Cancelar</Button>
              <Button type="submit" data-testid="save-employee-btn">Salvar</Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* Position Dialog */}
      <Dialog open={isPositionDialogOpen} onOpenChange={setIsPositionDialogOpen}>
        <DialogContent className="max-w-sm">
          <DialogHeader><DialogTitle>Novo Cargo</DialogTitle></DialogHeader>
          <div className="space-y-4">
            <div>
              <Label>Nome do Cargo</Label>
              <Input 
                value={newPositionName} 
                onChange={(e) => setNewPositionName(e.target.value)} 
                placeholder="Ex: Desenvolvedor, Analista, Gerente" 
                data-testid="new-position-name-input"
              />
            </div>
            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setIsPositionDialogOpen(false)}>Cancelar</Button>
              <Button onClick={handleCreatePosition} disabled={!newPositionName.trim()} data-testid="save-position-btn">Criar</Button>
            </DialogFooter>
          </div>
        </DialogContent>
      </Dialog>

      {/* Department Dialog */}
      <Dialog open={isDepartmentDialogOpen} onOpenChange={setIsDepartmentDialogOpen}>
        <DialogContent className="max-w-sm">
          <DialogHeader><DialogTitle>Novo Departamento</DialogTitle></DialogHeader>
          <div className="space-y-4">
            <div>
              <Label>Nome do Departamento</Label>
              <Input 
                value={newDepartmentName} 
                onChange={(e) => setNewDepartmentName(e.target.value)} 
                placeholder="Ex: TI, RH, Financeiro" 
                data-testid="new-department-name-input"
              />
            </div>
            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setIsDepartmentDialogOpen(false)}>Cancelar</Button>
              <Button onClick={handleCreateDepartment} disabled={!newDepartmentName.trim()} data-testid="save-department-btn">Criar</Button>
            </DialogFooter>
          </div>
        </DialogContent>
      </Dialog>

      {/* Delete Confirmation */}
      <AlertDialog open={deleteConfirmOpen} onOpenChange={setDeleteConfirmOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Confirmar Exclusão</AlertDialogTitle>
            <AlertDialogDescription>
              Tem certeza que deseja excluir o funcionário "{employeeToDelete?.name}"? Esta ação não pode ser desfeita.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancelar</AlertDialogCancel>
            <AlertDialogAction onClick={handleDelete} className="bg-red-500 hover:bg-red-600" data-testid="confirm-delete-btn">
              Excluir
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
};

// ==================== QUOTES PAGE (ORÇAMENTOS) ====================
const QUOTE_STATUSES = [
  { value: 'ABERTO', label: 'Em Aberto', color: 'bg-blue-500' },
  { value: 'ENVIADO', label: 'Enviado', color: 'bg-amber-500' },
  { value: 'NEGOCIANDO', label: 'Negociando', color: 'bg-purple-500' },
  { value: 'FECHADO', label: 'Fechado', color: 'bg-emerald-500' },
];

// ==================== CASH REGISTER PAGE (FRENTE DE CAIXA) ====================

const PAYMENT_METHODS = [
  { id: 'cash', label: 'Dinheiro', icon: Coins },
  { id: 'card', label: 'Cartão Crédito', icon: CreditCard },
  { id: 'debit', label: 'Cartão Débito', icon: CreditCard },
  { id: 'pix', label: 'PIX', icon: Receipt },
  { id: 'other', label: 'Outros', icon: DollarSign },
];

// ==================== SUB-USERS PAGE ====================

const SubUsersPage = ({ companies = [], user }) => {
  const { toast } = useToast();
  const [subUsers, setSubUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [editingUser, setEditingUser] = useState(null);
  const [deleteConfirmOpen, setDeleteConfirmOpen] = useState(false);
  const [userToDelete, setUserToDelete] = useState(null);
  
  // Garantir que companies é sempre um array
  const companiesList = Array.isArray(companies) ? companies : [];
  
  const emptyFormData = {
    name: '',
    email: '',
    phone: '+55 ',
    password: '',
    permissions: {
      can_manage_registers: false,
      can_manage_expenses: false,
      can_manage_revenues: false,
      can_manage_costs: false,
      can_manage_pos: false,
      can_manage_quotes: false,
      can_manage_receipts: false,
    },
    allowed_companies: []
  };
  
  const [formData, setFormData] = useState(emptyFormData);
  
  // Formatar telefone WhatsApp
  const formatWhatsAppPhone = (value) => {
    // Remove tudo que não é número ou +
    let cleaned = value.replace(/[^\d+]/g, '');
    
    // Garante que começa com +
    if (!cleaned.startsWith('+')) {
      cleaned = '+' + cleaned;
    }
    
    // Limita tamanho (DDI + DDD + 9 dígitos)
    if (cleaned.length > 14) {
      cleaned = cleaned.slice(0, 14);
    }
    
    // Formatar: +55 11 99999-9999
    if (cleaned.length <= 3) return cleaned;
    if (cleaned.length <= 5) return `${cleaned.slice(0, 3)} ${cleaned.slice(3)}`;
    if (cleaned.length <= 10) return `${cleaned.slice(0, 3)} ${cleaned.slice(3, 5)} ${cleaned.slice(5)}`;
    return `${cleaned.slice(0, 3)} ${cleaned.slice(3, 5)} ${cleaned.slice(5, 10)}-${cleaned.slice(10)}`;
  };
  
  useEffect(() => { loadSubUsers(); }, []);
  
  const loadSubUsers = async () => {
    try {
      setLoading(true);
      const res = await axios.get(`${API}/sub-users`);
      setSubUsers(res.data);
    } catch (error) {
      toast({ title: "Erro ao carregar usuários", variant: "destructive" });
    } finally {
      setLoading(false);
    }
  };
  
  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // Validações
    if (!formData.name.trim()) {
      toast({ title: "Nome é obrigatório", variant: "destructive" });
      return;
    }
    if (!formData.email.trim()) {
      toast({ title: "E-mail é obrigatório", variant: "destructive" });
      return;
    }
    if (!editingUser && formData.password.length < 6) {
      toast({ title: "A senha deve ter no mínimo 6 caracteres", variant: "destructive" });
      return;
    }
    if (formData.allowed_companies.length === 0) {
      toast({ title: "Selecione pelo menos uma empresa", variant: "destructive" });
      return;
    }
    
    try {
      if (editingUser) {
        // Atualizar - só envia senha se foi preenchida
        const updateData = { ...formData };
        if (!updateData.password) delete updateData.password;
        
        await axios.put(`${API}/sub-users/${editingUser.id}`, updateData);
        toast({ title: "Usuário atualizado!" });
      } else {
        // Criar
        await axios.post(`${API}/sub-users`, formData);
        toast({ title: "Usuário criado!" });
      }
      
      setIsDialogOpen(false);
      setEditingUser(null);
      setFormData(emptyFormData);
      loadSubUsers();
    } catch (error) {
      toast({ title: error.response?.data?.detail || "Erro ao salvar usuário", variant: "destructive" });
    }
  };
  
  const handleEdit = (subUser) => {
    setEditingUser(subUser);
    setFormData({
      name: subUser.name,
      email: subUser.email,
      phone: subUser.phone || '+55 ',
      password: '', // Não preenche senha na edição
      permissions: subUser.permissions || emptyFormData.permissions,
      allowed_companies: subUser.allowed_companies || []
    });
    setIsDialogOpen(true);
  };
  
  const handleDelete = async () => {
    if (!userToDelete) return;
    try {
      await axios.delete(`${API}/sub-users/${userToDelete.id}`);
      toast({ title: "Usuário removido!" });
      loadSubUsers();
    } catch (error) {
      toast({ title: "Erro ao remover usuário", variant: "destructive" });
    }
    setDeleteConfirmOpen(false);
    setUserToDelete(null);
  };
  
  const openNewDialog = () => {
    setEditingUser(null);
    setFormData(emptyFormData);
    setIsDialogOpen(true);
  };
  
  const togglePermission = (key) => {
    setFormData(prev => ({
      ...prev,
      permissions: {
        ...prev.permissions,
        [key]: !prev.permissions[key]
      }
    }));
  };
  
  const toggleCompany = (companyId) => {
    setFormData(prev => ({
      ...prev,
      allowed_companies: prev.allowed_companies.includes(companyId)
        ? prev.allowed_companies.filter(id => id !== companyId)
        : [...prev.allowed_companies, companyId]
    }));
  };
  
  const selectAllCompanies = () => {
    setFormData(prev => ({
      ...prev,
      allowed_companies: companiesList.map(c => c.id)
    }));
  };
  
  const deselectAllCompanies = () => {
    setFormData(prev => ({
      ...prev,
      allowed_companies: []
    }));
  };
  
  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-emerald-500"></div>
      </div>
    );
  }
  
  return (
    <div className="space-y-6" data-testid="sub-users-page">
      <div className="flex justify-between items-center">
        <div>
          <h2 className="text-2xl font-bold">Usuários</h2>
          <p className="text-muted-foreground">Gerencie os usuários que podem acessar sua conta</p>
        </div>
        <Button onClick={openNewDialog} data-testid="add-sub-user-btn">
          <Plus className="w-4 h-4 mr-2" /> Novo Usuário
        </Button>
      </div>
      
      {/* Lista de Sub-usuários */}
      <Card>
        <CardContent className="p-0">
          {subUsers.length === 0 ? (
            <div className="p-8 text-center text-muted-foreground">
              <UserPlus className="w-12 h-12 mx-auto mb-3 opacity-50" />
              <p>Nenhum usuário cadastrado</p>
              <p className="text-sm">Clique em "Novo Usuário" para adicionar</p>
            </div>
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Nome</TableHead>
                  <TableHead>E-mail</TableHead>
                  <TableHead>Telefone</TableHead>
                  <TableHead>Permissões</TableHead>
                  <TableHead>Empresas</TableHead>
                  <TableHead className="w-[80px]"></TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {subUsers.map(subUser => (
                  <TableRow key={subUser.id}>
                    <TableCell className="font-medium">{subUser.name}</TableCell>
                    <TableCell>{subUser.email}</TableCell>
                    <TableCell>{subUser.phone}</TableCell>
                    <TableCell>
                      <div className="flex flex-wrap gap-1">
                        {subUser.permissions?.can_manage_registers && <Badge variant="secondary" className="text-xs">Cadastros</Badge>}
                        {subUser.permissions?.can_manage_expenses && <Badge variant="secondary" className="text-xs">Despesas</Badge>}
                        {subUser.permissions?.can_manage_revenues && <Badge variant="secondary" className="text-xs">Receitas</Badge>}
                        {subUser.permissions?.can_manage_costs && <Badge variant="secondary" className="text-xs">Custos</Badge>}
                        {subUser.permissions?.can_manage_pos && <Badge variant="secondary" className="text-xs">PDV</Badge>}
                        {subUser.permissions?.can_manage_quotes && <Badge variant="secondary" className="text-xs">Orçamentos</Badge>}
                        {subUser.permissions?.can_manage_receipts && <Badge variant="secondary" className="text-xs">Recibos</Badge>}
                        {!Object.values(subUser.permissions || {}).some(v => v) && <Badge variant="outline" className="text-xs">Somente Leitura</Badge>}
                      </div>
                    </TableCell>
                    <TableCell>
                      <Badge variant="outline">{subUser.allowed_companies?.length || 0} empresa(s)</Badge>
                    </TableCell>
                    <TableCell>
                      <div className="flex gap-1">
                        <Button size="icon" variant="ghost" onClick={() => handleEdit(subUser)}>
                          <Pencil className="w-4 h-4" />
                        </Button>
                        <Button size="icon" variant="ghost" className="text-destructive" onClick={() => { setUserToDelete(subUser); setDeleteConfirmOpen(true); }}>
                          <Trash2 className="w-4 h-4" />
                        </Button>
                      </div>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>
      
      {/* Dialog Criar/Editar */}
      <Dialog open={isDialogOpen} onOpenChange={setIsDialogOpen}>
        <DialogContent className="max-w-2xl max-h-[85vh] overflow-auto">
          <DialogHeader>
            <DialogTitle>{editingUser ? 'Editar Usuário' : 'Novo Usuário'}</DialogTitle>
            <DialogDescription>
              {editingUser ? 'Altere os dados do usuário' : 'Preencha os dados para criar um novo usuário'}
            </DialogDescription>
          </DialogHeader>
          
          <form onSubmit={handleSubmit} className="space-y-6">
            {/* Dados Básicos */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <Label>Nome *</Label>
                <Input 
                  value={formData.name} 
                  onChange={(e) => setFormData(prev => ({ ...prev, name: e.target.value }))}
                  placeholder="Nome completo"
                  data-testid="sub-user-name-input"
                />
              </div>
              <div>
                <Label>E-mail *</Label>
                <Input 
                  type="email"
                  value={formData.email} 
                  onChange={(e) => setFormData(prev => ({ ...prev, email: e.target.value }))}
                  placeholder="email@exemplo.com"
                  disabled={!!editingUser}
                  data-testid="sub-user-email-input"
                />
                {editingUser && <p className="text-xs text-muted-foreground mt-1">O e-mail não pode ser alterado</p>}
              </div>
              <div>
                <Label>Telefone (WhatsApp)</Label>
                <Input 
                  value={formData.phone} 
                  onChange={(e) => setFormData(prev => ({ ...prev, phone: formatWhatsAppPhone(e.target.value) }))}
                  placeholder="+55 11 99999-9999"
                  data-testid="sub-user-phone-input"
                />
              </div>
              <div>
                <Label>{editingUser ? 'Nova Senha' : 'Senha *'}</Label>
                <Input 
                  type="password"
                  value={formData.password} 
                  onChange={(e) => setFormData(prev => ({ ...prev, password: e.target.value }))}
                  placeholder={editingUser ? "Deixe em branco para manter" : "Mínimo 6 caracteres"}
                  data-testid="sub-user-password-input"
                />
              </div>
            </div>
            
            <Separator />
            
            {/* Permissões */}
            <div>
              <Label className="text-base font-semibold mb-3 block">Permissões de Acesso</Label>
              
              {/* Cadastros */}
              <div className="mb-4 p-3 rounded-lg bg-muted/50">
                <div className="flex items-center gap-2 mb-2">
                  <Checkbox 
                    id="perm_registers"
                    checked={formData.permissions.can_manage_registers}
                    onCheckedChange={() => togglePermission('can_manage_registers')}
                  />
                  <Label htmlFor="perm_registers" className="font-medium cursor-pointer">
                    Cadastros
                  </Label>
                </div>
                <p className="text-xs text-muted-foreground pl-6">
                  Empresas, Clientes, Fornecedores, Produtos, Funcionários
                </p>
              </div>
              
              {/* Lançamentos */}
              <div className="mb-4 p-3 rounded-lg bg-muted/50">
                <p className="font-medium mb-2">Lançamentos</p>
                <div className="grid grid-cols-3 gap-3">
                  <div className="flex items-center gap-2">
                    <Checkbox 
                      id="perm_expenses"
                      checked={formData.permissions.can_manage_expenses}
                      onCheckedChange={() => togglePermission('can_manage_expenses')}
                    />
                    <Label htmlFor="perm_expenses" className="text-sm cursor-pointer">Despesas</Label>
                  </div>
                  <div className="flex items-center gap-2">
                    <Checkbox 
                      id="perm_revenues"
                      checked={formData.permissions.can_manage_revenues}
                      onCheckedChange={() => togglePermission('can_manage_revenues')}
                    />
                    <Label htmlFor="perm_revenues" className="text-sm cursor-pointer">Receitas</Label>
                  </div>
                  <div className="flex items-center gap-2">
                    <Checkbox 
                      id="perm_costs"
                      checked={formData.permissions.can_manage_costs}
                      onCheckedChange={() => togglePermission('can_manage_costs')}
                    />
                    <Label htmlFor="perm_costs" className="text-sm cursor-pointer">Custos</Label>
                  </div>
                </div>
              </div>
              
              {/* Vendas */}
              <div className="mb-4 p-3 rounded-lg bg-muted/50">
                <p className="font-medium mb-2">Vendas</p>
                <div className="grid grid-cols-3 gap-3">
                  <div className="flex items-center gap-2">
                    <Checkbox 
                      id="perm_pos"
                      checked={formData.permissions.can_manage_pos}
                      onCheckedChange={() => togglePermission('can_manage_pos')}
                    />
                    <Label htmlFor="perm_pos" className="text-sm cursor-pointer">Frente de Caixa</Label>
                  </div>
                  <div className="flex items-center gap-2">
                    <Checkbox 
                      id="perm_quotes"
                      checked={formData.permissions.can_manage_quotes}
                      onCheckedChange={() => togglePermission('can_manage_quotes')}
                    />
                    <Label htmlFor="perm_quotes" className="text-sm cursor-pointer">Orçamentos</Label>
                  </div>
                  <div className="flex items-center gap-2">
                    <Checkbox 
                      id="perm_receipts"
                      checked={formData.permissions.can_manage_receipts}
                      onCheckedChange={() => togglePermission('can_manage_receipts')}
                    />
                    <Label htmlFor="perm_receipts" className="text-sm cursor-pointer">Recibos</Label>
                  </div>
                </div>
              </div>
              
              {/* Info */}
              <div className="p-3 rounded-lg bg-emerald-500/10 border border-emerald-500/30">
                <p className="text-sm text-emerald-600 dark:text-emerald-400">
                  <CheckCircle className="w-4 h-4 inline mr-1" />
                  <strong>Sempre liberados:</strong> Relatórios, DRE e Suporte
                </p>
              </div>
            </div>
            
            <Separator />
            
            {/* Empresas Permitidas */}
            <div>
              <div className="flex items-center justify-between mb-3">
                <Label className="text-base font-semibold">Empresas Permitidas *</Label>
                <div className="flex gap-2">
                  <Button type="button" variant="outline" size="sm" onClick={selectAllCompanies}>
                    Selecionar Todas
                  </Button>
                  <Button type="button" variant="outline" size="sm" onClick={deselectAllCompanies}>
                    Limpar
                  </Button>
                </div>
              </div>
              
              <div className="grid grid-cols-1 md:grid-cols-2 gap-2 max-h-[200px] overflow-auto p-2 border rounded-lg">
                {companiesList.map(company => {
                  const isSelected = formData.allowed_companies.includes(company.id);
                  return (
                    <div 
                      key={company.id} 
                      className={`flex items-center gap-2 p-2 rounded-lg cursor-pointer transition-colors ${
                        isSelected 
                          ? 'bg-emerald-500/10 border border-emerald-500/50' 
                          : 'hover:bg-muted/50 border border-transparent'
                      }`}
                      onClick={() => toggleCompany(company.id)}
                    >
                      <div className={`w-4 h-4 rounded border flex items-center justify-center ${isSelected ? 'bg-emerald-500 border-emerald-500' : 'border-input'}`}>
                        {isSelected && <Check className="w-3 h-3 text-white" />}
                      </div>
                      <div className="flex-1 min-w-0">
                        <p className="font-medium text-sm truncate">{company.nome_fantasia || company.razao_social}</p>
                        <p className="text-xs text-muted-foreground truncate">{company.cnpj}</p>
                      </div>
                    </div>
                  );
                })}
              </div>
              {companiesList.length === 0 && (
                <p className="text-sm text-muted-foreground text-center py-4">
                  Nenhuma empresa cadastrada. Cadastre uma empresa primeiro.
                </p>
              )}
            </div>
            
            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setIsDialogOpen(false)}>
                Cancelar
              </Button>
              <Button type="submit" data-testid="save-sub-user-btn">
                {editingUser ? 'Salvar Alterações' : 'Criar Usuário'}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
      
      {/* Dialog Confirmar Exclusão */}
      <Dialog open={deleteConfirmOpen} onOpenChange={setDeleteConfirmOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Confirmar Exclusão</DialogTitle>
            <DialogDescription>
              Tem certeza que deseja remover o usuário <strong>{userToDelete?.name}</strong>?
              Esta ação não pode ser desfeita.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDeleteConfirmOpen(false)}>Cancelar</Button>
            <Button variant="destructive" onClick={handleDelete}>Excluir</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

const CashRegisterPage = ({ companyId, selectedCompany, user }) => {
  const { toast } = useToast();
  const [loading, setLoading] = useState(true);
  const [currentRegister, setCurrentRegister] = useState(null);
  const [registers, setRegisters] = useState([]);
  const [products, setProducts] = useState([]);
  const [employees, setEmployees] = useState([]);
  const [revenueAccounts, setRevenueAccounts] = useState([]);
  
  // Estados do carrinho
  const [cartItems, setCartItems] = useState([]);
  const [cartTotal, setCartTotal] = useState(0);
  
  // Estados dos modais
  const [showOpenModal, setShowOpenModal] = useState(false);
  const [showCloseModal, setShowCloseModal] = useState(false);
  const [showSangriaModal, setShowSangriaModal] = useState(false);
  const [showFinalizeModal, setShowFinalizeModal] = useState(false);
  const [showConsolidateModal, setShowConsolidateModal] = useState(false);
  const [showMenuDrawer, setShowMenuDrawer] = useState(false);
  const [showDashboard, setShowDashboard] = useState(false);
  const [showReportsModal, setShowReportsModal] = useState(false);
  const [showSaleDetailModal, setShowSaleDetailModal] = useState(false);
  const [showGoalsModal, setShowGoalsModal] = useState(false);
  const [consolidateSummary, setConsolidateSummary] = useState(null);
  
  // Estados de metas
  const [dailyGoal, setDailyGoal] = useState(() => {
    const saved = localStorage.getItem(`pos_daily_goal_${companyId}`);
    return saved ? parseFloat(saved) : 1000;
  });
  const [weeklyGoal, setWeeklyGoal] = useState(() => {
    const saved = localStorage.getItem(`pos_weekly_goal_${companyId}`);
    return saved ? parseFloat(saved) : 5000;
  });
  const [tempDailyGoal, setTempDailyGoal] = useState('');
  const [tempWeeklyGoal, setTempWeeklyGoal] = useState('');
  const [weekSalesHistory, setWeekSalesHistory] = useState([]);
  
  // Estados de relatórios
  const [reportTab, setReportTab] = useState('daily'); // daily, monthly
  const [salesByRegister, setSalesByRegister] = useState({});
  const [sangriasByRegister, setSangriasByRegister] = useState({});
  const [selectedSale, setSelectedSale] = useState(null);
  const [editingSaleItems, setEditingSaleItems] = useState([]);
  const [reportYear, setReportYear] = useState(new Date().getFullYear());
  const [reportMonths, setReportMonths] = useState([]);
  const [monthlyReport, setMonthlyReport] = useState(null);
  const [loadingReport, setLoadingReport] = useState(false);
  
  // Dados dos modais
  const [openingBalance, setOpeningBalance] = useState('');
  const [selectedEmployee, setSelectedEmployee] = useState('');
  const [sangriaAmount, setSangriaAmount] = useState('');
  const [sangriaBy, setSangriaBy] = useState('');
  const [selectedAccount, setSelectedAccount] = useState('');
  const [payments, setPayments] = useState([{ method: 'cash', amount: 0 }]);
  const [printReceipt, setPrintReceipt] = useState(false);
  
  // Busca de produtos
  const [productSearch, setProductSearch] = useState('');

  // Carregar metas salvas quando companyId mudar
  useEffect(() => {
    if (companyId) {
      const savedDaily = localStorage.getItem(`pos_daily_goal_${companyId}`);
      const savedWeekly = localStorage.getItem(`pos_weekly_goal_${companyId}`);
      if (savedDaily) setDailyGoal(parseFloat(savedDaily));
      if (savedWeekly) setWeeklyGoal(parseFloat(savedWeekly));
    }
  }, [companyId]);

  useEffect(() => {
    if (companyId) {
      loadData();
    }
  }, [companyId]);

  useEffect(() => {
    // Calcular total do carrinho
    const total = cartItems.reduce((sum, item) => sum + item.total, 0);
    setCartTotal(total);
  }, [cartItems]);

  const loadData = async () => {
    setLoading(true);
    try {
      const [registersRes, productsRes, employeesRes, accountsRes] = await Promise.all([
        axios.get(`${API}/cash-registers?company_id=${companyId}`),
        axios.get(`${API}/products?company_id=${companyId}`),
        axios.get(`${API}/employees?company_id=${companyId}`),
        axios.get(`${API}/chart-accounts/analytical?account_type=RECEITA`)
      ]);
      
      setRegisters(registersRes.data);
      setProducts(productsRes.data);
      setEmployees(employeesRes.data.filter(e => e.is_active));
      
      // Filtrar contas de Vendas (4.1.1.01.*)
      const vendasAccounts = accountsRes.data.filter(acc => 
        acc.code.startsWith('4.1.1.01') || acc.name.toLowerCase().includes('venda')
      );
      setRevenueAccounts(vendasAccounts);
      
      // Verificar se há caixa aberto
      const openRegister = registersRes.data.find(r => r.status === 'open');
      setCurrentRegister(openRegister || null);
      
      // Se não há caixa aberto, mostrar modal de abertura
      if (!openRegister) {
        setShowOpenModal(true);
      }
    } catch (error) {
      toast({ title: "Erro ao carregar dados", variant: "destructive" });
    }
    setLoading(false);
  };

  const handleOpenRegister = async () => {
    if (!selectedEmployee) {
      toast({ title: "Selecione um funcionário", variant: "destructive" });
      return;
    }
    try {
      const res = await axios.post(`${API}/cash-registers/open`, {
        company_id: companyId,
        employee_id: selectedEmployee,
        opening_balance: parseFloat(openingBalance) || 0
      });
      setCurrentRegister(res.data.register);
      setShowOpenModal(false);
      setOpeningBalance('');
      setSelectedEmployee('');
      toast({ title: "Caixa aberto!", description: res.data.register.name });
      loadData();
    } catch (error) {
      toast({ title: error.response?.data?.detail || "Erro ao abrir caixa", variant: "destructive" });
    }
  };

  const handleCloseRegister = async () => {
    if (!currentRegister) return;
    try {
      const res = await axios.post(`${API}/cash-registers/${currentRegister.id}/close`, {});
      setShowCloseModal(false);
      toast({ title: "Caixa fechado!", description: `${currentRegister.name} - Total: R$ ${currentRegister.total_sales.toFixed(2)}` });
      setCurrentRegister(null);
      loadData();
    } catch (error) {
      toast({ title: error.response?.data?.detail || "Erro ao fechar caixa", variant: "destructive" });
    }
  };

  const handleSangria = async () => {
    if (!sangriaAmount || !sangriaBy) {
      toast({ title: "Preencha todos os campos", variant: "destructive" });
      return;
    }
    try {
      await axios.post(`${API}/cash-registers/sangria`, {
        cash_register_id: currentRegister.id,
        amount: parseFloat(sangriaAmount),
        withdrawn_by: sangriaBy
      });
      setShowSangriaModal(false);
      setSangriaAmount('');
      setSangriaBy('');
      toast({ title: "Sangria realizada!" });
      loadData();
    } catch (error) {
      toast({ title: error.response?.data?.detail || "Erro na sangria", variant: "destructive" });
    }
  };

  const addProductToCart = (product) => {
    const existingIndex = cartItems.findIndex(item => item.product_id === product.id);
    if (existingIndex >= 0) {
      // Incrementar quantidade
      const updated = [...cartItems];
      updated[existingIndex].quantity += 1;
      updated[existingIndex].total = updated[existingIndex].quantity * updated[existingIndex].unit_price;
      setCartItems(updated);
    } else {
      // Adicionar novo
      setCartItems([...cartItems, {
        product_id: product.id,
        product_name: product.name,
        product_code: product.code || '',
        quantity: 1,
        unit_price: product.price || 0,
        cost_price: product.cost_price || 0,
        total: product.price || 0
      }]);
    }
    setProductSearch('');
  };

  const updateCartItemQuantity = (index, newQty) => {
    if (newQty <= 0) {
      removeCartItem(index);
      return;
    }
    const updated = [...cartItems];
    updated[index].quantity = newQty;
    updated[index].total = newQty * updated[index].unit_price;
    setCartItems(updated);
  };

  const removeCartItem = (index) => {
    setCartItems(cartItems.filter((_, i) => i !== index));
  };

  const handleFinalize = async () => {
    if (!selectedAccount) {
      toast({ title: "Selecione a conta de receita", variant: "destructive" });
      return;
    }
    
    const totalPayments = payments.reduce((sum, p) => sum + (parseFloat(p.amount) || 0), 0);
    if (Math.abs(totalPayments - cartTotal) > 0.01) {
      toast({ title: "Total dos pagamentos não confere", description: `Esperado: R$ ${cartTotal.toFixed(2)}, Informado: R$ ${totalPayments.toFixed(2)}`, variant: "destructive" });
      return;
    }

    try {
      const res = await axios.post(`${API}/cash-registers/sale`, {
        cash_register_id: currentRegister.id,
        items: cartItems,
        payments: payments.filter(p => p.amount > 0),
        account_id: selectedAccount,
        total: cartTotal
      });
      
      if (printReceipt && res.data?.sale?.id) {
        printCupom(res.data.sale.id);
      }
      
      // Limpar carrinho
      setCartItems([]);
      setPayments([{ method: 'cash', amount: 0 }]);
      setSelectedAccount('');
      setPrintReceipt(false);
      setShowFinalizeModal(false);
      toast({ title: "Venda finalizada!" });
      loadData();
    } catch (error) {
      toast({ title: error.response?.data?.detail || "Erro ao finalizar venda", variant: "destructive" });
    }
  };

  const handleConsolidate = async () => {
    try {
      const res = await axios.post(`${API}/cash-registers/consolidate?company_id=${companyId}`);
      setConsolidateSummary(res.data.summary);
      toast({ title: "Fechamento geral realizado!", description: `${res.data.consolidated_count} vendas consolidadas` });
      loadData();
    } catch (error) {
      toast({ title: error.response?.data?.detail || "Erro no fechamento geral", variant: "destructive" });
    }
  };

  // Carregar dados do relatório diário
  const loadDailyReport = async () => {
    setLoadingReport(true);
    try {
      // Buscar vendas e sangrias de cada caixa
      const salesData = {};
      const sangriasData = {};
      
      for (const reg of registers) {
        const [salesRes, sangriasRes] = await Promise.all([
          axios.get(`${API}/cash-registers/${reg.id}/sales`),
          axios.get(`${API}/cash-registers/${reg.id}/sangrias`)
        ]);
        salesData[reg.id] = salesRes.data;
        sangriasData[reg.id] = sangriasRes.data;
      }
      
      setSalesByRegister(salesData);
      setSangriasByRegister(sangriasData);
    } catch (error) {
      toast({ title: "Erro ao carregar relatório", variant: "destructive" });
    }
    setLoadingReport(false);
  };

  // Carregar relatório mensal/anual
  const loadMonthlyReport = async () => {
    setLoadingReport(true);
    try {
      const monthsParam = reportMonths.length > 0 ? reportMonths.join(',') : '';
      const res = await axios.get(`${API}/cash-registers/report/products?company_id=${companyId}&year=${reportYear}&months=${monthsParam}`);
      setMonthlyReport(res.data);
    } catch (error) {
      toast({ title: "Erro ao carregar relatório mensal", variant: "destructive" });
    }
    setLoadingReport(false);
  };

  // Salvar metas
  const saveGoals = () => {
    const newDaily = parseFloat(tempDailyGoal) || dailyGoal;
    const newWeekly = parseFloat(tempWeeklyGoal) || weeklyGoal;
    
    setDailyGoal(newDaily);
    setWeeklyGoal(newWeekly);
    
    localStorage.setItem(`pos_daily_goal_${companyId}`, newDaily.toString());
    localStorage.setItem(`pos_weekly_goal_${companyId}`, newWeekly.toString());
    
    setShowGoalsModal(false);
    toast({ title: "Metas atualizadas!", description: `Diária: R$ ${newDaily.toFixed(2)} | Semanal: R$ ${newWeekly.toFixed(2)}` });
  };

  // Carregar histórico de vendas da semana
  const loadWeekSalesHistory = async () => {
    try {
      const res = await axios.get(`${API}/cash-registers/report/week?company_id=${companyId}`);
      setWeekSalesHistory(res.data.days || []);
    } catch (error) {
      // Se endpoint não existir, usar dados vazios
      setWeekSalesHistory([]);
    }
  };

  // Calcular totais do dia
  const todayTotal = registers.reduce((sum, r) => sum + (r.total_sales || 0), 0);
  const dailyProgress = dailyGoal > 0 ? Math.min((todayTotal / dailyGoal) * 100, 100) : 0;
  const dailyRemaining = Math.max(dailyGoal - todayTotal, 0);
  
  // Calcular totais da semana (incluindo hoje)
  const weekTotal = weekSalesHistory.reduce((sum, day) => sum + (day.total || 0), 0) + todayTotal;
  const weeklyProgress = weeklyGoal > 0 ? Math.min((weekTotal / weeklyGoal) * 100, 100) : 0;
  const weeklyRemaining = Math.max(weeklyGoal - weekTotal, 0);

  // Abrir relatórios
  const openReports = async () => {
    setShowReportsModal(true);
    await loadDailyReport();
  };

  // Abrir dashboard com histórico
  const openDashboard = async () => {
    setShowDashboard(true);
    await loadWeekSalesHistory();
  };

  // Abrir detalhe da venda
  const openSaleDetail = (sale) => {
    setSelectedSale(sale);
    setEditingSaleItems([...sale.items]);
    setShowSaleDetailModal(true);
  };

  // Atualizar item da venda
  const updateSaleItem = (index, field, value) => {
    const updated = [...editingSaleItems];
    if (field === 'quantity') {
      updated[index].quantity = parseInt(value) || 1;
      updated[index].total = updated[index].quantity * updated[index].unit_price;
    }
    setEditingSaleItems(updated);
  };

  // Remover item da venda
  const removeSaleItem = (index) => {
    setEditingSaleItems(editingSaleItems.filter((_, i) => i !== index));
  };

  // Adicionar produto à venda existente
  const addProductToSale = (product) => {
    const existingIndex = editingSaleItems.findIndex(item => item.product_id === product.id);
    if (existingIndex >= 0) {
      const updated = [...editingSaleItems];
      updated[existingIndex].quantity += 1;
      updated[existingIndex].total = updated[existingIndex].quantity * updated[existingIndex].unit_price;
      setEditingSaleItems(updated);
    } else {
      setEditingSaleItems([...editingSaleItems, {
        product_id: product.id,
        product_name: product.name,
        product_code: product.code || '',
        quantity: 1,
        unit_price: product.price || 0,
        cost_price: product.cost_price || 0,
        total: product.price || 0
      }]);
    }
  };

  // Salvar alterações da venda
  const saveSaleChanges = async () => {
    if (!selectedSale) return;
    try {
      const newTotal = editingSaleItems.reduce((sum, item) => sum + item.total, 0);
      await axios.put(`${API}/cash-registers/sale/${selectedSale.id}`, {
        items: editingSaleItems,
        total: newTotal
      });
      toast({ title: "Venda atualizada!" });
      setShowSaleDetailModal(false);
      loadDailyReport();
      loadData();
    } catch (error) {
      toast({ title: error.response?.data?.detail || "Erro ao atualizar venda", variant: "destructive" });
    }
  };

  // Reimprimir cupom de uma venda específica
  const reprintSaleCupom = (sale) => {
    // Montar endereço completo
    const enderecoCompleto = [
      selectedCompany?.endereco,
      selectedCompany?.numero ? `Nº ${selectedCompany.numero}` : '',
      selectedCompany?.bairro
    ].filter(Boolean).join(', ');
    
    const cupomContent = `
      <html>
      <head>
        <style>
          body { font-family: 'Courier New', monospace; font-size: 12px; width: 80mm; margin: 0 auto; padding: 10px; }
          .center { text-align: center; }
          .divider { border-top: 1px dashed #000; margin: 5px 0; }
          .bold { font-weight: bold; }
          .item { display: flex; justify-content: space-between; margin: 2px 0; }
          .total { font-size: 14px; font-weight: bold; }
        </style>
      </head>
      <body>
        <div class="center bold">${selectedCompany?.nome_fantasia || selectedCompany?.razao_social || 'EMPRESA'}</div>
        <div class="center">${selectedCompany?.cnpj || ''}</div>
        <div class="center">${enderecoCompleto || ''}</div>
        <div class="divider"></div>
        <div class="center bold">CUPOM DE VENDA Nº ${sale.id.slice(-6).toUpperCase()}</div>
        <div class="center">${new Date(sale.created_at).toLocaleString('pt-BR')}</div>
        <div class="divider"></div>
        ${sale.items.map(item => `
          <div>${item.product_name}</div>
          <div class="item">
            <span>${item.quantity} x R$ ${item.unit_price?.toFixed(2)}</span>
            <span>R$ ${item.total?.toFixed(2)}</span>
          </div>
        `).join('')}
        <div class="divider"></div>
        <div class="item total">
          <span>TOTAL</span>
          <span>R$ ${sale.total?.toFixed(2)}</span>
        </div>
        <div class="divider"></div>
        <div class="bold">PAGAMENTO:</div>
        ${sale.payments?.map(p => `
          <div class="item">
            <span>${PAYMENT_METHODS.find(m => m.id === p.method)?.label || p.method}</span>
            <span>R$ ${parseFloat(p.amount).toFixed(2)}</span>
          </div>
        `).join('')}
        <div class="divider"></div>
        <div class="center">Obrigado pela preferência!</div>
        <div class="center">${sale.cash_register_name} - ${sale.employee_name}</div>
        <div class="center" style="font-size:10px;margin-top:5px;">2ª VIA</div>
      </body>
      </html>
    `;
    
    const printWindow = window.open('', '_blank', 'width=300,height=600');
    printWindow.document.write(cupomContent);
    printWindow.document.close();
    printWindow.print();
  };

  // Gerar PDF do relatório diário
  const generateDailyPDF = () => {
    const totalVendas = registers.reduce((sum, r) => sum + (r.total_sales || 0), 0);
    const totalSangrias = registers.reduce((sum, r) => sum + (r.total_sangria || 0), 0);
    
    const pdfContent = `
      <html>
      <head>
        <style>
          body { font-family: Arial, sans-serif; font-size: 12px; padding: 20px; }
          .header { text-align: center; margin-bottom: 20px; }
          .header h1 { margin: 0; font-size: 18px; }
          .header p { margin: 5px 0; color: #666; }
          table { width: 100%; border-collapse: collapse; margin: 10px 0; }
          th, td { border: 1px solid #ddd; padding: 8px; text-align: left; }
          th { background: #f5f5f5; }
          .total { font-weight: bold; background: #e8f5e9; }
          .section { margin: 20px 0; }
          .section-title { font-weight: bold; font-size: 14px; border-bottom: 2px solid #333; padding-bottom: 5px; }
          .summary-box { background: #f9f9f9; padding: 15px; border-radius: 5px; margin: 10px 0; }
          .text-right { text-align: right; }
          .text-center { text-align: center; }
        </style>
      </head>
      <body>
        <div class="header">
          <h1>${selectedCompany?.razao_social || 'EMPRESA'}</h1>
          <p>${selectedCompany?.cnpj || ''}</p>
          <p>RELATÓRIO DE VENDAS DO DIA</p>
          <p>${new Date().toLocaleDateString('pt-BR')}</p>
        </div>
        
        <div class="summary-box">
          <table>
            <tr><td><strong>Total de Vendas:</strong></td><td class="text-right"><strong>R$ ${totalVendas.toFixed(2)}</strong></td></tr>
            <tr><td>Dinheiro:</td><td class="text-right">R$ ${registers.reduce((sum, r) => sum + (r.total_cash || 0), 0).toFixed(2)}</td></tr>
            <tr><td>Cartões:</td><td class="text-right">R$ ${registers.reduce((sum, r) => sum + (r.total_card || 0) + (r.total_debit || 0), 0).toFixed(2)}</td></tr>
            <tr><td>PIX:</td><td class="text-right">R$ ${registers.reduce((sum, r) => sum + (r.total_pix || 0), 0).toFixed(2)}</td></tr>
            <tr><td>Sangrias:</td><td class="text-right text-red">R$ ${totalSangrias.toFixed(2)}</td></tr>
          </table>
        </div>
        
        ${registers.map(reg => {
          const regSales = salesByRegister[reg.id] || [];
          const regSangrias = sangriasByRegister[reg.id] || [];
          return `
            <div class="section">
              <div class="section-title">${reg.name} - ${reg.employee_name} (${reg.status === 'open' ? 'Aberto' : 'Fechado'})</div>
              <table>
                <tr><td>Abertura:</td><td>${new Date(reg.opened_at).toLocaleString('pt-BR')}</td></tr>
                <tr><td>Saldo Inicial:</td><td>R$ ${reg.opening_balance?.toFixed(2)}</td></tr>
                <tr><td>Total Vendas:</td><td><strong>R$ ${reg.total_sales?.toFixed(2)}</strong></td></tr>
                <tr><td>Sangrias:</td><td>R$ ${reg.total_sangria?.toFixed(2)}</td></tr>
                <tr><td>Saldo Final (Dinheiro):</td><td>R$ ${reg.current_balance?.toFixed(2)}</td></tr>
              </table>
              
              ${regSales.length > 0 ? `
                <p><strong>Vendas (${regSales.length}):</strong></p>
                <table>
                  <thead><tr><th>Nº</th><th>Hora</th><th>Itens</th><th class="text-right">Total</th></tr></thead>
                  <tbody>
                    ${regSales.map((sale, idx) => `
                      <tr>
                        <td>${(idx + 1).toString().padStart(3, '0')}</td>
                        <td>${new Date(sale.created_at).toLocaleTimeString('pt-BR')}</td>
                        <td>${sale.items?.length || 0} item(s)</td>
                        <td class="text-right">R$ ${sale.total?.toFixed(2)}</td>
                      </tr>
                    `).join('')}
                  </tbody>
                </table>
              ` : '<p><em>Nenhuma venda registrada</em></p>'}
              
              ${regSangrias.length > 0 ? `
                <p><strong>Sangrias (${regSangrias.length}):</strong></p>
                <table>
                  <thead><tr><th>Hora</th><th>Retirado por</th><th class="text-right">Valor</th></tr></thead>
                  <tbody>
                    ${regSangrias.map(s => `
                      <tr>
                        <td>${new Date(s.created_at).toLocaleTimeString('pt-BR')}</td>
                        <td>${s.withdrawn_by}</td>
                        <td class="text-right">R$ ${s.amount?.toFixed(2)}</td>
                      </tr>
                    `).join('')}
                  </tbody>
                </table>
              ` : ''}
            </div>
          `;
        }).join('')}
        
        <div class="section">
          <p class="text-center" style="margin-top:30px;color:#999;">
            Relatório gerado em ${new Date().toLocaleString('pt-BR')}
          </p>
        </div>
      </body>
      </html>
    `;
    
    const printWindow = window.open('', '_blank');
    printWindow.document.write(pdfContent);
    printWindow.document.close();
    printWindow.print();
  };

  const printCupom = (saleId) => {
    // Montar endereço completo
    const enderecoCompleto = [
      selectedCompany?.endereco,
      selectedCompany?.numero ? `Nº ${selectedCompany.numero}` : '',
      selectedCompany?.bairro
    ].filter(Boolean).join(', ');
    
    // Gerar número do cupom a partir do saleId ou timestamp
    const cupomNumero = saleId ? saleId.slice(-6).toUpperCase() : Date.now().toString(36).toUpperCase().slice(-6);
    
    const cupomContent = `
      <html>
      <head>
        <style>
          body { font-family: 'Courier New', monospace; font-size: 12px; width: 80mm; margin: 0 auto; padding: 10px; }
          .center { text-align: center; }
          .divider { border-top: 1px dashed #000; margin: 5px 0; }
          .bold { font-weight: bold; }
          .item { display: flex; justify-content: space-between; margin: 2px 0; }
          .total { font-size: 14px; font-weight: bold; }
        </style>
      </head>
      <body>
        <div class="center bold">${selectedCompany?.nome_fantasia || selectedCompany?.razao_social || 'EMPRESA'}</div>
        <div class="center">${selectedCompany?.cnpj || ''}</div>
        <div class="center">${enderecoCompleto || ''}</div>
        <div class="divider"></div>
        <div class="center bold">CUPOM DE VENDA Nº ${cupomNumero}</div>
        <div class="center">${new Date().toLocaleString('pt-BR')}</div>
        <div class="divider"></div>
        ${cartItems.map(item => `
          <div>${item.product_name}</div>
          <div class="item">
            <span>${item.quantity} x R$ ${item.unit_price.toFixed(2)}</span>
            <span>R$ ${item.total.toFixed(2)}</span>
          </div>
        `).join('')}
        <div class="divider"></div>
        <div class="item total">
          <span>TOTAL</span>
          <span>R$ ${cartTotal.toFixed(2)}</span>
        </div>
        <div class="divider"></div>
        <div class="bold">PAGAMENTO:</div>
        ${payments.filter(p => p.amount > 0).map(p => `
          <div class="item">
            <span>${PAYMENT_METHODS.find(m => m.id === p.method)?.label}</span>
            <span>R$ ${parseFloat(p.amount).toFixed(2)}</span>
          </div>
        `).join('')}
        <div class="divider"></div>
        <div class="center">Obrigado pela preferência!</div>
        <div class="center">${currentRegister?.name} - ${currentRegister?.employee_name}</div>
      </body>
      </html>
    `;
    
    const printWindow = window.open('', '_blank', 'width=300,height=600');
    printWindow.document.write(cupomContent);
    printWindow.document.close();
    printWindow.print();
  };

  const addPaymentMethod = () => {
    setPayments([...payments, { method: 'cash', amount: 0 }]);
  };

  const updatePayment = (index, field, value) => {
    const updated = [...payments];
    updated[index][field] = field === 'amount' ? value : value;
    setPayments(updated);
  };

  const removePayment = (index) => {
    if (payments.length > 1) {
      setPayments(payments.filter((_, i) => i !== index));
    }
  };

  const filteredProducts = products.filter(p => 
    p.name.toLowerCase().includes(productSearch.toLowerCase()) ||
    (p.code && p.code.toLowerCase().includes(productSearch.toLowerCase()))
  );

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
      </div>
    );
  }

  return (
    <div className="h-full flex flex-col" data-testid="cash-register-page">
      {/* Header */}
      <div className="flex items-center justify-between p-4 border-b border-border bg-card">
        <div className="flex items-center gap-4">
          <Button variant="ghost" size="icon" onClick={() => setShowMenuDrawer(true)} data-testid="cash-menu-btn">
            <LayoutDashboard className="w-5 h-5" />
          </Button>
          <div>
            <h1 className="text-xl font-bold">Frente de Caixa</h1>
            {currentRegister && (
              <p className="text-sm text-muted-foreground">
                {currentRegister.name} • {currentRegister.employee_name} • 
                Saldo: R$ {currentRegister.current_balance?.toFixed(2)}
              </p>
            )}
          </div>
        </div>
        {currentRegister && (
          <Badge className="bg-emerald-500">Caixa Aberto</Badge>
        )}
      </div>

      {/* Main Content */}
      <div className="flex-1 grid grid-cols-1 lg:grid-cols-3 gap-4 p-4 overflow-auto">
        {/* Produtos */}
        <div className="lg:col-span-2 space-y-4">
          <Card>
            <CardHeader className="pb-2">
              <CardTitle className="text-lg">Produtos</CardTitle>
            </CardHeader>
            <CardContent>
              <Input
                placeholder="Buscar produto por nome ou código..."
                value={productSearch}
                onChange={(e) => setProductSearch(e.target.value)}
                className="mb-4"
                data-testid="product-search"
              />
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 max-h-[400px] overflow-auto">
                {filteredProducts.map(product => (
                  <Button
                    key={product.id}
                    variant="outline"
                    className="h-auto py-3 px-4 justify-start text-left"
                    onClick={() => addProductToCart(product)}
                    disabled={!currentRegister}
                    data-testid={`product-${product.id}`}
                  >
                    <div className="flex-1">
                      <div className="font-medium">{product.name}</div>
                      <div className="text-sm text-muted-foreground">
                        {product.code && `${product.code} • `}R$ {product.price?.toFixed(2)}
                      </div>
                    </div>
                    <Plus className="w-4 h-4 ml-2" />
                  </Button>
                ))}
                {filteredProducts.length === 0 && (
                  <p className="text-muted-foreground col-span-2 text-center py-4">
                    Nenhum produto encontrado
                  </p>
                )}
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Carrinho */}
        <div className="space-y-4">
          <Card>
            <CardHeader className="pb-2">
              <CardTitle className="text-lg flex items-center gap-2">
                <ShoppingCart className="w-5 h-5" />
                Carrinho
              </CardTitle>
            </CardHeader>
            <CardContent>
              {cartItems.length === 0 ? (
                <p className="text-muted-foreground text-center py-8">
                  Carrinho vazio
                </p>
              ) : (
                <div className="space-y-2 max-h-[300px] overflow-auto">
                  {cartItems.map((item, index) => (
                    <div key={index} className="flex items-center gap-2 p-2 bg-muted rounded-lg">
                      <div className="flex-1">
                        <div className="font-medium text-sm">{item.product_name}</div>
                        <div className="text-xs text-muted-foreground">
                          R$ {item.unit_price.toFixed(2)} x {item.quantity}
                        </div>
                      </div>
                      <div className="flex items-center gap-1">
                        <Button size="icon" variant="ghost" className="h-6 w-6" onClick={() => updateCartItemQuantity(index, item.quantity - 1)}>
                          <ChevronDown className="w-4 h-4" />
                        </Button>
                        <span className="w-8 text-center">{item.quantity}</span>
                        <Button size="icon" variant="ghost" className="h-6 w-6" onClick={() => updateCartItemQuantity(index, item.quantity + 1)}>
                          <ChevronUp className="w-4 h-4" />
                        </Button>
                      </div>
                      <div className="text-right font-medium w-20">
                        R$ {item.total.toFixed(2)}
                      </div>
                      <Button size="icon" variant="ghost" className="h-6 w-6 text-destructive" onClick={() => removeCartItem(index)}>
                        <X className="w-4 h-4" />
                      </Button>
                    </div>
                  ))}
                </div>
              )}
              
              <Separator className="my-4" />
              
              <div className="flex justify-between items-center text-xl font-bold">
                <span>TOTAL</span>
                <span className="text-emerald-500">R$ {cartTotal.toFixed(2)}</span>
              </div>
              
              <Button 
                className="w-full mt-4" 
                size="lg"
                disabled={cartItems.length === 0 || !currentRegister}
                onClick={() => {
                  setPayments([{ method: 'cash', amount: cartTotal }]);
                  setShowFinalizeModal(true);
                }}
                data-testid="finalize-btn"
              >
                Finalizar Venda
              </Button>
            </CardContent>
          </Card>
        </div>
      </div>

      {/* Menu Drawer */}
      <Dialog open={showMenuDrawer} onOpenChange={setShowMenuDrawer}>
        <DialogContent className="max-w-xs">
          <DialogHeader>
            <DialogTitle>Menu do Caixa</DialogTitle>
          </DialogHeader>
          <div className="space-y-2">
            <Button variant="outline" className="w-full justify-start" onClick={() => { setShowMenuDrawer(false); openDashboard(); }}>
              <BarChart3 className="w-4 h-4 mr-2" /> Dashboard de Metas
            </Button>
            <Button variant="outline" className="w-full justify-start" onClick={() => { setShowMenuDrawer(false); setShowOpenModal(true); }} disabled={currentRegister}>
              <Plus className="w-4 h-4 mr-2" /> Abrir Caixa
            </Button>
            <Button variant="outline" className="w-full justify-start" onClick={() => { setShowMenuDrawer(false); setShowSangriaModal(true); }} disabled={!currentRegister}>
              <Coins className="w-4 h-4 mr-2" /> Sangria
            </Button>
            <Button variant="outline" className="w-full justify-start" onClick={() => { setShowMenuDrawer(false); setShowCloseModal(true); }} disabled={!currentRegister}>
              <Lock className="w-4 h-4 mr-2" /> Fechar Caixa Atual
            </Button>
            <Separator />
            <Button variant="outline" className="w-full justify-start" onClick={() => { setShowMenuDrawer(false); openReports(); }} data-testid="cash-reports-btn">
              <FileText className="w-4 h-4 mr-2" /> Relatórios
            </Button>
            <Button variant="outline" className="w-full justify-start text-amber-500" onClick={() => { setShowMenuDrawer(false); setShowConsolidateModal(true); }}>
              <RefreshCw className="w-4 h-4 mr-2" /> Fechamento Geral
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Modal Abrir Caixa */}
      <Dialog open={showOpenModal} onOpenChange={setShowOpenModal}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Abrir Caixa</DialogTitle>
            <DialogDescription>Informe o saldo inicial e selecione o operador</DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div>
              <Label>Funcionário *</Label>
              <Select value={selectedEmployee} onValueChange={setSelectedEmployee}>
                <SelectTrigger><SelectValue placeholder="Selecione o funcionário" /></SelectTrigger>
                <SelectContent>
                  {employees.map(emp => (
                    <SelectItem key={emp.id} value={emp.id}>{emp.name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label>Saldo Inicial (Dinheiro)</Label>
              <Input 
                type="number" 
                step="0.01" 
                value={openingBalance} 
                onChange={(e) => setOpeningBalance(e.target.value)}
                placeholder="0,00"
              />
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setShowOpenModal(false)}>Cancelar</Button>
              <Button onClick={handleOpenRegister}>Abrir Caixa</Button>
            </DialogFooter>
          </div>
        </DialogContent>
      </Dialog>

      {/* Modal Fechar Caixa */}
      <Dialog open={showCloseModal} onOpenChange={setShowCloseModal}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Fechar Caixa</DialogTitle>
            <DialogDescription>Confirme o fechamento do {currentRegister?.name}</DialogDescription>
          </DialogHeader>
          {currentRegister && (
            <div className="space-y-4">
              <div className="bg-muted p-4 rounded-lg space-y-2">
                <div className="flex justify-between"><span>Saldo Inicial:</span><span>R$ {currentRegister.opening_balance?.toFixed(2)}</span></div>
                <div className="flex justify-between"><span>Total Vendas:</span><span className="text-emerald-500">R$ {currentRegister.total_sales?.toFixed(2)}</span></div>
                <div className="flex justify-between"><span>- Dinheiro:</span><span>R$ {currentRegister.total_cash?.toFixed(2)}</span></div>
                <div className="flex justify-between"><span>- Cartão:</span><span>R$ {currentRegister.total_card?.toFixed(2)}</span></div>
                <div className="flex justify-between"><span>- PIX:</span><span>R$ {currentRegister.total_pix?.toFixed(2)}</span></div>
                <div className="flex justify-between"><span>- Débito:</span><span>R$ {currentRegister.total_debit?.toFixed(2)}</span></div>
                <div className="flex justify-between text-amber-500"><span>Sangrias:</span><span>R$ {currentRegister.total_sangria?.toFixed(2)}</span></div>
                <Separator />
                <div className="flex justify-between font-bold text-lg"><span>Saldo em Dinheiro:</span><span>R$ {currentRegister.current_balance?.toFixed(2)}</span></div>
              </div>
              <DialogFooter>
                <Button variant="outline" onClick={() => setShowCloseModal(false)}>Cancelar</Button>
                <Button onClick={handleCloseRegister}>Confirmar Fechamento</Button>
              </DialogFooter>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Modal Sangria */}
      <Dialog open={showSangriaModal} onOpenChange={setShowSangriaModal}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Sangria</DialogTitle>
            <DialogDescription>Retirada de dinheiro do caixa</DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div>
              <Label>Valor *</Label>
              <Input 
                type="number" 
                step="0.01" 
                value={sangriaAmount} 
                onChange={(e) => setSangriaAmount(e.target.value)}
                placeholder="0,00"
              />
              {currentRegister && (
                <p className="text-xs text-muted-foreground mt-1">
                  Saldo disponível: R$ {currentRegister.current_balance?.toFixed(2)}
                </p>
              )}
            </div>
            <div>
              <Label>Retirado por *</Label>
              <Input 
                value={sangriaBy} 
                onChange={(e) => setSangriaBy(e.target.value)}
                placeholder="Nome de quem retirou"
              />
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setShowSangriaModal(false)}>Cancelar</Button>
              <Button onClick={handleSangria}>Confirmar Sangria</Button>
            </DialogFooter>
          </div>
        </DialogContent>
      </Dialog>

      {/* Modal Finalizar Venda */}
      <Dialog open={showFinalizeModal} onOpenChange={setShowFinalizeModal}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Finalizar Venda</DialogTitle>
            <DialogDescription>Total: R$ {cartTotal.toFixed(2)}</DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div>
              <Label>Conta de Receita *</Label>
              <Select value={selectedAccount} onValueChange={setSelectedAccount}>
                <SelectTrigger><SelectValue placeholder="Selecione a conta" /></SelectTrigger>
                <SelectContent>
                  {revenueAccounts.map(acc => (
                    <SelectItem key={acc.code} value={acc.code}>{acc.code} - {acc.name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            
            <div>
              <Label className="mb-2 block">Formas de Pagamento</Label>
              {payments.map((payment, index) => (
                <div key={index} className="flex gap-2 mb-2">
                  <Select value={payment.method} onValueChange={(v) => updatePayment(index, 'method', v)}>
                    <SelectTrigger className="w-[140px]"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      {PAYMENT_METHODS.map(m => (
                        <SelectItem key={m.id} value={m.id}>{m.label}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  <Input 
                    type="number" 
                    step="0.01"
                    value={payment.amount}
                    onChange={(e) => updatePayment(index, 'amount', e.target.value)}
                    placeholder="0,00"
                    className="flex-1"
                  />
                  {payments.length > 1 && (
                    <Button size="icon" variant="ghost" onClick={() => removePayment(index)}>
                      <X className="w-4 h-4" />
                    </Button>
                  )}
                </div>
              ))}
              <Button variant="outline" size="sm" onClick={addPaymentMethod}>
                <Plus className="w-4 h-4 mr-1" /> Adicionar forma de pagamento
              </Button>
            </div>
            
            <div className="flex items-center gap-2 bg-muted p-3 rounded-lg">
              <Switch checked={printReceipt} onCheckedChange={setPrintReceipt} />
              <Label>Imprimir Cupom de Venda</Label>
            </div>
            
            <DialogFooter>
              <Button variant="outline" onClick={() => setShowFinalizeModal(false)}>Cancelar</Button>
              <Button onClick={handleFinalize}>Finalizar</Button>
            </DialogFooter>
          </div>
        </DialogContent>
      </Dialog>

      {/* Modal Fechamento Geral */}
      <Dialog open={showConsolidateModal} onOpenChange={setShowConsolidateModal}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle className="text-amber-500">Fechamento Geral do Dia</DialogTitle>
            <DialogDescription>
              Esta ação irá consolidar todas as vendas e limpar os caixas do dia.
            </DialogDescription>
          </DialogHeader>
          {consolidateSummary ? (
            <div className="space-y-4">
              <div className="bg-emerald-500/10 border border-emerald-500 p-4 rounded-lg">
                <p className="font-bold text-emerald-500 mb-2">Fechamento Realizado!</p>
                <p>Total de Caixas: {consolidateSummary.total_registers}</p>
                <p>Faturamento Total: R$ {consolidateSummary.total_sales?.toFixed(2)}</p>
              </div>
              <Accordion type="single" collapsible>
                <AccordionItem value="details">
                  <AccordionTrigger>Ver detalhes por caixa</AccordionTrigger>
                  <AccordionContent>
                    {consolidateSummary.registers?.map((reg, i) => (
                      <div key={i} className="bg-muted p-2 rounded mb-2">
                        <p className="font-medium">{reg.name} - {reg.employee_name}</p>
                        <p className="text-sm">Vendas: R$ {reg.total_sales?.toFixed(2)}</p>
                      </div>
                    ))}
                  </AccordionContent>
                </AccordionItem>
              </Accordion>
              <DialogFooter>
                <Button onClick={() => { setShowConsolidateModal(false); setConsolidateSummary(null); }}>Fechar</Button>
              </DialogFooter>
            </div>
          ) : (
            <div className="space-y-4">
              <div className="bg-amber-500/10 border border-amber-500 p-4 rounded-lg text-sm">
                <p className="font-bold mb-2">Atenção!</p>
                <ul className="list-disc list-inside space-y-1">
                  <li>Todos os caixas devem estar fechados</li>
                  <li>As vendas serão movidas para a tabela consolidada</li>
                  <li>Os caixas do dia serão limpos</li>
                  <li>Esta ação não pode ser desfeita</li>
                </ul>
              </div>
              <DialogFooter>
                <Button variant="outline" onClick={() => setShowConsolidateModal(false)}>Cancelar</Button>
                <Button className="bg-amber-500 hover:bg-amber-600" onClick={handleConsolidate}>Confirmar Fechamento</Button>
              </DialogFooter>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Dashboard de Vendas com Metas */}
      <Dialog open={showDashboard} onOpenChange={setShowDashboard}>
        <DialogContent className="max-w-4xl max-h-[85vh] overflow-auto">
          <DialogHeader>
            <div className="flex items-center justify-between">
              <DialogTitle>Dashboard de Metas e Vendas</DialogTitle>
              <Button variant="ghost" size="sm" onClick={() => { setTempDailyGoal(dailyGoal.toString()); setTempWeeklyGoal(weeklyGoal.toString()); setShowGoalsModal(true); }}>
                <Settings className="w-4 h-4 mr-1" /> Configurar Metas
              </Button>
            </div>
          </DialogHeader>
          
          <div className="space-y-6">
            {/* Metas com Barras de Progresso */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {/* Meta Diária */}
              <Card className={`border-2 ${dailyProgress >= 100 ? 'border-emerald-500 bg-emerald-500/5' : dailyProgress >= 70 ? 'border-blue-500 bg-blue-500/5' : 'border-amber-500 bg-amber-500/5'}`}>
                <CardHeader className="pb-2">
                  <CardTitle className="text-base flex items-center justify-between">
                    <span>Meta Diária</span>
                    {dailyProgress >= 100 && <Badge className="bg-emerald-500">Alcançada!</Badge>}
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="space-y-3">
                    <div className="flex justify-between items-end">
                      <div>
                        <p className="text-3xl font-bold text-emerald-500">R$ {todayTotal.toFixed(2)}</p>
                        <p className="text-sm text-muted-foreground">de R$ {dailyGoal.toFixed(2)}</p>
                      </div>
                      <p className={`text-4xl font-bold ${dailyProgress >= 100 ? 'text-emerald-500' : dailyProgress >= 70 ? 'text-blue-500' : 'text-amber-500'}`}>
                        {dailyProgress.toFixed(0)}%
                      </p>
                    </div>
                    
                    {/* Barra de Progresso */}
                    <div className="w-full bg-muted rounded-full h-4 overflow-hidden">
                      <div 
                        className={`h-full rounded-full transition-all duration-500 ${dailyProgress >= 100 ? 'bg-emerald-500' : dailyProgress >= 70 ? 'bg-blue-500' : 'bg-amber-500'}`}
                        style={{ width: `${Math.min(dailyProgress, 100)}%` }}
                      />
                    </div>
                    
                    {dailyRemaining > 0 && (
                      <p className="text-sm text-muted-foreground text-center">
                        Faltam <span className="font-semibold text-foreground">R$ {dailyRemaining.toFixed(2)}</span> para atingir a meta
                      </p>
                    )}
                  </div>
                </CardContent>
              </Card>

              {/* Meta Semanal */}
              <Card className={`border-2 ${weeklyProgress >= 100 ? 'border-emerald-500 bg-emerald-500/5' : weeklyProgress >= 70 ? 'border-blue-500 bg-blue-500/5' : 'border-amber-500 bg-amber-500/5'}`}>
                <CardHeader className="pb-2">
                  <CardTitle className="text-base flex items-center justify-between">
                    <span>Meta Semanal</span>
                    {weeklyProgress >= 100 && <Badge className="bg-emerald-500">Alcançada!</Badge>}
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="space-y-3">
                    <div className="flex justify-between items-end">
                      <div>
                        <p className="text-3xl font-bold text-emerald-500">R$ {weekTotal.toFixed(2)}</p>
                        <p className="text-sm text-muted-foreground">de R$ {weeklyGoal.toFixed(2)}</p>
                      </div>
                      <p className={`text-4xl font-bold ${weeklyProgress >= 100 ? 'text-emerald-500' : weeklyProgress >= 70 ? 'text-blue-500' : 'text-amber-500'}`}>
                        {weeklyProgress.toFixed(0)}%
                      </p>
                    </div>
                    
                    {/* Barra de Progresso */}
                    <div className="w-full bg-muted rounded-full h-4 overflow-hidden">
                      <div 
                        className={`h-full rounded-full transition-all duration-500 ${weeklyProgress >= 100 ? 'bg-emerald-500' : weeklyProgress >= 70 ? 'bg-blue-500' : 'bg-amber-500'}`}
                        style={{ width: `${Math.min(weeklyProgress, 100)}%` }}
                      />
                    </div>
                    
                    {weeklyRemaining > 0 && (
                      <p className="text-sm text-muted-foreground text-center">
                        Faltam <span className="font-semibold text-foreground">R$ {weeklyRemaining.toFixed(2)}</span> para atingir a meta
                      </p>
                    )}
                  </div>
                </CardContent>
              </Card>
            </div>

            {/* Gráfico da Semana */}
            <Card>
              <CardHeader className="pb-2">
                <CardTitle className="text-base">Desempenho da Semana</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="h-[200px]">
                  <ResponsiveContainer width="100%" height="100%">
                    <ComposedChart data={[...weekSalesHistory, { date: 'Hoje', day_name: 'Hoje', total: todayTotal, isToday: true }]}>
                      <CartesianGrid strokeDasharray="3 3" className="stroke-muted" />
                      <XAxis dataKey="day_name" className="text-xs" />
                      <YAxis className="text-xs" tickFormatter={(v) => `R$${v}`} />
                      <Tooltip 
                        formatter={(value) => [`R$ ${parseFloat(value).toFixed(2)}`, 'Vendas']}
                        labelFormatter={(label) => label}
                      />
                      <Bar 
                        dataKey="total" 
                        fill="#10b981"
                        radius={[4, 4, 0, 0]}
                      />
                      <Line 
                        type="monotone" 
                        dataKey={() => dailyGoal} 
                        stroke="#f59e0b" 
                        strokeDasharray="5 5"
                        name="Meta Diária"
                        dot={false}
                      />
                    </ComposedChart>
                  </ResponsiveContainer>
                </div>
                <div className="flex justify-center gap-4 mt-2 text-xs">
                  <span className="flex items-center gap-1"><span className="w-3 h-3 bg-emerald-500 rounded" /> Vendas</span>
                  <span className="flex items-center gap-1"><span className="w-3 h-3 border-2 border-amber-500 border-dashed rounded" /> Meta Diária</span>
                </div>
              </CardContent>
            </Card>

            {/* Resumo do Dia */}
            <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
              <Card>
                <CardContent className="p-3 text-center">
                  <p className="text-xl font-bold text-emerald-500">
                    R$ {registers.reduce((sum, r) => sum + (r.total_cash || 0), 0).toFixed(2)}
                  </p>
                  <p className="text-xs text-muted-foreground">Dinheiro</p>
                </CardContent>
              </Card>
              <Card>
                <CardContent className="p-3 text-center">
                  <p className="text-xl font-bold">
                    R$ {registers.reduce((sum, r) => sum + (r.total_card || 0) + (r.total_debit || 0), 0).toFixed(2)}
                  </p>
                  <p className="text-xs text-muted-foreground">Cartões</p>
                </CardContent>
              </Card>
              <Card>
                <CardContent className="p-3 text-center">
                  <p className="text-xl font-bold">
                    R$ {registers.reduce((sum, r) => sum + (r.total_pix || 0), 0).toFixed(2)}
                  </p>
                  <p className="text-xs text-muted-foreground">PIX</p>
                </CardContent>
              </Card>
              <Card>
                <CardContent className="p-3 text-center">
                  <p className="text-xl font-bold text-amber-500">
                    R$ {registers.reduce((sum, r) => sum + (r.total_sangria || 0), 0).toFixed(2)}
                  </p>
                  <p className="text-xs text-muted-foreground">Sangrias</p>
                </CardContent>
              </Card>
            </div>

            {/* Caixas do Dia */}
            <Card>
              <CardHeader className="pb-2"><CardTitle className="text-base">Caixas do Dia</CardTitle></CardHeader>
              <CardContent>
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Caixa</TableHead>
                      <TableHead>Operador</TableHead>
                      <TableHead>Status</TableHead>
                      <TableHead className="text-right">Vendas</TableHead>
                      <TableHead className="text-right">% Meta</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {registers.map(reg => {
                      const regPercent = dailyGoal > 0 ? ((reg.total_sales || 0) / dailyGoal) * 100 : 0;
                      return (
                        <TableRow key={reg.id}>
                          <TableCell className="font-medium">{reg.name}</TableCell>
                          <TableCell>{reg.employee_name}</TableCell>
                          <TableCell>
                            <Badge className={reg.status === 'open' ? 'bg-emerald-500' : 'bg-slate-500'}>
                              {reg.status === 'open' ? 'Aberto' : 'Fechado'}
                            </Badge>
                          </TableCell>
                          <TableCell className="text-right font-medium">R$ {reg.total_sales?.toFixed(2)}</TableCell>
                          <TableCell className="text-right">
                            <Badge variant="outline" className={regPercent >= 50 ? 'text-emerald-500 border-emerald-500' : 'text-amber-500 border-amber-500'}>
                              {regPercent.toFixed(0)}%
                            </Badge>
                          </TableCell>
                        </TableRow>
                      );
                    })}
                  </TableBody>
                </Table>
              </CardContent>
            </Card>
          </div>
        </DialogContent>
      </Dialog>

      {/* Modal Configurar Metas */}
      <Dialog open={showGoalsModal} onOpenChange={setShowGoalsModal}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Configurar Metas de Vendas</DialogTitle>
            <DialogDescription>Defina suas metas diárias e semanais para acompanhar o desempenho.</DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div>
              <Label>Meta Diária (R$)</Label>
              <Input 
                type="number" 
                step="0.01" 
                value={tempDailyGoal} 
                onChange={(e) => setTempDailyGoal(e.target.value)}
                placeholder={dailyGoal.toString()}
              />
              <p className="text-xs text-muted-foreground mt-1">Valor esperado de vendas por dia</p>
            </div>
            <div>
              <Label>Meta Semanal (R$)</Label>
              <Input 
                type="number" 
                step="0.01" 
                value={tempWeeklyGoal} 
                onChange={(e) => setTempWeeklyGoal(e.target.value)}
                placeholder={weeklyGoal.toString()}
              />
              <p className="text-xs text-muted-foreground mt-1">Valor esperado de vendas por semana (7 dias)</p>
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setShowGoalsModal(false)}>Cancelar</Button>
              <Button onClick={saveGoals}>Salvar Metas</Button>
            </DialogFooter>
          </div>
        </DialogContent>
      </Dialog>

      {/* Modal Relatórios */}
      <Dialog open={showReportsModal} onOpenChange={setShowReportsModal}>
        <DialogContent className="max-w-4xl max-h-[85vh] overflow-auto">
          <DialogHeader>
            <DialogTitle>Relatórios da Frente de Caixa</DialogTitle>
          </DialogHeader>
          
          {/* Tabs de Relatório */}
          <div className="flex gap-2 border-b border-border pb-2 mb-4">
            <Button 
              variant={reportTab === 'daily' ? 'default' : 'outline'} 
              size="sm"
              onClick={() => { setReportTab('daily'); loadDailyReport(); }}
              data-testid="report-tab-daily"
            >
              <FileText className="w-4 h-4 mr-1" /> Vendas do Dia
            </Button>
            <Button 
              variant={reportTab === 'products' ? 'default' : 'outline'} 
              size="sm"
              onClick={() => { setReportTab('products'); loadMonthlyReport(); }}
              data-testid="report-tab-products"
            >
              <Package className="w-4 h-4 mr-1" /> Por Produto
            </Button>
          </div>

          {loadingReport ? (
            <div className="flex items-center justify-center py-8">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
            </div>
          ) : (
            <>
              {/* Relatório Diário */}
              {reportTab === 'daily' && (
                <div className="space-y-4">
                  {/* Resumo Geral */}
                  <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                    <Card className="bg-emerald-500/10 border-emerald-500/50">
                      <CardContent className="p-3 text-center">
                        <p className="text-xl font-bold text-emerald-500">
                          R$ {registers.reduce((sum, r) => sum + (r.total_sales || 0), 0).toFixed(2)}
                        </p>
                        <p className="text-xs text-muted-foreground">Total Vendas</p>
                      </CardContent>
                    </Card>
                    <Card>
                      <CardContent className="p-3 text-center">
                        <p className="text-xl font-bold">
                          R$ {registers.reduce((sum, r) => sum + (r.total_cash || 0), 0).toFixed(2)}
                        </p>
                        <p className="text-xs text-muted-foreground">Dinheiro</p>
                      </CardContent>
                    </Card>
                    <Card>
                      <CardContent className="p-3 text-center">
                        <p className="text-xl font-bold">
                          R$ {registers.reduce((sum, r) => sum + (r.total_card || 0) + (r.total_debit || 0), 0).toFixed(2)}
                        </p>
                        <p className="text-xs text-muted-foreground">Cartões</p>
                      </CardContent>
                    </Card>
                    <Card>
                      <CardContent className="p-3 text-center">
                        <p className="text-xl font-bold">
                          R$ {registers.reduce((sum, r) => sum + (r.total_pix || 0), 0).toFixed(2)}
                        </p>
                        <p className="text-xs text-muted-foreground">PIX</p>
                      </CardContent>
                    </Card>
                  </div>

                  {/* Vendas por Caixa - Seções Colapsáveis */}
                  <Accordion type="multiple" className="w-full">
                    {registers.map(reg => {
                      const regSales = salesByRegister[reg.id] || [];
                      const regSangrias = sangriasByRegister[reg.id] || [];
                      return (
                        <AccordionItem key={reg.id} value={reg.id}>
                          <AccordionTrigger className="hover:no-underline">
                            <div className="flex items-center justify-between w-full pr-4">
                              <div className="flex items-center gap-2">
                                <Badge className={reg.status === 'open' ? 'bg-emerald-500' : 'bg-slate-500'}>
                                  {reg.status === 'open' ? 'Aberto' : 'Fechado'}
                                </Badge>
                                <span className="font-medium">{reg.name}</span>
                                <span className="text-muted-foreground text-sm">({reg.employee_name})</span>
                              </div>
                              <span className="text-emerald-500 font-bold">R$ {reg.total_sales?.toFixed(2)}</span>
                            </div>
                          </AccordionTrigger>
                          <AccordionContent>
                            <div className="space-y-3 pl-2">
                              {/* Info do Caixa */}
                              <div className="grid grid-cols-2 md:grid-cols-4 gap-2 text-sm bg-muted/50 p-3 rounded-lg">
                                <div><span className="text-muted-foreground">Abertura:</span> {new Date(reg.opened_at).toLocaleTimeString('pt-BR')}</div>
                                <div><span className="text-muted-foreground">Saldo Inicial:</span> R$ {reg.opening_balance?.toFixed(2)}</div>
                                <div><span className="text-muted-foreground">Sangrias:</span> <span className="text-amber-500">R$ {reg.total_sangria?.toFixed(2)}</span></div>
                                <div><span className="text-muted-foreground">Saldo Final:</span> R$ {reg.current_balance?.toFixed(2)}</div>
                              </div>

                              {/* Lista de Vendas */}
                              {regSales.length > 0 ? (
                                <div>
                                  <h4 className="font-medium mb-2 text-sm">Vendas ({regSales.length})</h4>
                                  <Table>
                                    <TableHeader>
                                      <TableRow>
                                        <TableHead className="w-[60px]">Nº</TableHead>
                                        <TableHead>Hora</TableHead>
                                        <TableHead>Itens</TableHead>
                                        <TableHead>Pagamento</TableHead>
                                        <TableHead className="text-right">Total</TableHead>
                                        <TableHead className="w-[80px]"></TableHead>
                                      </TableRow>
                                    </TableHeader>
                                    <TableBody>
                                      {regSales.map((sale, idx) => (
                                        <TableRow key={sale.id} className="cursor-pointer hover:bg-muted/50" onClick={() => openSaleDetail(sale)}>
                                          <TableCell className="font-mono text-xs">{String(idx + 1).padStart(3, '0')}</TableCell>
                                          <TableCell>{new Date(sale.created_at).toLocaleTimeString('pt-BR')}</TableCell>
                                          <TableCell>{sale.items?.length || 0} item(s)</TableCell>
                                          <TableCell>
                                            <div className="flex gap-1 flex-wrap">
                                              {sale.payments?.map((p, pi) => (
                                                <Badge key={pi} variant="outline" className="text-xs">
                                                  {PAYMENT_METHODS.find(m => m.id === p.method)?.label || p.method}: R$ {parseFloat(p.amount).toFixed(2)}
                                                </Badge>
                                              ))}
                                            </div>
                                          </TableCell>
                                          <TableCell className="text-right font-medium">R$ {sale.total?.toFixed(2)}</TableCell>
                                          <TableCell>
                                            <Button size="icon" variant="ghost" className="h-7 w-7" onClick={(e) => { e.stopPropagation(); openSaleDetail(sale); }}>
                                              <Eye className="w-4 h-4" />
                                            </Button>
                                          </TableCell>
                                        </TableRow>
                                      ))}
                                    </TableBody>
                                  </Table>
                                </div>
                              ) : (
                                <p className="text-muted-foreground text-sm italic">Nenhuma venda registrada neste caixa</p>
                              )}

                              {/* Lista de Sangrias */}
                              {regSangrias.length > 0 && (
                                <div>
                                  <h4 className="font-medium mb-2 text-sm text-amber-500">Sangrias ({regSangrias.length})</h4>
                                  <div className="space-y-1">
                                    {regSangrias.map(s => (
                                      <div key={s.id} className="flex justify-between items-center text-sm bg-amber-500/10 p-2 rounded">
                                        <span>{new Date(s.created_at).toLocaleTimeString('pt-BR')} - {s.withdrawn_by}</span>
                                        <span className="text-amber-500 font-medium">R$ {s.amount?.toFixed(2)}</span>
                                      </div>
                                    ))}
                                  </div>
                                </div>
                              )}
                            </div>
                          </AccordionContent>
                        </AccordionItem>
                      );
                    })}
                  </Accordion>

                  {registers.length === 0 && (
                    <p className="text-center text-muted-foreground py-8">Nenhum caixa registrado hoje</p>
                  )}

                  {/* Botão Gerar PDF */}
                  <div className="flex justify-end">
                    <Button onClick={generateDailyPDF} variant="outline">
                      <FileText className="w-4 h-4 mr-2" /> Gerar PDF
                    </Button>
                  </div>
                </div>
              )}

              {/* Relatório por Produto */}
              {reportTab === 'products' && (
                <div className="space-y-4">
                  {/* Filtros */}
                  <div className="flex flex-wrap gap-3 items-end">
                    <div>
                      <Label className="text-xs">Ano</Label>
                      <Select value={String(reportYear)} onValueChange={(v) => setReportYear(parseInt(v))}>
                        <SelectTrigger className="w-[100px]"><SelectValue /></SelectTrigger>
                        <SelectContent>
                          {[2024, 2025, 2026].map(y => (
                            <SelectItem key={y} value={String(y)}>{y}</SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                    <div className="flex-1">
                      <Label className="text-xs">Meses (deixe vazio para todos)</Label>
                      <div className="flex flex-wrap gap-1 mt-1">
                        {['Jan','Fev','Mar','Abr','Mai','Jun','Jul','Ago','Set','Out','Nov','Dez'].map((m, i) => (
                          <Button
                            key={i}
                            size="sm"
                            variant={reportMonths.includes(i + 1) ? 'default' : 'outline'}
                            className="h-7 w-10 text-xs"
                            onClick={() => {
                              if (reportMonths.includes(i + 1)) {
                                setReportMonths(reportMonths.filter(x => x !== i + 1));
                              } else {
                                setReportMonths([...reportMonths, i + 1]);
                              }
                            }}
                          >
                            {m}
                          </Button>
                        ))}
                      </div>
                    </div>
                    <Button onClick={loadMonthlyReport} size="sm">
                      <RefreshCw className="w-4 h-4 mr-1" /> Atualizar
                    </Button>
                  </div>

                  {monthlyReport ? (
                    <>
                      {/* Resumo */}
                      <div className="grid grid-cols-2 gap-3">
                        <Card className="bg-emerald-500/10 border-emerald-500/50">
                          <CardContent className="p-4 text-center">
                            <p className="text-2xl font-bold text-emerald-500">
                              R$ {monthlyReport.totals?.revenue?.toFixed(2) || '0.00'}
                            </p>
                            <p className="text-sm text-muted-foreground">Faturamento Total</p>
                          </CardContent>
                        </Card>
                        <Card className="bg-blue-500/10 border-blue-500/50">
                          <CardContent className="p-4 text-center">
                            <p className="text-2xl font-bold text-blue-500">
                              R$ {monthlyReport.totals?.profit?.toFixed(2) || '0.00'}
                            </p>
                            <p className="text-sm text-muted-foreground">Lucro Bruto</p>
                          </CardContent>
                        </Card>
                      </div>

                      {/* Gráfico de Pizza */}
                      {monthlyReport.products?.length > 0 && (
                        <Card>
                          <CardHeader className="pb-2">
                            <CardTitle className="text-sm">Produtos Mais Vendidos</CardTitle>
                          </CardHeader>
                          <CardContent>
                            <div className="h-[250px]">
                              <ResponsiveContainer width="100%" height="100%">
                                <RechartsPieChart>
                                  <Pie
                                    data={monthlyReport.products.slice(0, 8).map((p, i) => ({
                                      name: p.product_name?.length > 15 ? p.product_name.substring(0, 15) + '...' : p.product_name,
                                      value: p.quantity,
                                      fill: ['#10b981', '#3b82f6', '#f59e0b', '#ef4444', '#8b5cf6', '#ec4899', '#14b8a6', '#6366f1'][i]
                                    }))}
                                    cx="50%"
                                    cy="50%"
                                    outerRadius={80}
                                    label={({ name, percent }) => `${name} (${(percent * 100).toFixed(0)}%)`}
                                    labelLine={false}
                                    dataKey="value"
                                  />
                                  <Tooltip formatter={(v) => `${v} un.`} />
                                </RechartsPieChart>
                              </ResponsiveContainer>
                            </div>
                          </CardContent>
                        </Card>
                      )}

                      {/* Tabela de Produtos */}
                      <Card>
                        <CardHeader className="pb-2">
                          <CardTitle className="text-sm">Detalhamento por Produto</CardTitle>
                        </CardHeader>
                        <CardContent>
                          {monthlyReport.products?.length > 0 ? (
                            <Table>
                              <TableHeader>
                                <TableRow>
                                  <TableHead>Código</TableHead>
                                  <TableHead>Produto</TableHead>
                                  <TableHead className="text-right">Qtd</TableHead>
                                  <TableHead className="text-right">Preço Unit.</TableHead>
                                  <TableHead className="text-right">Custo Unit.</TableHead>
                                  <TableHead className="text-right">Receita</TableHead>
                                  <TableHead className="text-right">Lucro</TableHead>
                                </TableRow>
                              </TableHeader>
                              <TableBody>
                                {monthlyReport.products.map((p, idx) => (
                                  <TableRow key={idx}>
                                    <TableCell className="font-mono text-xs">{p.product_code || '-'}</TableCell>
                                    <TableCell>{p.product_name}</TableCell>
                                    <TableCell className="text-right">{p.quantity}</TableCell>
                                    <TableCell className="text-right">R$ {p.unit_price?.toFixed(2)}</TableCell>
                                    <TableCell className="text-right">R$ {p.cost_price?.toFixed(2)}</TableCell>
                                    <TableCell className="text-right text-emerald-500">R$ {p.total_revenue?.toFixed(2)}</TableCell>
                                    <TableCell className="text-right text-blue-500">R$ {p.profit?.toFixed(2)}</TableCell>
                                  </TableRow>
                                ))}
                              </TableBody>
                            </Table>
                          ) : (
                            <p className="text-center text-muted-foreground py-4">Nenhum dado consolidado para o período selecionado</p>
                          )}
                        </CardContent>
                      </Card>
                    </>
                  ) : (
                    <p className="text-center text-muted-foreground py-8">
                      Selecione o período e clique em "Atualizar" para carregar o relatório.
                      <br />
                      <span className="text-xs">Nota: Os dados são das vendas consolidadas (após fechamento geral).</span>
                    </p>
                  )}
                </div>
              )}
            </>
          )}
        </DialogContent>
      </Dialog>

      {/* Modal Detalhe da Venda */}
      <Dialog open={showSaleDetailModal} onOpenChange={setShowSaleDetailModal}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle>Detalhes da Venda</DialogTitle>
            <DialogDescription>
              {selectedSale && (
                <>Nº {selectedSale.id?.slice(-6).toUpperCase()} • {new Date(selectedSale.created_at).toLocaleString('pt-BR')}</>
              )}
            </DialogDescription>
          </DialogHeader>
          
          {selectedSale && (
            <div className="space-y-4">
              {/* Info do Caixa */}
              <div className="bg-muted p-3 rounded-lg text-sm">
                <p><span className="text-muted-foreground">Caixa:</span> {selectedSale.cash_register_name}</p>
                <p><span className="text-muted-foreground">Operador:</span> {selectedSale.employee_name}</p>
              </div>

              {/* Itens da Venda (editáveis) */}
              <div>
                <Label className="mb-2 block">Itens da Venda</Label>
                <div className="space-y-2 max-h-[200px] overflow-auto">
                  {editingSaleItems.map((item, index) => (
                    <div key={index} className="flex items-center gap-2 p-2 bg-muted/50 rounded-lg">
                      <div className="flex-1">
                        <p className="font-medium text-sm">{item.product_name}</p>
                        <p className="text-xs text-muted-foreground">R$ {item.unit_price?.toFixed(2)} / un.</p>
                      </div>
                      <div className="flex items-center gap-1">
                        <Button size="icon" variant="ghost" className="h-6 w-6" onClick={() => updateSaleItem(index, 'quantity', item.quantity - 1)}>
                          <ChevronDown className="w-4 h-4" />
                        </Button>
                        <span className="w-8 text-center text-sm">{item.quantity}</span>
                        <Button size="icon" variant="ghost" className="h-6 w-6" onClick={() => updateSaleItem(index, 'quantity', item.quantity + 1)}>
                          <ChevronUp className="w-4 h-4" />
                        </Button>
                      </div>
                      <div className="text-right w-20">
                        <p className="font-medium text-sm">R$ {item.total?.toFixed(2)}</p>
                      </div>
                      <Button size="icon" variant="ghost" className="h-6 w-6 text-destructive" onClick={() => removeSaleItem(index)}>
                        <Trash2 className="w-4 h-4" />
                      </Button>
                    </div>
                  ))}
                </div>

                {/* Adicionar produto à venda */}
                <div className="mt-2">
                  <Select onValueChange={(v) => { const p = products.find(x => x.id === v); if (p) addProductToSale(p); }}>
                    <SelectTrigger className="text-sm">
                      <SelectValue placeholder="+ Adicionar produto" />
                    </SelectTrigger>
                    <SelectContent>
                      {products.map(p => (
                        <SelectItem key={p.id} value={p.id}>{p.name} - R$ {p.price?.toFixed(2)}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              </div>

              <Separator />

              {/* Total */}
              <div className="flex justify-between items-center text-lg font-bold">
                <span>TOTAL</span>
                <span className="text-emerald-500">R$ {editingSaleItems.reduce((sum, item) => sum + (item.total || 0), 0).toFixed(2)}</span>
              </div>

              {/* Pagamentos (somente leitura) */}
              <div>
                <Label className="text-sm text-muted-foreground">Formas de Pagamento</Label>
                <div className="flex gap-2 mt-1 flex-wrap">
                  {selectedSale.payments?.map((p, i) => (
                    <Badge key={i} variant="secondary">
                      {PAYMENT_METHODS.find(m => m.id === p.method)?.label || p.method}: R$ {parseFloat(p.amount).toFixed(2)}
                    </Badge>
                  ))}
                </div>
              </div>

              <DialogFooter className="flex-col sm:flex-row gap-2">
                <Button variant="outline" onClick={() => reprintSaleCupom(selectedSale)}>
                  <Printer className="w-4 h-4 mr-1" /> Reimprimir Cupom
                </Button>
                <div className="flex gap-2">
                  <Button variant="outline" onClick={() => setShowSaleDetailModal(false)}>Cancelar</Button>
                  <Button onClick={saveSaleChanges}>Salvar Alterações</Button>
                </div>
              </DialogFooter>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
};

const QuotesPage = ({ userPlan = 1, onLockedFeatureClick, onNavigate, companyId, selectedCompany, user }) => {
  const [quotes, setQuotes] = useState([]);
  const [products, setProducts] = useState([]);
  const [services, setServices] = useState([]);
  const [clients, setClients] = useState([]);
  const [loading, setLoading] = useState(true);
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [editingQuote, setEditingQuote] = useState(null);
  const [filterStatus, setFilterStatus] = useState('');
  const { toast } = useToast();
  const isMobile = useIsMobile();

  // Form state
  const [formData, setFormData] = useState({
    number: '',
    title: '',
    client_id: '',
    client_name: '',
    items: [],
    notes: '',
    validity_days: 30
  });

  // New item state - added item_type and billing_unit
  const [newItem, setNewItem] = useState({ item_type: 'product', product_id: '', service_id: '', code: '', name: '', price: 0, quantity: 1, billing_unit: '' });

  const hasAccess = userPlan >= 2;

  // Billing units for display
  const billingUnitsMap = {
    'hora': 'Por Hora',
    'visita': 'Por Visita',
    'item': 'Por Item',
    'km': 'Por Km',
    'pacote': 'Pacote'
  };

  // Helper function to detect CPF/CNPJ
  const getDocumentType = (doc) => {
    if (!doc || doc.trim() === '') return '';
    const cleanDoc = doc.replace(/[^\d]/g, '');
    if (cleanDoc.length === 11) return 'CPF';
    if (cleanDoc.length === 14) return 'CNPJ';
    return '';
  };

  useEffect(() => {
    if (hasAccess) {
      loadData();
    } else {
      setLoading(false);
    }
  }, [companyId, hasAccess]);

  const loadData = async () => {
    try {
      setLoading(true);
      const companyParam = companyId ? `?company_id=${companyId}` : '';
      const [quotesRes, productsRes, servicesRes, clientsRes] = await Promise.all([
        axios.get(`${API}/quotes${companyParam}`),
        axios.get(`${API}/products${companyParam}`),
        axios.get(`${API}/services${companyParam}`),
        axios.get(`${API}/clients${companyParam}`)
      ]);
      setQuotes(quotesRes.data);
      setProducts(productsRes.data);
      setServices(servicesRes.data.filter(s => s.is_active !== false));
      setClients(clientsRes.data);
    } catch (error) {
      toast({ title: "Erro ao carregar dados", variant: "destructive" });
    } finally {
      setLoading(false);
    }
  };

  const calculateTotal = (items) => {
    return items.reduce((sum, item) => sum + (item.price * item.quantity), 0);
  };

  const handleProductSelect = (productId) => {
    if (!productId || productId === '_none') {
      setNewItem({ ...newItem, product_id: '', code: '', name: '', price: 0, quantity: 1, billing_unit: '' });
      return;
    }
    const product = products.find(p => p.id === productId);
    if (product) {
      setNewItem({
        ...newItem,
        item_type: 'product',
        product_id: product.id,
        service_id: '',
        code: product.code || '',
        name: product.name,
        price: product.price,
        quantity: 1,
        billing_unit: ''
      });
    }
  };

  const handleServiceSelect = (serviceId) => {
    if (!serviceId || serviceId === '_none') {
      setNewItem({ ...newItem, service_id: '', code: '', name: '', price: 0, quantity: 1, billing_unit: '' });
      return;
    }
    const service = services.find(s => s.id === serviceId);
    if (service) {
      setNewItem({
        ...newItem,
        item_type: 'service',
        service_id: service.id,
        product_id: '',
        code: service.code || '',
        name: service.name,
        price: service.price,
        quantity: 1,
        billing_unit: service.billing_unit || ''
      });
    }
  };

  const addItem = () => {
    if (!newItem.name || newItem.price <= 0) {
      toast({ title: "Preencha o nome e preço do item", variant: "destructive" });
      return;
    }
    const itemWithTotal = { 
      ...newItem, 
      total: newItem.price * newItem.quantity,
      item_type: newItem.item_type || 'product'
    };
    setFormData(prev => ({
      ...prev,
      items: [...prev.items, itemWithTotal]
    }));
    setNewItem({ item_type: 'product', product_id: '', service_id: '', code: '', name: '', price: 0, quantity: 1, billing_unit: '' });
  };

  const removeItem = (index) => {
    setFormData(prev => ({
      ...prev,
      items: prev.items.filter((_, i) => i !== index)
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (formData.items.length === 0) {
      toast({ title: "Adicione pelo menos um item ao orçamento", variant: "destructive" });
      return;
    }
    try {
      const data = { ...formData, company_id: companyId || null };
      if (editingQuote) {
        await axios.put(`${API}/quotes/${editingQuote.id}`, data);
        toast({ title: "Orçamento atualizado!" });
      } else {
        await axios.post(`${API}/quotes`, data);
        toast({ title: "Orçamento criado!" });
      }
      setIsDialogOpen(false);
      resetForm();
      loadData();
    } catch (error) {
      toast({ title: "Erro ao salvar orçamento", variant: "destructive" });
    }
  };

  const handleStatusChange = async (quoteId, newStatus) => {
    try {
      await axios.patch(`${API}/quotes/${quoteId}/status?status=${newStatus}`);
      toast({ title: `Status alterado para ${QUOTE_STATUSES.find(s => s.value === newStatus)?.label}` });
      loadData();
    } catch (error) {
      toast({ title: "Erro ao alterar status", variant: "destructive" });
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm("Confirma exclusão do orçamento?")) return;
    try {
      await axios.delete(`${API}/quotes/${id}`);
      toast({ title: "Orçamento excluído!" });
      loadData();
    } catch (error) {
      toast({ title: "Erro ao excluir", variant: "destructive" });
    }
  };

  const openEdit = (quote) => {
    setEditingQuote(quote);
    setFormData({
      number: quote.number || '',
      title: quote.title || '',
      client_id: quote.client_id || '',
      client_name: quote.client_name || '',
      items: quote.items || [],
      notes: quote.notes || '',
      validity_days: quote.validity_days || 30
    });
    setIsDialogOpen(true);
  };

  const resetForm = () => {
    setEditingQuote(null);
    setFormData({
      number: '',
      title: '',
      client_id: '',
      client_name: '',
      items: [],
      notes: '',
      validity_days: 30
    });
    setNewItem({ item_type: 'product', product_id: '', service_id: '', code: '', name: '', price: 0, quantity: 1, billing_unit: '' });
  };

  const exportToPDF = (quote) => {
    // Determine company display name and document
    let companyName = '';
    let documentLabel = '';
    let documentValue = '';
    
    if (selectedCompany) {
      if (selectedCompany.is_personal) {
        // Para Conta Principal, usar o nome do usuário do perfil
        companyName = user?.name || 'Conta Principal';
        // Verificar CPF/CNPJ
        const doc = selectedCompany.cnpj || '';
        const docType = getDocumentType(doc);
        if (docType && doc) {
          documentLabel = docType;
          documentValue = doc;
        }
      } else {
        // Para empresa normal - usar Nome Fantasia primeiro
        companyName = selectedCompany.nome_fantasia || selectedCompany.razao_social || '';
        const doc = selectedCompany.cnpj || '';
        const docType = getDocumentType(doc);
        if (docType && doc) {
          documentLabel = docType;
          documentValue = doc;
        }
      }
    }
    
    // Buscar dados completos do cliente
    const client = quote.client_id ? clients.find(c => c.id === quote.client_id) : null;
    const clientPhone = client?.phone || '';
    const clientEmail = client?.email || '';
    const clientAddress = client?.address || '';
    
    const printContent = `
      <!DOCTYPE html>
      <html>
      <head>
        <meta charset="utf-8">
        <title>Orçamento ${quote.number ? quote.number + ' - ' : ''}${quote.title}</title>
        <style>
          body { font-family: Arial, sans-serif; padding: 40px; color: #333; }
          .header { border-bottom: 3px solid #6366f1; padding-bottom: 20px; margin-bottom: 30px; }
          .header h1 { color: #6366f1; margin: 0 0 5px 0; font-size: 28px; }
          .header .quote-number { color: #6366f1; font-size: 16px; margin: 0 0 10px 0; }
          .header .company { font-size: 14px; color: #666; }
          .info-grid { display: grid; grid-template-columns: 1fr 1fr; gap: 20px; margin-bottom: 30px; }
          .info-box { background: #f8fafc; padding: 15px; border-radius: 8px; }
          .info-box label { font-size: 12px; color: #666; display: block; margin-bottom: 5px; }
          .info-box p { margin: 0; font-weight: 500; }
          .info-box .sub-info { font-size: 12px; color: #666; margin-top: 5px; font-weight: normal; }
          .client-section { background: #f0fdf4; padding: 20px; border-radius: 8px; margin-bottom: 30px; border-left: 4px solid #22c55e; }
          .client-section h3 { margin: 0 0 15px 0; color: #166534; font-size: 14px; }
          .client-details { display: grid; grid-template-columns: repeat(auto-fit, minmax(200px, 1fr)); gap: 15px; }
          .client-details .detail { }
          .client-details .detail label { font-size: 11px; color: #666; display: block; margin-bottom: 3px; }
          .client-details .detail p { margin: 0; font-size: 13px; }
          table { width: 100%; border-collapse: collapse; margin-bottom: 30px; }
          th { background: #6366f1; color: white; padding: 12px 15px; text-align: left; font-size: 14px; }
          td { padding: 12px 15px; border-bottom: 1px solid #e5e7eb; }
          .text-right { text-align: right; }
          .total-row { background: #f1f5f9; font-weight: bold; }
          .total-row td { border-top: 2px solid #6366f1; }
          .notes { background: #fef3c7; padding: 20px; border-radius: 8px; margin-bottom: 30px; border-left: 4px solid #f59e0b; }
          .notes h3 { margin: 0 0 10px 0; color: #92400e; }
          .notes p { margin: 0; white-space: pre-wrap; }
          .footer { text-align: center; color: #999; font-size: 12px; margin-top: 40px; padding-top: 20px; border-top: 1px solid #e5e7eb; }
          .footer p { margin: 5px 0; }
          .validity { background: #dbeafe; padding: 10px 15px; border-radius: 8px; display: inline-block; color: #1e40af; }
        </style>
      </head>
      <body>
        <div class="header">
          <h1>ORÇAMENTO</h1>
          ${quote.number ? `<p class="quote-number">Nº ${quote.number}</p>` : ''}
          ${companyName ? `<p class="company"><strong>${companyName}</strong>${documentLabel && documentValue ? ` - ${documentLabel}: ${documentValue}` : ''}</p>` : ''}
        </div>
        
        <div class="info-grid">
          <div class="info-box">
            <label>Título do Orçamento</label>
            <p>${quote.title}</p>
          </div>
          <div class="info-box">
            <label>Data de Emissão</label>
            <p>${new Date(quote.created_at).toLocaleDateString('pt-BR')}</p>
          </div>
          <div class="info-box">
            <label>Validade</label>
            <p class="validity">${quote.validity_days} dias</p>
          </div>
        </div>

        ${quote.client_name ? `
        <div class="client-section">
          <h3>DADOS DO CLIENTE</h3>
          <div class="client-details">
            <div class="detail">
              <label>Nome</label>
              <p>${quote.client_name}</p>
            </div>
            ${clientPhone ? `
            <div class="detail">
              <label>Celular</label>
              <p>${clientPhone}</p>
            </div>
            ` : ''}
            ${clientEmail ? `
            <div class="detail">
              <label>E-mail</label>
              <p>${clientEmail}</p>
            </div>
            ` : ''}
            ${clientAddress ? `
            <div class="detail">
              <label>Endereço</label>
              <p>${clientAddress}</p>
            </div>
            ` : ''}
          </div>
        </div>
        ` : ''}

        <table>
          <thead>
            <tr>
              <th style="width: 80px;">Código</th>
              <th>Descrição</th>
              <th style="width: 80px;" class="text-right">Qtd</th>
              <th style="width: 120px;" class="text-right">Preço Unit.</th>
              <th style="width: 120px;" class="text-right">Total</th>
            </tr>
          </thead>
          <tbody>
            ${quote.items.map(item => `
              <tr>
                <td>${item.code || '-'}</td>
                <td>${item.name}</td>
                <td class="text-right">${item.quantity}</td>
                <td class="text-right">${formatCurrency(item.price)}</td>
                <td class="text-right">${formatCurrency(item.price * item.quantity)}</td>
              </tr>
            `).join('')}
            <tr class="total-row">
              <td colspan="4" class="text-right">TOTAL DO ORÇAMENTO</td>
              <td class="text-right">${formatCurrency(quote.total)}</td>
            </tr>
          </tbody>
        </table>

        ${quote.notes ? `
          <div class="notes">
            <h3>Observações / Condições</h3>
            <p>${quote.notes}</p>
          </div>
        ` : ''}

        <div class="footer">
          <p>Orçamento gerado pelo DRE+ em ${new Date().toLocaleDateString('pt-BR')} às ${new Date().toLocaleTimeString('pt-BR')}</p>
          <p>www.dremais.com.br</p>
        </div>
      </body>
      </html>
    `;
    
    const printWindow = window.open('', '_blank');
    printWindow.document.write(printContent);
    printWindow.document.close();
    printWindow.focus();
    setTimeout(() => printWindow.print(), 250);
  };

  const getStatusBadge = (status) => {
    const statusConfig = QUOTE_STATUSES.find(s => s.value === status) || QUOTE_STATUSES[0];
    return <Badge className={`${statusConfig.color} text-white`}>{statusConfig.label}</Badge>;
  };

  const filteredQuotes = filterStatus 
    ? quotes.filter(q => q.status === filterStatus)
    : quotes;

  // Locked state
  if (!hasAccess) {
    return (
      <div className="animate-fadeIn space-y-6" data-testid="quotes-page">
        <div className="flex items-center justify-between">
          <div>
            <h2 className="text-3xl font-semibold tracking-tight">Orçamentos</h2>
            <p className="text-muted-foreground">Crie e gerencie seus orçamentos</p>
          </div>
        </div>
        <Card className="border-amber-500/50 bg-amber-500/5">
          <CardContent className="p-6 text-center">
            <Lock className="w-12 h-12 mx-auto text-amber-500 mb-4" />
            <h3 className="text-lg font-semibold mb-2">Funcionalidade Bloqueada</h3>
            <p className="text-muted-foreground mb-4">
              Os Orçamentos estão disponíveis a partir do plano Advanced.
            </p>
            <Button onClick={() => onLockedFeatureClick && onLockedFeatureClick(2)}>
              Ver Planos
            </Button>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="animate-fadeIn space-y-6" data-testid="quotes-page">
      <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
        <div>
          <h2 className="text-2xl md:text-3xl font-semibold tracking-tight">Orçamentos</h2>
          <p className="text-muted-foreground">Crie e gerencie seus orçamentos</p>
        </div>
        <div className="flex items-center gap-2">
          <Select value={filterStatus || "all"} onValueChange={(v) => setFilterStatus(v === "all" ? "" : v)}>
            <SelectTrigger className="w-40">
              <SelectValue placeholder="Filtrar status" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">Todos</SelectItem>
              {QUOTE_STATUSES.map(s => (
                <SelectItem key={s.value} value={s.value}>{s.label}</SelectItem>
              ))}
            </SelectContent>
          </Select>
          <Button onClick={() => { resetForm(); setIsDialogOpen(true); }} className="gap-2">
            <Plus className="w-4 h-4" /> {isMobile ? 'Novo' : 'Novo Orçamento'}
          </Button>
        </div>
      </div>

      {/* Status Summary Cards */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4" data-testid="quotes-status-cards">
        {QUOTE_STATUSES.map(status => {
          const count = quotes.filter(q => q.status === status.value).length;
          const icons = {
            'ABERTO': <FileText className="w-5 h-5" />,
            'ENVIADO': <Send className="w-5 h-5" />,
            'NEGOCIANDO': <MessageSquare className="w-5 h-5" />,
            'FECHADO': <CheckCircle className="w-5 h-5" />
          };
          return (
            <Card 
              key={status.value} 
              className={`cursor-pointer transition-all hover:scale-[1.02] ${filterStatus === status.value ? 'ring-2 ring-primary' : ''}`}
              onClick={() => setFilterStatus(filterStatus === status.value ? '' : status.value)}
              data-testid={`quote-status-card-${status.value.toLowerCase()}`}
            >
              <CardContent className="p-4">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm text-muted-foreground">{status.label}</p>
                    <p className="text-2xl font-bold">{count}</p>
                  </div>
                  <div className={`${status.color} text-white p-2 rounded-lg`}>
                    {icons[status.value]}
                  </div>
                </div>
              </CardContent>
            </Card>
          );
        })}
      </div>

      {loading ? (
        <div className="flex justify-center py-12">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
        </div>
      ) : filteredQuotes.length === 0 ? (
        <Card>
          <CardContent className="p-12 text-center">
            <FileText className="w-12 h-12 mx-auto text-muted-foreground mb-4" />
            <h3 className="text-lg font-semibold mb-2">Nenhum orçamento encontrado</h3>
            <p className="text-muted-foreground mb-4">Crie seu primeiro orçamento clicando no botão acima.</p>
          </CardContent>
        </Card>
      ) : (
        <div className="grid gap-4">
          {filteredQuotes.map((quote) => (
            <Card key={quote.id} className="card-hover cursor-pointer" onClick={() => openEdit(quote)}>
              <CardContent className="p-4">
                <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
                  <div className="flex-1">
                    <div className="flex items-center gap-3 mb-2">
                      {quote.number && (
                        <span className="text-sm font-mono bg-primary/10 text-primary px-2 py-0.5 rounded">{quote.number}</span>
                      )}
                      <h3 className="font-semibold text-lg">{quote.title}</h3>
                      {getStatusBadge(quote.status)}
                    </div>
                    <div className="flex flex-wrap gap-4 text-sm text-muted-foreground">
                      {quote.client_name && (
                        <span className="flex items-center gap-1">
                          <User className="w-4 h-4" /> {quote.client_name}
                        </span>
                      )}
                      <span className="flex items-center gap-1">
                        <Calendar className="w-4 h-4" /> {new Date(quote.created_at).toLocaleDateString('pt-BR')}
                      </span>
                      <span className="flex items-center gap-1">
                        <Package className="w-4 h-4" /> {quote.items?.length || 0} itens
                      </span>
                    </div>
                  </div>
                  <div className="flex items-center gap-4" onClick={(e) => e.stopPropagation()}>
                    <div className="text-right">
                      <p className="text-xs text-muted-foreground">Total</p>
                      <p className="text-xl font-bold text-primary">{formatCurrency(quote.total)}</p>
                    </div>
                    <div className="flex items-center gap-1">
                      <Select value={quote.status} onValueChange={(v) => handleStatusChange(quote.id, v)}>
                        <SelectTrigger className="w-32 h-8 text-xs">
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          {QUOTE_STATUSES.map(s => (
                            <SelectItem key={s.value} value={s.value}>{s.label}</SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                      <Button variant="ghost" size="icon" onClick={() => exportToPDF(quote)} title="Exportar PDF">
                        <Download className="w-4 h-4" />
                      </Button>
                      <Button variant="ghost" size="icon" onClick={() => handleDelete(quote.id)}>
                        <Trash2 className="w-4 h-4 text-destructive" />
                      </Button>
                    </div>
                  </div>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}

      {/* Create/Edit Dialog */}
      <Dialog open={isDialogOpen} onOpenChange={setIsDialogOpen}>
        <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>{editingQuote ? 'Editar Orçamento' : 'Novo Orçamento'}</DialogTitle>
            <DialogDescription>Preencha os dados do orçamento</DialogDescription>
          </DialogHeader>
          <form onSubmit={handleSubmit} className="space-y-6">
            {/* Basic Info */}
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <div>
                <Label>Número do Orçamento</Label>
                <Input 
                  value={formData.number} 
                  onChange={(e) => setFormData({...formData, number: e.target.value})} 
                  placeholder="Ex: ORC-001"
                  data-testid="quote-number-input"
                />
              </div>
              <div>
                <Label>Título do Orçamento *</Label>
                <Input 
                  value={formData.title} 
                  onChange={(e) => setFormData({...formData, title: e.target.value})} 
                  placeholder="Ex: Orçamento de Serviços"
                  required 
                />
              </div>
              <div>
                <Label>Cliente</Label>
                <Select 
                  value={formData.client_id} 
                  onValueChange={(v) => {
                    const client = clients.find(c => c.id === v);
                    setFormData({...formData, client_id: v, client_name: client?.name || ''});
                  }}
                >
                  <SelectTrigger><SelectValue placeholder="Selecione um cliente" /></SelectTrigger>
                  <SelectContent>
                    {clients.map(c => (
                      <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>

            {/* Items Section */}
            <div className="space-y-4">
              <div className="flex items-center justify-between">
                <Label className="text-base font-semibold">Itens do Orçamento</Label>
                <div className="text-right">
                  <span className="text-sm text-muted-foreground">Total: </span>
                  <span className="text-lg font-bold text-primary">{formatCurrency(calculateTotal(formData.items))}</span>
                </div>
              </div>

              {/* Add Item Row */}
              <Card className="p-4 bg-muted/30">
                <div className="space-y-3">
                  {/* Item Type Selection */}
                  <div className="flex gap-4 items-center">
                    <Label className="text-sm font-medium">Tipo:</Label>
                    <div className="flex gap-4">
                      <label className="flex items-center gap-2 cursor-pointer">
                        <input 
                          type="radio" 
                          name="item_type" 
                          checked={newItem.item_type === 'product'} 
                          onChange={() => setNewItem({ item_type: 'product', product_id: '', service_id: '', code: '', name: '', price: 0, quantity: 1, billing_unit: '' })}
                          className="w-4 h-4"
                        />
                        <span className="text-sm flex items-center gap-1"><Package className="w-4 h-4" /> Produto</span>
                      </label>
                      <label className="flex items-center gap-2 cursor-pointer">
                        <input 
                          type="radio" 
                          name="item_type" 
                          checked={newItem.item_type === 'service'} 
                          onChange={() => setNewItem({ item_type: 'service', product_id: '', service_id: '', code: '', name: '', price: 0, quantity: 1, billing_unit: '' })}
                          className="w-4 h-4"
                        />
                        <span className="text-sm flex items-center gap-1"><Wrench className="w-4 h-4" /> Serviço</span>
                      </label>
                    </div>
                  </div>

                  <div className="grid grid-cols-12 gap-2 items-end">
                    {/* Product/Service Selector */}
                    <div className="col-span-12 md:col-span-3">
                      <Label className="text-xs">{newItem.item_type === 'product' ? 'Produto' : 'Serviço'} (opcional)</Label>
                      {newItem.item_type === 'product' ? (
                        <Select value={newItem.product_id || '_none'} onValueChange={handleProductSelect}>
                          <SelectTrigger className="h-9">
                            <SelectValue placeholder="Selecionar..." />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="_none">Selecionar...</SelectItem>
                            {products.map(p => (
                              <SelectItem key={p.id} value={p.id}>{p.code ? `${p.code} - ` : ''}{p.name}</SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      ) : (
                        <Select value={newItem.service_id || '_none'} onValueChange={handleServiceSelect}>
                          <SelectTrigger className="h-9">
                            <SelectValue placeholder="Selecionar..." />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="_none">Selecionar...</SelectItem>
                            {services.map(s => (
                              <SelectItem key={s.id} value={s.id}>{s.code ? `${s.code} - ` : ''}{s.name}</SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      )}
                    </div>
                    <div className="col-span-3 md:col-span-1">
                      <Label className="text-xs">Código</Label>
                      <Input 
                        className="h-9"
                        value={newItem.code} 
                        onChange={(e) => setNewItem({...newItem, code: e.target.value})} 
                        placeholder="Cód."
                      />
                    </div>
                    <div className="col-span-9 md:col-span-3">
                      <Label className="text-xs">Nome *</Label>
                      <Input 
                        className="h-9"
                        value={newItem.name} 
                        onChange={(e) => setNewItem({...newItem, name: e.target.value})} 
                        placeholder="Nome do item"
                      />
                    </div>
                    <div className="col-span-4 md:col-span-2">
                      <Label className="text-xs">Preço (R$) *</Label>
                      <Input 
                        className="h-9"
                        type="number" 
                        step="0.01" 
                        min="0" 
                        value={newItem.price} 
                        onChange={(e) => setNewItem({...newItem, price: parseFloat(e.target.value) || 0})} 
                      />
                    </div>
                    <div className="col-span-4 md:col-span-1">
                      <Label className="text-xs">Qtd</Label>
                      <Input 
                        className="h-9"
                        type="number" 
                        min="1" 
                        step={newItem.billing_unit === 'hora' ? '0.5' : '1'}
                        value={newItem.quantity} 
                        onChange={(e) => setNewItem({...newItem, quantity: parseFloat(e.target.value) || 1})} 
                      />
                    </div>
                    <div className="col-span-4 md:col-span-2">
                      <Button type="button" onClick={addItem} className="w-full h-9 gap-1">
                        <Plus className="w-4 h-4" /> Adicionar
                      </Button>
                    </div>
                  </div>
                  {/* Billing unit hint for services */}
                  {newItem.item_type === 'service' && newItem.billing_unit && (
                    <p className="text-xs text-amber-600 flex items-center gap-1">
                      <AlertCircle className="w-3 h-3" />
                      Este serviço é cobrado <strong>{billingUnitsMap[newItem.billing_unit] || newItem.billing_unit}</strong>. Informe a quantidade corretamente.
                    </p>
                  )}
                </div>
              </Card>

              {/* Items List */}
              {formData.items.length > 0 && (
                <Card className="overflow-hidden">
                  <Table>
                    <TableHeader>
                      <TableRow className="bg-muted/50">
                        <TableHead className="w-20">Código</TableHead>
                        <TableHead>Nome</TableHead>
                        <TableHead className="text-right w-24">Preço</TableHead>
                        <TableHead className="text-center w-16">Qtd</TableHead>
                        <TableHead className="text-right w-24">Total</TableHead>
                        <TableHead className="w-12"></TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {formData.items.map((item, index) => (
                        <TableRow key={index}>
                          <TableCell className="font-mono text-sm">{item.code || '-'}</TableCell>
                          <TableCell>{item.name}</TableCell>
                          <TableCell className="text-right font-mono">{formatCurrency(item.price)}</TableCell>
                          <TableCell className="text-center">{item.quantity}</TableCell>
                          <TableCell className="text-right font-mono font-semibold">{formatCurrency(item.price * item.quantity)}</TableCell>
                          <TableCell>
                            <Button 
                              type="button" 
                              variant="ghost" 
                              size="icon" 
                              className="h-8 w-8"
                              onClick={() => removeItem(index)}
                            >
                              <Trash2 className="w-4 h-4 text-destructive" />
                            </Button>
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </Card>
              )}
            </div>

            {/* Notes */}
            <div>
              <Label>Observações / Condições</Label>
              <textarea 
                className="w-full min-h-[100px] p-3 rounded-md border border-input bg-background text-sm resize-y"
                value={formData.notes}
                onChange={(e) => setFormData({...formData, notes: e.target.value})}
                placeholder="Adicione prazos, condições de pagamento, garantias, etc."
              />
            </div>

            {/* Validity */}
            <div className="w-48">
              <Label>Validade (dias)</Label>
              <Input 
                type="number" 
                min="1" 
                value={formData.validity_days} 
                onChange={(e) => setFormData({...formData, validity_days: parseInt(e.target.value) || 30})} 
              />
            </div>

            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setIsDialogOpen(false)}>Cancelar</Button>
              <Button type="submit">Salvar Orçamento</Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
};

// ==================== WALLETS PAGE ====================
const WALLET_TYPES = ['Banco', 'Cartão de Crédito', 'Cripto'];

const WalletsPage = ({ onNavigate, companyId }) => {
  const [wallets, setWallets] = useState([]);
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [editingWallet, setEditingWallet] = useState(null);
  const [loading, setLoading] = useState(true);
  const [formData, setFormData] = useState({
    account: '',
    wallet_type: '',
    description: ''
  });
  const { toast } = useToast();
  const isMobile = useIsMobile();

  useEffect(() => {
    loadWallets();
  }, [companyId]);

  const loadWallets = async () => {
    try {
      setLoading(true);
      const url = companyId ? `${API}/wallets?company_id=${companyId}` : `${API}/wallets`;
      const response = await axios.get(url);
      setWallets(response.data);
    } catch (error) {
      console.error('Error loading wallets:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!formData.wallet_type) {
      toast({ title: "Erro", description: "Selecione o tipo da carteira", variant: "destructive" });
      return;
    }
    
    try {
      const data = { ...formData, company_id: companyId || null };
      if (editingWallet) {
        await axios.put(`${API}/wallets/${editingWallet.id}`, data);
        toast({ title: "Carteira atualizada com sucesso!" });
      } else {
        await axios.post(`${API}/wallets`, data);
        toast({ title: "Carteira criada com sucesso!" });
      }
      loadWallets();
      setIsDialogOpen(false);
      resetForm();
    } catch (error) {
      toast({ 
        title: "Erro ao salvar carteira", 
        description: error.response?.data?.detail || "Tente novamente",
        variant: "destructive"
      });
    }
  };

  const handleEdit = (wallet) => {
    setEditingWallet(wallet);
    setFormData({
      account: wallet.account,
      wallet_type: wallet.wallet_type,
      description: wallet.description || ''
    });
    setIsDialogOpen(true);
  };

  const handleDelete = async (id) => {
    if (!confirm('Tem certeza que deseja excluir esta carteira?')) return;
    try {
      await axios.delete(`${API}/wallets/${id}`);
      toast({ title: "Carteira excluída com sucesso!" });
      loadWallets();
    } catch (error) {
      toast({ title: "Erro ao excluir carteira", variant: "destructive" });
    }
  };

  const resetForm = () => {
    setEditingWallet(null);
    setFormData({ account: '', wallet_type: '', description: '' });
  };

  const getTypeIcon = (type) => {
    switch(type) {
      case 'Banco': return <Building className="w-4 h-4" />;
      case 'Cartão de Crédito': return <CreditCard className="w-4 h-4" />;
      case 'Cripto': return <Coins className="w-4 h-4" />;
      default: return <CreditCard className="w-4 h-4" />;
    }
  };

  const getTypeShort = (type) => {
    switch(type) {
      case 'Banco': return 'Banco';
      case 'Cartão de Crédito': return 'Crédito';
      case 'Cripto': return 'Cripto';
      default: return type;
    }
  };

  return (
    <div className="animate-fadeIn space-y-6" data-testid="wallets-page">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-4">
          <button 
            onClick={() => onNavigate('dashboard')}
            className="p-2 hover:bg-muted rounded-lg transition-colors"
            data-testid="back-to-dashboard"
          >
            <X className="w-5 h-5" />
          </button>
          <div>
            <h2 className="text-3xl font-semibold tracking-tight">Carteiras</h2>
            <p className="text-muted-foreground">Gerencie suas contas bancárias, cartões e carteiras</p>
          </div>
        </div>
        <Button onClick={() => { resetForm(); setIsDialogOpen(true); }} data-testid="add-wallet-btn">
          <Plus className="w-4 h-4 mr-2" /> Incluir
        </Button>
      </div>

      {loading ? (
        <div className="flex justify-center py-12">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
        </div>
      ) : wallets.length === 0 ? (
        <Card>
          <CardContent className="flex flex-col items-center justify-center py-12">
            <CreditCard className="w-12 h-12 text-muted-foreground mb-4" />
            <p className="text-muted-foreground">Nenhuma carteira cadastrada</p>
            <Button className="mt-4" onClick={() => { resetForm(); setIsDialogOpen(true); }}>
              <Plus className="w-4 h-4 mr-2" /> Cadastrar Primeira Carteira
            </Button>
          </CardContent>
        </Card>
      ) : (
        <Card>
          <CardContent className="p-0">
            <Table>
              <TableHeader>
                <TableRow className="bg-muted/50">
                  <TableHead>Conta</TableHead>
                  <TableHead>Tipo</TableHead>
                  {!isMobile && <TableHead>Descrição</TableHead>}
                  <TableHead className="text-right">Ações</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {wallets.map((wallet) => (
                  <TableRow key={wallet.id}>
                    <TableCell className="font-medium">{wallet.account}</TableCell>
                    <TableCell>
                      <div className="flex items-center gap-2">
                        {getTypeIcon(wallet.wallet_type)}
                        <span>{isMobile ? getTypeShort(wallet.wallet_type) : wallet.wallet_type}</span>
                      </div>
                    </TableCell>
                    {!isMobile && <TableCell className="text-muted-foreground">{wallet.description || '-'}</TableCell>}
                    <TableCell className="text-right">
                      <div className="flex justify-end gap-1">
                        <Button variant="ghost" size="icon" onClick={() => handleEdit(wallet)} data-testid={`edit-wallet-${wallet.id}`}>
                          <Edit className="w-4 h-4" />
                        </Button>
                        <Button variant="ghost" size="icon" onClick={() => handleDelete(wallet.id)} data-testid={`delete-wallet-${wallet.id}`}>
                          <Trash2 className="w-4 h-4 text-red-500" />
                        </Button>
                      </div>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </CardContent>
        </Card>
      )}

      {/* Dialog para Criar/Editar Carteira */}
      <Dialog open={isDialogOpen} onOpenChange={setIsDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>{editingWallet ? 'Editar Carteira' : 'Nova Carteira'}</DialogTitle>
          </DialogHeader>
          <form onSubmit={handleSubmit} className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="account">Conta *</Label>
              <Input
                id="account"
                value={formData.account}
                onChange={(e) => setFormData({ ...formData, account: e.target.value })}
                placeholder="Ex: Nubank, Itaú, Binance"
                required
                data-testid="wallet-account-input"
              />
            </div>
            
            <div className="space-y-2">
              <Label htmlFor="wallet_type">Tipo *</Label>
              <Select 
                value={formData.wallet_type} 
                onValueChange={(v) => setFormData({ ...formData, wallet_type: v })}
              >
                <SelectTrigger data-testid="wallet-type-select">
                  <SelectValue placeholder="Selecione o tipo" />
                </SelectTrigger>
                <SelectContent>
                  {WALLET_TYPES.map((type) => (
                    <SelectItem key={type} value={type}>{type}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            
            <div className="space-y-2">
              <Label htmlFor="description">Descrição</Label>
              <Input
                id="description"
                value={formData.description}
                onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                placeholder="Descrição opcional"
                data-testid="wallet-description-input"
              />
            </div>
            
            <div className="flex justify-end gap-2 pt-4">
              <Button type="button" variant="outline" onClick={() => setIsDialogOpen(false)}>
                Cancelar
              </Button>
              <Button type="submit" data-testid="save-wallet-btn">
                {editingWallet ? 'Salvar' : 'Criar'}
              </Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
};

// ==================== COMPANIES PAGE ====================
const CompaniesPage = ({ onNavigate, userPlan, companyLimit, onRefresh }) => {
  const [companies, setCompanies] = useState([]);
  const [loading, setLoading] = useState(true);
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [editingCompany, setEditingCompany] = useState(null);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(null);
  const [showConciliationConfirm, setShowConciliationConfirm] = useState(null); // Modal de confirmação de ressincronização
  const [pendingFormData, setPendingFormData] = useState(null); // Dados pendentes aguardando confirmação
  const [formData, setFormData] = useState({
    razao_social: '',
    nome_fantasia: '',
    endereco: '',
    numero: '',
    cep: '',
    bairro: '',
    cidade: '',
    uf: '',
    document_type: '',
    cnpj: '',
    chave_pix: '',
    responsavel_1: '',
    responsavel_2: '',
    saldo_inicial: '',
    // Conciliação de Contas
    conciliation_type: 'OFF',
    accumulated_day: '',
    closing_day: ''
  });
  const { toast } = useToast();

  // Helper functions for document formatting
  const formatCPF = (value) => {
    const digits = value.replace(/\D/g, '').slice(0, 11);
    if (digits.length <= 3) return digits;
    if (digits.length <= 6) return `${digits.slice(0,3)}.${digits.slice(3)}`;
    if (digits.length <= 9) return `${digits.slice(0,3)}.${digits.slice(3,6)}.${digits.slice(6)}`;
    return `${digits.slice(0,3)}.${digits.slice(3,6)}.${digits.slice(6,9)}-${digits.slice(9)}`;
  };

  const formatCNPJ = (value) => {
    const digits = value.replace(/\D/g, '').slice(0, 14);
    if (digits.length <= 2) return digits;
    if (digits.length <= 5) return `${digits.slice(0,2)}.${digits.slice(2)}`;
    if (digits.length <= 8) return `${digits.slice(0,2)}.${digits.slice(2,5)}.${digits.slice(5)}`;
    if (digits.length <= 12) return `${digits.slice(0,2)}.${digits.slice(2,5)}.${digits.slice(5,8)}/${digits.slice(8)}`;
    return `${digits.slice(0,2)}.${digits.slice(2,5)}.${digits.slice(5,8)}/${digits.slice(8,12)}-${digits.slice(12)}`;
  };

  const handleDocumentChange = (value) => {
    const formatted = formData.document_type === 'CPF' ? formatCPF(value) : 
                      formData.document_type === 'CNPJ' ? formatCNPJ(value) : value;
    setFormData({...formData, cnpj: formatted});
  };

  useEffect(() => {
    loadCompanies();
  }, []);

  const loadCompanies = async () => {
    try {
      setLoading(true);
      const response = await axios.get(`${API}/companies`);
      setCompanies(response.data.companies || []);
    } catch (error) {
      toast({ title: "Erro", description: "Erro ao carregar empresas", variant: "destructive" });
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e, skipConciliationCheck = false) => {
    e.preventDefault();
    if (!formData.razao_social.trim()) {
      toast({ title: "Erro", description: "Razão Social é obrigatória", variant: "destructive" });
      return;
    }
    
    // Verificar se o tipo de conciliação mudou (apenas ao editar)
    if (editingCompany && !skipConciliationCheck) {
      const oldType = editingCompany.conciliation_type || 'OFF';
      const newType = formData.conciliation_type || 'OFF';
      
      if (oldType !== newType && !editingCompany.is_personal) {
        // Mostrar modal de confirmação
        setShowConciliationConfirm({
          oldType,
          newType,
          companyName: editingCompany.razao_social
        });
        return;
      }
    }
    
    await saveCompany();
  };

  const saveCompany = async () => {
    try {
      // Prepare data with numeric fields converted
      const dataToSend = {
        ...formData,
        saldo_inicial: formData.saldo_inicial !== '' ? parseFloat(formData.saldo_inicial) : 0.0,
        accumulated_day: formData.accumulated_day !== '' ? parseInt(formData.accumulated_day) : null,
        closing_day: formData.closing_day !== '' ? parseInt(formData.closing_day) : null
      };
      
      if (editingCompany) {
        await axios.put(`${API}/companies/${editingCompany.id}`, dataToSend);
        
        // Verificar se o tipo de conciliação mudou para mostrar mensagem apropriada
        const oldType = editingCompany.conciliation_type || 'OFF';
        const newType = formData.conciliation_type || 'OFF';
        
        if (oldType !== newType && !editingCompany.is_personal) {
          toast({ 
            title: "Empresa atualizada", 
            description: "As transações espelho/acumuladas foram ressincronizadas automaticamente."
          });
        } else {
          toast({ title: "Sucesso", description: "Empresa atualizada com sucesso" });
        }
      } else {
        await axios.post(`${API}/companies`, dataToSend);
        toast({ title: "Sucesso", description: "Empresa criada com sucesso" });
      }
      setIsDialogOpen(false);
      setEditingCompany(null);
      setShowConciliationConfirm(null);
      setFormData({ razao_social: '', nome_fantasia: '', endereco: '', numero: '', cep: '', bairro: '', cidade: '', uf: '', document_type: '', cnpj: '', chave_pix: '', responsavel_1: '', responsavel_2: '', saldo_inicial: '', conciliation_type: 'OFF', accumulated_day: '', closing_day: '' });
      loadCompanies();
      if (onRefresh) onRefresh();
    } catch (error) {
      const message = error.response?.data?.detail || "Erro ao salvar empresa";
      toast({ title: "Erro", description: message, variant: "destructive" });
    }
  };

  const handleConfirmConciliationChange = async () => {
    setShowConciliationConfirm(null);
    await saveCompany();
  };

  const handleMigrateData = async () => {
    try {
      const response = await axios.post(`${API}/companies/migrate-data`);
      const results = response.data.results;
      const total = results.clients + results.suppliers + results.products + results.wallets + results.transactions;
      if (total > 0) {
        toast({ 
          title: "Migração concluída!", 
          description: `${results.transactions} lançamentos, ${results.clients} clientes, ${results.suppliers} fornecedores, ${results.products} produtos e ${results.wallets} carteiras foram migrados para a Conta Principal.`
        });
        loadCompanies();
        if (onRefresh) onRefresh();
      } else {
        toast({ title: "Nenhum dado para migrar", description: "Todos os dados já estão associados a uma empresa." });
      }
    } catch (error) {
      toast({ title: "Erro na migração", description: error.response?.data?.detail || "Tente novamente", variant: "destructive" });
    }
  };

  const handleEdit = (company) => {
    setEditingCompany(company);
    // Format document for display if exists
    let displayDoc = company.cnpj || '';
    if (company.cnpj && company.document_type) {
      displayDoc = company.document_type === 'CPF' ? formatCPF(company.cnpj) : 
                   company.document_type === 'CNPJ' ? formatCNPJ(company.cnpj) : company.cnpj;
    }
    setFormData({
      razao_social: company.razao_social || '',
      nome_fantasia: company.nome_fantasia || '',
      endereco: company.endereco || '',
      numero: company.numero || '',
      cep: company.cep || '',
      bairro: company.bairro || '',
      cidade: company.cidade || '',
      uf: company.uf || '',
      document_type: company.document_type || '',
      cnpj: displayDoc,
      chave_pix: company.chave_pix || '',
      responsavel_1: company.responsavel_1 || '',
      responsavel_2: company.responsavel_2 || '',
      saldo_inicial: company.saldo_inicial !== undefined ? company.saldo_inicial : '',
      // Conciliação de Contas
      conciliation_type: company.conciliation_type || 'OFF',
      accumulated_day: company.accumulated_day !== undefined && company.accumulated_day !== null ? company.accumulated_day : '',
      closing_day: company.closing_day !== undefined && company.closing_day !== null ? company.closing_day : ''
    });
    setIsDialogOpen(true);
  };

  const handleDelete = async (company) => {
    try {
      const response = await axios.delete(`${API}/companies/${company.id}`);
      toast({ 
        title: "Empresa excluída", 
        description: `${company.razao_social} e ${response.data.deleted_counts?.transactions || 0} lançamentos foram excluídos.`
      });
      setShowDeleteConfirm(null);
      loadCompanies();
      if (onRefresh) onRefresh();
    } catch (error) {
      const message = error.response?.data?.detail || "Erro ao excluir empresa";
      toast({ title: "Erro", description: message, variant: "destructive" });
    }
  };

  const openNewDialog = () => {
    setEditingCompany(null);
    setFormData({ razao_social: '', nome_fantasia: '', endereco: '', numero: '', cep: '', bairro: '', cidade: '', uf: '', document_type: '', cnpj: '', chave_pix: '', responsavel_1: '', responsavel_2: '', saldo_inicial: '', conciliation_type: 'OFF', accumulated_day: '', closing_day: '' });
    setIsDialogOpen(true);
  };

  const canCreateMore = companies.length < companyLimit;

  return (
    <div className="animate-fadeIn space-y-6" data-testid="companies-page">
      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
        <div className="flex items-center justify-between w-full md:w-auto">
          <div>
            <h2 className="text-3xl font-semibold tracking-tight flex items-center gap-2">
              <Building className="w-8 h-8" />
              Contas e Empresas
            </h2>
            <p className="text-muted-foreground">
              Gerencie suas empresas e contas ({companies.length}/{companyLimit})
            </p>
          </div>
          {/* Close button - mobile only */}
          <Button 
            variant="ghost" 
            size="sm" 
            className="md:hidden"
            onClick={() => onNavigate && onNavigate('dashboard')}
            data-testid="close-companies-btn"
          >
            <X className="w-6 h-6" />
          </Button>
        </div>
        <div className="flex flex-wrap gap-2">
          <Button 
            onClick={openNewDialog} 
            disabled={!canCreateMore}
            data-testid="new-company-btn"
          >
            <Plus className="w-4 h-4 mr-2" />
            Nova Empresa
          </Button>
        </div>
      </div>

      {!canCreateMore && (
        <div className="p-4 rounded-lg bg-amber-50 border border-amber-200 text-amber-800 flex items-center gap-2">
          <AlertCircle className="w-5 h-5" />
          <span>Limite de empresas atingido para seu plano ({PLANS[userPlan]?.name}). Faça upgrade para adicionar mais.</span>
        </div>
      )}

      {loading ? (
        <div className="flex justify-center py-12">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
        </div>
      ) : (
        <div className="grid gap-4">
          {companies.map((company) => (
            <Card 
              key={company.id} 
              className={`${company.is_personal ? 'border-l-4 border-l-blue-500' : ''} cursor-pointer hover:bg-muted/50 transition-colors`}
              onClick={() => handleEdit(company)}
              data-testid={`company-card-${company.id}`}
            >
              <CardContent className="p-4">
                <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
                  <div className="flex-1">
                    <div className="flex items-center gap-2">
                      {company.is_personal ? (
                        <Badge variant="secondary" className="text-xs">Conta Principal</Badge>
                      ) : (
                        <Badge variant="outline" className="text-xs">Empresa</Badge>
                      )}
                      <h3 className="font-semibold">{company.razao_social}</h3>
                    </div>
                    {company.nome_fantasia && (
                      <p className="text-sm text-muted-foreground mt-1">Nome Fantasia: {company.nome_fantasia}</p>
                    )}
                    <div className="flex flex-wrap gap-4 mt-2 text-sm text-muted-foreground">
                      {company.cnpj && <span>CPF/CNPJ: {company.cnpj}</span>}
                      {company.endereco && <span>{company.endereco}{company.numero ? `, ${company.numero}` : ''}</span>}
                      {company.cep && <span>CEP: {company.cep}</span>}
                    </div>
                    {company.chave_pix && (
                      <p className="text-sm text-muted-foreground mt-1">Chave PIX: {company.chave_pix}</p>
                    )}
                  </div>
                  <div className="flex gap-2" onClick={(e) => e.stopPropagation()}>
                    {/* Edit button - desktop only */}
                    <Button 
                      variant="outline" 
                      size="sm" 
                      className="hidden md:flex"
                      onClick={() => handleEdit(company)}
                      data-testid={`edit-company-${company.id}`}
                    >
                      <Pencil className="w-4 h-4" />
                    </Button>
                    {!company.is_personal && (
                      <Button 
                        variant="outline" 
                        size="sm" 
                        className="text-red-500 hover:text-red-700"
                        onClick={() => setShowDeleteConfirm(company)}
                        data-testid={`delete-company-${company.id}`}
                      >
                        <Trash2 className="w-4 h-4" />
                      </Button>
                    )}
                  </div>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}

      {/* Create/Edit Dialog */}
      <Dialog open={isDialogOpen} onOpenChange={setIsDialogOpen}>
        <DialogContent className="max-w-md max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>{editingCompany ? 'Editar Empresa' : 'Nova Empresa'}</DialogTitle>
          </DialogHeader>
          <form onSubmit={handleSubmit} className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="razao_social">Razão Social *</Label>
              <Input
                id="razao_social"
                value={formData.razao_social}
                onChange={(e) => setFormData({ ...formData, razao_social: e.target.value })}
                placeholder="Nome da empresa"
                data-testid="company-razao-social"
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="nome_fantasia">Nome Fantasia</Label>
              <Input
                id="nome_fantasia"
                value={formData.nome_fantasia}
                onChange={(e) => setFormData({ ...formData, nome_fantasia: e.target.value })}
                placeholder="Nome fantasia"
                data-testid="company-nome-fantasia"
              />
            </div>
            <div className="grid grid-cols-3 gap-2">
              <div className="space-y-2">
                <Label>Tipo Documento</Label>
                <Select value={formData.document_type} onValueChange={(v) => setFormData({...formData, document_type: v, cnpj: ''})}>
                  <SelectTrigger><SelectValue placeholder="Selecione" /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="CPF">CPF</SelectItem>
                    <SelectItem value="CNPJ">CNPJ</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="col-span-2 space-y-2">
                <Label htmlFor="cnpj">{formData.document_type || 'CPF/CNPJ'}</Label>
                <Input
                  id="cnpj"
                  value={formData.cnpj}
                  onChange={(e) => handleDocumentChange(e.target.value)}
                  placeholder={formData.document_type === 'CPF' ? '000.000.000-00' : '00.000.000/0000-00'}
                  disabled={!formData.document_type}
                  data-testid="company-cnpj"
                />
              </div>
            </div>
            
            {/* Endereço - Collapsible */}
            <Accordion type="single" collapsible className="w-full border rounded-lg">
              <AccordionItem value="endereco" className="border-0">
                <AccordionTrigger className="px-4 hover:no-underline">
                  <div className="flex items-center gap-2">
                    <MapPin className="w-4 h-4 text-primary" />
                    <span>Endereço</span>
                  </div>
                </AccordionTrigger>
                <AccordionContent className="px-4 pb-4">
                  <div className="space-y-4">
                    <div className="grid grid-cols-3 gap-2">
                      <div className="col-span-2 space-y-2">
                        <Label htmlFor="endereco">Logradouro</Label>
                        <Input
                          id="endereco"
                          value={formData.endereco}
                          onChange={(e) => setFormData({ ...formData, endereco: e.target.value })}
                          placeholder="Rua, Av..."
                          data-testid="company-endereco"
                        />
                      </div>
                      <div className="space-y-2">
                        <Label htmlFor="numero">Número</Label>
                        <Input
                          id="numero"
                          value={formData.numero}
                          onChange={(e) => setFormData({ ...formData, numero: e.target.value })}
                          placeholder="Nº"
                          data-testid="company-numero"
                        />
                      </div>
                    </div>
                    <div className="grid grid-cols-2 gap-2">
                      <div className="space-y-2">
                        <Label htmlFor="bairro">Bairro</Label>
                        <Input
                          id="bairro"
                          value={formData.bairro}
                          onChange={(e) => setFormData({ ...formData, bairro: e.target.value })}
                          placeholder="Bairro"
                          data-testid="company-bairro"
                        />
                      </div>
                      <div className="space-y-2">
                        <Label htmlFor="cep">CEP</Label>
                        <Input
                          id="cep"
                          value={formData.cep}
                          onChange={(e) => setFormData({ ...formData, cep: e.target.value })}
                          placeholder="00000-000"
                          data-testid="company-cep"
                        />
                      </div>
                    </div>
                    <div className="grid grid-cols-3 gap-2">
                      <div className="col-span-2 space-y-2">
                        <Label htmlFor="cidade">Cidade</Label>
                        <Input
                          id="cidade"
                          value={formData.cidade}
                          onChange={(e) => setFormData({ ...formData, cidade: e.target.value })}
                          placeholder="Cidade"
                          data-testid="company-cidade"
                        />
                      </div>
                      <div className="space-y-2">
                        <Label htmlFor="uf">UF</Label>
                        <Select value={formData.uf || '_none'} onValueChange={(v) => setFormData({...formData, uf: v === '_none' ? '' : v})}>
                          <SelectTrigger data-testid="company-uf"><SelectValue placeholder="UF" /></SelectTrigger>
                          <SelectContent>
                            <SelectItem value="_none">Selecione</SelectItem>
                            {UF_OPTIONS.map(uf => <SelectItem key={uf} value={uf}>{uf}</SelectItem>)}
                          </SelectContent>
                        </Select>
                      </div>
                    </div>
                  </div>
                </AccordionContent>
              </AccordionItem>
            </Accordion>
            <div className="space-y-2">
              <Label htmlFor="chave_pix">Chave PIX</Label>
              <div className="flex gap-2">
                <Input
                  id="chave_pix"
                  value={formData.chave_pix}
                  onChange={(e) => setFormData({ ...formData, chave_pix: e.target.value })}
                  placeholder="CPF, CNPJ, E-mail, Telefone ou Chave Aleatória"
                  data-testid="company-chave-pix"
                  className="flex-1"
                />
                <Button 
                  type="button" 
                  variant="outline" 
                  size="icon"
                  onClick={() => {
                    if (formData.chave_pix) {
                      navigator.clipboard.writeText(formData.chave_pix);
                      toast({ title: "Copiado!", description: "Chave PIX copiada para a área de transferência" });
                    }
                  }}
                  disabled={!formData.chave_pix}
                  title="Copiar Chave PIX"
                >
                  <Copy className="w-4 h-4" />
                </Button>
              </div>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <Label htmlFor="responsavel_1">Responsável 1</Label>
                <Input
                  id="responsavel_1"
                  value={formData.responsavel_1}
                  onChange={(e) => setFormData({ ...formData, responsavel_1: e.target.value })}
                  placeholder="Nome do responsável"
                  data-testid="company-responsavel-1"
                />
              </div>
              <div>
                <Label htmlFor="responsavel_2">Responsável 2</Label>
                <Input
                  id="responsavel_2"
                  value={formData.responsavel_2}
                  onChange={(e) => setFormData({ ...formData, responsavel_2: e.target.value })}
                  placeholder="Nome do responsável"
                  data-testid="company-responsavel-2"
                />
              </div>
            </div>
            <div className="space-y-2">
              <Label htmlFor="saldo_inicial">Saldo Inicial (R$)</Label>
              <Input
                id="saldo_inicial"
                type="number"
                step="0.01"
                value={formData.saldo_inicial}
                onChange={(e) => setFormData({ ...formData, saldo_inicial: e.target.value })}
                placeholder="0.00"
                data-testid="company-saldo-inicial"
              />
              <p className="text-xs text-muted-foreground">Saldo inicial para cálculos nos relatórios de Fluxo de Caixa, Projeção e DRE</p>
            </div>
            
            {/* Conciliação de Contas - Mostrar apenas para empresas NÃO pessoais */}
            {(!editingCompany || !editingCompany.is_personal) && (
              <Accordion type="single" collapsible className="w-full border rounded-lg">
                <AccordionItem value="conciliacao" className="border-0">
                  <AccordionTrigger className="px-4 hover:no-underline">
                    <div className="flex items-center gap-2">
                      <RefreshCw className="w-4 h-4 text-primary" />
                      <span>Conciliação de Contas</span>
                    </div>
                  </AccordionTrigger>
                  <AccordionContent className="px-4 pb-4">
                    <div className="space-y-4">
                      <div className="space-y-2">
                        <Label>Tipo de Conciliação</Label>
                        <Select value={formData.conciliation_type} onValueChange={(v) => setFormData({...formData, conciliation_type: v})}>
                          <SelectTrigger data-testid="company-conciliation-type">
                            <SelectValue placeholder="Selecione" />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="OFF">Desligado</SelectItem>
                            <SelectItem value="MIRROR">Aparecer na Conta Principal</SelectItem>
                            <SelectItem value="ACCUMULATE">Acumular na Conta Principal</SelectItem>
                          </SelectContent>
                        </Select>
                        <p className="text-xs text-muted-foreground">
                          {formData.conciliation_type === 'OFF' && 'Os lançamentos desta conta não afetam a Conta Principal'}
                          {formData.conciliation_type === 'MIRROR' && 'Cada lançamento será espelhado individualmente na Conta Principal'}
                          {formData.conciliation_type === 'ACCUMULATE' && 'O total mensal de receitas/despesas será lançado na Conta Principal'}
                        </p>
                      </div>
                      
                      {formData.conciliation_type === 'ACCUMULATE' && (
                        <div className="space-y-2">
                          <Label htmlFor="accumulated_day">Dia do Lançamento Acumulado</Label>
                          <Input
                            id="accumulated_day"
                            type="number"
                            min="1"
                            max="31"
                            value={formData.accumulated_day}
                            onChange={(e) => setFormData({ ...formData, accumulated_day: e.target.value })}
                            placeholder="Ex: 10"
                            data-testid="company-accumulated-day"
                          />
                          <p className="text-xs text-muted-foreground">Dia do mês em que os valores acumulados serão lançados na Conta Principal</p>
                        </div>
                      )}
                      
                      <div className="space-y-2">
                        <Label htmlFor="closing_day">Dia de Fechamento (para despesas)</Label>
                        <Input
                          id="closing_day"
                          type="number"
                          min="1"
                          max="31"
                          value={formData.closing_day}
                          onChange={(e) => setFormData({ ...formData, closing_day: e.target.value })}
                          placeholder="Ex: 25"
                          data-testid="company-closing-day"
                        />
                        <p className="text-xs text-muted-foreground">Despesas lançadas após este dia terão vencimento no mês seguinte (ideal para cartões de crédito)</p>
                      </div>
                    </div>
                  </AccordionContent>
                </AccordionItem>
              </Accordion>
            )}
            
            <div className="flex justify-end gap-2 pt-4">
              <Button type="button" variant="outline" onClick={() => setIsDialogOpen(false)}>
                Cancelar
              </Button>
              <Button type="submit" data-testid="save-company-btn">
                {editingCompany ? 'Salvar' : 'Criar'}
              </Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <Dialog open={!!showDeleteConfirm} onOpenChange={() => setShowDeleteConfirm(null)}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle className="text-red-600">Confirmar Exclusão</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <p>
              Tem certeza que deseja excluir a empresa <strong>{showDeleteConfirm?.razao_social}</strong>?
            </p>
            <div className="p-4 bg-red-50 border border-red-200 rounded-lg text-sm text-red-800">
              <p className="font-semibold">Esta ação irá excluir permanentemente:</p>
              <ul className="list-disc list-inside mt-2">
                <li>Todos os clientes desta empresa</li>
                <li>Todos os fornecedores desta empresa</li>
                <li>Todos os produtos desta empresa</li>
                <li>Todas as carteiras desta empresa</li>
                <li>Todas as transações (receitas, despesas, custos)</li>
              </ul>
            </div>
            <div className="flex justify-end gap-2">
              <Button variant="outline" onClick={() => setShowDeleteConfirm(null)}>
                Cancelar
              </Button>
              <Button 
                variant="destructive" 
                onClick={() => handleDelete(showDeleteConfirm)}
                data-testid="confirm-delete-company"
              >
                Excluir Empresa
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Modal de Confirmação de Ressincronização de Conciliação */}
      <Dialog open={!!showConciliationConfirm} onOpenChange={() => setShowConciliationConfirm(null)}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <RefreshCw className="w-5 h-5 text-amber-500" />
              Confirmar Alteração de Conciliação
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <p>
              Você está alterando o tipo de conciliação da empresa <strong>{showConciliationConfirm?.companyName}</strong>.
            </p>
            <div className="p-4 bg-amber-50 border border-amber-200 rounded-lg text-sm text-amber-800">
              <p className="font-semibold mb-2">O que vai acontecer:</p>
              <ul className="list-disc list-inside space-y-1">
                <li>As transações espelho/acumuladas existentes serão <strong>removidas</strong></li>
                <li>Novas transações serão criadas conforme a nova configuração</li>
                <li>Este processo é automático e pode levar alguns segundos</li>
              </ul>
            </div>
            <div className="p-3 bg-slate-100 rounded-lg text-sm">
              <div className="flex items-center gap-2">
                <span className="text-muted-foreground">De:</span>
                <span className="font-medium">
                  {showConciliationConfirm?.oldType === 'OFF' && 'Desligado'}
                  {showConciliationConfirm?.oldType === 'MIRROR' && 'Aparecer na Conta Principal'}
                  {showConciliationConfirm?.oldType === 'ACCUMULATE' && 'Acumular na Conta Principal'}
                </span>
              </div>
              <div className="flex items-center gap-2 mt-1">
                <span className="text-muted-foreground">Para:</span>
                <span className="font-medium text-primary">
                  {showConciliationConfirm?.newType === 'OFF' && 'Desligado'}
                  {showConciliationConfirm?.newType === 'MIRROR' && 'Aparecer na Conta Principal'}
                  {showConciliationConfirm?.newType === 'ACCUMULATE' && 'Acumular na Conta Principal'}
                </span>
              </div>
            </div>
            <div className="flex justify-end gap-2">
              <Button variant="outline" onClick={() => setShowConciliationConfirm(null)}>
                Cancelar
              </Button>
              <Button 
                onClick={handleConfirmConciliationChange}
                data-testid="confirm-conciliation-change"
              >
                Confirmar e Ressincronizar
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

// ==================== AUDIT LOGS PAGE ====================
const AuditLogsPage = ({ onNavigate, companyId, companies = [] }) => {
  const [logs, setLogs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filters, setFilters] = useState({
    company_id: '',
    log_type: '',
    action: '',
    start_date: '',
    end_date: ''
  });
  const { toast } = useToast();

  useEffect(() => {
    loadLogs();
  }, [filters]);

  const loadLogs = async () => {
    try {
      setLoading(true);
      let url = `${API}/audit-logs?limit=200`;
      if (filters.company_id) url += `&company_id=${filters.company_id}`;
      if (filters.log_type) url += `&log_type=${filters.log_type}`;
      if (filters.action) url += `&action=${filters.action}`;
      if (filters.start_date) url += `&start_date=${filters.start_date}`;
      if (filters.end_date) url += `&end_date=${filters.end_date}`;
      
      const response = await axios.get(url);
      setLogs(response.data.logs || []);
    } catch (error) {
      toast({ title: "Erro", description: "Erro ao carregar logs", variant: "destructive" });
    } finally {
      setLoading(false);
    }
  };

  const formatDateTime = (dateStr) => {
    if (!dateStr) return '-';
    const date = new Date(dateStr);
    return date.toLocaleString('pt-BR', { 
      day: '2-digit', month: '2-digit', year: 'numeric',
      hour: '2-digit', minute: '2-digit'
    });
  };

  const getActionColor = (action) => {
    switch (action) {
      case 'INCLUSAO': return 'bg-green-100 text-green-800';
      case 'ALTERACAO': return 'bg-blue-100 text-blue-800';
      case 'EXCLUSAO': return 'bg-red-100 text-red-800';
      case 'VISUALIZACAO': return 'bg-gray-100 text-gray-800';
      case 'IMPRESSAO': return 'bg-purple-100 text-purple-800';
      default: return 'bg-gray-100 text-gray-800';
    }
  };

  const getTypeColor = (type) => {
    switch (type) {
      case 'CADASTRO': return 'bg-indigo-100 text-indigo-800';
      case 'LANCAMENTO': return 'bg-teal-100 text-teal-800';
      case 'RELATORIO': return 'bg-amber-100 text-amber-800';
      case 'DRE': return 'bg-cyan-100 text-cyan-800';
      default: return 'bg-gray-100 text-gray-800';
    }
  };

  const logTypes = ['CADASTRO', 'LANCAMENTO', 'RELATORIO', 'DRE', 'IMPRESSAO'];
  const actions = ['INCLUSAO', 'ALTERACAO', 'EXCLUSAO', 'VISUALIZACAO', 'IMPRESSAO'];

  return (
    <div className="animate-fadeIn space-y-6" data-testid="audit-logs-page">
      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
        <div>
          <h2 className="text-3xl font-semibold tracking-tight flex items-center gap-2">
            <FileText className="w-8 h-8" />
            Logs de Auditoria
          </h2>
          <p className="text-muted-foreground">
            Histórico de ações realizadas no sistema
          </p>
        </div>
        <Button onClick={loadLogs} variant="outline" size="sm">
          Atualizar
        </Button>
      </div>

      {/* Filters */}
      <Card>
        <CardContent className="p-4">
          <div className="grid grid-cols-1 md:grid-cols-5 gap-4">
            <div>
              <Label className="text-xs">Empresa</Label>
              <Select value={filters.company_id} onValueChange={(v) => setFilters({...filters, company_id: v === 'all' ? '' : v})}>
                <SelectTrigger>
                  <SelectValue placeholder="Todas" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">Todas</SelectItem>
                  {companies.map((c) => (
                    <SelectItem key={c.id} value={c.id}>{c.razao_social}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-xs">Tipo</Label>
              <Select value={filters.log_type} onValueChange={(v) => setFilters({...filters, log_type: v === 'all' ? '' : v})}>
                <SelectTrigger>
                  <SelectValue placeholder="Todos" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">Todos</SelectItem>
                  {logTypes.map((t) => (
                    <SelectItem key={t} value={t}>{t}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-xs">Ação</Label>
              <Select value={filters.action} onValueChange={(v) => setFilters({...filters, action: v === 'all' ? '' : v})}>
                <SelectTrigger>
                  <SelectValue placeholder="Todas" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">Todas</SelectItem>
                  {actions.map((a) => (
                    <SelectItem key={a} value={a}>{a}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-xs">Data Início</Label>
              <Input 
                type="date" 
                value={filters.start_date}
                onChange={(e) => setFilters({...filters, start_date: e.target.value})}
              />
            </div>
            <div>
              <Label className="text-xs">Data Fim</Label>
              <Input 
                type="date" 
                value={filters.end_date}
                onChange={(e) => setFilters({...filters, end_date: e.target.value})}
              />
            </div>
          </div>
        </CardContent>
      </Card>

      {loading ? (
        <div className="flex justify-center py-12">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
        </div>
      ) : logs.length === 0 ? (
        <div className="text-center py-12 text-muted-foreground">
          <FileText className="w-12 h-12 mx-auto mb-4 opacity-50" />
          <p>Nenhum log encontrado</p>
        </div>
      ) : (
        <div className="overflow-x-auto">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Data/Hora</TableHead>
                <TableHead>Usuário</TableHead>
                <TableHead>Empresa</TableHead>
                <TableHead>Tipo</TableHead>
                <TableHead>Ação</TableHead>
                <TableHead>Descrição</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {logs.map((log) => (
                <TableRow key={log.id}>
                  <TableCell className="whitespace-nowrap text-sm">
                    {formatDateTime(log.created_at)}
                  </TableCell>
                  <TableCell className="text-sm">{log.user_name || '-'}</TableCell>
                  <TableCell className="text-sm">{log.company_name || '-'}</TableCell>
                  <TableCell>
                    <Badge className={`text-xs ${getTypeColor(log.log_type)}`}>
                      {log.log_type}
                    </Badge>
                  </TableCell>
                  <TableCell>
                    <Badge className={`text-xs ${getActionColor(log.action)}`}>
                      {log.action}
                    </Badge>
                  </TableCell>
                  <TableCell className="text-sm max-w-xs truncate">{log.option}</TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>
      )}
    </div>
  );
};

// ==================== PROFILE PAGE ====================
const ProfilePage = ({ user, onNavigate }) => {
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [showSuccessModal, setShowSuccessModal] = useState(false);
  const [profile, setProfile] = useState({
    name: '',
    email: '',
    cpf_cnpj: '',
    phone: '',
    address: '',
    address_number: '',
    address_complement: '',
    pix_key: '',
    google_email: ''
  });
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const { toast } = useToast();
  const isMobile = useIsMobile();

  useEffect(() => {
    loadProfile();
  }, []);

  const loadProfile = async () => {
    try {
      const response = await axios.get(`${API}/auth/profile`);
      setProfile({
        name: response.data.name || '',
        email: response.data.email || '',
        cpf_cnpj: response.data.cpf_cnpj || '',
        phone: response.data.phone || '',
        address: response.data.address || '',
        address_number: response.data.address_number || '',
        address_complement: response.data.address_complement || '',
        pix_key: response.data.pix_key || '',
        google_email: response.data.google_email || ''
      });
    } catch (error) {
      toast({ title: "Erro ao carregar perfil", variant: "destructive" });
    } finally {
      setLoading(false);
    }
  };

  // Format CPF/CNPJ
  const formatCpfCnpj = (value) => {
    const numbers = value.replace(/\D/g, '');
    if (numbers.length <= 11) {
      // CPF format: 000.000.000-00
      return numbers
        .replace(/(\d{3})(\d)/, '$1.$2')
        .replace(/(\d{3})(\d)/, '$1.$2')
        .replace(/(\d{3})(\d{1,2})$/, '$1-$2');
    } else {
      // CNPJ format: 00.000.000/0000-00
      return numbers
        .replace(/(\d{2})(\d)/, '$1.$2')
        .replace(/(\d{3})(\d)/, '$1.$2')
        .replace(/(\d{3})(\d)/, '$1/$2')
        .replace(/(\d{4})(\d{1,2})$/, '$1-$2');
    }
  };

  // Format phone
  const formatPhoneNumber = (value) => {
    const numbers = value.replace(/\D/g, '');
    if (numbers.length <= 10) {
      return numbers
        .replace(/(\d{2})(\d)/, '($1) $2')
        .replace(/(\d{4})(\d{1,4})$/, '$1-$2');
    } else {
      return numbers
        .replace(/(\d{2})(\d)/, '($1) $2')
        .replace(/(\d{5})(\d{1,4})$/, '$1-$2');
    }
  };

  const handleSave = async () => {
    // Validate password if provided
    if (password || confirmPassword) {
      if (password !== confirmPassword) {
        toast({ title: "As senhas não coincidem", variant: "destructive" });
        return;
      }
      if (password.length < 6) {
        toast({ title: "A senha deve ter pelo menos 6 caracteres", variant: "destructive" });
        return;
      }
    }

    // Validate Google email if provided
    if (profile.google_email && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(profile.google_email)) {
      toast({ title: "E-mail do Google inválido", variant: "destructive" });
      return;
    }

    setSaving(true);
    try {
      const updateData = {
        name: profile.name,
        cpf_cnpj: profile.cpf_cnpj,
        phone: profile.phone,
        address: profile.address,
        address_number: profile.address_number,
        address_complement: profile.address_complement,
        pix_key: profile.pix_key,
        google_email: profile.google_email
      };

      if (password) {
        updateData.password = password;
      }

      await axios.put(`${API}/auth/profile`, updateData);
      setPassword('');
      setConfirmPassword('');
      setShowSuccessModal(true);
    } catch (error) {
      toast({ 
        title: error.response?.data?.detail || "Erro ao atualizar perfil", 
        variant: "destructive" 
      });
    } finally {
      setSaving(false);
    }
  };

  const handleSuccessOk = () => {
    setShowSuccessModal(false);
    if (onNavigate) {
      onNavigate('dashboard');
    }
  };

  if (loading) {
    return (
      <div className="flex justify-center py-12">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
      </div>
    );
  }

  return (
    <div className="animate-fadeIn space-y-6" data-testid="profile-page">
      <div className="flex items-start justify-between">
        <div>
          <h2 className="text-2xl md:text-3xl font-semibold tracking-tight">Meu Perfil</h2>
          <p className="text-muted-foreground">Gerencie suas informações pessoais</p>
        </div>
        <button
          onClick={() => onNavigate && onNavigate('dashboard')}
          className="p-2 rounded-lg hover:bg-muted transition-colors text-muted-foreground hover:text-foreground"
          title="Fechar sem salvar"
          data-testid="close-profile-btn"
        >
          <XCircle className="w-6 h-6" />
        </button>
      </div>

      <Card>
        <CardHeader>
          <CardTitle className="text-lg">Meus Dados</CardTitle>
        </CardHeader>
        <CardContent className="space-y-6">
          {/* Nome */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <Label>Nome *</Label>
              <Input
                value={profile.name}
                onChange={(e) => setProfile({ ...profile, name: e.target.value })}
                placeholder="Seu nome completo"
                data-testid="profile-name"
              />
            </div>
            
            {/* CPF/CNPJ */}
            <div>
              <Label>CPF / CNPJ</Label>
              <Input
                value={profile.cpf_cnpj}
                onChange={(e) => setProfile({ ...profile, cpf_cnpj: formatCpfCnpj(e.target.value) })}
                placeholder="000.000.000-00 ou 00.000.000/0000-00"
                maxLength={18}
                data-testid="profile-cpf-cnpj"
              />
            </div>
          </div>

          {/* Email e Celular */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <Label>E-mail (não editável)</Label>
              <Input
                value={profile.email}
                disabled
                className="bg-muted cursor-not-allowed"
                data-testid="profile-email"
              />
              <p className="text-xs text-muted-foreground mt-1">O e-mail de login não pode ser alterado</p>
            </div>
            
            <div>
              <Label>Celular</Label>
              <Input
                value={profile.phone}
                onChange={(e) => setProfile({ ...profile, phone: formatPhoneNumber(e.target.value) })}
                placeholder="(00) 00000-0000"
                maxLength={15}
                data-testid="profile-phone"
              />
            </div>
          </div>

          {/* Endereço */}
          <div className="space-y-4">
            <Label className="text-base font-medium">Endereço</Label>
            <div className="grid grid-cols-1 md:grid-cols-6 gap-4">
              <div className="md:col-span-4">
                <Label className="text-xs text-muted-foreground">Logradouro</Label>
                <Input
                  value={profile.address}
                  onChange={(e) => setProfile({ ...profile, address: e.target.value })}
                  placeholder="Rua, Avenida, etc."
                  data-testid="profile-address"
                />
              </div>
              <div className="md:col-span-1">
                <Label className="text-xs text-muted-foreground">Número</Label>
                <Input
                  value={profile.address_number}
                  onChange={(e) => setProfile({ ...profile, address_number: e.target.value })}
                  placeholder="Nº"
                  data-testid="profile-address-number"
                />
              </div>
              <div className="md:col-span-1">
                <Label className="text-xs text-muted-foreground">Complemento</Label>
                <Input
                  value={profile.address_complement}
                  onChange={(e) => setProfile({ ...profile, address_complement: e.target.value })}
                  placeholder="Apto, Sala"
                  data-testid="profile-address-complement"
                />
              </div>
            </div>
          </div>

          {/* Chave PIX */}
          <div>
            <Label>Chave PIX</Label>
            <Input
              value={profile.pix_key}
              onChange={(e) => setProfile({ ...profile, pix_key: e.target.value })}
              placeholder="CPF, E-mail, Celular ou Chave Aleatória"
              className="font-mono"
              data-testid="profile-pix"
            />
            <p className="text-xs text-muted-foreground mt-1">Pode ser CPF, e-mail, celular ou chave aleatória</p>
          </div>

          {/* Email do Google */}
          <div>
            <Label>E-mail do Google</Label>
            <Input
              type="email"
              value={profile.google_email}
              onChange={(e) => setProfile({ ...profile, google_email: e.target.value })}
              placeholder="seu.email@gmail.com"
              data-testid="profile-google-email"
            />
          </div>

          <Separator />

          {/* Alterar Senha */}
          <div className="space-y-4">
            <Label className="text-base font-medium">Alterar Senha</Label>
            <p className="text-sm text-muted-foreground">Deixe em branco para manter a senha atual</p>
            
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <Label className="text-xs text-muted-foreground">Nova Senha</Label>
                <div className="relative">
                  <Input
                    type={showPassword ? "text" : "password"}
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    placeholder="Mínimo 6 caracteres"
                    className="pr-10"
                    data-testid="profile-password"
                  />
                  <button
                    type="button"
                    onClick={() => setShowPassword(!showPassword)}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground"
                  >
                    {showPassword ? <EyeOffIcon className="w-4 h-4" /> : <EyeIcon className="w-4 h-4" />}
                  </button>
                </div>
              </div>
              <div>
                <Label className="text-xs text-muted-foreground">Confirmar Nova Senha</Label>
                <div className="relative">
                  <Input
                    type={showConfirmPassword ? "text" : "password"}
                    value={confirmPassword}
                    onChange={(e) => setConfirmPassword(e.target.value)}
                    placeholder="Confirme a nova senha"
                    className="pr-10"
                    data-testid="profile-confirm-password"
                  />
                  <button
                    type="button"
                    onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground"
                  >
                    {showConfirmPassword ? <EyeOffIcon className="w-4 h-4" /> : <EyeIcon className="w-4 h-4" />}
                  </button>
                </div>
              </div>
            </div>
          </div>

          <Separator />

          {/* Save Button */}
          <div className="flex justify-end">
            <Button 
              onClick={handleSave} 
              disabled={saving}
              className="w-full md:w-auto"
              data-testid="save-profile-btn"
            >
              {saving ? (
                <div className="flex items-center gap-2">
                  <div className="animate-spin rounded-full h-4 w-4 border-2 border-background border-t-transparent"></div>
                  Salvando...
                </div>
              ) : (
                'Salvar Alterações'
              )}
            </Button>
          </div>
        </CardContent>
      </Card>

      {/* Success Modal */}
      <Dialog open={showSuccessModal} onOpenChange={setShowSuccessModal}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="text-center text-xl">✓</DialogTitle>
          </DialogHeader>
          <div className="text-center py-4">
            <p className="text-lg">Alterações efetuadas com sucesso!!</p>
          </div>
          <div className="flex justify-center">
            <Button 
              onClick={handleSuccessOk}
              className="px-8"
              data-testid="success-ok-btn"
            >
              OK
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

// ==================== MAIN LAYOUT ====================
const MainLayout = ({ user, initialPage }) => {
  const [currentPage, setCurrentPage] = useState(initialPage || 'dashboard');
  const [showLockedModal, setShowLockedModal] = useState(false);
  const [lockedPlanRequired, setLockedPlanRequired] = useState(null);
  const [showExpirationModal, setShowExpirationModal] = useState(false);
  const [upsellExpanded, setUpsellExpanded] = useState(false);
  const [showInstallPrompt, setShowInstallPrompt] = useState(false);
  const [deferredInstallPrompt, setDeferredInstallPrompt] = useState(null);
  const [showOverdueExpenses, setShowOverdueExpenses] = useState(false);
  
  // Company state
  const [companies, setCompanies] = useState([]);
  const [selectedCompany, setSelectedCompany] = useState(null);
  const [companyLimit, setCompanyLimit] = useState(2);
  const [loadingCompanies, setLoadingCompanies] = useState(true);
  
  const { toast } = useToast();
  
  // Custom navigation handler that can pass extra state
  const handleNavigate = (page, options = {}) => {
    if (options.showOverdue) {
      setShowOverdueExpenses(true);
    } else {
      setShowOverdueExpenses(false);
    }
    setCurrentPage(page);
  };
  
  // Reset overdue filter when leaving despesas page
  useEffect(() => {
    if (currentPage !== 'despesas') {
      setShowOverdueExpenses(false);
    }
  }, [currentPage]);
  
  // Get user plan from user object (default to 1)
  const userPlan = user?.plan || 1;
  const userPlanName = user?.plan_name || PLANS[userPlan]?.name || "Básico";

  // Load companies on mount
  useEffect(() => {
    const loadCompanies = async () => {
      try {
        setLoadingCompanies(true);
        const response = await axios.get(`${API}/companies`);
        const companiesData = response.data.companies || [];
        setCompanies(companiesData);
        setCompanyLimit(response.data.limit || 2);
        
        // Load selected company from localStorage or use first (personal) company
        const savedCompanyId = localStorage.getItem(LS_SELECTED_COMPANY);
        const savedCompany = companiesData.find(c => c.id === savedCompanyId);
        
        if (savedCompany) {
          setSelectedCompany(savedCompany);
        } else if (companiesData.length > 0) {
          setSelectedCompany(companiesData[0]); // Default to personal company
          localStorage.setItem(LS_SELECTED_COMPANY, companiesData[0].id);
        }
      } catch (error) {
        console.error('Error loading companies:', error);
      } finally {
        setLoadingCompanies(false);
      }
    };
    
    if (user) {
      loadCompanies();
    }
  }, [user]);
  
  // Handle company change
  const handleCompanyChange = (companyId) => {
    const company = companies.find(c => c.id === companyId);
    if (company) {
      setSelectedCompany(company);
      localStorage.setItem(LS_SELECTED_COMPANY, companyId);
    }
  };
  
  // Refresh companies list
  const refreshCompanies = async () => {
    try {
      const response = await axios.get(`${API}/companies`);
      const companiesData = response.data.companies || [];
      setCompanies(companiesData);
      setCompanyLimit(response.data.limit || 2);
      
      // Update selected company if it still exists
      if (selectedCompany) {
        const updated = companiesData.find(c => c.id === selectedCompany.id);
        if (updated) {
          setSelectedCompany(updated);
        } else if (companiesData.length > 0) {
          setSelectedCompany(companiesData[0]);
          localStorage.setItem(LS_SELECTED_COMPANY, companiesData[0].id);
        }
      }
    } catch (error) {
      console.error('Error refreshing companies:', error);
    }
  };

  // Sub-user permissions - ocultar menus baseado nas permissões
  const isSubUser = user?.is_sub_user === true;
  const subUserPermissions = user?.permissions || {};
  
  // Função para verificar se o usuário pode acessar um menu
  const canAccessMenu = (menuId) => {
    // Master sempre pode acessar tudo
    if (!isSubUser) return true;
    
    // Menus sempre liberados para sub-usuários
    const alwaysAllowed = ['dashboard', 'profile', 'relatorios', 'relatorio-despesas', 'relatorio-receitas', 
                          'relatorio-custos', 'relatorio-fluxo', 'dre', 'suporte', 'chat-ajuda'];
    if (alwaysAllowed.includes(menuId)) return true;
    
    // Cadastros
    const registerMenus = ['cadastros', 'administrativo', 'clients', 'suppliers', 'products', 'employees', 
                          'empresas', 'wallets'];
    if (registerMenus.includes(menuId)) {
      return subUserPermissions.can_manage_registers === true;
    }
    
    // Lançamentos
    if (menuId === 'despesas') return subUserPermissions.can_manage_expenses === true;
    if (menuId === 'receitas') return subUserPermissions.can_manage_revenues === true;
    if (menuId === 'custos') return subUserPermissions.can_manage_costs === true;
    
    // Vendas
    if (menuId === 'frente-caixa') return subUserPermissions.can_manage_pos === true;
    if (menuId === 'orcamentos') return subUserPermissions.can_manage_quotes === true;
    if (menuId === 'recibos') return subUserPermissions.can_manage_receipts === true;
    
    // Sub-usuários - apenas master pode acessar
    if (menuId === 'sub-users') return false;
    
    // Vendas submenu - verificar se tem alguma permissão de vendas
    if (menuId === 'vendas') {
      return subUserPermissions.can_manage_pos === true || 
             subUserPermissions.can_manage_quotes === true || 
             subUserPermissions.can_manage_receipts === true;
    }
    
    // Lançamentos submenu - verificar se tem alguma permissão de lançamentos
    if (menuId === 'lancamentos') {
      return subUserPermissions.can_manage_expenses === true || 
             subUserPermissions.can_manage_revenues === true || 
             subUserPermissions.can_manage_costs === true ||
             subUserPermissions.can_manage_pos === true ||
             subUserPermissions.can_manage_quotes === true ||
             subUserPermissions.can_manage_receipts === true;
    }
    
    // Default: permitir
    return true;
  };
  
  // Filtrar menuItems baseado nas permissões
  const filterMenuItems = (items) => {
    if (!isSubUser) return items; // Master vê tudo
    
    return items
      .filter(item => {
        // Se é item "masterOnly", só master pode ver
        if (item.masterOnly) return false;
        
        // Verificar permissão do menu
        return canAccessMenu(item.id);
      })
      .map(item => {
        // Se tem submenu, filtrar também
        if (item.submenu) {
          const filteredSubmenu = item.submenu
            .filter(subItem => {
              if (subItem.masterOnly) return false;
              if (subItem.isSeparator) return true; // Manter separadores
              return canAccessMenu(subItem.id);
            })
            .map(subItem => {
              // Se tem children (submenu interno), filtrar também
              if (subItem.children) {
                const filteredChildren = subItem.children.filter(child => {
                  if (child.masterOnly) return false;
                  return canAccessMenu(child.id);
                });
                return filteredChildren.length > 0 ? { ...subItem, children: filteredChildren } : null;
              }
              return subItem;
            })
            .filter(Boolean); // Remove nulls
          
          // Remover separadores órfãos (no início/fim ou consecutivos)
          const cleanedSubmenu = filteredSubmenu.filter((item, idx, arr) => {
            if (!item.isSeparator) return true;
            // Remove separador se é o primeiro, último, ou se o próximo também é separador
            if (idx === 0 || idx === arr.length - 1) return false;
            if (arr[idx + 1]?.isSeparator) return false;
            return true;
          });
          
          // Se não tem itens no submenu, ocultar o menu pai
          if (cleanedSubmenu.length === 0) return null;
          
          return { ...item, submenu: cleanedSubmenu };
        }
        
        // Se tem children diretamente, filtrar
        if (item.children) {
          const filteredChildren = item.children.filter(child => {
            if (child.masterOnly) return false;
            return canAccessMenu(child.id);
          });
          if (filteredChildren.length === 0) return null;
          return { ...item, children: filteredChildren };
        }
        
        return item;
      })
      .filter(Boolean); // Remove nulls
  };
  
  // Aplicar filtro aos menuItems
  const filteredMenuItems = filterMenuItems(GLOBAL_MENU_ITEMS);

  // PWA Back button handler - prevent accidental app close on mobile
  const [showExitConfirm, setShowExitConfirm] = useState(false);
  const [navigationHistory, setNavigationHistory] = useState(['dashboard']);
  
  // Track navigation history
  useEffect(() => {
    if (currentPage !== navigationHistory[navigationHistory.length - 1]) {
      setNavigationHistory(prev => [...prev, currentPage]);
    }
  }, [currentPage]);
  
  // Handle hardware back button (mobile)
  useEffect(() => {
    // Check if running as PWA (standalone mode)
    const isPWA = window.matchMedia('(display-mode: standalone)').matches || 
                  window.navigator.standalone === true;
    
    if (!isPWA) return;
    
    // Push initial state to prevent immediate exit
    const pushState = () => {
      window.history.pushState({ page: 'app' }, '', window.location.href);
    };
    
    pushState();
    
    const handlePopState = (event) => {
      // Always push state back to prevent exit
      pushState();
      
      if (navigationHistory.length > 1) {
        // Go back to previous page
        const newHistory = [...navigationHistory];
        newHistory.pop(); // Remove current page
        const previousPage = newHistory[newHistory.length - 1] || 'dashboard';
        setNavigationHistory(newHistory);
        setCurrentPage(previousPage);
      } else {
        // On dashboard/first page - show exit confirmation
        setShowExitConfirm(true);
      }
    };
    
    window.addEventListener('popstate', handlePopState);
    
    return () => {
      window.removeEventListener('popstate', handlePopState);
    };
  }, [navigationHistory]);
  
  // Handle exit confirmation
  const handleExitApp = () => {
    setShowExitConfirm(false);
    // Close the PWA by navigating away
    window.location.href = 'about:blank';
  };

  // PWA Install prompt for mobile - show on first visit
  useEffect(() => {
    // Check if mobile
    const isMobile = /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(navigator.userAgent) 
                     || window.innerWidth < 768;
    
    // Check if already installed as PWA
    const isStandalone = window.matchMedia('(display-mode: standalone)').matches 
                        || window.navigator.standalone === true;
    
    // Check if we already showed the prompt
    const alreadyPrompted = localStorage.getItem('dre_pwa_prompted');
    
    // Only show on mobile, not installed, and not prompted before
    // Also wait until expiration modal is not showing
    if (isMobile && !isStandalone && !alreadyPrompted && !showExpirationModal) {
      // Listen for the beforeinstallprompt event
      const handleBeforeInstallPrompt = (e) => {
        e.preventDefault();
        setDeferredInstallPrompt(e);
        // Show our custom prompt after a delay (let expiration modal close first)
        setTimeout(() => {
          if (!showExpirationModal) {
            setShowInstallPrompt(true);
          }
        }, 2500);
      };
      
      window.addEventListener('beforeinstallprompt', handleBeforeInstallPrompt);
      
      // For browsers that don't fire beforeinstallprompt, still show our prompt
      // with manual instructions - but only after expiration modal
      const timer = setTimeout(() => {
        if (!deferredInstallPrompt && !showExpirationModal) {
          setShowInstallPrompt(true);
        }
      }, 4000);
      
      return () => {
        window.removeEventListener('beforeinstallprompt', handleBeforeInstallPrompt);
        clearTimeout(timer);
      };
    }
  }, [showExpirationModal]);

  const handleInstallClick = async () => {
    localStorage.setItem('dre_pwa_prompted', 'true');
    
    if (deferredInstallPrompt) {
      // Use native install prompt
      deferredInstallPrompt.prompt();
      const { outcome } = await deferredInstallPrompt.userChoice;
      if (outcome === 'accepted') {
        toast({ title: "Instalando o DRE+..." });
      }
      setDeferredInstallPrompt(null);
    } else {
      // Show manual instructions
      toast({ 
        title: "Como instalar o DRE+",
        description: "Toque no menu do navegador (⋮) e selecione 'Adicionar à tela inicial' ou 'Instalar app'.",
      });
    }
    setShowInstallPrompt(false);
  };

  const handleCloseInstallPrompt = () => {
    localStorage.setItem('dre_pwa_prompted', 'true');
    setShowInstallPrompt(false);
  };

  // Check if plan is expired
  const isPlanExpired = () => {
    if (!user?.plan_expiry) return false;
    const expiryDate = new Date(user.plan_expiry);
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    expiryDate.setHours(0, 0, 0, 0);
    return expiryDate < today;
  };

  // Show expiration modal on mount if plan is expired
  useEffect(() => {
    // Check expiration when user data is available
    if (user && user.plan_expiry) {
      const checkExpiration = () => {
        if (isPlanExpired()) {
          setShowExpirationModal(true);
        }
      };
      // Small delay to let the dashboard render first
      const timer = setTimeout(checkExpiration, 1500);
      return () => clearTimeout(timer);
    }
  }, [user]);

  // Handle expanding the upsell section from expiration modal
  const handleExpandUpsell = () => {
    setCurrentPage('dashboard');
    setUpsellExpanded(true);
    // Scroll to upsell section after a short delay to allow rendering
    setTimeout(() => {
      const upsellSection = document.getElementById('plan-upsell-section');
      if (upsellSection) {
        upsellSection.scrollIntoView({ behavior: 'smooth', block: 'center' });
      }
    }, 500);
  };

  // Handle locked menu click
  const handleLockedMenuClick = (requiredPlan, menuId) => {
    setLockedPlanRequired(requiredPlan);
    setShowLockedModal(true);
  };

  // Handle contract button click (placeholder for now)
  const handleContractClick = (planId) => {
    toast({ 
      title: `Contratação do Plano ${PLANS[planId]?.name}`,
      description: "Funcionalidade em desenvolvimento. Em breve você poderá contratar!"
    });
  };

  // Render page content directly based on currentPage
  const pageContent = (() => {
    const companyId = selectedCompany?.id;
    switch (currentPage) {
      case 'dashboard': return <Dashboard user={user} userPlan={userPlan} onContractClick={handleContractClick} upsellExpanded={upsellExpanded} setUpsellExpanded={setUpsellExpanded} onNavigate={handleNavigate} companyId={companyId} selectedCompany={selectedCompany} companies={companies} onCompanyChange={handleCompanyChange} loadingCompanies={loadingCompanies} />;
      case 'profile': return <ProfilePage user={user} onNavigate={setCurrentPage} />;
      case 'receitas': return <TransactionPage transactionType="RECEITA" title="Receitas" colorClass="text-emerald-500" onNavigate={setCurrentPage} companyId={companyId} />;
      case 'despesas': return <TransactionPage transactionType="DESPESA" title="Despesas" colorClass="text-red-500" onNavigate={setCurrentPage} companyId={companyId} initialShowOverdue={showOverdueExpenses} />;
      case 'custos': return <TransactionPage transactionType="CUSTO" title="Custos" colorClass="text-amber-500" onNavigate={setCurrentPage} companyId={companyId} />;
      case 'frente-caixa': return <CashRegisterPage companyId={companyId} selectedCompany={selectedCompany} user={user} />;
      case 'orcamentos': return <QuotesPage userPlan={userPlan} onLockedFeatureClick={handleLockedMenuClick} onNavigate={setCurrentPage} companyId={companyId} selectedCompany={selectedCompany} user={user} />;
      case 'dre': return <DREPage userPlan={userPlan} onLockedFeatureClick={handleLockedMenuClick} companyId={companyId} selectedCompany={selectedCompany} user={user} />;
      case 'reports': return <ReportsPage userPlan={userPlan} onLockedFeatureClick={handleLockedMenuClick} companyId={companyId} selectedCompany={selectedCompany} user={user} />;
      case 'contas-pagar': return <AccountsPayablePage userPlan={userPlan} onLockedFeatureClick={handleLockedMenuClick} companyId={companyId} selectedCompany={selectedCompany} />;
      case 'contas-receber': return <AccountsReceivablePage userPlan={userPlan} onLockedFeatureClick={handleLockedMenuClick} companyId={companyId} selectedCompany={selectedCompany} />;
      case 'livro-caixa': return <CashBookPage userPlan={userPlan} onLockedFeatureClick={handleLockedMenuClick} companyId={companyId} selectedCompany={selectedCompany} />;
      case 'recibos': return <ReceiptsPage userPlan={userPlan} onLockedFeatureClick={handleLockedMenuClick} companyId={companyId} selectedCompany={selectedCompany} user={user} onNavigate={setCurrentPage} />;
      case 'report-chart-accounts': return <ReportChartAccountsPage userPlan={userPlan} onLockedFeatureClick={handleLockedMenuClick} />;
      case 'clients': return <ClientsPage onNavigate={setCurrentPage} companyId={companyId} />;
      case 'suppliers': return <SuppliersPage onNavigate={setCurrentPage} companyId={companyId} />;
      case 'products': return <ProductsPage onNavigate={setCurrentPage} companyId={companyId} />;
      case 'employees': return <EmployeesPage onNavigate={setCurrentPage} companyId={companyId} />;
      case 'sub-users': return <SubUsersPage companies={companies} user={user} />;
      case 'wallets': return <WalletsPage onNavigate={setCurrentPage} companyId={companyId} />;
      case 'empresas': return <CompaniesPage onNavigate={setCurrentPage} userPlan={userPlan} companyLimit={companyLimit} onRefresh={refreshCompanies} />;
      case 'audit-logs': return <AuditLogsPage onNavigate={setCurrentPage} companyId={companyId} companies={companies} />;
      case 'chat-ajuda': return <HelpChatPage user={user} selectedCompany={selectedCompany} />;
      default: return <Dashboard user={user} userPlan={userPlan} onContractClick={handleContractClick} upsellExpanded={upsellExpanded} setUpsellExpanded={setUpsellExpanded} onNavigate={handleNavigate} companyId={companyId} selectedCompany={selectedCompany} companies={companies} onCompanyChange={handleCompanyChange} loadingCompanies={loadingCompanies} />;
    }
  })();

  return (
    <div className="min-h-screen bg-background flex flex-col">
      <Header 
        user={user} 
        currentPage={currentPage} 
        onNavigate={setCurrentPage} 
        userPlan={userPlan}
        onLockedMenuClick={handleLockedMenuClick}
        menuItemsFiltered={filteredMenuItems}
      />
      <main className="pt-20 px-4 pb-8 max-w-7xl mx-auto flex-1 w-full" data-testid="main-content">
        <div key={currentPage} className="w-full max-w-full">
          {pageContent}
        </div>
      </main>
      <footer className="py-4 px-4 border-t border-border">
        <div className="max-w-7xl mx-auto flex flex-col md:flex-row items-center">
          {/* Company name - hidden on mobile (already shown in footer center) */}
          <div className="hidden md:flex items-center gap-2 text-sm text-muted-foreground w-1/3">
            <Building className="w-4 h-4 flex-shrink-0" />
            <span className="font-medium truncate max-w-[200px]" title={selectedCompany?.razao_social}>
              {selectedCompany?.razao_social || 'Carregando...'}
            </span>
          </div>
          {/* Plan and Version - center on desktop, right on mobile */}
          <div className="flex items-center justify-center md:justify-center gap-3 w-full md:w-1/3">
            {/* Mobile: show company name inline with plan */}
            <div className="flex md:hidden items-center gap-2 text-sm text-muted-foreground mr-auto">
              <Building className="w-4 h-4 flex-shrink-0" />
              <span className="font-medium truncate max-w-[120px]" title={selectedCompany?.razao_social}>
                {selectedCompany?.razao_social || '...'}
              </span>
            </div>
            <Badge variant="outline" className="text-xs">
              Plano {userPlanName}
            </Badge>
            <span className="text-xs text-muted-foreground">v{APP_VERSION}</span>
          </div>
          <div className="hidden md:block w-1/3"></div>
        </div>
      </footer>

      {/* Floating Help Button */}
      {currentPage !== 'chat-ajuda' && (
        <button
          onClick={() => setCurrentPage('chat-ajuda')}
          className="fixed bottom-20 right-4 md:bottom-24 md:right-6 w-12 h-12 bg-primary text-primary-foreground rounded-full shadow-lg hover:shadow-xl hover:scale-110 transition-all duration-200 flex items-center justify-center z-40"
          title="Precisa de ajuda?"
          data-testid="floating-help-btn"
        >
          <HelpCircle className="w-6 h-6" />
        </button>
      )}

      {/* Plan Expiration Modal */}
      <PlanExpirationModal
        user={user}
        userPlan={userPlan}
        isOpen={showExpirationModal}
        onClose={() => setShowExpirationModal(false)}
        onExpandUpsell={handleExpandUpsell}
      />

      {/* Locked Menu Modal */}
      <Dialog open={showLockedModal} onOpenChange={setShowLockedModal}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Lock className="w-5 h-5 text-primary" />
              Funcionalidade Bloqueada
            </DialogTitle>
          </DialogHeader>
          <div className="py-4">
            <p className="text-muted-foreground">
              Esta funcionalidade está disponível a partir do{' '}
              <span className="font-semibold text-foreground">
                Plano {lockedPlanRequired ? PLANS[lockedPlanRequired]?.name : ''}
              </span>.
            </p>
            <p className="text-sm text-muted-foreground mt-2">
              Contrate e tenha acesso a esta e outras funcionalidades exclusivas.
            </p>
          </div>
          <div className="flex gap-2 justify-end">
            <Button variant="outline" onClick={() => setShowLockedModal(false)}>
              Fechar
            </Button>
            <Button onClick={() => {
              setShowLockedModal(false);
              handleContractClick(lockedPlanRequired);
            }}>
              Quero Contratar
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* PWA Install Prompt for Mobile */}
      {showInstallPrompt && (
        <div className="fixed inset-0 bg-black/60 backdrop-blur-sm z-50 flex items-center justify-center p-4" data-testid="pwa-install-modal">
          <div className="bg-card border border-border rounded-2xl shadow-2xl w-full max-w-sm relative animate-in fade-in zoom-in duration-300">
            {/* Close button */}
            <button
              onClick={handleCloseInstallPrompt}
              className="absolute top-3 right-3 w-8 h-8 rounded-full bg-muted flex items-center justify-center hover:bg-muted/80 transition-colors"
              data-testid="pwa-close-btn"
            >
              <XCircle className="w-5 h-5 text-muted-foreground" />
            </button>
            
            {/* Content */}
            <div className="p-6 pt-8 text-center space-y-4">
              {/* Icon */}
              <div className="w-16 h-16 mx-auto bg-emerald-500/10 rounded-2xl flex items-center justify-center">
                <Download className="w-8 h-8 text-emerald-500" />
              </div>
              
              {/* Text */}
              <div className="space-y-2">
                <h3 className="text-lg font-semibold text-foreground">
                  Instalar DRE+
                </h3>
                <p className="text-sm text-muted-foreground">
                  Deseja criar o ícone da aplicação no seu aparelho?
                </p>
              </div>
              
              {/* Button */}
              <Button 
                onClick={handleInstallClick}
                className="w-full h-12 text-base font-semibold bg-emerald-600 hover:bg-emerald-700"
                data-testid="pwa-install-btn"
              >
                OK
              </Button>
            </div>
          </div>
        </div>
      )}

      {/* PWA Exit Confirmation Modal */}
      <Dialog open={showExitConfirm} onOpenChange={setShowExitConfirm}>
        <DialogContent className="max-w-sm">
          <DialogHeader>
            <DialogTitle>Sair do aplicativo?</DialogTitle>
          </DialogHeader>
          <p className="text-muted-foreground text-sm">
            Deseja realmente fechar o DRE+?
          </p>
          <div className="flex justify-end gap-2 mt-4">
            <Button variant="outline" onClick={() => setShowExitConfirm(false)}>
              Cancelar
            </Button>
            <Button variant="destructive" onClick={handleExitApp}>
              Sair
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

// ==================== ADMIN PANEL ====================

// Admin Login Page
const AdminLoginPage = () => {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const navigate = useNavigate();

  const handleLogin = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);

    try {
      const response = await axios.post(`${API}/admin/auth/login`, { email, password });
      navigate('/admin/dashboard', { state: { admin: response.data }, replace: true });
    } catch (err) {
      setError(err.response?.data?.detail || 'E-mail ou senha incorretos');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-slate-900 flex items-center justify-center p-4">
      <Card className="w-full max-w-md bg-slate-800 border-slate-700">
        <CardHeader className="text-center space-y-2">
          <h1 className="text-3xl font-bold text-emerald-500">DRE+ Admin</h1>
          <CardDescription className="text-slate-400">Painel Administrativo</CardDescription>
        </CardHeader>
        <CardContent>
          <form onSubmit={handleLogin} className="space-y-4">
            <div>
              <Label className="text-slate-300">E-mail</Label>
              <Input
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="Digite seu e-mail"
                className="bg-slate-700 border-slate-600 text-white"
                required
              />
            </div>
            <div>
              <Label className="text-slate-300">Senha</Label>
              <Input
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                placeholder="Digite sua senha"
                className="bg-slate-700 border-slate-600 text-white"
                required
              />
            </div>
            {error && <p className="text-red-400 text-sm">{error}</p>}
            <Button type="submit" className="w-full bg-emerald-600 hover:bg-emerald-700" disabled={loading}>
              {loading ? 'Entrando...' : 'Entrar'}
            </Button>
          </form>
        </CardContent>
        <div className="pb-4 text-center">
          <span className="text-xs text-slate-500">Versão {APP_VERSION}</span>
        </div>
      </Card>
    </div>
  );
};

// Admin Protected Route
const AdminProtectedRoute = ({ children, requiredProfile }) => {
  const [admin, setAdmin] = useState(null);
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();
  const location = useLocation();

  useEffect(() => {
    if (location.state?.admin) {
      setAdmin(location.state.admin);
      setLoading(false);
      window.history.replaceState({}, document.title);
      return;
    }

    const checkAuth = async () => {
      try {
        const response = await axios.get(`${API}/admin/auth/me`);
        setAdmin(response.data);
      } catch (error) {
        navigate('/admin', { replace: true });
      } finally {
        setLoading(false);
      }
    };
    checkAuth();
  }, [navigate, location.state]);

  if (loading) {
    return (
      <div className="min-h-screen bg-slate-900 flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-emerald-500"></div>
      </div>
    );
  }

  if (!admin) return null;
  
  if (requiredProfile && admin.profile !== requiredProfile && admin.profile !== 'Admin') {
    return (
      <div className="min-h-screen bg-slate-900 flex items-center justify-center">
        <Card className="bg-slate-800 border-slate-700 p-6">
          <p className="text-red-400">Acesso não autorizado</p>
        </Card>
      </div>
    );
  }

  return children({ admin });
};

// Admin Header
const AdminHeader = ({ admin, currentPage, onNavigate }) => {
  const navigate = useNavigate();
  const [openMenu, setOpenMenu] = useState(null);
  const menuTimeoutRef = useRef(null);

  // Menu hover handlers with delay to prevent accidental closing
  const handleMenuEnter = (menuId) => {
    if (menuTimeoutRef.current) {
      clearTimeout(menuTimeoutRef.current);
      menuTimeoutRef.current = null;
    }
    setOpenMenu(menuId);
  };

  const handleMenuLeave = () => {
    menuTimeoutRef.current = setTimeout(() => {
      setOpenMenu(null);
    }, 150); // 150ms delay before closing
  };

  // Cleanup timeout on unmount
  useEffect(() => {
    return () => {
      if (menuTimeoutRef.current) {
        clearTimeout(menuTimeoutRef.current);
      }
    };
  }, []);

  const handleLogout = async () => {
    try {
      await axios.post(`${API}/admin/auth/logout`);
      navigate('/admin');
    } catch (error) {
      console.error('Logout error:', error);
    }
  };

  const menuItems = [
    { id: 'dashboard', label: 'Dashboard', icon: LayoutDashboard },
    ...(admin?.profile === 'Admin' ? [{
      id: 'cadastro',
      label: 'Cadastro',
      icon: Users,
      submenu: [
        { id: 'admin-users', label: 'Contas de Usuários', icon: User },
        { id: 'admin-chart-accounts', label: 'Plano de Contas', icon: Receipt }
      ]
    }] : []),
    {
      id: 'manutencao',
      label: 'Manutenção',
      icon: Building2,
      submenu: [
        { id: 'clients-plans', label: 'Clientes e Planos', icon: Users },
        { id: 'system-config', label: 'Configurações', icon: Settings }
      ]
    },
    {
      id: 'relatorios',
      label: 'Relatórios',
      icon: FileText,
      submenu: [
        { id: 'report-clients', label: 'Clientes', icon: Users },
        { id: 'report-emails', label: 'Emails', icon: Receipt },
        { id: 'report-chart-accounts', label: 'Plano de Contas', icon: FileText },
        { id: 'report-audit-logs', label: 'Logs de Auditoria', icon: FileText }
      ]
    }
  ];

  const isActive = (item) => {
    if (item.id === currentPage) return true;
    if (item.submenu) return item.submenu.some(sub => sub.id === currentPage);
    return false;
  };

  return (
    <header className="fixed top-0 left-0 right-0 h-16 bg-slate-800 border-b border-slate-700 z-50">
      <div className="h-full max-w-7xl mx-auto px-4 flex items-center justify-between">
        <div className="flex items-center gap-8">
          <h1 className="text-xl font-bold text-emerald-500">DRE+ Admin</h1>
          
          <nav className="hidden md:flex items-center gap-1">
            {menuItems.map((item) => (
              <div key={item.id} className="relative">
                {item.submenu ? (
                  <div
                    className="relative"
                    onMouseEnter={() => handleMenuEnter(item.id)}
                    onMouseLeave={handleMenuLeave}
                  >
                    <button
                      className={`flex items-center gap-2 px-3 py-2 rounded-lg transition-all ${isActive(item) ? 'bg-emerald-600/20 text-emerald-500' : 'text-slate-300 hover:bg-slate-700'}`}
                    >
                      <item.icon className="w-4 h-4" />
                      <span className="text-sm">{item.label}</span>
                      <ChevronDown className="w-3 h-3" />
                    </button>

                    {openMenu === item.id && (
                      <div 
                        className="absolute top-full left-0 pt-1 w-48"
                        onMouseEnter={() => handleMenuEnter(item.id)}
                        onMouseLeave={handleMenuLeave}
                      >
                        <div className="bg-slate-800 border border-slate-700 rounded-lg shadow-lg py-2">
                          {item.submenu.map((sub) => (
                            <button
                              key={sub.id}
                              onClick={() => { onNavigate(sub.id); setOpenMenu(null); }}
                              className={`w-full flex items-center gap-3 px-4 py-2 text-left transition-all ${currentPage === sub.id ? 'bg-emerald-600/20 text-emerald-500' : 'text-slate-300 hover:bg-slate-700'}`}
                            >
                              <sub.icon className="w-4 h-4" />
                              <span className="text-sm">{sub.label}</span>
                            </button>
                          ))}
                        </div>
                      </div>
                    )}
                  </div>
                ) : (
                  <button
                    onClick={() => onNavigate(item.id)}
                    className={`flex items-center gap-2 px-3 py-2 rounded-lg transition-all ${isActive(item) ? 'bg-emerald-600/20 text-emerald-500' : 'text-slate-300 hover:bg-slate-700'}`}
                  >
                    <item.icon className="w-4 h-4" />
                    <span className="text-sm">{item.label}</span>
                  </button>
                )}
              </div>
            ))}
          </nav>
        </div>

        <div className="flex items-center gap-4">
          <div className="text-right hidden sm:block">
            <p className="text-sm text-slate-300">{admin?.name}</p>
            <p className="text-xs text-slate-500">{admin?.profile}</p>
          </div>
          <Button variant="ghost" size="icon" onClick={handleLogout} className="text-slate-400 hover:text-white">
            <LogOut className="w-5 h-5" />
          </Button>
        </div>
      </div>
    </header>
  );
};

// Admin Users Page (Admin only)
const AdminUsersPage = () => {
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [editingUser, setEditingUser] = useState(null);
  const [formData, setFormData] = useState({ name: '', email: '', profile: 'Usuario', department: '', password: '', is_active: true });
  const { toast } = useToast();

  useEffect(() => { loadUsers(); }, []);

  const loadUsers = async () => {
    try {
      const response = await axios.get(`${API}/admin/users`);
      setUsers(response.data);
    } catch (error) {
      toast({ title: 'Erro ao carregar usuários', variant: 'destructive' });
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async () => {
    try {
      if (editingUser) {
        await axios.put(`${API}/admin/users/${editingUser.id}`, formData);
        toast({ title: 'Usuário atualizado!' });
      } else {
        if (!formData.password) {
          toast({ title: 'Senha é obrigatória', variant: 'destructive' });
          return;
        }
        await axios.post(`${API}/admin/users`, formData);
        toast({ title: 'Usuário criado!' });
      }
      setIsDialogOpen(false);
      loadUsers();
    } catch (error) {
      toast({ title: error.response?.data?.detail || 'Erro ao salvar', variant: 'destructive' });
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Tem certeza que deseja excluir este usuário?')) return;
    try {
      await axios.delete(`${API}/admin/users/${id}`);
      toast({ title: 'Usuário excluído!' });
      loadUsers();
    } catch (error) {
      toast({ title: error.response?.data?.detail || 'Erro ao excluir', variant: 'destructive' });
    }
  };

  const openEdit = (user) => {
    setEditingUser(user);
    setFormData({ name: user.name, email: user.email, profile: user.profile, department: user.department || '', password: '', is_active: user.is_active });
    setIsDialogOpen(true);
  };

  const openNew = () => {
    setEditingUser(null);
    setFormData({ name: '', email: '', profile: 'Usuario', department: '', password: '', is_active: true });
    setIsDialogOpen(true);
  };

  return (
    <div className="space-y-6">
      <div className="flex justify-between items-center">
        <h2 className="text-2xl font-bold text-white">Contas de Usuários</h2>
        <Button onClick={openNew} className="bg-emerald-600 hover:bg-emerald-700"><Plus className="w-4 h-4 mr-2" /> Novo Usuário</Button>
      </div>

      <Card className="bg-slate-800 border-slate-700">
        <Table>
          <TableHeader>
            <TableRow className="border-slate-700">
              <TableHead className="text-slate-300">Nome</TableHead>
              <TableHead className="text-slate-300">E-mail</TableHead>
              <TableHead className="text-slate-300">Perfil</TableHead>
              <TableHead className="text-slate-300">Departamento</TableHead>
              <TableHead className="text-slate-300">Status</TableHead>
              <TableHead className="text-slate-300 text-right">Ações</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {loading ? (
              <TableRow><TableCell colSpan={6} className="text-center text-slate-400 py-8">Carregando...</TableCell></TableRow>
            ) : users.map((user) => (
              <TableRow key={user.id} className="border-slate-700">
                <TableCell className="text-white">{user.name}</TableCell>
                <TableCell className="text-slate-300">{user.email}</TableCell>
                <TableCell><Badge className={user.profile === 'Admin' ? 'bg-purple-600' : user.profile === 'Gerente' ? 'bg-blue-600' : 'bg-slate-600'}>{user.profile}</Badge></TableCell>
                <TableCell className="text-slate-300">{user.department || '-'}</TableCell>
                <TableCell><Badge className={user.is_active ? 'bg-emerald-600' : 'bg-red-600'}>{user.is_active ? 'Ativo' : 'Inativo'}</Badge></TableCell>
                <TableCell className="text-right">
                  <Button variant="ghost" size="icon" onClick={() => openEdit(user)} className="text-slate-400 hover:text-white"><Pencil className="w-4 h-4" /></Button>
                  <Button variant="ghost" size="icon" onClick={() => handleDelete(user.id)} className="text-red-400 hover:text-red-300"><Trash2 className="w-4 h-4" /></Button>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </Card>

      <Dialog open={isDialogOpen} onOpenChange={setIsDialogOpen}>
        <DialogContent className="bg-slate-800 border-slate-700 text-white">
          <DialogHeader><DialogTitle>{editingUser ? 'Editar Usuário' : 'Novo Usuário'}</DialogTitle></DialogHeader>
          <div className="space-y-4">
            <div><Label className="text-slate-300">Nome</Label><Input value={formData.name} onChange={(e) => setFormData({ ...formData, name: e.target.value })} className="bg-slate-700 border-slate-600" /></div>
            <div><Label className="text-slate-300">E-mail</Label><Input type="email" value={formData.email} onChange={(e) => setFormData({ ...formData, email: e.target.value })} className="bg-slate-700 border-slate-600" /></div>
            <div><Label className="text-slate-300">Perfil</Label>
              <Select value={formData.profile} onValueChange={(v) => setFormData({ ...formData, profile: v })}>
                <SelectTrigger className="bg-slate-700 border-slate-600"><SelectValue /></SelectTrigger>
                <SelectContent><SelectItem value="Admin">Admin</SelectItem><SelectItem value="Gerente">Gerente</SelectItem><SelectItem value="Usuario">Usuário</SelectItem></SelectContent>
              </Select>
            </div>
            <div><Label className="text-slate-300">Departamento</Label><Input value={formData.department} onChange={(e) => setFormData({ ...formData, department: e.target.value })} className="bg-slate-700 border-slate-600" /></div>
            <div><Label className="text-slate-300">Senha {editingUser && '(deixe em branco para manter)'}</Label><Input type="password" value={formData.password} onChange={(e) => setFormData({ ...formData, password: e.target.value })} className="bg-slate-700 border-slate-600" /></div>
            <div className="flex items-center gap-2">
              <input type="checkbox" checked={formData.is_active} onChange={(e) => setFormData({ ...formData, is_active: e.target.checked })} className="rounded" />
              <Label className="text-slate-300">Usuário Ativo</Label>
            </div>
          </div>
          <DialogFooter><Button onClick={handleSave} className="bg-emerald-600 hover:bg-emerald-700">Salvar</Button></DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

// Clients and Plans Page
const AdminClientsPage = ({ adminProfile }) => {
  const [clients, setClients] = useState([]);
  const [filteredClients, setFilteredClients] = useState([]);
  const [loading, setLoading] = useState(true);
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [isCreateDialogOpen, setIsCreateDialogOpen] = useState(false);
  const [editingClient, setEditingClient] = useState(null);
  const [formData, setFormData] = useState({});
  const [createFormData, setCreateFormData] = useState({ name: '', email: '', password: '', cpf_cnpj: '', phone: '', plan: 1, plan_expiry: '', max_companies: 2, is_active: true });
  const [clientToDelete, setClientToDelete] = useState(null);
  const [isDeleteDialogOpen, setIsDeleteDialogOpen] = useState(false);
  const [togglingStatus, setTogglingStatus] = useState(null);
  const [simulatingLogin, setSimulatingLogin] = useState(false);
  
  // Limite default por plano
  const PLAN_COMPANY_LIMITS = { 1: 2, 2: 4, 3: 6 };
  
  // Filters state
  const [filters, setFilters] = useState({ name: '', cpf: '', cnpj: '' });
  
  const { toast } = useToast();
  const canEdit = adminProfile === 'Admin' || adminProfile === 'Gerente';
  const isAdmin = adminProfile === 'Admin';

  useEffect(() => { loadClients(); }, []);
  
  // Apply filters when clients or filters change
  useEffect(() => {
    let result = [...clients];
    
    if (filters.name) {
      result = result.filter(c => c.name?.toLowerCase().includes(filters.name.toLowerCase()));
    }
    if (filters.cpf) {
      result = result.filter(c => {
        const doc = c.cpf_cnpj || '';
        // CPF has 11 digits, CNPJ has 14
        const isLikelyCPF = doc.replace(/\D/g, '').length <= 11;
        return isLikelyCPF && doc.includes(filters.cpf);
      });
    }
    if (filters.cnpj) {
      result = result.filter(c => {
        const doc = c.cpf_cnpj || '';
        // CNPJ has more than 11 digits
        const isLikelyCNPJ = doc.replace(/\D/g, '').length > 11;
        return isLikelyCNPJ && doc.includes(filters.cnpj);
      });
    }
    
    setFilteredClients(result);
  }, [clients, filters]);

  const loadClients = async () => {
    try {
      const response = await axios.get(`${API}/admin/clients`);
      setClients(response.data);
    } catch (error) {
      toast({ title: 'Erro ao carregar clientes', variant: 'destructive' });
    } finally {
      setLoading(false);
    }
  };

  const clearFilters = () => {
    setFilters({ name: '', cpf: '', cnpj: '' });
  };

  const handleSimulateLogin = async () => {
    if (!editingClient) return;
    
    setSimulatingLogin(true);
    try {
      // Generate simulation token
      const response = await axios.post(`${API}/admin/clients/${editingClient.user_id}/simulate-login`);
      const { simulation_token } = response.data;
      
      // Open the production app in a new tab with the simulation token
      const deployUrl = 'https://app.dremais.com.br';
      const simulationUrl = `${deployUrl}/simulate?token=${simulation_token}`;
      
      window.open(simulationUrl, '_blank');
      toast({ title: 'Simulação iniciada em nova aba' });
    } catch (error) {
      toast({ title: error.response?.data?.detail || 'Erro ao simular login', variant: 'destructive' });
    } finally {
      setSimulatingLogin(false);
    }
  };

  const handleSave = async () => {
    try {
      // Validar max_companies
      const minLimit = PLAN_COMPANY_LIMITS[formData.plan] || 2;
      if (!formData.max_companies || formData.max_companies < minLimit) {
        toast({ title: `Qtde de empresas deve ser maior ou igual a ${minLimit}`, variant: 'destructive' });
        return;
      }
      await axios.put(`${API}/admin/clients/${editingClient.user_id}`, formData);
      toast({ title: 'Cliente atualizado!' });
      setIsDialogOpen(false);
      loadClients();
    } catch (error) {
      toast({ title: error.response?.data?.detail || 'Erro ao salvar', variant: 'destructive' });
    }
  };

  const handleCreate = async () => {
    try {
      if (!createFormData.name || !createFormData.email || !createFormData.password) {
        toast({ title: 'Preencha nome, email e senha', variant: 'destructive' });
        return;
      }
      // Validar max_companies
      const minLimit = PLAN_COMPANY_LIMITS[createFormData.plan] || 2;
      if (!createFormData.max_companies || createFormData.max_companies < minLimit) {
        toast({ title: `Qtde de empresas deve ser maior ou igual a ${minLimit}`, variant: 'destructive' });
        return;
      }
      await axios.post(`${API}/admin/clients`, createFormData);
      toast({ title: 'Cliente criado com sucesso!' });
      setIsCreateDialogOpen(false);
      setCreateFormData({ name: '', email: '', password: '', cpf_cnpj: '', phone: '', plan: 1, plan_expiry: '', max_companies: 2, is_active: true });
      loadClients();
    } catch (error) {
      toast({ title: error.response?.data?.detail || 'Erro ao criar cliente', variant: 'destructive' });
    }
  };

  const handleDeleteClick = (client) => {
    setClientToDelete(client);
    setIsDeleteDialogOpen(true);
  };

  const handleDeleteConfirm = async () => {
    if (!clientToDelete) return;
    try {
      await axios.delete(`${API}/admin/clients/${clientToDelete.user_id}`);
      toast({ title: 'Cliente excluído!' });
      loadClients();
    } catch (error) {
      toast({ title: error.response?.data?.detail || 'Erro ao excluir', variant: 'destructive' });
    } finally {
      setIsDeleteDialogOpen(false);
      setClientToDelete(null);
    }
  };

  const handleToggleStatus = async (client) => {
    setTogglingStatus(client.user_id);
    try {
      const newStatus = !client.is_active;
      await axios.put(`${API}/admin/clients/${client.user_id}`, { is_active: newStatus });
      toast({ title: newStatus ? 'Cliente ativado!' : 'Cliente desativado!' });
      loadClients();
    } catch (error) {
      toast({ title: error.response?.data?.detail || 'Erro ao alterar status', variant: 'destructive' });
    } finally {
      setTogglingStatus(null);
    }
  };

  const openEdit = (client) => {
    setEditingClient(client);
    setFormData({
      name: client.name || '',
      email: client.email || '',
      cpf_cnpj: client.cpf_cnpj || '',
      phone: client.phone || '',
      plan: client.plan || 1,
      plan_expiry: client.plan_expiry || '',
      max_companies: client.max_companies || PLAN_COMPANY_LIMITS[client.plan || 1],
      password: ''
    });
    setIsDialogOpen(true);
  };

  return (
    <div className="space-y-6">
      <div className="flex justify-between items-center">
        <h2 className="text-2xl font-bold text-white">Clientes e Planos</h2>
        {canEdit && (
          <Button onClick={() => setIsCreateDialogOpen(true)} className="bg-emerald-600 hover:bg-emerald-700" data-testid="add-client-btn">
            <UserPlus className="w-4 h-4 mr-2" /> Novo Cliente
          </Button>
        )}
      </div>

      {/* Filters Section */}
      <Card className="bg-slate-800/50 border-slate-700">
        <CardContent className="p-4">
          <div className="flex flex-wrap gap-4 items-end">
            <div className="flex-1 min-w-[200px]">
              <Label className="text-slate-400 text-xs mb-1 block">Filtrar por Nome</Label>
              <Input 
                placeholder="Digite o nome..." 
                value={filters.name}
                onChange={(e) => setFilters({ ...filters, name: e.target.value })}
                className="bg-slate-700 border-slate-600 text-white h-9"
                data-testid="filter-name"
              />
            </div>
            <div className="w-40">
              <Label className="text-slate-400 text-xs mb-1 block">Filtrar por CPF</Label>
              <Input 
                placeholder="000.000.000-00" 
                value={filters.cpf}
                onChange={(e) => setFilters({ ...filters, cpf: e.target.value })}
                className="bg-slate-700 border-slate-600 text-white h-9"
                data-testid="filter-cpf"
              />
            </div>
            <div className="w-44">
              <Label className="text-slate-400 text-xs mb-1 block">Filtrar por CNPJ</Label>
              <Input 
                placeholder="00.000.000/0000-00" 
                value={filters.cnpj}
                onChange={(e) => setFilters({ ...filters, cnpj: e.target.value })}
                className="bg-slate-700 border-slate-600 text-white h-9"
                data-testid="filter-cnpj"
              />
            </div>
            <Button 
              variant="outline" 
              size="sm" 
              onClick={clearFilters}
              className="border-slate-600 text-slate-300 hover:bg-slate-700 h-9"
              data-testid="clear-filters-btn"
            >
              Limpar
            </Button>
          </div>
          {(filters.name || filters.cpf || filters.cnpj) && (
            <p className="text-xs text-slate-500 mt-2">
              Exibindo {filteredClients.length} de {clients.length} clientes
            </p>
          )}
        </CardContent>
      </Card>

      <Card className="bg-slate-800 border-slate-700">
        <Table>
          <TableHeader>
            <TableRow className="border-slate-700">
              <TableHead className="text-slate-300">Nome</TableHead>
              <TableHead className="text-slate-300">E-mail</TableHead>
              <TableHead className="text-slate-300">CPF/CNPJ</TableHead>
              <TableHead className="text-slate-300">Plano</TableHead>
              <TableHead className="text-slate-300 text-center">Empresas</TableHead>
              <TableHead className="text-slate-300">Validade</TableHead>
              <TableHead className="text-slate-300">Cadastro</TableHead>
              <TableHead className="text-slate-300 text-center">Status</TableHead>
              {canEdit && <TableHead className="text-slate-300 text-right">Ações</TableHead>}
            </TableRow>
          </TableHeader>
          <TableBody>
            {loading ? (
              <TableRow><TableCell colSpan={9} className="text-center text-slate-400 py-8">Carregando...</TableCell></TableRow>
            ) : filteredClients.length === 0 ? (
              <TableRow><TableCell colSpan={9} className="text-center text-slate-400 py-8">
                {clients.length === 0 ? 'Nenhum cliente cadastrado' : 'Nenhum cliente encontrado com os filtros aplicados'}
              </TableCell></TableRow>
            ) : filteredClients.map((client) => (
              <TableRow key={client.user_id} className={`border-slate-700 ${client.is_active === false ? 'opacity-60' : ''}`} data-testid={`client-row-${client.user_id}`}>
                <TableCell className="text-white">{client.name}</TableCell>
                <TableCell className="text-slate-300">{client.email}</TableCell>
                <TableCell className="text-slate-300">{client.cpf_cnpj || '-'}</TableCell>
                <TableCell><Badge className={client.plan === 3 ? 'bg-purple-600' : client.plan === 2 ? 'bg-blue-600' : 'bg-slate-600'}>{client.plan_name}</Badge></TableCell>
                <TableCell className="text-center text-slate-300">{client.max_companies || PLAN_COMPANY_LIMITS[client.plan || 1]}</TableCell>
                <TableCell className="text-slate-300">{client.plan_expiry || '-'}</TableCell>
                <TableCell className="text-slate-400 text-xs">
                  {client.created_at ? new Date(client.created_at).toLocaleDateString('pt-BR') : '-'}
                </TableCell>
                <TableCell className="text-center">
                  {canEdit ? (
                    <div className="flex items-center justify-center gap-2">
                      <Switch
                        checked={client.is_active !== false}
                        onCheckedChange={() => handleToggleStatus(client)}
                        disabled={togglingStatus === client.user_id}
                        className={client.is_active !== false ? 'data-[state=checked]:bg-emerald-600' : ''}
                        data-testid={`client-status-toggle-${client.user_id}`}
                      />
                      <span className={`text-xs ${client.is_active !== false ? 'text-emerald-400' : 'text-red-400'}`}>
                        {client.is_active !== false ? 'Ativo' : 'Inativo'}
                      </span>
                    </div>
                  ) : (
                    <Badge className={client.is_active !== false ? 'bg-emerald-600' : 'bg-red-600'}>
                      {client.is_active !== false ? 'Ativo' : 'Inativo'}
                    </Badge>
                  )}
                </TableCell>
                {canEdit && (
                  <TableCell className="text-right">
                    <Button variant="ghost" size="icon" onClick={() => openEdit(client)} className="text-slate-400 hover:text-white" data-testid={`edit-client-${client.user_id}`}>
                      <Pencil className="w-4 h-4" />
                    </Button>
                    {isAdmin && (
                      <Button variant="ghost" size="icon" onClick={() => handleDeleteClick(client)} className="text-red-400 hover:text-red-300" data-testid={`delete-client-${client.user_id}`}>
                        <Trash2 className="w-4 h-4" />
                      </Button>
                    )}
                  </TableCell>
                )}
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </Card>

      {/* Edit Dialog */}
      <Dialog open={isDialogOpen} onOpenChange={setIsDialogOpen}>
        <DialogContent className="bg-slate-800 border-slate-700 text-white max-w-lg max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <div className="flex items-center justify-between">
              <DialogTitle>Editar Cliente</DialogTitle>
              {canEdit && (
                <Button 
                  variant="ghost" 
                  size="sm" 
                  onClick={handleSimulateLogin}
                  disabled={simulatingLogin}
                  className="text-xs text-blue-400 hover:text-blue-300 hover:bg-slate-700 h-7 px-2"
                  data-testid="simulate-login-btn"
                >
                  {simulatingLogin ? 'Abrindo...' : 'Simular'}
                </Button>
              )}
            </div>
          </DialogHeader>
          <div className="space-y-4 max-h-96 overflow-y-auto">
            {/* Read-only: Data de Cadastro */}
            {editingClient?.created_at && (
              <div className="bg-slate-700/50 rounded-lg p-3 border border-slate-600">
                <Label className="text-slate-400 text-xs">Data de Cadastro</Label>
                <p className="text-white text-sm mt-1">
                  {new Date(editingClient.created_at).toLocaleString('pt-BR', { 
                    day: '2-digit', month: '2-digit', year: 'numeric',
                    hour: '2-digit', minute: '2-digit'
                  })}
                </p>
              </div>
            )}
            <div><Label className="text-slate-300">Nome</Label><Input value={formData.name || ''} onChange={(e) => setFormData({ ...formData, name: e.target.value })} className="bg-slate-700 border-slate-600" /></div>
            <div><Label className="text-slate-300">E-mail</Label><Input type="email" value={formData.email || ''} onChange={(e) => setFormData({ ...formData, email: e.target.value })} className="bg-slate-700 border-slate-600" /></div>
            <div><Label className="text-slate-300">CPF/CNPJ</Label><Input value={formData.cpf_cnpj || ''} onChange={(e) => setFormData({ ...formData, cpf_cnpj: e.target.value })} className="bg-slate-700 border-slate-600" /></div>
            <div><Label className="text-slate-300">Telefone</Label><Input value={formData.phone || ''} onChange={(e) => setFormData({ ...formData, phone: e.target.value })} className="bg-slate-700 border-slate-600" /></div>
            <div><Label className="text-slate-300">Plano</Label>
              <Select value={String(formData.plan || 1)} onValueChange={(v) => {
                const newPlan = parseInt(v);
                const minLimit = PLAN_COMPANY_LIMITS[newPlan];
                setFormData({ 
                  ...formData, 
                  plan: newPlan,
                  // Se max_companies atual é menor que o minimo do novo plano, atualiza
                  max_companies: Math.max(formData.max_companies || minLimit, minLimit)
                });
              }}>
                <SelectTrigger className="bg-slate-700 border-slate-600"><SelectValue /></SelectTrigger>
                <SelectContent><SelectItem value="1">Básico</SelectItem><SelectItem value="2">Advanced</SelectItem><SelectItem value="3">Contábil</SelectItem></SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-slate-300">Qtde Máx. Empresas</Label>
              <div className="flex items-center gap-2">
                <Input 
                  type="number" 
                  min={PLAN_COMPANY_LIMITS[formData.plan || 1]} 
                  value={formData.max_companies || PLAN_COMPANY_LIMITS[formData.plan || 1]} 
                  onChange={(e) => setFormData({ ...formData, max_companies: parseInt(e.target.value) || PLAN_COMPANY_LIMITS[formData.plan || 1] })} 
                  className="bg-slate-700 border-slate-600 w-24" 
                />
                <span className="text-xs text-slate-400">(mín: {PLAN_COMPANY_LIMITS[formData.plan || 1]})</span>
              </div>
            </div>
            <div><Label className="text-slate-300">Validade do Plano</Label><Input type="date" value={formData.plan_expiry || ''} onChange={(e) => setFormData({ ...formData, plan_expiry: e.target.value })} className="bg-slate-700 border-slate-600" /></div>
            <div><Label className="text-slate-300">Nova Senha (deixe em branco para manter)</Label><Input type="password" value={formData.password || ''} onChange={(e) => setFormData({ ...formData, password: e.target.value })} className="bg-slate-700 border-slate-600" /></div>
          </div>
          <DialogFooter><Button onClick={handleSave} className="bg-emerald-600 hover:bg-emerald-700" data-testid="save-edit-client-btn">Salvar</Button></DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Create Client Dialog */}
      <Dialog open={isCreateDialogOpen} onOpenChange={setIsCreateDialogOpen}>
        <DialogContent className="bg-slate-800 border-slate-700 text-white max-w-lg max-h-[90vh] overflow-y-auto">
          <DialogHeader><DialogTitle>Novo Cliente</DialogTitle></DialogHeader>
          <div className="space-y-4 max-h-96 overflow-y-auto">
            <div><Label className="text-slate-300">Nome *</Label><Input value={createFormData.name} onChange={(e) => setCreateFormData({ ...createFormData, name: e.target.value })} className="bg-slate-700 border-slate-600" placeholder="Nome completo" data-testid="create-client-name" /></div>
            <div><Label className="text-slate-300">E-mail *</Label><Input type="email" value={createFormData.email} onChange={(e) => setCreateFormData({ ...createFormData, email: e.target.value })} className="bg-slate-700 border-slate-600" placeholder="email@exemplo.com" data-testid="create-client-email" /></div>
            <div><Label className="text-slate-300">Senha *</Label><Input type="password" value={createFormData.password} onChange={(e) => setCreateFormData({ ...createFormData, password: e.target.value })} className="bg-slate-700 border-slate-600" placeholder="Senha de acesso" data-testid="create-client-password" /></div>
            <div><Label className="text-slate-300">CPF/CNPJ</Label><Input value={createFormData.cpf_cnpj} onChange={(e) => setCreateFormData({ ...createFormData, cpf_cnpj: e.target.value })} className="bg-slate-700 border-slate-600" placeholder="000.000.000-00" /></div>
            <div><Label className="text-slate-300">Telefone</Label><Input value={createFormData.phone} onChange={(e) => setCreateFormData({ ...createFormData, phone: e.target.value })} className="bg-slate-700 border-slate-600" placeholder="(00) 00000-0000" /></div>
            <div><Label className="text-slate-300">Plano</Label>
              <Select value={String(createFormData.plan)} onValueChange={(v) => {
                const newPlan = parseInt(v);
                const minLimit = PLAN_COMPANY_LIMITS[newPlan];
                setCreateFormData({ 
                  ...createFormData, 
                  plan: newPlan,
                  max_companies: minLimit  // Ao trocar plano na criação, usa o default
                });
              }}>
                <SelectTrigger className="bg-slate-700 border-slate-600"><SelectValue /></SelectTrigger>
                <SelectContent><SelectItem value="1">Básico</SelectItem><SelectItem value="2">Advanced</SelectItem><SelectItem value="3">Contábil</SelectItem></SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-slate-300">Qtde Máx. Empresas</Label>
              <div className="flex items-center gap-2">
                <Input 
                  type="number" 
                  min={PLAN_COMPANY_LIMITS[createFormData.plan]} 
                  value={createFormData.max_companies} 
                  onChange={(e) => setCreateFormData({ ...createFormData, max_companies: parseInt(e.target.value) || PLAN_COMPANY_LIMITS[createFormData.plan] })} 
                  className="bg-slate-700 border-slate-600 w-24" 
                />
                <span className="text-xs text-slate-400">(mín: {PLAN_COMPANY_LIMITS[createFormData.plan]})</span>
              </div>
            </div>
            <div><Label className="text-slate-300">Validade do Plano</Label><Input type="date" value={createFormData.plan_expiry} onChange={(e) => setCreateFormData({ ...createFormData, plan_expiry: e.target.value })} className="bg-slate-700 border-slate-600" /></div>
            <div className="flex items-center gap-3">
              <Switch checked={createFormData.is_active} onCheckedChange={(v) => setCreateFormData({ ...createFormData, is_active: v })} className="data-[state=checked]:bg-emerald-600" />
              <Label className="text-slate-300">Cliente Ativo</Label>
            </div>
          </div>
          <DialogFooter><Button onClick={handleCreate} className="bg-emerald-600 hover:bg-emerald-700" data-testid="create-client-submit-btn">Criar Cliente</Button></DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <AlertDialog open={isDeleteDialogOpen} onOpenChange={setIsDeleteDialogOpen}>
        <AlertDialogContent className="bg-slate-800 border-slate-700">
          <AlertDialogHeader>
            <AlertDialogTitle className="text-white flex items-center gap-2">
              <AlertCircle className="w-5 h-5 text-red-500" /> Confirmar Exclusão
            </AlertDialogTitle>
            <AlertDialogDescription className="text-slate-300">
              <p className="mb-2">Você está prestes a excluir o cliente:</p>
              <p className="font-bold text-white mb-4">{clientToDelete?.name} ({clientToDelete?.email})</p>
              <div className="bg-red-900/30 border border-red-700 rounded-lg p-3 text-red-300">
                <p className="font-bold mb-1">⚠️ ATENÇÃO: Esta ação é IRREVERSÍVEL!</p>
                <p>Todos os dados do cliente serão permanentemente excluídos, incluindo:</p>
                <ul className="list-disc list-inside mt-2 text-sm">
                  <li>Todos os lançamentos financeiros</li>
                  <li>Clientes, fornecedores e produtos cadastrados</li>
                  <li>Relatórios e histórico completo</li>
                </ul>
              </div>
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel className="bg-slate-700 text-white hover:bg-slate-600 border-slate-600" data-testid="cancel-delete-client-btn">Cancelar</AlertDialogCancel>
            <AlertDialogAction onClick={handleDeleteConfirm} className="bg-red-600 hover:bg-red-700 text-white" data-testid="confirm-delete-client-btn">
              Excluir Permanentemente
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
};

// ==================== ADMIN CHART OF ACCOUNTS PAGE ====================
const AdminChartAccountsPage = () => {
  const [accounts, setAccounts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [stats, setStats] = useState({ total: 0, analiticas: { receitas: 0, custos: 0, despesas: 0 } });
  const [filterType, setFilterType] = useState('all');
  const [filterLevel, setFilterLevel] = useState('all');
  const [searchTerm, setSearchTerm] = useState('');
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [newAccount, setNewAccount] = useState({ code: '', name: '', account_type: 'DESPESA', level: 5, parent_code: '', is_synthetic: false });
  const { toast } = useToast();

  const loadAccounts = async () => {
    try {
      setLoading(true);
      const [accountsRes, statsRes] = await Promise.all([
        axios.get(`${API}/admin/chart-accounts`),
        axios.get(`${API}/admin/chart-accounts/stats`)
      ]);
      setAccounts(accountsRes.data);
      setStats(statsRes.data);
    } catch (error) {
      toast({ title: "Erro ao carregar contas", variant: "destructive" });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { loadAccounts(); }, []);

  const filteredAccounts = accounts.filter(acc => {
    if (filterType !== 'all' && acc.account_type !== filterType) return false;
    if (filterLevel !== 'all' && acc.level !== parseInt(filterLevel)) return false;
    if (searchTerm && !acc.name.toLowerCase().includes(searchTerm.toLowerCase()) && !acc.code.includes(searchTerm)) return false;
    return true;
  });

  const handleCreate = async (e) => {
    e.preventDefault();
    try {
      await axios.post(`${API}/admin/chart-accounts`, newAccount);
      toast({ title: "Conta criada com sucesso!" });
      setIsDialogOpen(false);
      setNewAccount({ code: '', name: '', account_type: 'DESPESA', level: 5, parent_code: '', is_synthetic: false });
      loadAccounts();
    } catch (error) {
      toast({ title: error.response?.data?.detail || "Erro ao criar conta", variant: "destructive" });
    }
  };

  const handleDelete = async (accountId, accountName) => {
    if (!confirm(`Tem certeza que deseja excluir a conta "${accountName}"?`)) return;
    try {
      await axios.delete(`${API}/admin/chart-accounts/${accountId}`);
      toast({ title: "Conta excluída com sucesso!" });
      loadAccounts();
    } catch (error) {
      toast({ title: error.response?.data?.detail || "Erro ao excluir conta", variant: "destructive" });
    }
  };

  const getTypeColor = (type) => {
    switch (type) {
      case 'RECEITA': return 'bg-emerald-500/20 text-emerald-400';
      case 'CUSTO': return 'bg-amber-500/20 text-amber-400';
      case 'DESPESA': return 'bg-red-500/20 text-red-400';
      default: return 'bg-slate-500/20 text-slate-400';
    }
  };

  const getLevelLabel = (level) => {
    switch (level) {
      case 1: return 'Classe';
      case 2: return 'Grupo';
      case 3: return 'Subgrupo';
      case 4: return 'Sintética';
      case 5: return 'Analítica';
      default: return level;
    }
  };

  return (
    <div className="space-y-6 animate-fadeIn">
      {/* Header */}
      <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-4">
        <div>
          <h1 className="text-2xl font-bold text-white">Plano de Contas</h1>
          <p className="text-slate-400 text-sm">Gerencie as contas contábeis do sistema</p>
        </div>
        <Button onClick={() => setIsDialogOpen(true)} className="bg-emerald-600 hover:bg-emerald-700">
          <Plus className="w-4 h-4 mr-2" /> Nova Conta
        </Button>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <Card className="bg-slate-800 border-slate-700">
          <CardContent className="p-4">
            <p className="text-slate-400 text-sm">Total de Contas</p>
            <p className="text-2xl font-bold text-white">{stats.total}</p>
          </CardContent>
        </Card>
        <Card className="bg-slate-800 border-slate-700">
          <CardContent className="p-4">
            <p className="text-emerald-400 text-sm">Receitas</p>
            <p className="text-2xl font-bold text-white">{stats.analiticas?.receitas || 0}</p>
          </CardContent>
        </Card>
        <Card className="bg-slate-800 border-slate-700">
          <CardContent className="p-4">
            <p className="text-amber-400 text-sm">Custos</p>
            <p className="text-2xl font-bold text-white">{stats.analiticas?.custos || 0}</p>
          </CardContent>
        </Card>
        <Card className="bg-slate-800 border-slate-700">
          <CardContent className="p-4">
            <p className="text-red-400 text-sm">Despesas</p>
            <p className="text-2xl font-bold text-white">{stats.analiticas?.despesas || 0}</p>
          </CardContent>
        </Card>
      </div>

      {/* Filters */}
      <Card className="bg-slate-800 border-slate-700">
        <CardContent className="p-4">
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
            <div>
              <Label className="text-slate-300">Buscar</Label>
              <Input
                placeholder="Nome ou código..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="bg-slate-700 border-slate-600 text-white"
              />
            </div>
            <div>
              <Label className="text-slate-300">Tipo</Label>
              <Select value={filterType} onValueChange={setFilterType}>
                <SelectTrigger className="bg-slate-700 border-slate-600 text-white">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">Todos</SelectItem>
                  <SelectItem value="RECEITA">Receitas</SelectItem>
                  <SelectItem value="CUSTO">Custos</SelectItem>
                  <SelectItem value="DESPESA">Despesas</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-slate-300">Nível</Label>
              <Select value={filterLevel} onValueChange={setFilterLevel}>
                <SelectTrigger className="bg-slate-700 border-slate-600 text-white">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">Todos</SelectItem>
                  <SelectItem value="1">1 - Classe</SelectItem>
                  <SelectItem value="2">2 - Grupo</SelectItem>
                  <SelectItem value="3">3 - Subgrupo</SelectItem>
                  <SelectItem value="4">4 - Sintética</SelectItem>
                  <SelectItem value="5">5 - Analítica</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="flex items-end">
              <Button variant="outline" onClick={() => { setSearchTerm(''); setFilterType('all'); setFilterLevel('all'); }} className="w-full border-slate-600 text-slate-300 hover:bg-slate-700">
                Limpar Filtros
              </Button>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Table */}
      <Card className="bg-slate-800 border-slate-700">
        <CardContent className="p-0">
          {loading ? (
            <div className="flex items-center justify-center py-12">
              <div className="h-8 w-8 animate-spin rounded-full border-4 border-emerald-500 border-t-transparent"></div>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <Table>
                <TableHeader>
                  <TableRow className="border-slate-700 hover:bg-slate-700/50">
                    <TableHead className="text-slate-300">Código</TableHead>
                    <TableHead className="text-slate-300">Nome</TableHead>
                    <TableHead className="text-slate-300">Tipo</TableHead>
                    <TableHead className="text-slate-300">Nível</TableHead>
                    <TableHead className="text-slate-300">Categoria</TableHead>
                    <TableHead className="text-slate-300 text-right">Ações</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {filteredAccounts.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={6} className="text-center text-slate-400 py-8">
                        Nenhuma conta encontrada
                      </TableCell>
                    </TableRow>
                  ) : (
                    filteredAccounts.map((acc) => (
                      <TableRow key={acc.id} className="border-slate-700 hover:bg-slate-700/50">
                        <TableCell className="font-mono text-slate-300">{acc.code}</TableCell>
                        <TableCell className="text-white" style={{ paddingLeft: `${(acc.level - 1) * 16}px` }}>
                          {acc.is_synthetic ? <span className="text-slate-400">{acc.name}</span> : acc.name}
                        </TableCell>
                        <TableCell>
                          <Badge className={getTypeColor(acc.account_type)}>{acc.account_type}</Badge>
                        </TableCell>
                        <TableCell className="text-slate-300">{acc.level}</TableCell>
                        <TableCell className="text-slate-400 text-sm">{getLevelLabel(acc.level)}</TableCell>
                        <TableCell className="text-right">
                          {!acc.is_synthetic && (
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => handleDelete(acc.id, acc.name)}
                              className="text-red-400 hover:text-red-300 hover:bg-red-500/20"
                            >
                              <Trash2 className="w-4 h-4" />
                            </Button>
                          )}
                        </TableCell>
                      </TableRow>
                    ))
                  )}
                </TableBody>
              </Table>
            </div>
          )}
          <div className="p-4 border-t border-slate-700">
            <p className="text-sm text-slate-400">
              Exibindo {filteredAccounts.length} de {accounts.length} contas
            </p>
          </div>
        </CardContent>
      </Card>

      {/* Create Dialog */}
      <Dialog open={isDialogOpen} onOpenChange={setIsDialogOpen}>
        <DialogContent className="bg-slate-800 border-slate-700 text-white">
          <DialogHeader>
            <DialogTitle>Nova Conta Contábil</DialogTitle>
            <DialogDescription className="text-slate-400">Adicione uma nova conta ao plano de contas</DialogDescription>
          </DialogHeader>
          <form onSubmit={handleCreate} className="space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label className="text-slate-300">Código *</Label>
                <Input
                  required
                  placeholder="Ex: 3.1.1.05.009"
                  value={newAccount.code}
                  onChange={(e) => setNewAccount({ ...newAccount, code: e.target.value })}
                  className="bg-slate-700 border-slate-600"
                />
              </div>
              <div>
                <Label className="text-slate-300">Tipo *</Label>
                <Select value={newAccount.account_type} onValueChange={(v) => setNewAccount({ ...newAccount, account_type: v })}>
                  <SelectTrigger className="bg-slate-700 border-slate-600">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="RECEITA">Receita</SelectItem>
                    <SelectItem value="CUSTO">Custo</SelectItem>
                    <SelectItem value="DESPESA">Despesa</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
            <div>
              <Label className="text-slate-300">Nome *</Label>
              <Input
                required
                placeholder="Nome da conta"
                value={newAccount.name}
                onChange={(e) => setNewAccount({ ...newAccount, name: e.target.value })}
                className="bg-slate-700 border-slate-600"
              />
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label className="text-slate-300">Nível *</Label>
                <Select value={String(newAccount.level)} onValueChange={(v) => setNewAccount({ ...newAccount, level: parseInt(v), is_synthetic: parseInt(v) < 5 })}>
                  <SelectTrigger className="bg-slate-700 border-slate-600">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="1">1 - Classe</SelectItem>
                    <SelectItem value="2">2 - Grupo</SelectItem>
                    <SelectItem value="3">3 - Subgrupo</SelectItem>
                    <SelectItem value="4">4 - Sintética</SelectItem>
                    <SelectItem value="5">5 - Analítica</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label className="text-slate-300">Código Pai</Label>
                <Input
                  placeholder="Ex: 3.1.1.05"
                  value={newAccount.parent_code}
                  onChange={(e) => setNewAccount({ ...newAccount, parent_code: e.target.value })}
                  className="bg-slate-700 border-slate-600"
                />
              </div>
            </div>
            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setIsDialogOpen(false)} className="border-slate-600 text-slate-300">
                Cancelar
              </Button>
              <Button type="submit" className="bg-emerald-600 hover:bg-emerald-700">
                Criar Conta
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
};

// Clients Report Page
const AdminReportClientsPage = () => {
  const [clients, setClients] = useState([]);
  const [loading, setLoading] = useState(true);
  const [stats, setStats] = useState({ total: 0, by_plan: {} });
  const [filters, setFilters] = useState({ name: '', cpf_cnpj: '', plan: '', expiry_from: '', expiry_to: '' });
  const { toast } = useToast();

  useEffect(() => { loadReport(); }, []);

  const loadReport = async () => {
    try {
      setLoading(true);
      const params = new URLSearchParams();
      if (filters.name) params.append('name', filters.name);
      if (filters.cpf_cnpj) params.append('cpf_cnpj', filters.cpf_cnpj);
      if (filters.plan) params.append('plan', filters.plan);
      if (filters.expiry_from) params.append('expiry_from', filters.expiry_from);
      if (filters.expiry_to) params.append('expiry_to', filters.expiry_to);
      
      const response = await axios.get(`${API}/admin/reports/clients?${params.toString()}`);
      setClients(response.data.clients);
      setStats({ total: response.data.total, by_plan: response.data.by_plan });
    } catch (error) {
      toast({ title: 'Erro ao carregar relatório', variant: 'destructive' });
    } finally {
      setLoading(false);
    }
  };

  const handlePrint = () => {
    window.print();
  };

  return (
    <div className="space-y-6">
      <div className="flex justify-between items-center">
        <h2 className="text-2xl font-bold text-white">Relatório de Clientes</h2>
        <Button onClick={handlePrint} className="bg-slate-700 hover:bg-slate-600"><Printer className="w-4 h-4 mr-2" /> Imprimir</Button>
      </div>

      {/* Filters */}
      <Card className="bg-slate-800 border-slate-700 p-4">
        <div className="grid grid-cols-1 md:grid-cols-5 gap-4">
          <div><Label className="text-slate-300 text-xs">Nome</Label><Input value={filters.name} onChange={(e) => setFilters({ ...filters, name: e.target.value })} placeholder="Filtrar por nome" className="bg-slate-700 border-slate-600 text-white" /></div>
          <div><Label className="text-slate-300 text-xs">CPF/CNPJ</Label><Input value={filters.cpf_cnpj} onChange={(e) => setFilters({ ...filters, cpf_cnpj: e.target.value })} placeholder="Filtrar por CPF/CNPJ" className="bg-slate-700 border-slate-600 text-white" /></div>
          <div><Label className="text-slate-300 text-xs">Plano</Label>
            <Select value={filters.plan || "all"} onValueChange={(v) => setFilters({ ...filters, plan: v === "all" ? "" : v })}>
              <SelectTrigger className="bg-slate-700 border-slate-600 text-white"><SelectValue placeholder="Todos" /></SelectTrigger>
              <SelectContent><SelectItem value="all">Todos</SelectItem><SelectItem value="1">Básico</SelectItem><SelectItem value="2">Advanced</SelectItem><SelectItem value="3">Contábil</SelectItem></SelectContent>
            </Select>
          </div>
          <div><Label className="text-slate-300 text-xs">Validade De</Label><Input type="date" value={filters.expiry_from} onChange={(e) => setFilters({ ...filters, expiry_from: e.target.value })} className="bg-slate-700 border-slate-600 text-white" /></div>
          <div><Label className="text-slate-300 text-xs">Validade Até</Label><Input type="date" value={filters.expiry_to} onChange={(e) => setFilters({ ...filters, expiry_to: e.target.value })} className="bg-slate-700 border-slate-600 text-white" /></div>
        </div>
        <Button onClick={loadReport} className="mt-4 bg-emerald-600 hover:bg-emerald-700">Filtrar</Button>
      </Card>

      {/* Stats */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <Card className="bg-slate-800 border-slate-700 p-4"><p className="text-slate-400 text-sm">Total</p><p className="text-2xl font-bold text-white">{stats.total}</p></Card>
        <Card className="bg-slate-800 border-slate-700 p-4"><p className="text-slate-400 text-sm">Básico</p><p className="text-2xl font-bold text-slate-300">{stats.by_plan?.basico || 0}</p></Card>
        <Card className="bg-slate-800 border-slate-700 p-4"><p className="text-slate-400 text-sm">Advanced</p><p className="text-2xl font-bold text-blue-400">{stats.by_plan?.advanced || 0}</p></Card>
        <Card className="bg-slate-800 border-slate-700 p-4"><p className="text-slate-400 text-sm">Contábil</p><p className="text-2xl font-bold text-purple-400">{stats.by_plan?.contabil || 0}</p></Card>
      </div>

      {/* Table */}
      <Card className="bg-slate-800 border-slate-700 print:bg-white print:text-black">
        <Table>
          <TableHeader>
            <TableRow className="border-slate-700">
              <TableHead className="text-slate-300 print:text-black">Nome</TableHead>
              <TableHead className="text-slate-300 print:text-black">E-mail</TableHead>
              <TableHead className="text-slate-300 print:text-black">CPF/CNPJ</TableHead>
              <TableHead className="text-slate-300 print:text-black">Plano</TableHead>
              <TableHead className="text-slate-300 print:text-black">Validade</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {loading ? (
              <TableRow><TableCell colSpan={5} className="text-center text-slate-400 py-8">Carregando...</TableCell></TableRow>
            ) : clients.length === 0 ? (
              <TableRow><TableCell colSpan={5} className="text-center text-slate-400 py-8">Nenhum cliente encontrado</TableCell></TableRow>
            ) : clients.map((client) => (
              <TableRow key={client.user_id} className="border-slate-700">
                <TableCell className="text-white print:text-black">{client.name}</TableCell>
                <TableCell className="text-slate-300 print:text-black">{client.email}</TableCell>
                <TableCell className="text-slate-300 print:text-black">{client.cpf_cnpj || '-'}</TableCell>
                <TableCell className="print:text-black">{client.plan_name}</TableCell>
                <TableCell className="text-slate-300 print:text-black">{client.plan_expiry || '-'}</TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </Card>
    </div>
  );
};

// Admin Dashboard Page
const AdminDashboardPage = ({ admin, onNavigate }) => {
  const [stats, setStats] = useState({ total: 0, by_plan: {} });

  useEffect(() => {
    const loadStats = async () => {
      try {
        const response = await axios.get(`${API}/admin/reports/clients`);
        setStats({ total: response.data.total, by_plan: response.data.by_plan });
      } catch (error) {
        console.error('Error loading stats:', error);
      }
    };
    loadStats();
  }, []);

  return (
    <div className="space-y-6">
      <h2 className="text-2xl font-bold text-white">Dashboard</h2>
      <p className="text-slate-400">Bem-vindo, {admin?.name}!</p>

      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <Card 
          className="bg-slate-800 border-slate-700 p-6 cursor-pointer hover:bg-slate-700 transition-colors"
          onClick={() => onNavigate && onNavigate('clients-plans')}
          data-testid="admin-total-clients-card"
        >
          <div className="flex items-center gap-4">
            <div className="p-3 bg-emerald-600/20 rounded-lg"><Users className="w-6 h-6 text-emerald-500" /></div>
            <div><p className="text-slate-400 text-sm">Total Clientes</p><p className="text-2xl font-bold text-white">{stats.total}</p></div>
          </div>
        </Card>
        <Card className="bg-slate-800 border-slate-700 p-6">
          <div className="flex items-center gap-4">
            <div className="p-3 bg-slate-600/20 rounded-lg"><User className="w-6 h-6 text-slate-400" /></div>
            <div><p className="text-slate-400 text-sm">Plano Básico</p><p className="text-2xl font-bold text-slate-300">{stats.by_plan?.basico || 0}</p></div>
          </div>
        </Card>
        <Card className="bg-slate-800 border-slate-700 p-6">
          <div className="flex items-center gap-4">
            <div className="p-3 bg-blue-600/20 rounded-lg"><TrendingUp className="w-6 h-6 text-blue-400" /></div>
            <div><p className="text-slate-400 text-sm">Plano Advanced</p><p className="text-2xl font-bold text-blue-400">{stats.by_plan?.advanced || 0}</p></div>
          </div>
        </Card>
        <Card className="bg-slate-800 border-slate-700 p-6">
          <div className="flex items-center gap-4">
            <div className="p-3 bg-purple-600/20 rounded-lg"><FileText className="w-6 h-6 text-purple-400" /></div>
            <div><p className="text-slate-400 text-sm">Plano Contábil</p><p className="text-2xl font-bold text-purple-400">{stats.by_plan?.contabil || 0}</p></div>
          </div>
        </Card>
      </div>
    </div>
  );
};

// Admin Email Report Page
const AdminReportEmailsPage = () => {
  const [data, setData] = useState({ events: [], logs: [], stats: {} });
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('all');
  const { toast } = useToast();

  useEffect(() => {
    loadReport();
  }, []);

  const loadReport = async () => {
    try {
      const response = await axios.get(`${API}/admin/reports/emails`);
      setData(response.data);
    } catch (error) {
      toast({ title: 'Erro ao carregar relatório', variant: 'destructive' });
    } finally {
      setLoading(false);
    }
  };

  const filteredEvents = filter === 'all' 
    ? data.events 
    : data.events.filter(e => e.type?.includes(filter));

  const getStatusBadge = (type) => {
    if (type?.includes('delivered')) return <Badge className="bg-emerald-600">Entregue</Badge>;
    if (type?.includes('bounced')) return <Badge className="bg-red-600">Rejeitado</Badge>;
    if (type?.includes('complained')) return <Badge className="bg-orange-600">Spam</Badge>;
    if (type?.includes('opened')) return <Badge className="bg-blue-600">Aberto</Badge>;
    if (type?.includes('clicked')) return <Badge className="bg-purple-600">Clicado</Badge>;
    return <Badge className="bg-slate-600">{type}</Badge>;
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return '-';
    try {
      return new Date(dateStr).toLocaleString('pt-BR');
    } catch {
      return dateStr;
    }
  };

  return (
    <div className="space-y-6">
      <div className="flex justify-between items-center">
        <h2 className="text-2xl font-bold text-white">Relatório de Emails</h2>
        <Button onClick={loadReport} variant="outline" className="border-slate-600 text-slate-300">
          Atualizar
        </Button>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-2 md:grid-cols-6 gap-4">
        <Card className="bg-slate-800 border-slate-700">
          <CardContent className="p-4 text-center">
            <p className="text-2xl font-bold text-white">{data.stats.total || 0}</p>
            <p className="text-xs text-slate-400">Total</p>
          </CardContent>
        </Card>
        <Card className="bg-slate-800 border-slate-700">
          <CardContent className="p-4 text-center">
            <p className="text-2xl font-bold text-emerald-400">{data.stats.delivered || 0}</p>
            <p className="text-xs text-slate-400">Entregues</p>
          </CardContent>
        </Card>
        <Card className="bg-slate-800 border-slate-700">
          <CardContent className="p-4 text-center">
            <p className="text-2xl font-bold text-red-400">{data.stats.bounced || 0}</p>
            <p className="text-xs text-slate-400">Rejeitados</p>
          </CardContent>
        </Card>
        <Card className="bg-slate-800 border-slate-700">
          <CardContent className="p-4 text-center">
            <p className="text-2xl font-bold text-blue-400">{data.stats.opened || 0}</p>
            <p className="text-xs text-slate-400">Abertos</p>
          </CardContent>
        </Card>
        <Card className="bg-slate-800 border-slate-700">
          <CardContent className="p-4 text-center">
            <p className="text-2xl font-bold text-purple-400">{data.stats.clicked || 0}</p>
            <p className="text-xs text-slate-400">Clicados</p>
          </CardContent>
        </Card>
        <Card className="bg-slate-800 border-slate-700">
          <CardContent className="p-4 text-center">
            <p className="text-2xl font-bold text-orange-400">{data.stats.complained || 0}</p>
            <p className="text-xs text-slate-400">Spam</p>
          </CardContent>
        </Card>
      </div>

      {/* Filters */}
      <div className="flex gap-2 flex-wrap">
        {['all', 'delivered', 'bounced', 'opened', 'clicked'].map((f) => (
          <Button
            key={f}
            variant={filter === f ? 'default' : 'outline'}
            size="sm"
            onClick={() => setFilter(f)}
            className={filter !== f ? 'border-slate-600 text-slate-300' : ''}
          >
            {f === 'all' ? 'Todos' : f === 'delivered' ? 'Entregues' : f === 'bounced' ? 'Rejeitados' : f === 'opened' ? 'Abertos' : 'Clicados'}
          </Button>
        ))}
      </div>

      {/* Events Table */}
      <Card className="bg-slate-800 border-slate-700">
        <CardHeader>
          <CardTitle className="text-white">Eventos de Webhook</CardTitle>
          <CardDescription className="text-slate-400">
            Eventos recebidos do Resend (delivered, bounced, etc.)
          </CardDescription>
        </CardHeader>
        <CardContent>
          <Table>
            <TableHeader>
              <TableRow className="border-slate-700">
                <TableHead className="text-slate-300">Data</TableHead>
                <TableHead className="text-slate-300">Destinatário</TableHead>
                <TableHead className="text-slate-300">Assunto</TableHead>
                <TableHead className="text-slate-300">Status</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {loading ? (
                <TableRow>
                  <TableCell colSpan={4} className="text-center text-slate-400 py-8">Carregando...</TableCell>
                </TableRow>
              ) : filteredEvents.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={4} className="text-center text-slate-400 py-8">
                    Nenhum evento registrado ainda. Configure o webhook do Resend para receber eventos.
                  </TableCell>
                </TableRow>
              ) : filteredEvents.map((event, idx) => (
                <TableRow key={idx} className="border-slate-700">
                  <TableCell className="text-slate-300">{formatDate(event.received_at)}</TableCell>
                  <TableCell className="text-white">{event.to || '-'}</TableCell>
                  <TableCell className="text-slate-300">{event.subject || '-'}</TableCell>
                  <TableCell>{getStatusBadge(event.type)}</TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </CardContent>
      </Card>

      {/* Email Logs Table */}
      <Card className="bg-slate-800 border-slate-700">
        <CardHeader>
          <CardTitle className="text-white">Emails Enviados</CardTitle>
          <CardDescription className="text-slate-400">
            Log de emails enviados pela aplicação
          </CardDescription>
        </CardHeader>
        <CardContent>
          <Table>
            <TableHeader>
              <TableRow className="border-slate-700">
                <TableHead className="text-slate-300">Data</TableHead>
                <TableHead className="text-slate-300">Tipo</TableHead>
                <TableHead className="text-slate-300">Destinatário</TableHead>
                <TableHead className="text-slate-300">Assunto</TableHead>
                <TableHead className="text-slate-300">Status</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {data.logs.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={5} className="text-center text-slate-400 py-8">
                    Nenhum email enviado ainda.
                  </TableCell>
                </TableRow>
              ) : data.logs.map((log, idx) => (
                <TableRow key={idx} className="border-slate-700">
                  <TableCell className="text-slate-300">{formatDate(log.created_at)}</TableCell>
                  <TableCell><Badge className="bg-slate-600">{log.type || 'email'}</Badge></TableCell>
                  <TableCell className="text-white">{log.to}</TableCell>
                  <TableCell className="text-slate-300">{log.subject}</TableCell>
                  <TableCell>{getStatusBadge(log.status)}</TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </CardContent>
      </Card>

      {/* Webhook Info */}
      <Card className="bg-slate-800/50 border-slate-700">
        <CardContent className="p-4">
          <p className="text-sm text-slate-400">
            <strong className="text-white">URL do Webhook:</strong>{' '}
            <code className="bg-slate-700 px-2 py-1 rounded text-emerald-400">
              https://app.dremais.com.br/api/webhooks/resend
            </code>
          </p>
          <p className="text-xs text-slate-500 mt-2">
            Configure esta URL no painel do Resend para receber eventos de delivered, bounced, complained, opened e clicked.
          </p>
        </CardContent>
      </Card>
    </div>
  );
};

// ==================== ADMIN REPORT CHART ACCOUNTS PAGE ====================
const AdminReportChartAccountsPage = () => {
  const [accounts, setAccounts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [stats, setStats] = useState({ total: 0, analiticas: { receitas: 0, custos: 0, despesas: 0 } });
  const [filterType, setFilterType] = useState('all');
  const [filterLevel, setFilterLevel] = useState('all');
  const [filterSynthetic, setFilterSynthetic] = useState('all');
  const [searchTerm, setSearchTerm] = useState('');
  const { toast } = useToast();

  const loadAccounts = async () => {
    try {
      setLoading(true);
      const [accountsRes, statsRes] = await Promise.all([
        axios.get(`${API}/admin/chart-accounts`),
        axios.get(`${API}/admin/chart-accounts/stats`)
      ]);
      setAccounts(accountsRes.data);
      setStats(statsRes.data);
    } catch (error) {
      toast({ title: "Erro ao carregar contas", variant: "destructive" });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { loadAccounts(); }, []);

  const filteredAccounts = accounts.filter(acc => {
    if (filterType !== 'all' && acc.account_type !== filterType) return false;
    if (filterLevel !== 'all' && acc.level !== parseInt(filterLevel)) return false;
    if (filterSynthetic === 'synthetic' && !acc.is_synthetic) return false;
    if (filterSynthetic === 'analytical' && acc.is_synthetic) return false;
    if (searchTerm && !acc.name.toLowerCase().includes(searchTerm.toLowerCase()) && !acc.code.includes(searchTerm)) return false;
    return true;
  });

  const getTypeColor = (type) => {
    switch (type) {
      case 'RECEITA': return 'bg-emerald-500/20 text-emerald-400';
      case 'CUSTO': return 'bg-amber-500/20 text-amber-400';
      case 'DESPESA': return 'bg-red-500/20 text-red-400';
      default: return 'bg-slate-500/20 text-slate-400';
    }
  };

  const getLevelLabel = (level) => {
    switch (level) {
      case 1: return 'Classe';
      case 2: return 'Grupo';
      case 3: return 'Subgrupo';
      case 4: return 'Sintética';
      case 5: return 'Analítica';
      default: return level;
    }
  };

  const handlePrint = () => {
    const filterDescription = [];
    if (filterType !== 'all') filterDescription.push(`Tipo: ${filterType}`);
    if (filterLevel !== 'all') filterDescription.push(`Nível: ${filterLevel}`);
    if (filterSynthetic !== 'all') filterDescription.push(`Categoria: ${filterSynthetic === 'synthetic' ? 'Sintéticas' : 'Analíticas'}`);
    if (searchTerm) filterDescription.push(`Busca: "${searchTerm}"`);

    const printContent = `
      <!DOCTYPE html>
      <html>
      <head>
        <title>Relatório - Plano de Contas</title>
        <style>
          body { font-family: Arial, sans-serif; padding: 20px; font-size: 12px; }
          h1 { font-size: 18px; margin-bottom: 5px; }
          .subtitle { color: #666; margin-bottom: 15px; font-size: 11px; }
          .filters { background: #f5f5f5; padding: 10px; margin-bottom: 15px; border-radius: 4px; font-size: 11px; }
          .stats { display: flex; gap: 20px; margin-bottom: 15px; }
          .stat { background: #f0f0f0; padding: 8px 12px; border-radius: 4px; }
          .stat-label { font-size: 10px; color: #666; }
          .stat-value { font-size: 16px; font-weight: bold; }
          table { width: 100%; border-collapse: collapse; margin-top: 10px; }
          th, td { border: 1px solid #ddd; padding: 6px 8px; text-align: left; }
          th { background: #f5f5f5; font-weight: bold; }
          .code { font-family: monospace; }
          .synthetic { color: #888; font-style: italic; }
          .type-receita { color: #059669; }
          .type-custo { color: #d97706; }
          .type-despesa { color: #dc2626; }
          .footer { margin-top: 20px; text-align: center; font-size: 10px; color: #666; }
          @media print { body { padding: 0; } }
        </style>
      </head>
      <body>
        <h1>Relatório - Plano de Contas</h1>
        <p class="subtitle">Gerado em ${new Date().toLocaleString('pt-BR')}</p>
        
        ${filterDescription.length > 0 ? `<div class="filters"><strong>Filtros aplicados:</strong> ${filterDescription.join(' | ')}</div>` : ''}
        
        <div class="stats">
          <div class="stat"><div class="stat-label">Total</div><div class="stat-value">${filteredAccounts.length}</div></div>
          <div class="stat"><div class="stat-label">Receitas</div><div class="stat-value">${filteredAccounts.filter(a => a.account_type === 'RECEITA').length}</div></div>
          <div class="stat"><div class="stat-label">Custos</div><div class="stat-value">${filteredAccounts.filter(a => a.account_type === 'CUSTO').length}</div></div>
          <div class="stat"><div class="stat-label">Despesas</div><div class="stat-value">${filteredAccounts.filter(a => a.account_type === 'DESPESA').length}</div></div>
        </div>
        
        <table>
          <thead>
            <tr>
              <th>Código</th>
              <th>Nome</th>
              <th>Tipo</th>
              <th>Nível</th>
              <th>Categoria</th>
            </tr>
          </thead>
          <tbody>
            ${filteredAccounts.map(acc => `
              <tr>
                <td class="code">${acc.code}</td>
                <td class="${acc.is_synthetic ? 'synthetic' : ''}" style="padding-left: ${(acc.level - 1) * 12}px">${acc.name}</td>
                <td class="type-${acc.account_type.toLowerCase()}">${acc.account_type}</td>
                <td>${acc.level}</td>
                <td>${getLevelLabel(acc.level)}</td>
              </tr>
            `).join('')}
          </tbody>
        </table>
        
        <div class="footer">DRE+ Controle Financeiro - Relatório de Plano de Contas</div>
      </body>
      </html>
    `;

    const printWindow = window.open('', '_blank');
    printWindow.document.write(printContent);
    printWindow.document.close();
    printWindow.focus();
    setTimeout(() => { printWindow.print(); }, 250);
  };

  return (
    <div className="space-y-6 animate-fadeIn">
      {/* Header */}
      <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-4">
        <div>
          <h1 className="text-2xl font-bold text-white">Relatório - Plano de Contas</h1>
          <p className="text-slate-400 text-sm">Visualize e exporte o plano de contas completo</p>
        </div>
        <Button onClick={handlePrint} className="bg-emerald-600 hover:bg-emerald-700">
          <Printer className="w-4 h-4 mr-2" /> Imprimir / PDF
        </Button>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
        <Card className="bg-slate-800 border-slate-700">
          <CardContent className="p-4">
            <p className="text-slate-400 text-sm">Total Geral</p>
            <p className="text-2xl font-bold text-white">{stats.total}</p>
          </CardContent>
        </Card>
        <Card className="bg-slate-800 border-slate-700">
          <CardContent className="p-4">
            <p className="text-slate-400 text-sm">Filtradas</p>
            <p className="text-2xl font-bold text-blue-400">{filteredAccounts.length}</p>
          </CardContent>
        </Card>
        <Card className="bg-slate-800 border-slate-700">
          <CardContent className="p-4">
            <p className="text-emerald-400 text-sm">Receitas (Anal.)</p>
            <p className="text-2xl font-bold text-white">{stats.analiticas?.receitas || 0}</p>
          </CardContent>
        </Card>
        <Card className="bg-slate-800 border-slate-700">
          <CardContent className="p-4">
            <p className="text-amber-400 text-sm">Custos (Anal.)</p>
            <p className="text-2xl font-bold text-white">{stats.analiticas?.custos || 0}</p>
          </CardContent>
        </Card>
        <Card className="bg-slate-800 border-slate-700">
          <CardContent className="p-4">
            <p className="text-red-400 text-sm">Despesas (Anal.)</p>
            <p className="text-2xl font-bold text-white">{stats.analiticas?.despesas || 0}</p>
          </CardContent>
        </Card>
      </div>

      {/* Filters */}
      <Card className="bg-slate-800 border-slate-700">
        <CardContent className="p-4">
          <div className="grid grid-cols-1 md:grid-cols-5 gap-4">
            <div>
              <Label className="text-slate-300">Buscar</Label>
              <Input
                placeholder="Nome ou código..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="bg-slate-700 border-slate-600 text-white"
              />
            </div>
            <div>
              <Label className="text-slate-300">Tipo</Label>
              <Select value={filterType} onValueChange={setFilterType}>
                <SelectTrigger className="bg-slate-700 border-slate-600 text-white">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">Todos</SelectItem>
                  <SelectItem value="RECEITA">Receitas</SelectItem>
                  <SelectItem value="CUSTO">Custos</SelectItem>
                  <SelectItem value="DESPESA">Despesas</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-slate-300">Nível</Label>
              <Select value={filterLevel} onValueChange={setFilterLevel}>
                <SelectTrigger className="bg-slate-700 border-slate-600 text-white">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">Todos</SelectItem>
                  <SelectItem value="1">1 - Classe</SelectItem>
                  <SelectItem value="2">2 - Grupo</SelectItem>
                  <SelectItem value="3">3 - Subgrupo</SelectItem>
                  <SelectItem value="4">4 - Sintética</SelectItem>
                  <SelectItem value="5">5 - Analítica</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-slate-300">Categoria</Label>
              <Select value={filterSynthetic} onValueChange={setFilterSynthetic}>
                <SelectTrigger className="bg-slate-700 border-slate-600 text-white">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">Todas</SelectItem>
                  <SelectItem value="synthetic">Sintéticas</SelectItem>
                  <SelectItem value="analytical">Analíticas</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="flex items-end">
              <Button variant="outline" onClick={() => { setSearchTerm(''); setFilterType('all'); setFilterLevel('all'); setFilterSynthetic('all'); }} className="w-full border-slate-600 text-slate-300 hover:bg-slate-700">
                Limpar Filtros
              </Button>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Table */}
      <Card className="bg-slate-800 border-slate-700">
        <CardContent className="p-0">
          {loading ? (
            <div className="flex items-center justify-center py-12">
              <div className="h-8 w-8 animate-spin rounded-full border-4 border-emerald-500 border-t-transparent"></div>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <Table>
                <TableHeader>
                  <TableRow className="border-slate-700 hover:bg-slate-700/50">
                    <TableHead className="text-slate-300">Código</TableHead>
                    <TableHead className="text-slate-300">Nome</TableHead>
                    <TableHead className="text-slate-300">Tipo</TableHead>
                    <TableHead className="text-slate-300">Nível</TableHead>
                    <TableHead className="text-slate-300">Categoria</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {filteredAccounts.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={5} className="text-center text-slate-400 py-8">
                        Nenhuma conta encontrada com os filtros selecionados
                      </TableCell>
                    </TableRow>
                  ) : (
                    filteredAccounts.map((acc) => (
                      <TableRow key={acc.id} className="border-slate-700 hover:bg-slate-700/50">
                        <TableCell className="font-mono text-slate-300">{acc.code}</TableCell>
                        <TableCell className="text-white" style={{ paddingLeft: `${(acc.level - 1) * 16}px` }}>
                          {acc.is_synthetic ? <span className="text-slate-400 italic">{acc.name}</span> : acc.name}
                        </TableCell>
                        <TableCell>
                          <Badge className={getTypeColor(acc.account_type)}>{acc.account_type}</Badge>
                        </TableCell>
                        <TableCell className="text-slate-300">{acc.level}</TableCell>
                        <TableCell className="text-slate-400 text-sm">{getLevelLabel(acc.level)}</TableCell>
                      </TableRow>
                    ))
                  )}
                </TableBody>
              </Table>
            </div>
          )}
          <div className="p-4 border-t border-slate-700">
            <p className="text-sm text-slate-400">
              Exibindo {filteredAccounts.length} de {accounts.length} contas
            </p>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

// ==================== ADMIN CONFIG PAGE ====================
const AdminConfigPage = () => {
  const [config, setConfig] = useState({
    registration_validity_days: 30
  });
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [migrating, setMigrating] = useState(false);
  const [migrationResult, setMigrationResult] = useState(null);
  const { toast } = useToast();

  useEffect(() => {
    loadConfig();
  }, []);

  const loadConfig = async () => {
    try {
      const response = await axios.get(`${API}/admin/config`);
      setConfig(response.data);
    } catch (error) {
      console.error('Error loading config:', error);
      toast({ title: "Erro ao carregar configurações", variant: "destructive" });
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      const response = await axios.put(`${API}/admin/config`, {
        registration_validity_days: config.registration_validity_days
      });
      setConfig(response.data);
      toast({ title: "Configurações salvas com sucesso!" });
    } catch (error) {
      const message = error.response?.data?.detail || "Erro ao salvar configurações";
      toast({ title: message, variant: "destructive" });
    } finally {
      setSaving(false);
    }
  };

  const handleMigrateAllUsers = async () => {
    if (!window.confirm("⚠️ ATENÇÃO: Esta operação irá migrar TODOS os dados de TODOS os clientes para suas Contas Pessoais e deletar segmentos legados. Deseja continuar?")) {
      return;
    }
    setMigrating(true);
    setMigrationResult(null);
    try {
      const response = await axios.post(`${API}/admin/migrate-all-users`);
      setMigrationResult(response.data.results);
      toast({ title: "Migração concluída com sucesso!" });
    } catch (error) {
      const message = error.response?.data?.detail || "Erro ao executar migração";
      toast({ title: message, variant: "destructive" });
    } finally {
      setMigrating(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-emerald-500"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6 animate-fadeIn" data-testid="admin-config-page">
      <div>
        <h2 className="text-2xl font-bold text-white">Configurações do Sistema</h2>
        <p className="text-slate-400">Gerencie as configurações gerais da aplicação</p>
      </div>

      {/* Configurações de Cadastro */}
      <Card className="bg-slate-800/50 border-slate-700">
        <CardHeader>
          <CardTitle className="text-white flex items-center gap-2">
            <UserPlus className="w-5 h-5 text-emerald-500" />
            Configurações de Cadastro
          </CardTitle>
          <CardDescription className="text-slate-400">
            Defina as configurações padrão para novos cadastros de clientes
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-6">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div className="space-y-2">
              <Label className="text-slate-300">Validade Inicial do Cadastro (dias)</Label>
              <div className="flex items-center gap-3">
                <Input
                  type="number"
                  min="1"
                  max="365"
                  value={config.registration_validity_days || 30}
                  onChange={(e) => setConfig({ ...config, registration_validity_days: parseInt(e.target.value) || 30 })}
                  className="w-24 bg-slate-700 border-slate-600 text-white"
                  data-testid="validity-days-input"
                />
                <span className="text-slate-400 text-sm">dias</span>
              </div>
              <p className="text-xs text-slate-500">
                Número de dias de validade ao criar um novo cadastro de cliente (mínimo: 1, máximo: 365)
              </p>
            </div>
          </div>

          {/* Info sobre última atualização */}
          {config.updated_at && (
            <div className="pt-4 border-t border-slate-700">
              <p className="text-xs text-slate-500">
                Última atualização: {new Date(config.updated_at).toLocaleString('pt-BR')}
                {config.updated_by && ` por ${config.updated_by}`}
              </p>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Futuras Configurações - Placeholder */}
      <Card className="bg-slate-800/50 border-slate-700 opacity-50">
        <CardHeader>
          <CardTitle className="text-slate-400 flex items-center gap-2">
            <Settings className="w-5 h-5" />
            Configurações Adicionais
            <Badge variant="outline" className="text-xs border-slate-600 text-slate-500">Em breve</Badge>
          </CardTitle>
          <CardDescription className="text-slate-500">
            Novas configurações serão adicionadas aqui em futuras atualizações
          </CardDescription>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-slate-500 text-sm">
            <div className="flex items-center gap-2">
              <Lock className="w-4 h-4" />
              <span>Configurações de Email</span>
            </div>
            <div className="flex items-center gap-2">
              <Lock className="w-4 h-4" />
              <span>Configurações de Notificações</span>
            </div>
            <div className="flex items-center gap-2">
              <Lock className="w-4 h-4" />
              <span>Configurações de Backup</span>
            </div>
            <div className="flex items-center gap-2">
              <Lock className="w-4 h-4" />
              <span>Configurações de Integração</span>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Migração de Dados */}
      <Card className="bg-slate-800/50 border-slate-700 border-amber-600/50">
        <CardHeader>
          <CardTitle className="text-white flex items-center gap-2">
            <Download className="w-5 h-5 text-amber-500" />
            Migração de Dados (Multi-Empresa)
            <Badge variant="outline" className="text-xs border-amber-600 text-amber-500">Cuidado</Badge>
          </CardTitle>
          <CardDescription className="text-slate-400">
            Migra dados de todos os clientes para suas respectivas Contas Pessoais. 
            Também limpa segmentos legados sem empresa vinculada.
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="bg-amber-900/20 border border-amber-600/30 rounded-lg p-4">
            <p className="text-amber-200 text-sm">
              <strong>⚠️ Atenção:</strong> Esta operação irá:
            </p>
            <ul className="text-amber-200/80 text-sm mt-2 space-y-1 list-disc list-inside">
              <li>Deletar segmentos sem user_id ou company_id</li>
              <li>Criar Conta Principal para usuários que não possuem</li>
              <li>Associar cadastros e lançamentos sem company_id à Conta Principal de cada cliente</li>
              <li>Atualizar segmentos com user_id e company_id</li>
              <li>Definir max_companies para clientes que não possuem (conforme plano)</li>
            </ul>
          </div>
          
          {migrationResult && (
            <div className="bg-emerald-900/20 border border-emerald-600/30 rounded-lg p-4">
              <p className="text-emerald-200 text-sm font-medium mb-2">✅ Migração Concluída!</p>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-2 text-xs text-emerald-200/80">
                <div>Segmentos deletados: {migrationResult.legacy_segments_deleted}</div>
                <div>Usuários processados: {migrationResult.users_processed}</div>
                <div>Com migrações: {migrationResult.users_with_migrations}</div>
                <div>Max empresas setado: {migrationResult.users_max_companies_set}</div>
                <div>Clientes: {migrationResult.total_clients}</div>
                <div>Fornecedores: {migrationResult.total_suppliers}</div>
                <div>Produtos: {migrationResult.total_products}</div>
                <div>Carteiras: {migrationResult.total_wallets}</div>
                <div>Transações: {migrationResult.total_transactions}</div>
                <div>Segmentos: {migrationResult.total_segments}</div>
              </div>
            </div>
          )}
          
          <div className="flex justify-end">
            <Button 
              onClick={handleMigrateAllUsers} 
              disabled={migrating}
              variant="outline"
              className="border-amber-600 text-amber-500 hover:bg-amber-600/20"
              data-testid="migrate-all-users-btn"
            >
              {migrating ? (
                <>
                  <div className="animate-spin rounded-full h-4 w-4 border-2 border-amber-500 border-t-transparent mr-2"></div>
                  Migrando...
                </>
              ) : (
                <>
                  <Download className="w-4 h-4 mr-2" />
                  Executar Migração
                </>
              )}
            </Button>
          </div>
        </CardContent>
      </Card>

      {/* Botão Salvar */}
      <div className="flex justify-end">
        <Button 
          onClick={handleSave} 
          disabled={saving}
          className="bg-emerald-600 hover:bg-emerald-700"
          data-testid="save-config-btn"
        >
          {saving ? (
            <>
              <div className="animate-spin rounded-full h-4 w-4 border-2 border-white border-t-transparent mr-2"></div>
              Salvando...
            </>
          ) : (
            'Salvar Configurações'
          )}
        </Button>
      </div>
    </div>
  );
};

// ==================== ADMIN AUDIT LOGS PAGE ====================
const AdminAuditLogsPage = () => {
  const [logs, setLogs] = useState([]);
  const [users, setUsers] = useState([]);
  const [companies, setCompanies] = useState([]);
  const [loading, setLoading] = useState(false);
  const [loadingCompanies, setLoadingCompanies] = useState(false);
  const [filters, setFilters] = useState({
    user_id: '',
    company_id: '',
    log_type: '',
    action: '',
    start_date: '',
    end_date: ''
  });
  const [searchTerm, setSearchTerm] = useState('');
  const { toast } = useToast();

  // Load users on mount
  useEffect(() => {
    loadUsers();
  }, []);

  // Load companies when user changes
  useEffect(() => {
    if (filters.user_id) {
      loadCompaniesForUser(filters.user_id);
    } else {
      setCompanies([]);
      setFilters(prev => ({ ...prev, company_id: '' }));
    }
  }, [filters.user_id]);

  // Load logs when filters change
  useEffect(() => {
    if (filters.user_id) {
      loadLogs();
    } else {
      setLogs([]);
    }
  }, [filters]);

  const loadUsers = async () => {
    try {
      // Buscar clientes (usuários de planos), não usuários admin
      const response = await axios.get(`${API}/admin/clients`);
      // Sort users by name
      const sortedUsers = (response.data || []).sort((a, b) => 
        (a.name || '').localeCompare(b.name || '')
      );
      setUsers(sortedUsers);
    } catch (error) {
      toast({ title: "Erro ao carregar clientes", variant: "destructive" });
    }
  };

  const loadCompaniesForUser = async (userId) => {
    try {
      setLoadingCompanies(true);
      // Get companies for this specific user
      const response = await axios.get(`${API}/admin/users/${userId}/companies`);
      setCompanies(response.data.companies || []);
    } catch (error) {
      console.error('Error loading companies:', error);
      setCompanies([]);
    } finally {
      setLoadingCompanies(false);
    }
  };

  const loadLogs = async () => {
    try {
      setLoading(true);
      let url = `${API}/admin/audit-logs?limit=500`;
      if (filters.user_id) url += `&user_id=${filters.user_id}`;
      if (filters.company_id) url += `&company_id=${filters.company_id}`;
      if (filters.log_type) url += `&log_type=${filters.log_type}`;
      if (filters.action) url += `&action=${filters.action}`;
      if (filters.start_date) url += `&start_date=${filters.start_date}`;
      if (filters.end_date) url += `&end_date=${filters.end_date}`;
      
      const response = await axios.get(url);
      // Logs already come sorted by created_at DESC from backend
      setLogs(response.data.logs || []);
    } catch (error) {
      toast({ title: "Erro ao carregar logs", variant: "destructive" });
    } finally {
      setLoading(false);
    }
  };

  const formatDateTime = (dateStr) => {
    if (!dateStr) return '-';
    const date = new Date(dateStr);
    return date.toLocaleString('pt-BR', { 
      day: '2-digit', month: '2-digit', year: 'numeric',
      hour: '2-digit', minute: '2-digit', second: '2-digit'
    });
  };

  const getActionColor = (action) => {
    switch (action) {
      case 'INCLUSAO': return 'bg-green-600 text-white';
      case 'ALTERACAO': return 'bg-blue-600 text-white';
      case 'EXCLUSAO': return 'bg-red-600 text-white';
      case 'VISUALIZACAO': return 'bg-gray-600 text-white';
      case 'IMPRESSAO': return 'bg-purple-600 text-white';
      default: return 'bg-gray-600 text-white';
    }
  };

  const getTypeColor = (type) => {
    switch (type) {
      case 'CADASTRO': return 'bg-indigo-600 text-white';
      case 'LANCAMENTO': return 'bg-teal-600 text-white';
      case 'RELATORIO': return 'bg-amber-600 text-white';
      case 'DRE': return 'bg-cyan-600 text-white';
      default: return 'bg-gray-600 text-white';
    }
  };

  // Filter users by search term (name or CPF)
  const filteredUsers = users.filter(user => {
    if (!searchTerm) return true;
    const term = searchTerm.toLowerCase();
    const nameMatch = (user.name || '').toLowerCase().includes(term);
    const cpfMatch = (user.cpf || '').toLowerCase().includes(term);
    const emailMatch = (user.email || '').toLowerCase().includes(term);
    return nameMatch || cpfMatch || emailMatch;
  });

  const logTypes = ['CADASTRO', 'LANCAMENTO', 'RELATORIO', 'DRE', 'IMPRESSAO'];
  const actions = ['INCLUSAO', 'ALTERACAO', 'EXCLUSAO', 'VISUALIZACAO', 'IMPRESSAO'];

  const selectedUser = users.find(u => u.user_id === filters.user_id);

  return (
    <div className="space-y-6 animate-fadeIn" data-testid="admin-audit-logs-page">
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-2xl font-bold text-white">Logs de Auditoria</h2>
          <p className="text-slate-400">Histórico de ações dos clientes no sistema</p>
        </div>
      </div>

      {/* Filters */}
      <Card className="bg-slate-800 border-slate-700">
        <CardContent className="p-4">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
            {/* User Search/Select */}
            <div className="lg:col-span-2">
              <Label className="text-slate-300 text-xs">Cliente (Nome, CPF ou Email)</Label>
              <div className="space-y-2">
                <Input
                  placeholder="Buscar cliente..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="bg-slate-700 border-slate-600 text-white"
                />
                <Select 
                  value={filters.user_id} 
                  onValueChange={(v) => setFilters({...filters, user_id: v === 'none' ? '' : v, company_id: ''})}
                >
                  <SelectTrigger className="bg-slate-700 border-slate-600 text-white">
                    <SelectValue placeholder="Selecione o cliente" />
                  </SelectTrigger>
                  <SelectContent className="max-h-60">
                    <SelectItem value="none">-- Selecione --</SelectItem>
                    {filteredUsers.map((user) => (
                      <SelectItem key={user.user_id} value={user.user_id}>
                        {user.name || user.email} {user.cpf ? `(${user.cpf})` : ''}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>

            {/* Company Select */}
            <div>
              <Label className="text-slate-300 text-xs">Empresa</Label>
              <Select 
                value={filters.company_id} 
                onValueChange={(v) => setFilters({...filters, company_id: v === 'all' ? '' : v})}
                disabled={!filters.user_id || loadingCompanies}
              >
                <SelectTrigger className="bg-slate-700 border-slate-600 text-white">
                  <SelectValue placeholder={loadingCompanies ? "Carregando..." : "Todas as empresas"} />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">Todas as empresas</SelectItem>
                  {companies.map((c) => (
                    <SelectItem key={c.id} value={c.id}>
                      {c.is_personal ? '👤 ' : '🏢 '}{c.razao_social}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            {/* Log Type */}
            <div>
              <Label className="text-slate-300 text-xs">Tipo</Label>
              <Select value={filters.log_type} onValueChange={(v) => setFilters({...filters, log_type: v === 'all' ? '' : v})}>
                <SelectTrigger className="bg-slate-700 border-slate-600 text-white">
                  <SelectValue placeholder="Todos" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">Todos</SelectItem>
                  {logTypes.map((t) => (
                    <SelectItem key={t} value={t}>{t}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            {/* Action */}
            <div>
              <Label className="text-slate-300 text-xs">Ação</Label>
              <Select value={filters.action} onValueChange={(v) => setFilters({...filters, action: v === 'all' ? '' : v})}>
                <SelectTrigger className="bg-slate-700 border-slate-600 text-white">
                  <SelectValue placeholder="Todas" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">Todas</SelectItem>
                  {actions.map((a) => (
                    <SelectItem key={a} value={a}>{a}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            {/* Date Range */}
            <div>
              <Label className="text-slate-300 text-xs">Data Início</Label>
              <Input 
                type="date" 
                value={filters.start_date}
                onChange={(e) => setFilters({...filters, start_date: e.target.value})}
                className="bg-slate-700 border-slate-600 text-white"
              />
            </div>
            <div>
              <Label className="text-slate-300 text-xs">Data Fim</Label>
              <Input 
                type="date" 
                value={filters.end_date}
                onChange={(e) => setFilters({...filters, end_date: e.target.value})}
                className="bg-slate-700 border-slate-600 text-white"
              />
            </div>
          </div>

          {/* Selected user info and Clear button */}
          <div className="mt-4 flex items-center justify-between gap-4">
            {selectedUser ? (
              <div className="flex-1 p-3 bg-slate-700 rounded-lg">
                <p className="text-sm text-slate-300">
                  <span className="font-medium text-white">{selectedUser.name}</span>
                  {selectedUser.email && <span className="ml-2 text-slate-400">({selectedUser.email})</span>}
                  {selectedUser.cpf && <span className="ml-2 text-slate-400">CPF: {selectedUser.cpf}</span>}
                </p>
                <p className="text-xs text-slate-400 mt-1">
                  {companies.length} empresa(s) cadastrada(s)
                </p>
              </div>
            ) : (
              <div className="flex-1" />
            )}
            <Button
              variant="outline"
              onClick={() => {
                setFilters({
                  user_id: '',
                  company_id: '',
                  log_type: '',
                  action: '',
                  start_date: '',
                  end_date: ''
                });
                setSearchTerm('');
                setCompanies([]);
                setLogs([]);
              }}
              className="bg-slate-700 border-slate-600 text-slate-300 hover:bg-slate-600 hover:text-white"
              data-testid="clear-filters-btn"
            >
              <X className="w-4 h-4 mr-2" />
              Limpar
            </Button>
          </div>
        </CardContent>
      </Card>

      {/* Results */}
      {!filters.user_id ? (
        <div className="text-center py-12 text-slate-400">
          <FileText className="w-12 h-12 mx-auto mb-4 opacity-50" />
          <p>Selecione um cliente para visualizar os logs</p>
        </div>
      ) : loading ? (
        <div className="flex justify-center py-12">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-emerald-500"></div>
        </div>
      ) : logs.length === 0 ? (
        <div className="text-center py-12 text-slate-400">
          <FileText className="w-12 h-12 mx-auto mb-4 opacity-50" />
          <p>Nenhum log encontrado para os filtros selecionados</p>
        </div>
      ) : (
        <Card className="bg-slate-800 border-slate-700">
          <CardContent className="p-0">
            <div className="overflow-x-auto">
              <Table>
                <TableHeader>
                  <TableRow className="border-slate-700 hover:bg-slate-700/50">
                    <TableHead className="text-slate-300">Data/Hora</TableHead>
                    <TableHead className="text-slate-300">Empresa</TableHead>
                    <TableHead className="text-slate-300">Tipo</TableHead>
                    <TableHead className="text-slate-300">Ação</TableHead>
                    <TableHead className="text-slate-300">Descrição</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {logs.map((log) => (
                    <TableRow key={log.id} className="border-slate-700 hover:bg-slate-700/50">
                      <TableCell className="text-slate-300 whitespace-nowrap text-sm">
                        {formatDateTime(log.created_at)}
                      </TableCell>
                      <TableCell className="text-slate-300 text-sm">
                        {log.company_name || '-'}
                      </TableCell>
                      <TableCell>
                        <Badge className={`text-xs ${getTypeColor(log.log_type)}`}>
                          {log.log_type}
                        </Badge>
                      </TableCell>
                      <TableCell>
                        <Badge className={`text-xs ${getActionColor(log.action)}`}>
                          {log.action}
                        </Badge>
                      </TableCell>
                      <TableCell className="text-slate-300 text-sm max-w-md truncate" title={log.option}>
                        {log.option}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
            <div className="p-4 border-t border-slate-700">
              <p className="text-sm text-slate-400">
                Total: {logs.length} registro(s) - Ordenado do mais recente para o mais antigo
              </p>
            </div>
          </CardContent>
        </Card>
      )}
    </div>
  );
};

// Admin Main Layout
const AdminMainLayout = ({ admin }) => {
  const [currentPage, setCurrentPage] = useState('dashboard');

  const renderPage = () => {
    switch (currentPage) {
      case 'dashboard': return <AdminDashboardPage admin={admin} onNavigate={setCurrentPage} />;
      case 'admin-users': return <AdminUsersPage />;
      case 'admin-chart-accounts': return <AdminChartAccountsPage />;
      case 'clients-plans': return <AdminClientsPage adminProfile={admin?.profile} />;
      case 'system-config': return <AdminConfigPage />;
      case 'report-clients': return <AdminReportClientsPage />;
      case 'report-emails': return <AdminReportEmailsPage />;
      case 'report-chart-accounts': return <AdminReportChartAccountsPage />;
      case 'report-audit-logs': return <AdminAuditLogsPage />;
      default: return <AdminDashboardPage admin={admin} onNavigate={setCurrentPage} />;
    }
  };

  return (
    <div className="min-h-screen bg-slate-900">
      <AdminHeader admin={admin} currentPage={currentPage} onNavigate={setCurrentPage} />
      <main className="pt-20 px-4 pb-8 max-w-7xl mx-auto">
        {renderPage()}
      </main>
      <footer className="py-4 text-center border-t border-slate-700">
        <span className="text-xs text-slate-500">DRE+ Admin - Versão {APP_VERSION}</span>
      </footer>
    </div>
  );
};

// ==================== LEGAL PAGES ====================
const TermosDeUsoPage = () => {
  const navigate = useNavigate();
  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900">
      <div className="max-w-4xl mx-auto px-4 py-8">
        <div className="flex items-center justify-between mb-8">
          <h1 className="text-2xl md:text-3xl font-bold text-white">Termos de Uso</h1>
          <Button variant="outline" onClick={() => navigate(-1)} className="text-slate-300 border-slate-600 hover:bg-slate-700">
            Voltar
          </Button>
        </div>
        
        <Card className="bg-slate-800/50 border-slate-700">
          <CardContent className="p-6 md:p-8 prose prose-invert max-w-none">
            <p className="text-slate-400 text-sm mb-6">Última atualização: 27 de Janeiro de 2026</p>
            
            <h2 className="text-xl font-semibold text-white mt-6 mb-4">1. Aceitação dos Termos</h2>
            <p className="text-slate-300 mb-4">
              Ao acessar e utilizar o sistema DRE+ ("Serviço"), você concorda em cumprir e estar vinculado aos presentes Termos de Uso. 
              Se você não concordar com qualquer parte destes termos, não poderá acessar o Serviço.
            </p>

            <h2 className="text-xl font-semibold text-white mt-6 mb-4">2. Descrição do Serviço</h2>
            <p className="text-slate-300 mb-4">
              O DRE+ é uma plataforma de controle financeiro desenvolvida para empreendedores e pequenas empresas brasileiras, 
              oferecendo funcionalidades de gestão de receitas, despesas, custos, clientes, fornecedores, produtos e relatórios 
              financeiros como DRE (Demonstrativo de Resultado do Exercício) e Fluxo de Caixa.
            </p>

            <h2 className="text-xl font-semibold text-white mt-6 mb-4">3. Planos e Assinaturas</h2>
            <p className="text-slate-300 mb-4">O Serviço é oferecido nos seguintes planos:</p>
            <ul className="list-disc list-inside text-slate-300 mb-4 space-y-2">
              <li><strong className="text-white">Plano Básico (Gratuito):</strong> Funcionalidades essenciais de controle financeiro</li>
              <li><strong className="text-white">Plano Advanced (R$ 39,90/mês):</strong> Funcionalidades adicionais como orçamentos, recibos e relatórios avançados</li>
              <li><strong className="text-white">Plano Contábil (R$ 69,90/mês):</strong> Funcionalidades completas incluindo integração contábil e automações</li>
            </ul>
            <p className="text-slate-300 mb-4">
              Os valores e funcionalidades dos planos podem ser alterados mediante aviso prévio de 30 dias aos usuários ativos.
            </p>

            <h2 className="text-xl font-semibold text-white mt-6 mb-4">4. Cadastro e Conta do Usuário</h2>
            <p className="text-slate-300 mb-4">
              Para utilizar o Serviço, você deve criar uma conta fornecendo informações verdadeiras, precisas e completas. 
              Você é responsável por manter a confidencialidade de sua senha e por todas as atividades realizadas em sua conta.
            </p>

            <h2 className="text-xl font-semibold text-white mt-6 mb-4">5. Uso Aceitável</h2>
            <p className="text-slate-300 mb-4">Você concorda em não:</p>
            <ul className="list-disc list-inside text-slate-300 mb-4 space-y-2">
              <li>Utilizar o Serviço para fins ilegais ou não autorizados</li>
              <li>Tentar acessar dados de outros usuários sem autorização</li>
              <li>Interferir ou interromper o funcionamento do Serviço</li>
              <li>Transmitir vírus, malware ou código malicioso</li>
              <li>Reproduzir, duplicar ou revender qualquer parte do Serviço</li>
            </ul>

            <h2 className="text-xl font-semibold text-white mt-6 mb-4">6. Propriedade Intelectual</h2>
            <p className="text-slate-300 mb-4">
              O Serviço, incluindo seu código, design, logotipos, textos e demais elementos, é de propriedade exclusiva da 
              <strong className="text-white"> Interacess Tecnologia em Segurança Automação e Informática Ltda</strong>. 
              Nenhum direito de propriedade intelectual é transferido ao usuário pelo uso do Serviço.
            </p>

            <h2 className="text-xl font-semibold text-white mt-6 mb-4">7. Limitação de Responsabilidade</h2>
            <p className="text-slate-300 mb-4">
              O Serviço é fornecido "como está", sem garantias de qualquer tipo. Não nos responsabilizamos por:
            </p>
            <ul className="list-disc list-inside text-slate-300 mb-4 space-y-2">
              <li>Decisões financeiras tomadas com base nos dados do sistema</li>
              <li>Perdas ou danos indiretos decorrentes do uso do Serviço</li>
              <li>Interrupções temporárias para manutenção ou atualizações</li>
              <li>Erros de cálculo decorrentes de dados inseridos incorretamente pelo usuário</li>
            </ul>

            <h2 className="text-xl font-semibold text-white mt-6 mb-4">8. Cancelamento</h2>
            <p className="text-slate-300 mb-4">
              O usuário pode solicitar o cancelamento de sua conta a qualquer momento. Após o cancelamento:
            </p>
            <ul className="list-disc list-inside text-slate-300 mb-4 space-y-2">
              <li>O acesso ao Serviço será imediatamente suspenso</li>
              <li>Os dados serão mantidos por 30 dias para eventual reativação</li>
              <li>Após 30 dias, todos os dados serão permanentemente excluídos</li>
              <li>Não haverá reembolso de valores já pagos</li>
            </ul>

            <h2 className="text-xl font-semibold text-white mt-6 mb-4">9. Modificações dos Termos</h2>
            <p className="text-slate-300 mb-4">
              Reservamo-nos o direito de modificar estes Termos a qualquer momento. Alterações significativas serão 
              comunicadas por e-mail ou através do próprio Serviço com antecedência mínima de 15 dias.
            </p>

            <h2 className="text-xl font-semibold text-white mt-6 mb-4">10. Foro</h2>
            <p className="text-slate-300 mb-4">
              Estes Termos são regidos pelas leis da República Federativa do Brasil. Qualquer disputa será resolvida 
              no Foro da Comarca de São Paulo/SP.
            </p>

            <h2 className="text-xl font-semibold text-white mt-6 mb-4">11. Contato</h2>
            <p className="text-slate-300 mb-4">
              Para dúvidas sobre estes Termos, entre em contato:
            </p>
            <div className="bg-slate-700/50 rounded-lg p-4 text-slate-300">
              <p><strong className="text-white">Interacess Tecnologia em Segurança Automação e Informática Ltda</strong></p>
              <p>CNPJ: 06.286.180/0001-04</p>
              <p>Endereço: Rua Max Planck, 57 - CEP: 03693-030 - São Paulo/SP</p>
              <p>E-mail: contato@dremais.com.br</p>
              <p>Telefone: (11) 99425-6860</p>
            </div>
          </CardContent>
        </Card>
        
        <footer className="mt-8 text-center">
          <span className="text-xs text-slate-500">DRE+ - Versão {APP_VERSION}</span>
        </footer>
      </div>
    </div>
  );
};

const PoliticaPrivacidadePage = () => {
  const navigate = useNavigate();
  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900">
      <div className="max-w-4xl mx-auto px-4 py-8">
        <div className="flex items-center justify-between mb-8">
          <h1 className="text-2xl md:text-3xl font-bold text-white">Política de Privacidade</h1>
          <Button variant="outline" onClick={() => navigate(-1)} className="text-slate-300 border-slate-600 hover:bg-slate-700">
            Voltar
          </Button>
        </div>
        
        <Card className="bg-slate-800/50 border-slate-700">
          <CardContent className="p-6 md:p-8 prose prose-invert max-w-none">
            <p className="text-slate-400 text-sm mb-6">Última atualização: 27 de Janeiro de 2026</p>
            
            <p className="text-slate-300 mb-6">
              A <strong className="text-white">Interacess Tecnologia em Segurança Automação e Informática Ltda</strong> ("nós", "nosso" ou "Empresa"), 
              operadora do sistema DRE+, está comprometida em proteger a privacidade dos usuários em conformidade com a 
              Lei Geral de Proteção de Dados (LGPD - Lei nº 13.709/2018).
            </p>

            <h2 className="text-xl font-semibold text-white mt-6 mb-4">1. Dados Coletados</h2>
            <p className="text-slate-300 mb-4">Coletamos os seguintes tipos de dados:</p>
            
            <h3 className="text-lg font-medium text-white mt-4 mb-2">1.1. Dados de Cadastro</h3>
            <ul className="list-disc list-inside text-slate-300 mb-4 space-y-1">
              <li>Nome completo</li>
              <li>Endereço de e-mail</li>
              <li>CPF ou CNPJ</li>
              <li>Telefone</li>
              <li>Endereço</li>
            </ul>

            <h3 className="text-lg font-medium text-white mt-4 mb-2">1.2. Dados Financeiros</h3>
            <ul className="list-disc list-inside text-slate-300 mb-4 space-y-1">
              <li>Lançamentos de receitas, despesas e custos</li>
              <li>Informações de clientes e fornecedores cadastrados</li>
              <li>Dados de produtos e serviços</li>
              <li>Relatórios financeiros gerados</li>
            </ul>

            <h3 className="text-lg font-medium text-white mt-4 mb-2">1.3. Dados de Navegação</h3>
            <ul className="list-disc list-inside text-slate-300 mb-4 space-y-1">
              <li>Endereço IP</li>
              <li>Tipo de navegador e dispositivo</li>
              <li>Páginas acessadas e tempo de permanência</li>
              <li>Cookies essenciais para funcionamento da sessão</li>
            </ul>

            <h2 className="text-xl font-semibold text-white mt-6 mb-4">2. Finalidade do Tratamento</h2>
            <p className="text-slate-300 mb-4">Utilizamos seus dados para:</p>
            <ul className="list-disc list-inside text-slate-300 mb-4 space-y-2">
              <li><strong className="text-white">Prestação do Serviço:</strong> Processar seus dados financeiros e gerar relatórios</li>
              <li><strong className="text-white">Autenticação:</strong> Verificar sua identidade e manter sua sessão ativa</li>
              <li><strong className="text-white">Comunicação:</strong> Enviar notificações sobre sua conta e atualizações do serviço</li>
              <li><strong className="text-white">Melhorias:</strong> Aprimorar a experiência do usuário e corrigir problemas</li>
              <li><strong className="text-white">Obrigações Legais:</strong> Cumprir exigências legais e regulatórias</li>
            </ul>

            <h2 className="text-xl font-semibold text-white mt-6 mb-4">3. Base Legal (LGPD)</h2>
            <p className="text-slate-300 mb-4">O tratamento de dados é realizado com base em:</p>
            <ul className="list-disc list-inside text-slate-300 mb-4 space-y-2">
              <li><strong className="text-white">Execução de Contrato (Art. 7º, V):</strong> Para prestação do serviço contratado</li>
              <li><strong className="text-white">Consentimento (Art. 7º, I):</strong> Para comunicações de marketing (opcional)</li>
              <li><strong className="text-white">Legítimo Interesse (Art. 7º, IX):</strong> Para melhorias e segurança do serviço</li>
              <li><strong className="text-white">Obrigação Legal (Art. 7º, II):</strong> Para cumprimento de exigências legais</li>
            </ul>

            <h2 className="text-xl font-semibold text-white mt-6 mb-4">4. Cookies</h2>
            <p className="text-slate-300 mb-4">
              Utilizamos apenas <strong className="text-white">cookies essenciais</strong> para:
            </p>
            <ul className="list-disc list-inside text-slate-300 mb-4 space-y-1">
              <li>Manter sua sessão de login ativa</li>
              <li>Salvar suas preferências de uso</li>
              <li>Garantir a segurança da aplicação</li>
            </ul>
            <p className="text-slate-300 mb-4">
              Não utilizamos cookies de rastreamento ou publicidade.
            </p>

            <h2 className="text-xl font-semibold text-white mt-6 mb-4">5. Compartilhamento de Dados</h2>
            <p className="text-slate-300 mb-4">Seus dados podem ser compartilhados com:</p>
            <ul className="list-disc list-inside text-slate-300 mb-4 space-y-2">
              <li><strong className="text-white">Google (OAuth):</strong> Apenas para autenticação, quando você opta por login com Google</li>
              <li><strong className="text-white">Processadores de Pagamento:</strong> Para processar assinaturas dos planos pagos</li>
              <li><strong className="text-white">Autoridades Competentes:</strong> Quando exigido por lei ou ordem judicial</li>
            </ul>
            <p className="text-slate-300 mb-4">
              <strong className="text-white">Não vendemos, alugamos ou compartilhamos seus dados com terceiros para fins de marketing.</strong>
            </p>

            <h2 className="text-xl font-semibold text-white mt-6 mb-4">6. Segurança dos Dados</h2>
            <p className="text-slate-300 mb-4">Implementamos medidas de segurança incluindo:</p>
            <ul className="list-disc list-inside text-slate-300 mb-4 space-y-1">
              <li>Criptografia de dados em trânsito (HTTPS/TLS)</li>
              <li>Senhas armazenadas com hash seguro (SHA-256)</li>
              <li>Controle de acesso baseado em perfis</li>
              <li>Monitoramento de atividades suspeitas</li>
              <li>Backups regulares</li>
            </ul>

            <h2 className="text-xl font-semibold text-white mt-6 mb-4">7. Retenção de Dados</h2>
            <p className="text-slate-300 mb-4">
              Seus dados são mantidos enquanto sua conta estiver ativa. Após o cancelamento:
            </p>
            <ul className="list-disc list-inside text-slate-300 mb-4 space-y-1">
              <li>Dados são mantidos por 30 dias para possível reativação</li>
              <li>Após 30 dias, são permanentemente excluídos</li>
              <li>Dados necessários para obrigações legais podem ser retidos por até 5 anos</li>
            </ul>

            <h2 className="text-xl font-semibold text-white mt-6 mb-4">8. Seus Direitos (LGPD)</h2>
            <p className="text-slate-300 mb-4">Você tem direito a:</p>
            <ul className="list-disc list-inside text-slate-300 mb-4 space-y-2">
              <li><strong className="text-white">Confirmação e Acesso:</strong> Saber se tratamos seus dados e acessá-los</li>
              <li><strong className="text-white">Correção:</strong> Corrigir dados incompletos ou desatualizados</li>
              <li><strong className="text-white">Anonimização ou Eliminação:</strong> Solicitar a exclusão de dados desnecessários</li>
              <li><strong className="text-white">Portabilidade:</strong> Receber seus dados em formato estruturado</li>
              <li><strong className="text-white">Revogação:</strong> Revogar consentimento a qualquer momento</li>
              <li><strong className="text-white">Oposição:</strong> Opor-se ao tratamento em determinadas situações</li>
            </ul>
            <p className="text-slate-300 mb-4">
              Para exercer seus direitos, entre em contato pelo e-mail: <strong className="text-white">contato@dremais.com.br</strong>
            </p>

            <h2 className="text-xl font-semibold text-white mt-6 mb-4">9. Transferência Internacional</h2>
            <p className="text-slate-300 mb-4">
              Nossos servidores estão localizados no Brasil. Caso haja necessidade de transferência internacional de dados, 
              garantimos que será feita em conformidade com a LGPD, utilizando cláusulas contratuais padrão ou outras 
              salvaguardas apropriadas.
            </p>

            <h2 className="text-xl font-semibold text-white mt-6 mb-4">10. Alterações nesta Política</h2>
            <p className="text-slate-300 mb-4">
              Esta Política pode ser atualizada periodicamente. Alterações significativas serão comunicadas por e-mail 
              ou através do próprio Serviço com antecedência mínima de 15 dias.
            </p>

            <h2 className="text-xl font-semibold text-white mt-6 mb-4">11. Encarregado de Dados (DPO)</h2>
            <p className="text-slate-300 mb-4">
              Para questões relacionadas à proteção de dados pessoais, entre em contato com nosso Encarregado:
            </p>
            <div className="bg-slate-700/50 rounded-lg p-4 text-slate-300">
              <p><strong className="text-white">Interacess Tecnologia em Segurança Automação e Informática Ltda</strong></p>
              <p>CNPJ: 06.286.180/0001-04</p>
              <p>Endereço: Rua Max Planck, 57 - CEP: 03693-030 - São Paulo/SP</p>
              <p>E-mail: contato@dremais.com.br</p>
              <p>Telefone: (11) 99425-6860</p>
            </div>

            <h2 className="text-xl font-semibold text-white mt-6 mb-4">12. Autoridade Nacional de Proteção de Dados</h2>
            <p className="text-slate-300 mb-4">
              Caso entenda que o tratamento de seus dados viola a LGPD, você pode apresentar reclamação à 
              Autoridade Nacional de Proteção de Dados (ANPD):
            </p>
            <div className="bg-slate-700/50 rounded-lg p-4 text-slate-300">
              <p>Site: <a href="https://www.gov.br/anpd" target="_blank" rel="noopener noreferrer" className="text-primary underline">www.gov.br/anpd</a></p>
            </div>
          </CardContent>
        </Card>
        
        <footer className="mt-8 text-center">
          <span className="text-xs text-slate-500">DRE+ - Versão {APP_VERSION}</span>
        </footer>
      </div>
    </div>
  );
};

// ==================== FORGOT PASSWORD PAGE ====================
const ForgotPasswordPage = () => {
  const [email, setEmail] = useState('');
  const [loading, setLoading] = useState(false);
  const [sent, setSent] = useState(false);
  const [error, setError] = useState('');
  const navigate = useNavigate();

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');
    
    try {
      await axios.post(`${API}/auth/forgot-password`, { email });
      setSent(true);
    } catch (err) {
      setError(err.response?.data?.detail || 'Erro ao enviar email');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900 flex items-center justify-center p-4">
      <Card className="w-full max-w-md bg-card border-border">
        <CardHeader className="text-center">
          <div className="mx-auto mb-4">
            <h1 className="text-3xl font-bold text-primary">DRE+</h1>
            <p className="text-xs text-muted-foreground">Controle Financeiro</p>
          </div>
          <CardTitle className="text-xl text-foreground">
            {sent ? 'Email Enviado!' : 'Esqueci minha senha'}
          </CardTitle>
        </CardHeader>
        <CardContent>
          {sent ? (
            <div className="text-center space-y-4">
              <div className="w-16 h-16 mx-auto bg-primary/20 rounded-full flex items-center justify-center">
                <CheckCircle className="w-8 h-8 text-primary" />
              </div>
              <p className="text-muted-foreground">
                Se o email <strong className="text-foreground">{email}</strong> estiver cadastrado, 
                você receberá um link para redefinir sua senha.
              </p>
              <p className="text-sm text-muted-foreground">
                Verifique também a pasta de spam.
              </p>
              <Button onClick={() => navigate('/login')} className="w-full mt-4">
                Voltar ao Login
              </Button>
            </div>
          ) : (
            <form onSubmit={handleSubmit} className="space-y-4">
              <p className="text-sm text-muted-foreground text-center mb-4">
                Digite seu email e enviaremos um link para redefinir sua senha.
              </p>
              
              {error && (
                <div className="p-3 bg-destructive/10 border border-destructive/30 rounded-lg text-destructive text-sm">
                  {error}
                </div>
              )}
              
              <div>
                <Label htmlFor="email" className="text-muted-foreground">E-mail</Label>
                <Input
                  id="email"
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="seu@email.com"
                  required
                  className="mt-1"
                  data-testid="forgot-email-input"
                />
              </div>
              
              <Button type="submit" className="w-full" disabled={loading} data-testid="forgot-submit-btn">
                {loading ? (
                  <div className="w-5 h-5 border-2 border-background border-t-transparent rounded-full animate-spin" />
                ) : (
                  'Enviar Link de Recuperação'
                )}
              </Button>
              
              <Button
                type="button"
                variant="ghost"
                className="w-full"
                onClick={() => navigate('/login')}
              >
                Voltar ao Login
              </Button>
            </form>
          )}
        </CardContent>
      </Card>
      <span className="fixed bottom-4 text-xs text-muted-foreground">Versão {APP_VERSION}</span>
    </div>
  );
};

// ==================== RESET PASSWORD PAGE ====================
const ResetPasswordPage = () => {
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const [verifying, setVerifying] = useState(true);
  const [success, setSuccess] = useState(false);
  const [error, setError] = useState('');
  const [tokenEmail, setTokenEmail] = useState('');
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const token = searchParams.get('token');

  useEffect(() => {
    const verifyToken = async () => {
      if (!token) {
        setError('Token não fornecido');
        setVerifying(false);
        return;
      }
      
      try {
        const response = await axios.get(`${API}/auth/verify-reset-token?token=${token}`);
        setTokenEmail(response.data.email);
        setVerifying(false);
      } catch (err) {
        setError(err.response?.data?.detail || 'Token inválido ou expirado');
        setVerifying(false);
      }
    };
    
    verifyToken();
  }, [token]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    
    if (password !== confirmPassword) {
      setError('As senhas não coincidem');
      return;
    }
    
    if (password.length < 6) {
      setError('A senha deve ter pelo menos 6 caracteres');
      return;
    }
    
    setLoading(true);
    
    try {
      await axios.post(`${API}/auth/reset-password`, {
        token,
        new_password: password
      });
      setSuccess(true);
    } catch (err) {
      setError(err.response?.data?.detail || 'Erro ao redefinir senha');
    } finally {
      setLoading(false);
    }
  };

  if (verifying) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900 flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900 flex items-center justify-center p-4">
      <Card className="w-full max-w-md bg-card border-border">
        <CardHeader className="text-center">
          <div className="mx-auto mb-4">
            <h1 className="text-3xl font-bold text-primary">DRE+</h1>
            <p className="text-xs text-muted-foreground">Controle Financeiro</p>
          </div>
          <CardTitle className="text-xl text-foreground">
            {success ? 'Senha Alterada!' : 'Redefinir Senha'}
          </CardTitle>
        </CardHeader>
        <CardContent>
          {success ? (
            <div className="text-center space-y-4">
              <div className="w-16 h-16 mx-auto bg-primary/20 rounded-full flex items-center justify-center">
                <CheckCircle className="w-8 h-8 text-primary" />
              </div>
              <p className="text-muted-foreground">
                Sua senha foi alterada com sucesso!
              </p>
              <Button onClick={() => navigate('/login')} className="w-full mt-4">
                Fazer Login
              </Button>
            </div>
          ) : error && !tokenEmail ? (
            <div className="text-center space-y-4">
              <div className="w-16 h-16 mx-auto bg-destructive/20 rounded-full flex items-center justify-center">
                <XCircle className="w-8 h-8 text-destructive" />
              </div>
              <p className="text-destructive">{error}</p>
              <Button onClick={() => navigate('/forgot-password')} className="w-full mt-4">
                Solicitar Novo Link
              </Button>
            </div>
          ) : (
            <form onSubmit={handleSubmit} className="space-y-4">
              <p className="text-sm text-muted-foreground text-center mb-4">
                Criando nova senha para <strong className="text-foreground">{tokenEmail}</strong>
              </p>
              
              {error && (
                <div className="p-3 bg-destructive/10 border border-destructive/30 rounded-lg text-destructive text-sm">
                  {error}
                </div>
              )}
              
              <div>
                <Label htmlFor="password" className="text-muted-foreground">Nova Senha</Label>
                <Input
                  id="password"
                  type="password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  placeholder="Mínimo 6 caracteres"
                  required
                  className="mt-1"
                  data-testid="reset-password-input"
                />
              </div>
              
              <div>
                <Label htmlFor="confirmPassword" className="text-muted-foreground">Confirmar Senha</Label>
                <Input
                  id="confirmPassword"
                  type="password"
                  value={confirmPassword}
                  onChange={(e) => setConfirmPassword(e.target.value)}
                  placeholder="Repita a senha"
                  required
                  className="mt-1"
                  data-testid="reset-confirm-password-input"
                />
              </div>
              
              <Button type="submit" className="w-full" disabled={loading} data-testid="reset-submit-btn">
                {loading ? (
                  <div className="w-5 h-5 border-2 border-background border-t-transparent rounded-full animate-spin" />
                ) : (
                  'Redefinir Senha'
                )}
              </Button>
            </form>
          )}
        </CardContent>
      </Card>
      <span className="fixed bottom-4 text-xs text-muted-foreground">Versão {APP_VERSION}</span>
    </div>
  );
};

// ==================== SIMULATE LOGIN PAGE ====================
const SimulateLoginPage = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [status, setStatus] = useState('loading');
  const [error, setError] = useState('');

  useEffect(() => {
    const token = searchParams.get('token');
    if (!token) {
      setStatus('error');
      setError('Token de simulação não fornecido');
      return;
    }

    // Authenticate with simulation token
    const authenticateSimulation = async () => {
      try {
        // First, clear any existing session to avoid conflicts
        try {
          await axios.post(`${API}/auth/logout`);
        } catch (e) {
          // Ignore logout errors - user might not be logged in
        }
        
        // Clear localStorage
        localStorage.removeItem('dre_user');
        localStorage.removeItem('dre_simulated');
        
        // Now authenticate with simulation token
        const response = await axios.post(`${API}/auth/simulate?token=${token}`);
        if (response.data) {
          // Store user data in localStorage
          localStorage.setItem('dre_user', JSON.stringify(response.data));
          localStorage.setItem('dre_simulated', 'true');
          setStatus('success');
          // Redirect to dashboard after short delay
          setTimeout(() => {
            // Force page reload to ensure fresh state
            window.location.href = '/dashboard';
          }, 1500);
        }
      } catch (err) {
        setStatus('error');
        setError(err.response?.data?.detail || 'Erro ao processar simulação');
      }
    };

    authenticateSimulation();
  }, [searchParams, navigate]);

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-900 to-slate-800 flex items-center justify-center p-4">
      <Card className="w-full max-w-md bg-slate-800/50 border-slate-700">
        <CardContent className="p-8 text-center">
          {status === 'loading' && (
            <>
              <div className="animate-spin rounded-full h-12 w-12 border-4 border-emerald-500 border-t-transparent mx-auto mb-4"></div>
              <h2 className="text-xl text-white mb-2">Simulando Login</h2>
              <p className="text-slate-400">Autenticando como cliente...</p>
            </>
          )}
          {status === 'success' && (
            <>
              <CheckCircle className="w-12 h-12 text-emerald-500 mx-auto mb-4" />
              <h2 className="text-xl text-white mb-2">Login Simulado!</h2>
              <p className="text-slate-400">Redirecionando para o dashboard...</p>
              <Badge className="mt-4 bg-amber-600">Modo Simulação</Badge>
            </>
          )}
          {status === 'error' && (
            <>
              <XCircle className="w-12 h-12 text-red-500 mx-auto mb-4" />
              <h2 className="text-xl text-white mb-2">Erro na Simulação</h2>
              <p className="text-red-400">{error}</p>
              <Button 
                onClick={() => navigate('/admin')} 
                className="mt-4 bg-slate-700 hover:bg-slate-600"
              >
                Voltar ao Admin
              </Button>
            </>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

// ==================== APP ROUTER ====================
// ==================== LANDING PAGE ====================
const LandingPage = () => {
  return (
    <div className="min-h-screen bg-white font-body" data-testid="landing-page">
      <LandingHeader />
      <Hero />
      <Features />
      <Screenshots />
      <Testimonials />
      <FAQ />
      <CTA />
      <LandingFooter />
    </div>
  );
};

// ==================== HELP CHAT PAGE ====================
const HelpChatPage = ({ user, selectedCompany }) => {
  const [messages, setMessages] = useState([
    { 
      id: 1, 
      type: 'bot', 
      text: `Olá ${user?.name?.split(' ')[0] || 'Cliente'}! 👋\n\nSou o assistente virtual do DRE+. Como posso ajudá-lo hoje?\n\nVocê pode me perguntar sobre:\n• Como usar os menus e funcionalidades\n• Cadastros (Clientes, Fornecedores, Produtos, etc.)\n• Lançamentos (Despesas, Receitas, Custos)\n• Relatórios (DRE, Fluxo de Caixa, Projeção 360, etc.)\n• Empresas e Multi-empresa\n\nDigite "sair" para encerrar ou "atendente" para falar com nossa equipe.`
    }
  ]);
  const [inputValue, setInputValue] = useState('');
  const [isTyping, setIsTyping] = useState(false);
  const messagesEndRef = useRef(null);
  const { toast } = useToast();

  // Scroll to bottom when new messages arrive
  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages]);

  // Knowledge base - FAQ about the system
  const knowledgeBase = {
    // Dashboard
    dashboard: {
      keywords: ['dashboard', 'painel', 'inicio', 'início', 'home', 'resumo'],
      answer: '📊 **Dashboard**\n\nO Dashboard é a tela inicial do sistema onde você vê:\n• Resumo financeiro do mês (Receitas, Despesas, Custos)\n• Gráficos de evolução\n• Seletor de empresa (se tiver mais de uma)\n• Seletor de mês/ano para filtrar os dados\n\nPara acessar, clique no menu "Dashboard" ou no logo DRE+.'
    },
    // Empresas
    empresas: {
      keywords: ['empresa', 'empresas', 'multi-empresa', 'conta pessoal', 'cadastrar empresa', 'trocar empresa'],
      answer: '🏢 **Empresas e Multi-Empresa**\n\nO DRE+ permite gerenciar múltiplas empresas:\n\n• **Conta Principal**: Criada automaticamente, não pode ser excluída\n• **Empresas**: Cadastre via menu Cadastros → Contas\n\n**Limites por plano:**\n• Básico: 2 empresas\n• Advanced: 4 empresas\n• Contábil: 6 empresas\n\n**Trocar empresa**: Use o seletor no Dashboard (canto superior direito)\n\nCada empresa tem seus próprios cadastros, lançamentos e relatórios separados.'
    },
    // Cadastros
    clientes: {
      keywords: ['cliente', 'clientes', 'cadastro cliente', 'cadastrar cliente'],
      answer: '👥 **Cadastro de Clientes**\n\nAcesse: Cadastros → Clientes\n\n**Campos disponíveis:**\n• Nome, CPF/CNPJ, Email, Telefone, Endereço\n\n**Ações:**\n• ➕ Incluir: Botão "Incluir"\n• ✏️ Editar: Ícone do lápis ou clique na linha\n• 🗑️ Excluir: Ícone da lixeira\n\nOs clientes são usados nos lançamentos de Receitas para identificar quem pagou.'
    },
    fornecedores: {
      keywords: ['fornecedor', 'fornecedores', 'cadastro fornecedor'],
      answer: '🚚 **Cadastro de Fornecedores**\n\nAcesse: Cadastros → Fornecedores\n\n**Campos disponíveis:**\n• Nome, Razão Social, CPF/CNPJ, Email, Telefone, Endereço\n• Segmento (para categorização)\n\n**Ações:**\n• ➕ Incluir: Botão "Incluir"\n• ✏️ Editar: Ícone do lápis ou clique na linha\n• 🗑️ Excluir: Ícone da lixeira\n\nOs fornecedores são usados nos lançamentos de Despesas e Custos.'
    },
    produtos: {
      keywords: ['produto', 'produtos', 'cadastro produto', 'serviço', 'servico'],
      answer: '📦 **Cadastro de Produtos/Serviços**\n\nAcesse: Cadastros → Produtos\n\n**Campos disponíveis:**\n• Nome, Descrição, Preço, Categoria\n• Fornecedor associado\n\n**Ações:**\n• ➕ Incluir: Botão "Incluir"\n• ✏️ Editar: Ícone do lápis\n• 🗑️ Excluir: Ícone da lixeira\n\nOs produtos podem ser usados para detalhar seus lançamentos.'
    },
    carteiras: {
      keywords: ['carteira', 'carteiras', 'banco', 'conta bancária', 'cartão', 'cartao', 'pix'],
      answer: '💳 **Cadastro de Carteiras**\n\nAcesse: Cadastros → Carteiras\n\n**Tipos de carteira:**\n• Banco (conta corrente, poupança)\n• Cartão de Crédito\n• Cripto\n\n**Campos:**\n• Nome da conta, Banco, Agência, Conta, Chave PIX\n\nAs carteiras são usadas nos lançamentos de Despesas e Receitas para identificar de onde sai ou entra o dinheiro.'
    },
    // Lançamentos
    despesas: {
      keywords: ['despesa', 'despesas', 'lançar despesa', 'pagar', 'pagamento', 'contas a pagar'],
      answer: '💸 **Lançamentos de Despesas**\n\nAcesse: Lançamentos → Despesas\n\n**Campos principais:**\n• Descrição, Valor, Data de Vencimento\n• Conta Contábil (classificação no DRE)\n• Fornecedor, Carteira, Centro de Custo\n• Status (Pendente, Pago, Cancelado)\n\n**Funcionalidades:**\n• ➕ Incluir: Botão "Incluir"\n• ✏️ Editar: Clique na linha ou ícone do lápis\n• 📋 Duplicar: Ícone de cópia (azul)\n• 🗑️ Excluir: Ícone da lixeira\n• 🔄 Copiar para X meses: Ao incluir, defina quantos meses replicar\n• Parcelas: Divida em X parcelas'
    },
    receitas: {
      keywords: ['receita', 'receitas', 'lançar receita', 'receber', 'recebimento', 'contas a receber', 'entrada'],
      answer: '💰 **Lançamentos de Receitas**\n\nAcesse: Lançamentos → Receitas\n\n**Campos principais:**\n• Descrição, Valor, Data de Vencimento\n• Conta Contábil (classificação no DRE)\n• Cliente, Carteira\n• Status (Pendente, Recebido, Cancelado)\n\n**Funcionalidades:**\n• ➕ Incluir: Botão "Incluir"\n• ✏️ Editar: Clique na linha ou ícone do lápis\n• 📋 Duplicar: Ícone de cópia (azul)\n• 🗑️ Excluir: Ícone da lixeira\n• 🔄 Copiar para X meses: Ao incluir, defina quantos meses replicar'
    },
    custos: {
      keywords: ['custo', 'custos', 'lançar custo', 'custo fixo', 'custo variável'],
      answer: '📉 **Lançamentos de Custos**\n\nAcesse: Lançamentos → Custos\n\nCustos são despesas diretamente ligadas à produção/prestação de serviços.\n\n**Campos principais:**\n• Descrição, Valor, Data de Vencimento\n• Conta Contábil (classificação no DRE)\n• Fornecedor, Centro de Custo\n• Status (Pendente, Pago, Cancelado)\n\n**No DRE:**\nCustos são subtraídos das Receitas para calcular o Lucro Bruto.'
    },
    // Relatórios
    projecaocaixa: {
      keywords: ['projeção', 'projecao', 'projeção de caixa', 'projecao de caixa', 'projeção 360', '360', '360 dias', 'livro caixa', 'saldo anterior', 'saldo acumulado', 'previsão', 'previsao', 'realizado'],
      answer: '📈 **Projeção de Caixa 360**\n\nAcesse: Relatórios → Projeção de Caixa\n\nRelatório completo que consolida Entradas e Saídas com visão de **Previsão** e **Realizado**.\n\n**Estrutura:**\n• **Entradas (A)**: Todas as receitas\n• **Saídas (B+C)**: Despesas + Custos\n• **Saldo do Período (D)**: A - B - C\n• **Saldo Anterior (E)**: Acumulado de períodos anteriores\n• **Saldo Acumulado (F)**: D + E\n• **Saldo Final (G)**: Previsão + Realizado (prova real)\n\n**Filtros:**\n• Ano e meses (seleção múltipla horizontal)\n• Botão "Todos" para selecionar todos os meses\n\n**Gráfico:**\n• Evolução do saldo com barras (receitas, despesas, custos) e linha (saldo acumulado)\n\n**Exportar:** Botão PDF para impressão\n\n**Disponível:** Plano Advanced ou superior'
    },
    dre: {
      keywords: ['dre', 'demonstrativo', 'resultado', 'lucro', 'prejuízo', 'prejuizo'],
      answer: '📊 **Relatório DRE**\n\nAcesse: Relatórios → DRE\n\nO DRE (Demonstrativo de Resultado do Exercício) mostra:\n\n**Estrutura:**\n• (+) Receitas\n• (-) Custos\n• (=) Lucro Bruto\n• (-) Despesas\n• (=) Resultado Líquido\n\n**Filtros:**\n• Ano: Selecione o ano\n• Meses: Selecione um ou mais meses (botões horizontais)\n• Botão "Todos": Seleciona todos os meses\n\n**Exportar:**\n• Gráfico: Visualização em pizza/barras\n• PDF: Exporta para impressão'
    },
    fluxocaixa: {
      keywords: ['fluxo', 'fluxo de caixa', 'cash flow', 'entradas', 'saídas', 'saidas'],
      answer: '💹 **Relatório de Fluxo de Caixa**\n\nAcesse: Relatórios → Fluxo de Caixa\n\nMostra a movimentação financeira:\n• Entradas (Receitas recebidas)\n• Saídas (Despesas e Custos pagos)\n• Saldo do período\n\n**Filtros:**\n• Selecione o ano e os meses desejados\n• Pode selecionar múltiplos meses\n\n**Exportar:** Botão PDF para impressão'
    },
    contaspagar: {
      keywords: ['contas a pagar', 'pagar', 'vencimento', 'vencidas', 'a vencer'],
      answer: '📋 **Relatório de Contas a Pagar**\n\nAcesse: Relatórios → Contas a Pagar\n\nLista todas as despesas e custos pendentes:\n• Vencidas (em atraso)\n• A vencer\n\n**Filtros:**\n• Ano e meses (seleção múltipla)\n• Filtro por status\n\n**Ações:**\n• Alterar status diretamente na lista\n• Exportar para PDF'
    },
    contasreceber: {
      keywords: ['contas a receber', 'receber', 'recebíveis', 'recebiveis'],
      answer: '📋 **Relatório de Contas a Receber**\n\nAcesse: Relatórios → Contas a Receber\n\nLista todas as receitas pendentes:\n• Vencidas (em atraso)\n• A vencer\n\n**Filtros:**\n• Ano e meses (seleção múltipla)\n• Filtro por status\n\n**Ações:**\n• Alterar status diretamente na lista\n• Exportar para PDF'
    },
    // Planos
    planos: {
      keywords: ['plano', 'planos', 'assinatura', 'básico', 'basico', 'advanced', 'contábil', 'contabil', 'preço', 'preco', 'valor'],
      answer: '💎 **Planos DRE+**\n\n**Básico** - R$ 19,90/mês\n• 2 empresas (1 pessoal + 1)\n• Funcionalidades essenciais\n\n**Advanced** - R$ 39,90/mês\n• 4 empresas\n• Relatórios avançados\n\n**Contábil** - R$ 99,90/mês\n• 6 empresas\n• Recursos completos\n\n**Para alterar plano:**\nEntre em contato com nosso suporte ou acesse a área do cliente.'
    },
    // Perfil
    perfil: {
      keywords: ['perfil', 'meus dados', 'alterar senha', 'email', 'telefone', 'cpf', 'cnpj'],
      answer: '👤 **Perfil do Usuário**\n\nAcesse: Menu do usuário (canto superior direito) → Perfil\n\n**Dados editáveis:**\n• Nome, Email, Telefone\n• CPF/CNPJ\n• Endereço completo\n• Chave PIX\n\n**Informações do plano:**\n• Plano atual\n• Data de expiração'
    },
    // Logs
    logs: {
      keywords: ['log', 'logs', 'histórico', 'historico', 'auditoria', 'rastreamento'],
      answer: '📝 **Logs de Auditoria**\n\nAcesse: Relatórios → Logs\n\nRegistra todas as ações realizadas:\n• Inclusões, Alterações, Exclusões\n• Cadastros e Lançamentos\n• Data/hora de cada ação\n\n**Filtros:**\n• Por tipo de operação\n• Por período\n• Por empresa'
    },
    // Geral
    instalacao: {
      keywords: ['instalar', 'instalação', 'instalacao', 'app', 'celular', 'mobile', 'pwa'],
      answer: '📱 **Instalar o App**\n\nO DRE+ é um PWA (Progressive Web App):\n\n**No celular:**\n1. Acesse www.dremais.com.br\n2. Faça login\n3. Clique em "Instalar App" no menu Suporte\n4. Ou use "Adicionar à tela inicial" do navegador\n\n**Vantagens:**\n• Acesso rápido pelo ícone\n• Funciona offline (parcialmente)\n• Notificações'
    }
  };

  // Find answer based on keywords
  const findAnswer = (question) => {
    const questionLower = question.toLowerCase().normalize('NFD').replace(/[\u0300-\u036f]/g, '');
    
    // Check for exit commands
    if (questionLower.includes('sair') || questionLower.includes('encerrar') || questionLower.includes('tchau') || questionLower.includes('bye')) {
      return { type: 'exit', text: '👋 Obrigado por usar o Chat de Ajuda do DRE+!\n\nSe ainda tiver dúvidas, pode voltar a qualquer momento.\n\nBons negócios!' };
    }
    
    // Check for human support request
    if (questionLower.includes('atendente') || questionLower.includes('humano') || questionLower.includes('pessoa') || 
        questionLower.includes('falar com') || questionLower.includes('suporte') || questionLower.includes('atendimento')) {
      return { type: 'human', text: '📧 **Atendimento Humanizado**\n\nPara falar com nossa equipe de suporte, entre em contato por email.\n\nClique no botão abaixo para abrir seu email com os dados preenchidos:' };
    }
    
    // Search in knowledge base
    for (const [key, entry] of Object.entries(knowledgeBase)) {
      for (const keyword of entry.keywords) {
        if (questionLower.includes(keyword.toLowerCase())) {
          return { type: 'answer', text: entry.answer };
        }
      }
    }
    
    // No answer found
    return { 
      type: 'unknown', 
      text: '🤔 Desculpe, não encontrei uma resposta específica para sua pergunta.\n\nVocê pode:\n• Reformular a pergunta usando palavras-chave\n• Perguntar sobre: Dashboard, Empresas, Cadastros, Lançamentos, Relatórios, DRE, Planos\n• Digitar "atendente" para falar com nossa equipe'
    };
  };

  // Open email client with pre-filled data
  const openEmailSupport = () => {
    const userName = user?.name || 'Cliente';
    const userCpf = user?.cpf_cnpj || user?.cpf || '';
    const companyName = selectedCompany?.razao_social || 'Conta Principal';
    const companyCnpj = selectedCompany?.cnpj || '';
    
    // Build conversation transcript
    const transcript = messages
      .map(m => `${m.type === 'bot' ? 'Assistente' : 'Você'}: ${m.text.replace(/\n/g, ' ').replace(/\*/g, '')}`)
      .join('\n\n');
    
    const subject = encodeURIComponent(`DRE+ - Dúvida de uso do cliente ${userName}`);
    const body = encodeURIComponent(
`Olá ${userName},
Descreva aqui a sua dúvida:




------------------------------------
${userName}
${companyName}
${userCpf || companyCnpj ? (userCpf || companyCnpj) : ''}

------------------------------------
Transcrição da conversa:

${transcript}
`
    );
    
    window.location.href = `mailto:suporte@dremais.com.br?subject=${subject}&body=${body}`;
    toast({ title: 'Abrindo seu cliente de email...' });
  };

  // Handle send message
  const handleSend = () => {
    if (!inputValue.trim()) return;
    
    const userMessage = { id: Date.now(), type: 'user', text: inputValue };
    setMessages(prev => [...prev, userMessage]);
    setInputValue('');
    setIsTyping(true);
    
    // Simulate typing delay
    setTimeout(() => {
      const response = findAnswer(inputValue);
      const botMessage = { 
        id: Date.now() + 1, 
        type: 'bot', 
        text: response.text,
        action: response.type === 'human' ? 'email' : (response.type === 'exit' ? 'exit' : null)
      };
      setMessages(prev => [...prev, botMessage]);
      setIsTyping(false);
    }, 800);
  };

  // Handle key press
  const handleKeyPress = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSend();
    }
  };

  return (
    <div className="animate-fadeIn h-[calc(100vh-180px)] flex flex-col" data-testid="help-chat-page">
      <div className="mb-4">
        <h2 className="text-3xl font-semibold tracking-tight flex items-center gap-2">
          <MessageCircle className="w-8 h-8" />
          Chat de Ajuda
        </h2>
        <p className="text-muted-foreground">Tire suas dúvidas sobre o DRE+</p>
      </div>

      {/* Chat messages */}
      <Card className="flex-1 flex flex-col overflow-hidden">
        <CardContent className="flex-1 overflow-y-auto p-4 space-y-4">
          {messages.map((message) => (
            <div key={message.id} className={`flex ${message.type === 'user' ? 'justify-end' : 'justify-start'}`}>
              <div className={`max-w-[80%] rounded-lg px-4 py-2 ${
                message.type === 'user' 
                  ? 'bg-primary text-primary-foreground' 
                  : 'bg-muted'
              }`}>
                <p className="text-sm whitespace-pre-wrap">{message.text}</p>
                {message.action === 'email' && (
                  <Button 
                    onClick={openEmailSupport} 
                    className="mt-3 w-full"
                    size="sm"
                  >
                    <Send className="w-4 h-4 mr-2" />
                    Enviar Email para Suporte
                  </Button>
                )}
              </div>
            </div>
          ))}
          {isTyping && (
            <div className="flex justify-start">
              <div className="bg-muted rounded-lg px-4 py-2">
                <div className="flex gap-1">
                  <span className="w-2 h-2 bg-muted-foreground rounded-full animate-bounce" style={{ animationDelay: '0ms' }}></span>
                  <span className="w-2 h-2 bg-muted-foreground rounded-full animate-bounce" style={{ animationDelay: '150ms' }}></span>
                  <span className="w-2 h-2 bg-muted-foreground rounded-full animate-bounce" style={{ animationDelay: '300ms' }}></span>
                </div>
              </div>
            </div>
          )}
          <div ref={messagesEndRef} />
        </CardContent>
        
        {/* Input area */}
        <div className="border-t p-4">
          <div className="flex gap-2">
            <Input
              value={inputValue}
              onChange={(e) => setInputValue(e.target.value)}
              onKeyPress={handleKeyPress}
              placeholder="Digite sua pergunta..."
              className="flex-1"
              data-testid="help-chat-input"
            />
            <Button onClick={handleSend} disabled={!inputValue.trim()} data-testid="help-chat-send">
              <Send className="w-4 h-4" />
            </Button>
          </div>
          <p className="text-xs text-muted-foreground mt-2">
            Dica: Pergunte sobre Dashboard, Cadastros, Lançamentos, Relatórios, DRE ou digite "atendente" para suporte humano.
          </p>
        </div>
      </Card>
    </div>
  );
};

function AppRouter() {
  const location = useLocation();
  if (location.hash?.includes('session_id=')) { return <AuthCallback />; }
  return (
    <Routes>
      {/* Landing Page */}
      <Route path="/site" element={<LandingPage />} />
      
      {/* Legal Pages */}
      <Route path="/termos" element={<TermosDeUsoPage />} />
      <Route path="/privacidade" element={<PoliticaPrivacidadePage />} />
      
      {/* Auth Pages */}
      <Route path="/forgot-password" element={<ForgotPasswordPage />} />
      <Route path="/reset-password" element={<ResetPasswordPage />} />
      <Route path="/simulate" element={<SimulateLoginPage />} />
      
      {/* Main App Routes */}
      <Route path="/login" element={<LoginPage />} />
      <Route path="/dashboard" element={<ProtectedRoute>{({ user }) => <MainLayout user={user} />}</ProtectedRoute>} />
      <Route path="/caixa" element={<ProtectedRoute>{({ user }) => <MainLayout user={user} initialPage="frente-caixa" />}</ProtectedRoute>} />
      
      {/* Admin Routes */}
      <Route path="/admin" element={<AdminLoginPage />} />
      <Route path="/admin/dashboard" element={<AdminProtectedRoute>{({ admin }) => <AdminMainLayout admin={admin} />}</AdminProtectedRoute>} />
      
      {/* Default redirect */}
      <Route path="/" element={<Navigate to="/dashboard" replace />} />
    </Routes>
  );
}

function App() {
  return (
    <ThemeProvider>
      <div className="App">
        <BrowserRouter>
          <AppRouter />
          <CookieConsentBanner />
        </BrowserRouter>
        <Toaster />
      </div>
    </ThemeProvider>
  );
}

export default App;
